import { useState, useReducer, useEffect, useRef, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import { createClient } from "@supabase/supabase-js";

// ─── APP VERSIE ─── elke sessie bijgewerkt door Claude ──────
// Bevat: #1 rol-opslaan, #2 delete-bevestigingen, #3 audit-log, #4 AVG park-toegang
const APP_VERSION = "2025-04-15-v3";
console.log("🐾 Dierenparkje App versie:", APP_VERSION);
// ────────────────────────────────────────────────────────────

/* ═══════════════════════════════════════════════════════════
   TOAST SYSTEM
   ═══════════════════════════════════════════════════════════ */
let _toastEmit = null;
const toast = {
  show(msg, type = 'success', duration = 3500) { _toastEmit?.({ msg, type, duration }); },
  ok(msg)   { toast.show(msg, 'success'); },
  err(msg)  { toast.show(msg, 'error'); },
  warn(msg) { toast.show(msg, 'warn'); },
  info(msg) { toast.show(msg, 'info'); },
  confirm(msg, onYes) { _toastEmit?.({ msg, type: 'confirm', onYes }); },
};

const getQrImageUrl=(url,size=320)=>`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`;

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    _toastEmit = ({ msg, type, duration = 3500, onYes }) => {
      const id = Date.now() + Math.random();
      setToasts(prev => [...prev.slice(-4), { id, msg, type, onYes }]);
      if (type !== 'confirm') {
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
      }
    };
    return () => { _toastEmit = null; };
  }, []);
  const dismiss = id => setToasts(prev => prev.filter(t => t.id !== id));
  const ICONS = { success: '✅', error: '❌', warn: '⚠️', info: 'ℹ️', confirm: '❓' };
  return <>
    {children}
    <div className="toast-ctr">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span className="toast-icon">{ICONS[t.type]}</span>
          <span className="toast-msg">{t.msg}</span>
          {t.type === 'confirm'
            ? <div className="toast-btns">
                <button className="toast-yes" onClick={() => { t.onYes?.(); dismiss(t.id); }}>Ja</button>
                <button className="toast-no"  onClick={() => dismiss(t.id)}>Nee</button>
              </div>
            : <button className="toast-x" onClick={() => dismiss(t.id)}>×</button>
          }
        </div>
      ))}
    </div>
  </>;
}

/* ═══════════════════════════════════════════════════════════
   EMPTY STATE COMPONENT
   ═══════════════════════════════════════════════════════════ */
function EmptyState({ icon, title, sub, action, onAction }) {
  return <div className="empty-state">
    <div className="empty-icon">{icon}</div>
    <div className="empty-title">{title}</div>
    {sub && <div className="empty-sub">{sub}</div>}
    {action && <button className="fbtn" style={{ marginTop: 16 }} onClick={onAction}>{action}</button>}
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   PAW BUTTON — loading state with paw spinner
   ═══════════════════════════════════════════════════════════ */
function PawBtn({ loading, onClick, children, className = "fbtn", type = "button", disabled, style }) {
  return <button
    type={type}
    className={`${className}${loading ? ' paw-loading' : ''}`}
    onClick={onClick}
    disabled={loading || disabled}
    style={style}
  >
    {loading ? <><span className="paw-spin">🐾</span> Bezig...</> : children}
  </button>;
}

/* ═══════════════════════════════════════════════════════════
   SUPABASE CLIENT
   ═══════════════════════════════════════════════════════════ */
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://rbbmfbycrjwcalxaydxh.supabase.co";
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJiYm1mYnljcmp3Y2FseGF5ZHhoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE2MDQ2ODYsImV4cCI6MjA4NzE4MDY4Nn0.BbkJcGLksnYFf3MtZqsj-Sv6c7_f0UVt1hMwODTWMkw";
// Simpele in-memory lock als navigator.locks niet beschikbaar is
const _locks = {};
const simpleLock = async (name, timeout, fn) => {
  if (_locks[name]) {
    // Wacht tot vorige klaar is (max 5 sec)
    const start = Date.now();
    while (_locks[name] && Date.now() - start < 5000) {
      await new Promise(r => setTimeout(r, 50));
    }
  }
  _locks[name] = true;
  try { return await fn(); } finally { _locks[name] = false; }
};

const sb = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    detectSessionInUrl: true,
    flowType: "implicit",
    lock: simpleLock,
  }
});

const db = {
  getParken: async () => { const {data} = await sb.from("parken").select("*").eq("actief",true); return data||[]; },
  getAllParken: async () => { const {data} = await sb.from("parken").select("*").order("naam"); return data||[]; },
  addPark: async (park) => {
    const {data} = await sb.from("parken").insert(park).select().single();
    return data;
  },
  updatePark: async (id, data) => {
    const {error} = await sb.from("parken").update(data).eq("id", id);
    if(error) throw error;
    return {id, ...data};
  },
  getGebruikers: async (parkId) => { const {data} = await sb.from("gebruikers").select("*").eq("park_id",parkId); return data||[]; },
  getGebruikersMetStatus: async (parkId) => { const {data} = await sb.rpc("get_gebruikers_met_status",{p_park_id:parkId}); return data||[]; },
  addGebruiker: async (g) => { const {data} = await sb.from("gebruikers").insert(g).select().single(); return data; },
  addAuditLog: async (entry) => { await sb.from("park_audit_log").insert(entry); },
  updateGebruiker: async (id,g) => { const {error}=await sb.from("gebruikers").update(g).eq("id",id); if(error) throw error; },
  deleteGebruiker: async (id) => { await sb.from("gebruikers").delete().eq("id",id); },
  getDieren: async (parkId) => { const {data} = await sb.from("dieren").select("*").eq("park_id",parkId); return data||[]; },
  getDierenadvies: async (parkId) => {
    try {
      const {data, error} = await sb.from("dierenadvies").select("*").eq("park_id", parkId).order("prioriteit", {ascending:false}).order("created_at", {ascending:false});
      if (error) {
        console.warn("dierenadvies load warning:", error.message || error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.warn("dierenadvies unavailable:", err?.message || err);
      return [];
    }
  },
  addDierenadvies: async (advies) => {
    const {data, error} = await sb.from("dierenadvies").insert(advies).select().single();
    if (error) throw error;
    return data;
  },
  updateDierenadvies: async (id, advies) => {
    const {data, error} = await sb.from("dierenadvies").update(advies).eq("id", id).select().single();
    if (error) throw error;
    return data;
  },
  deleteDierenadvies: async (id) => {
    const {error} = await sb.from("dierenadvies").delete().eq("id", id);
    if (error) throw error;
  },
  getDierInfo: async ({dierId, soortId, rasId, parkId}) => {
    const filters = [];
    if(dierId) filters.push(`dier_id.eq.${dierId}`);
    if(soortId) filters.push(`soort_id.eq.${soortId}`);
    if(rasId) filters.push(`ras_id.eq.${rasId}`);
    if(!filters.length) return [];
    const {data,error} = await sb.from("dier_info").select("*").or(filters.join(","));
    if(error) throw error;
    return (data||[]).filter(r => r.park_id===null || r.park_id===parkId);
  },
  upsertDierInfo: async (record) => {
    let query = sb.from("dier_info").select("id")
      .eq("categorie", record.categorie)
      .eq("vraag", record.vraag);
    if(record.dier_id) query = query.eq("dier_id", record.dier_id);
    else if(record.ras_id) query = query.eq("ras_id", record.ras_id).is("dier_id", null);
    else query = query.eq("soort_id", record.soort_id).is("dier_id", null).is("ras_id", null);
    if(record.park_id) query = query.eq("park_id", record.park_id);
    else query = query.is("park_id", null);
    const {data: rows} = await query.limit(1);
    const existing = rows?.[0];
    if(existing?.id){
      const {data, error} = await sb.from("dier_info").update({tekst: record.tekst}).eq("id", existing.id).select().single();
      if(error) throw error;
      return data;
    } else {
      const {data, error} = await sb.from("dier_info").insert(record).select().single();
      if(error) throw error;
      return data;
    }
  },
  deleteDierInfo: async (id) => {
    const {error} = await sb.from("dier_info").delete().eq("id",id);
    if(error) throw error;
  },
  addDier: async (d) => { 
    console.log("📊 addDier called with:", d);
    try {
      const {data, error} = await sb.from("dieren").insert(d).select().single(); 
      if(error) {
        console.error("❌ addDier error:", error);
        throw error;
      }
      console.log("✅ addDier success, returned:", data);
      return data;
    } catch(err) {
      console.error("❌ addDier exception:", err);
      throw err;
    }
  },
  updateDier: async (id,d) => { const {data} = await sb.from("dieren").update(d).eq("id",id).select().single(); return data; },
  deleteDier: async (id) => { await sb.from("dieren").delete().eq("id",id); },
  getVoeding: async (parkId) => { const {data} = await sb.from("voeding").select("*").eq("park_id",parkId).order("created_at",{ascending:false}); return data||[]; },
  addVoeding: async (v) => { const {data} = await sb.from("voeding").insert(v).select().single(); return data; },
  updateVoeding: async (id,v) => { await sb.from("voeding").update(v).eq("id",id); },
  deleteVoeding: async (id) => { await sb.from("voeding").delete().eq("id",id); },
  // Voeding-dieren koppelingen (maaltijdpakketten)
  getVoedingDieren: async (parkId) => { const {data} = await sb.from("voeding_dieren").select("*").eq("park_id",parkId); return data||[]; },
  addVoedingDier: async (v) => { const {data} = await sb.from("voeding_dieren").insert(v).select().single(); return data; },
  updateVoedingDier: async (id,v) => { await sb.from("voeding_dieren").update(v).eq("id",id); },
  deleteVoedingDier: async (id) => { await sb.from("voeding_dieren").delete().eq("id",id); },
  // Maaltijd checks (dagelijkse afvinken per pakket)
  getMaaltijdChecks: async (parkId, datum) => { const {data} = await sb.from("maaltijd_check").select("*").eq("park_id",parkId).eq("datum",datum); return data||[]; },
  upsertMaaltijdCheck: async (check) => { const {data} = await sb.from("maaltijd_check").upsert(check, {onConflict:"park_id,group_key,dagdeel,datum"}).select().single(); return data; },

  // Leveranciers
  getLeveranciers: async (parkId) => { 
    try {
      const {data, error} = await sb.from("leveranciers").select("*").eq("park_id",parkId).order("naam");
      if(error) {
        console.error("❌ getLeveranciers error:", error);
        throw error;
      }
      console.log("✅ getLeveranciers success:", data);
      return data||[];
    } catch(err) {
      console.error("❌ getLeveranciers exception:", err);
      return [];
    }
  },
  addLeverancier: async (l) => { 
    try {
      console.log("📝 addLeverancier called with:", l);
      const {data, error} = await sb.from("leveranciers").insert(l).select().single();
      if(error) {
        console.error("❌ addLeverancier error:", error);
        throw error;
      }
      console.log("✅ addLeverancier success:", data);
      return data;
    } catch(err) {
      console.error("❌ addLeverancier exception:", err);
      throw err;
    }
  },
  updateLeverancier: async (id,l) => { await sb.from("leveranciers").update(l).eq("id",id); },
  deleteLeverancier: async (id) => { await sb.from("leveranciers").delete().eq("id",id); },

  // Sleutels (Keys)
  getSleutels: async (parkId) => { const {data} = await sb.from("sleutels").select("*").eq("park_id",parkId).order("omschrijving"); return data||[]; },
  addSleutel: async (s) => { const {data} = await sb.from("sleutels").insert(s).select().single(); return data; },
  updateSleutel: async (id,s) => { await sb.from("sleutels").update(s).eq("id",id); },
  deleteSleutel: async (id) => { await sb.from("sleutels").delete().eq("id",id); },
  issueSleutelToUser: async (params) => { return await sb.rpc('issue_sleutel_to_user', params); },
  issueSleutelToContact: async (params) => { return await sb.rpc('issue_sleutel_to_contact', params); },
  issueSleutelToPark: async (params) => { return await sb.rpc('issue_sleutel_to_park', params); },
  returnSleutel: async (params) => { return await sb.rpc('return_sleutel', params); },
  getSleutelInBezit: async (parkId, sleutelId) => { const {data} = await sb.from("sleutel_in_bezit").select("*").eq("park_id",parkId).eq("sleutel_id",sleutelId).is("datum_teruggebracht",null); return data||[]; },
  getSleutelInBezitByUser: async (parkId, sleutelId, gebruikerId) => { const {data} = await sb.from("sleutel_in_bezit").select("id").eq("park_id",parkId).eq("sleutel_id",sleutelId).eq("gebruiker_id",gebruikerId).is("datum_teruggebracht",null).single(); return data; },
  getSleutelInBezitByContact: async (parkId, sleutelId, contactId) => { const {data} = await sb.from("sleutel_in_bezit").select("id").eq("park_id",parkId).eq("sleutel_id",sleutelId).eq("contact_id",contactId).is("datum_teruggebracht",null).single(); return data; },
  getSleutelInBezitByPark: async (parkId, sleutelId) => { const {data} = await sb.from("sleutel_in_bezit").select("id").eq("park_id",parkId).eq("sleutel_id",sleutelId).eq("op_park",true).is("datum_teruggebracht",null).single(); return data; },

  // Functiegroepen
  getFunctiegroepen: async () => { const {data} = await sb.from("functiegroepen").select("*").order("volgorde"); return data||[]; },
  getFunctiegroepIndeling: async (parkId) => { const {data} = await sb.from("functiegroep_indeling").select("*, functiegroepen(naam, kleur)").eq("park_id",parkId); return data||[]; },
  getContactCategorieen: async () => { const {data} = await sb.from("contact_categorie").select("*").order("categorie"); return data||[]; },
  addToFunctiegroep: async (functiegroepId, parkId, gebruikerId, contactId) => { 
    const {data} = await sb.from("functiegroep_indeling").insert({functiegroep_id: functiegroepId, park_id: parkId, gebruiker_id: gebruikerId, contact_id: contactId}).select().single();
    return data;
  },
  removeFromFunctiegroep: async (indelingId) => { await sb.from("functiegroep_indeling").delete().eq("id", indelingId); },

  // Bestellingen
  getBestellingen: async (parkId) => { const {data} = await sb.from("bestellingen").select("*").eq("park_id",parkId).order("datum",{ascending:false}); return data||[]; },
  addBestelling: async (b) => { const {data} = await sb.from("bestellingen").insert(b).select().single(); return data; },
  updateBestelling: async (id,b) => { await sb.from("bestellingen").update(b).eq("id",id); },
  deleteBestelling: async (id) => { await sb.from("bestellingen").delete().eq("id",id); },

  getMedicatie: async () => { const {data} = await sb.from("medicatie").select("*").order("medicijn"); return data||[]; },
  addMedicatie: async (m) => { const {data} = await sb.from("medicatie").insert(m).select().single(); return data; },
  updateMedicatie: async (id,m) => { await sb.from("medicatie").update(m).eq("id",id); },
  deleteMedicatie: async (id) => { await sb.from("medicatie").delete().eq("id",id); },
  getTaken: async (parkId) => { const {data} = await sb.from("taken").select("*").eq("park_id",parkId); return data||[]; },
  addTaak: async (t) => { const {data} = await sb.from("taken").insert(t).select().single(); return data; },
  updateTaak: async (id,t) => { await sb.from("taken").update(t).eq("id",id); },
  deleteTaak: async (id) => { await sb.from("taken").delete().eq("id",id); },
  // Taken toewijzingen
  getTakenToewijzingen: async (parkId) => { const {data} = await sb.from("taken_toewijzingen").select("*").eq("park_id",parkId); return data||[]; },
  addTaakToewijzing: async (t) => { const {data} = await sb.from("taken_toewijzingen").insert(t).select().single(); return data; },
  deleteTaakToewijzing: async (id) => { await sb.from("taken_toewijzingen").delete().eq("id",id); },
  deleteTaakToewijzingenByTaak: async (taakId) => { await sb.from("taken_toewijzingen").delete().eq("taak_id",taakId); },
  setTaakToewijzingen: async (taakId, parkId, gebruikerIds) => {
    await sb.from("taken_toewijzingen").delete().eq("taak_id",taakId);
    if(gebruikerIds.length>0){
      const rows=gebruikerIds.map(gid=>({taak_id:taakId,gebruiker_id:gid,park_id:parkId}));
      await sb.from("taken_toewijzingen").insert(rows);
    }
  },
  getAgenda: async (parkId) => { const {data} = await sb.from("agenda").select("*").eq("park_id",parkId).order("datum"); return data||[]; },
  addAgendaItem: async (a) => { const {data} = await sb.from("agenda").insert(a).select().single(); return data; },
  updateAgendaItem: async (id,a) => { await sb.from("agenda").update(a).eq("id",id); },
  deleteAgendaItem: async (id) => { await sb.from("agenda").delete().eq("id",id); },
  getBerichten: async (parkId) => { const {data} = await sb.from("berichten").select("*").eq("park_id",parkId).order("created_at",{ascending:false}); return data||[]; },
  addBericht: async (b) => { const {data} = await sb.from("berichten").insert(b).select().single(); return data; },
  deleteBericht: async (id) => { await sb.from("berichten").delete().eq("id",id); },
  // Direct messaging functions
  getBerichten_Directe: async (userId) => { const {data} = await sb.from("berichten_directe").select("*").or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`).order("created_at",{ascending:false}); return data||[]; },
  addBericht_Directe: async (b) => { const {data} = await sb.from("berichten_directe").insert(b).select().single(); return data; },
  deleteBericht_Directe: async (id) => { await sb.from("berichten_directe").delete().eq("id",id); },
  markAsRead: async (id) => { await sb.from("berichten_directe").update({gelezen:true,gelezen_op:new Date().toISOString()}).eq("id",id); },
  getContacten: async (parkId) => { const {data} = await sb.from("contacten").select("*").eq("park_id",parkId); return data||[]; },
  addContact: async (c) => { const {data} = await sb.from("contacten").insert(c).select().single(); return data; },
  updateContact: async (id,c) => { await sb.from("contacten").update(c).eq("id",id); },
  deleteContact: async (id) => { await sb.from("contacten").delete().eq("id",id); },
  getPlanning: async (parkId) => { const {data} = await sb.from("planning").select("*").eq("park_id",parkId); return data||[]; },
  addPlanning: async (p) => { const {data} = await sb.from("planning").insert(p).select().single(); return data; },
  deletePlanning: async (id) => { await sb.from("planning").delete().eq("id",id); },
  updatePlanning: async (id,p) => { await sb.from("planning").update(p).eq("id",id); },

  // Auth helpers
  signIn: async (email,password) => {
    const {data,error} = await sb.auth.signInWithPassword({email,password});
    return {data,error};
  },
  signOut: async () => { await sb.auth.signOut(); },
  getSignedPhotoUrl: async (filename) => {
    try {
      const { data, error } = await sb.storage
        .from("DierenBucket")
        .createSignedUrl(filename, 3600); // 1 hour validity
      if (error) throw error;
      return data?.signedUrl || null;
    } catch (err) {
      console.error("Error creating signed URL:", err);
      return null;
    }
  },
  getSession: async () => { const {data} = await sb.auth.getSession(); return data.session; },
  getToken: async () => { const session = await db.getSession(); return session?.access_token || null; },
  onAuthChange: (cb) => { const {data} = sb.auth.onAuthStateChange(cb); return data; },
  getUserByAuthId: async (authId) => {
    const {data} = await sb.from("gebruikers").select("*,parken(*)").eq("auth_id",authId).maybeSingle();
    return data;
  },
  getGebruikerModules: async (gebruikerId) => {
    const {data} = await sb.from("gebruiker_modules").select("*").eq("gebruiker_id",gebruikerId);
    return data||[];
  },
  saveGebruikerModule: async (gebruikerId, parkId, module, niveau) => {
    const {error} = await sb.from("gebruiker_modules")
      .upsert({gebruiker_id:gebruikerId, park_id:parkId, module, niveau}, {onConflict:"gebruiker_id,module"});
    if(error) throw error;
  },
  resetPassword: async (email) => {
    const {error} = await sb.auth.resetPasswordForEmail(email, {redirectTo: window.location.origin});
    return {error};
  },
  updatePassword: async (newPassword) => {
    const {error} = await sb.auth.updateUser({password: newPassword});
    return {error};
  },
  inviteUser: async (email) => {
    const {data,error} = await sb.auth.admin?.inviteUserByEmail?.(email) || {error:{message:"Gebruik Supabase dashboard voor uitnodigingen"}};
    return {data,error};
  },
  signUp: async (email,password) => {
    const {data,error} = await sb.auth.signUp({email,password,options:{emailRedirectTo:window.location.origin}});
    return {data,error};
  },

  // Vaste taken
  getVasteTaken: async (parkId) => { const {data} = await sb.from("vaste_taken").select("*").eq("park_id",parkId).eq("actief",true).order("volgorde"); return data||[]; },
  addVasteTaak: async (t) => { const {data} = await sb.from("vaste_taken").insert(t).select().single(); return data; },
  updateVasteTaak: async (id,t) => { await sb.from("vaste_taken").update(t).eq("id",id); },
  deleteVasteTaak: async (id) => { await sb.from("vaste_taken").update({actief:false}).eq("id",id); },

  // Vaste taak checks (afvinkingen per dag)
  getChecks: async (parkId, datum) => { const {data} = await sb.from("vaste_taak_check").select("*").eq("park_id",parkId).eq("datum",datum); return data||[]; },
  upsertCheck: async (check) => { const {data} = await sb.from("vaste_taak_check").upsert(check, {onConflict:"vaste_taak_id,datum,dagdeel"}).select().single(); return data; },

  // Taken checks (dagelijkse afvinken voor weekplanning taken)
  getTakenChecks: async (parkId, datum) => { const {data} = await sb.from("taken_check").select("*").eq("park_id",parkId).eq("datum",datum); return data||[]; },
  upsertTaakCheck: async (check) => { const {data} = await sb.from("taken_check").upsert(check, {onConflict:"taak_id,datum"}).select().single(); return data; },

  // Dienst logboek
  getLogboek: async (parkId, datum) => { const {data} = await sb.from("dienst_logboek").select("*").eq("park_id",parkId).eq("datum",datum).order("created_at"); return data||[]; },
  getLogboekAll: async (parkId) => { const {data} = await sb.from("dienst_logboek").select("*").eq("park_id",parkId).order("created_at",{ascending:false}).limit(50); return data||[]; },
  addLogboek: async (entry) => { const {data} = await sb.from("dienst_logboek").insert(entry).select().single(); return data; },
  delLogboek: async (id) => { await sb.from("dienst_logboek").delete().eq("id",id); },

  // Waarschuwingen
  getWaarschuwingen: async (parkId) => { const {data} = await sb.from("waarschuwingen").select("*").eq("park_id",parkId).eq("actief",true).order("created_at",{ascending:false}); return data||[]; },
  addWaarschuwing: async (w) => { const {data} = await sb.from("waarschuwingen").insert(w).select().single(); return data; },
  deactiveerWaarschuwing: async (id) => { await sb.from("waarschuwingen").update({actief:false}).eq("id",id); },
  // Protocollen
  getProtocollen: async (parkId) => { const {data} = await sb.from("protocollen").select("*").eq("park_id",parkId).order("created_at",{ascending:false}); return data||[]; },
  addProtocol: async (p) => { const {data} = await sb.from("protocollen").insert(p).select().single(); return data; },
  updateProtocol: async (id,p) => { const {data} = await sb.from("protocollen").update(p).eq("id",id).select().single(); return data; },
  deleteProtocol: async (id) => { await sb.from("protocollen").delete().eq("id",id); },
  getRassen: async () => { const {data} = await sb.from("rassen").select("*").order("ras_naam"); return data||[]; },
  addRas: async (r) => { const {data} = await sb.from("rassen").insert(r).select().single(); return data; },
  updateRas: async (id,r) => { const {data} = await sb.from("rassen").update(r).eq("id",id).select().single(); return data; },
  deleteRas: async (id) => { await sb.from("rassen").delete().eq("id",id); },

  // Gezondheid (Health Records)
  getGezondheid: async (parkId) => { 
    const {data} = await sb.from("gezondheid").select("*").eq("park_id",parkId).order("datum",{ascending:false}); 
    return data||[]; 
  },
  addGezondheid: async (g) => { 
    const {data, error} = await sb.from("gezondheid").insert(g).select("*").single(); 
    if (error) throw error;
    return data; 
  },
  updateGezondheid: async (id, g) => { 
    const {data, error} = await sb.from("gezondheid").update(g).eq("id",id).select("*").single(); 
    if (error) throw error;
    return data; 
  },
  deleteGezondheid: async (id) => { 
    await sb.from("gezondheid").delete().eq("id",id); 
  },
  addGezondheidsMedicatie: async (gezondheid_id, medicatie_id, dosering, freq, eind) => { 
    const {data} = await sb.from("gezondheid_medicatie").insert({gezondheid_id, medicatie_id, dosering, freq, eind}).select().single(); 
    return data; 
  },
  deleteGezondheidsMedicatie: async (gezondheid_id, medicatie_id) => { 
    await sb.from("gezondheid_medicatie").delete().eq("gezondheid_id",gezondheid_id).eq("medicatie_id",medicatie_id); 
  },
  getMedicatieGegeven: async (gezondheid_medicatie_id) => {
    const {data} = await sb.from("medicatie_gegeven").select("*,gebruikers(roepnaam,naam)").eq("gezondheid_medicatie_id",gezondheid_medicatie_id).order("gegeven_op",{ascending:false});
    return data||[];
  },
  addMedicatieGegeven: async (gezondheid_medicatie_id, gebruiker_id) => {
    const now = new Date();
    const h = now.getHours();
    const dagdeel = h<12?"ochtend":h<18?"middag":"avond";
    const {data} = await sb.from("medicatie_gegeven").insert({gezondheid_medicatie_id,gebruiker_id,gegeven_op:now.toISOString(),dagdeel}).select("*,gebruikers(roepnaam,naam)").single();
    return data;
  },
  deleteMedicatieGegeven: async (id) => {
    await sb.from("medicatie_gegeven").delete().eq("id",id);
  },
  getGezondheidsMedicaties: async (gezondheid_id) => { 
    const {data} = await sb.from("gezondheid_medicatie").select("*,medicatie(*)").eq("gezondheid_id",gezondheid_id); 
    return data||[]; 
  },
  getAllGezondheidMedicatie: async (parkId) => {
    // First get all gezondheid ids for this park, then fetch their medicatie
    const {data: gez} = await sb.from("gezondheid").select("id").eq("park_id", parkId);
    if(!gez || gez.length === 0) return [];
    const ids = gez.map(g => g.id);
    const {data} = await sb.from("gezondheid_medicatie")
      .select("*,medicatie(*)")
      .in("gezondheid_id", ids);
    return data||[];
  },

  // Bezoeken (Visitor tracking)
  getBezoeken: async (bezoekerID) => {
    const {data} = await sb.from("bezoeken").select("*,parken(naam,plaats,lat,lng)").eq("bezoeker_id", bezoekerID).order("datum", {ascending: false});
    return data || [];
  },
  addBezoek: async (bezoek) => {
    const {data} = await sb.from("bezoeken").insert(bezoek).select().single();
    return data;
  },
  updateBezoek: async (id, bezoek) => {
    const numId = Number(id); // Ensure it's a number
    const {data, error} = await sb.from("bezoeken").update(bezoek).match({id: numId}).select().single();
    if (error) console.error("updateBezoek error:", error);
    return data;
  },
  deleteBezoek: async (id) => {
    await sb.from("bezoeken").delete().eq("id", id);
  },
  
  // Media functions
  getMediaByPark: async (parkId) => {
    const {data} = await sb.from("park_media").select("*").eq("park_id", parkId).order("created_at", {ascending: false});
    return data || [];
  },
  uploadMedia: async (parkId, file, fileType, userId) => {
    // Upload to ParkMedia bucket using direct HTTP
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${parkId}/${Date.now()}_${safeName}`;
    
    console.log("📤 Uploading to:", fileName);
    
    try {
      // Get auth token
      const {data: {session}} = await sb.auth.getSession();
      const token = session?.access_token;
      
      // Direct HTTP upload
      const response = await fetch(
        `https://rbbmfbycrjwcalxaydxh.supabase.co/storage/v1/object/ParkMedia/${fileName}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        }
      );
      
      if (!response.ok) {
        const err = await response.text();
        console.error("Upload failed:", response.status, err);
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      const fileUrl = `https://rbbmfbycrjwcalxaydxh.supabase.co/storage/v1/object/public/ParkMedia/${fileName}`;
      console.log("✅ File uploaded to:", fileUrl);
      
      const {data: mediaRecord} = await sb.from("park_media").insert({
        park_id: parkId,
        file_name: file.name,
        file_url: fileUrl,
        file_type: fileType,
        mime_type: file.type,
        file_size: file.size,
        uploaded_by: userId
      }).select().single();
      
      return mediaRecord;
    } catch (err) {
      console.error("Upload error:", err);
      throw err;
    }
  },
  deleteMedia: async (mediaId) => {
    // First get the media record to get the file path
    const {data: media} = await sb.from("park_media").select("file_url").eq("id", mediaId).single();
    
    if (media && media.file_url) {
      // Extract file path from URL
      // URL format: https://...supabase.co/storage/v1/object/public/ParkMedia/{parkId}/{filename}
      const urlParts = media.file_url.split("/ParkMedia/");
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        
        // Delete from storage
        try {
          await sb.storage.from("ParkMedia").remove([filePath]);
          console.log("🗑️ Deleted from storage:", filePath);
        } catch (err) {
          console.error("Storage delete error:", err);
          // Continue even if storage delete fails
        }
      }
    }
    
    // Delete from database
    await sb.from("park_media").delete().eq("id", mediaId);
    console.log("✅ Deleted from database:", mediaId);
  },

  // Virtueel Parkje
  getVerblijvenByPark: async (parkId, alleenActief=true) => {
    let q = sb.from("verblijven").select("*").eq("park_id", parkId).order("volgorde").order("naam");
    if(alleenActief) q = q.eq("actief", true);
    const {data, error} = await q;
    if(error) throw error;
    return data || [];
  },
  addVerblijf: async (verblijf) => {
    const {data, error} = await sb.from("verblijven").insert(verblijf).select().single();
    if(error) throw error;
    return data;
  },
  updateVerblijf: async (id, upd) => {
    const {data: updated, error} = await sb.from("verblijven").update(upd).eq("id", id).select().single();
    if(error) throw error;
    return updated;
  },
  deleteVerblijf: async (id) => {
    const {error} = await sb.from("verblijven").delete().eq("id", id);
    if(error) throw error;
  },
  getBewoners: async (verblijfId) => {
    const {data, error} = await sb.from("verblijf_bewoners")
      .select("*, dieren(id,naam,soort,soort_id,geslacht,fotos,geboortedatum,herkomst,notitie,ras_id,rassen(ras_naam))")
      .eq("verblijf_id", verblijfId).order("volgorde");
    if(error) throw error;
    return data || [];
  },
  addBewoner: async (bewoner) => {
    const {data, error} = await sb.from("verblijf_bewoners")
      .insert(bewoner).select("*, dieren(id,naam,soort,soort_id,geslacht,fotos,geboortedatum,herkomst,notitie,ras_id,rassen(ras_naam))").single();
    if(error) throw error;
    return data;
  },
  updateBewoner: async (id, upd) => {
    const {data: updated, error} = await sb.from("verblijf_bewoners")
      .update(upd).eq("id", id).select("*, dieren(id,naam,soort,soort_id,geslacht,fotos,geboortedatum,herkomst,notitie,ras_id,rassen(ras_naam))").single();
    if(error) throw error;
    return updated;
  },
  deleteBewoner: async (id) => {
    const {error} = await sb.from("verblijf_bewoners").delete().eq("id", id);
    if(error) throw error;
  },
  getVirtueelParkUrl: (parkId) => `${window.location.origin}/park/${encodeURIComponent(parkId)}`,
  getVerblijfVoorDier: async (dierId) => {
    const {data,error} = await sb.from("verblijf_bewoners")
      .select("verblijf_id, verblijven(id,naam,type)")
      .eq("dier_id", dierId)
      .limit(1);
    if(error) throw error;
    return data?.[0]?.verblijven || null;
  },
  verwijderDierUitVerblijven: async (dierId) => {
    const {error} = await sb.from("verblijf_bewoners").delete().eq("dier_id", dierId);
    if(error) throw error;
  },
  // Prikbord (cross-park bulletin board)
  getPrikbord: async () => {
    const {data,error} = await sb.from("prikbord").select("*, gebruikers(naam,roepnaam), parken(naam)").order("created_at",{ascending:false});
    if(error) throw error; return data||[];
  },
  addPrikbordPost: async (post) => {
    const {data,error} = await sb.from("prikbord").insert(post).select("*, gebruikers(naam,roepnaam), parken(naam)").single();
    if(error) throw error; return data;
  },
  deletePrikbordPost: async (id) => {
    const {error} = await sb.from("prikbord").delete().eq("id",id);
    if(error) throw error;
  },
  getPrikbordReacties: async (prikbordId) => {
    const {data,error} = await sb.from("prikbord_reacties").select("*, gebruikers(naam,roepnaam), parken(naam)").eq("prikbord_id",prikbordId).order("created_at",{ascending:true});
    if(error) throw error; return data||[];
  },
  addPrikbordReactie: async (reactie) => {
    const {data,error} = await sb.from("prikbord_reacties").insert(reactie).select("*, gebruikers(naam,roepnaam), parken(naam)").single();
    if(error) throw error; return data;
  },
  deletePrikbordReactie: async (id) => {
    const {error} = await sb.from("prikbord_reacties").delete().eq("id",id);
    if(error) throw error;
  },
};

/* ═══════════════════════════════════════════════════════════
   DIERENPARKJE.NL — PLATFORM
   ═══════════════════════════════════════════════════════════ */

// Auth wordt afgehandeld via Supabase Auth — geen hardcoded wachtwoorden meer

// Statische kaart-data voor parkjes zonder beheer (blijft voor de zoekkaart)
const PARKEN_STATISCH = [];

/* ═══════════════════════════════════════════════════════════
   PERMISSIONS & CONSTANTS
   ═══════════════════════════════════════════════════════════ */

const PERMISSIONS = {
  beheerder: { label:"Beheerder", beschrijving:"Volledige toegang eigen park + gebruikersbeheer", color:"#c44536",
    can:["voeding_lezen","voeding_afvinken","voeding_beheren","medicatie_lezen","medicatie_afvinken","medicatie_beheren","taken_lezen","taken_afvinken","taken_beheren","agenda_lezen","agenda_beheren","berichten_lezen","berichten_schrijven","berichten_verwijderen","berichten_gemeente","contacten_lezen","contacten_beheren","gebruikers_beheren","rollen_beheren","planning_lezen","planning_beheren","logboek_lezen","logboek_schrijven","sleutels_beheren","leveranciers_beheren","bestellingen_beheren"] },
  deelbeheer: { label:"Deelbeheer", beschrijving:"Toegang op basis van geconfigureerde modules", color:"#7a4fb5",
    can:[] },
  verzorger: { label:"Verzorger", beschrijving:"Dierenverzorging, taken & berichten", color:"#2e6da4",
    can:["voeding_lezen","voeding_afvinken","voeding_beheren","medicatie_lezen","medicatie_afvinken","medicatie_beheren","taken_lezen","taken_afvinken","taken_beheren","agenda_lezen","berichten_lezen","berichten_schrijven","contacten_lezen","planning_lezen","planning_beheren","logboek_lezen","logboek_schrijven","sleutels_beheren","leveranciers_beheren","bestellingen_beheren"] },
  vrijwilliger: { label:"Vrijwilliger", beschrijving:"Afvinken toegewezen taken & voeding", color:"#4a7a2e",
    can:["voeding_lezen","voeding_afvinken","medicatie_lezen","medicatie_afvinken","taken_lezen","taken_afvinken","agenda_lezen","berichten_lezen","berichten_schrijven","contacten_lezen","planning_lezen","logboek_lezen"] },
  gast: { label:"Gast", beschrijving:"Alleen lezen — hele app zichtbaar, niets aanpassen", color:"#888",
    can:["voeding_lezen","medicatie_lezen","taken_lezen","agenda_lezen","berichten_lezen","contacten_lezen","planning_lezen","logboek_lezen","leveranciers_lezen","bestellingen_lezen","gezondheid_lezen"] },
};

// Module definities voor deelbeheer
const MODULES=[
  {code:"dieren_verblijven", label:"Dieren, Verblijven & Bezoekersinfo", icon:"🐄"},
  {code:"gezondheid",        label:"Gezondheid & Medicatie",             icon:"💊"},
  {code:"voeding",           label:"Voeding & Schema's",                 icon:"🥕"},
  {code:"taken",             label:"Taken & Diensten",                   icon:"✅"},
  {code:"planning",          label:"Planning & Roosters",                icon:"📅"},
  {code:"sleutelbeheer",     label:"Sleutelbeheer",                      icon:"🔑"},
  {code:"bestellingen",      label:"Bestellingen & Leveranciers",        icon:"📦"},
  {code:"prikbord",          label:"Prikbord",                           icon:"📌"},
];

function hasPerm(user, p) {
  if(!user) return false;
  if(user.isAppAdmin || PERMISSIONS[user.role]?.can?.includes(p)) return true;
  return false;
}
function hasModuleBeheer(user, module) {
  if(!user) return false;
  if(user.isAppAdmin || user.role==="beheerder") return true;
  if(user.role==="deelbeheer") return user.modules?.[module]==="beheer";
  return false;
}
function hasModuleLezen(user, module) {
  if(!user) return false;
  if(user.isAppAdmin || user.role==="beheerder") return true;
  if(user.role==="deelbeheer") return ["beheer","lezen"].includes(user.modules?.[module]);
  return false;
}

const SOORTEN = ["Schaap","Geit","Hert","Konijn","Gans","Kip","Vogel"];
const emoji = {Schaap:"\uD83D\uDC11",Geit:"\uD83D\uDC10",Hert:"\uD83E\uDD8C",Konijn:"\uD83D\uDC07",Gans:"\uD83E\uDEBF",Kip:"\uD83D\uDC14",Vogel:"\uD83D\uDC26"};
const meervoud = {Schaap:"Schapen",Geit:"Geiten",Hert:"Herten",Konijn:"Konijnen",Gans:"Ganzen",Kip:"Kippen",Vogel:"Vogels"};
function soortIcon(soort){ return emoji[soort]||"\uD83D\uDC3E"; }

// Diersoort informatie voor bezoekers
const DIER_INFO = {
  Schaap: { info: "Schapen zijn grasdieren die in kuddes leven. Ze zijn zeer sociale dieren en hebben een sterk familiebetrokken systeem.", diet: "Gras en hooi" },
  Geit: { info: "Geiten zijn intelligente, nieuwsgierige dieren die zeer bewegelijk zijn. Ze zijn bekend om hun sprongen en klim-avonturen.", diet: "Gras, struiken en bladeren" },
  Hert: { info: "Herten zijn elegante grasdieren. Mannetjes krijgen ieder jaar nieuwe geweiden. Ze zijn snel en hebben uitstekende reflexen.", diet: "Gras, blaadjes en zachte planten" },
  Konijn: { info: "Konijnen zijn sociale dieren die graag in groepen leven. Ze kunnen zeer snel rennen - tot 55 km/h! Ze communiceren door geluid en lichaamstaal.", diet: "Wortels, groenten en gras" },
  Gans: { info: "Ganzen zijn watervogels die zeer loyaal zijn en sterke familieband hebben. Ze kunnen tot 40 jaar oud worden!", diet: "Gras, zaden en waterplanten" },
  Kip: { info: "Kippen zijn sociale vogels met een duidelijke hiërarchie in de groep. Ze zijn actief en graag aan het pikken en graven.", diet: "Graan, insecten en plantaardig materiaal" },
  Vogel: { info: "Vogels zijn fascinerend met hun vluchtcapaciteiten en zingende gaven. Elk soort heeft unieke gedragingen en vaardigheden.", diet: "Zaden, insecten en fruit (afhankelijk van soort)" },
};
function getEmoji(dier,animals){const a=animals?.find(x=>x.name===dier);return a?emoji[a.soort]||"\uD83D\uDC3E":"\uD83D\uDC3E";}

const DAGEN_LANG = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];
const SHIFTS = [
  {id:"ochtend",label:"Ochtend",tijd:"07:00 – 12:00",color:"#e87c28",bg:"#fff3e6"},
  {id:"middag",label:"Middag",tijd:"12:00 – 17:00",color:"#2e6da4",bg:"#e6f0fa"},
  {id:"avond",label:"Avond",tijd:"17:00 – 20:00",color:"#6b4c8a",bg:"#f0e6fa"},
];
function getWeekDates(offset=0){
  const now=new Date();const day=now.getDay();const mon=new Date(now);
  mon.setDate(now.getDate()-(day===0?6:day-1)+offset*7);
  return Array.from({length:7},(_,i)=>{const d=new Date(mon);d.setDate(mon.getDate()+i);return d.toISOString().split("T")[0];});
}
function genRecurDates(startDate,herhaling,eindDatum){
  if(!herhaling||herhaling==="geen") return [startDate];
  // If no end date, generate a sensible horizon
  let end;
  if(!eindDatum||eindDatum===startDate){
    end=new Date(startDate);
    if(herhaling==="dagelijks") end.setMonth(end.getMonth()+3);      // 3 maanden
    else if(herhaling==="wekelijks") end.setFullYear(end.getFullYear()+1); // 1 jaar
    else if(herhaling==="maandelijks") end.setFullYear(end.getFullYear()+2); // 2 jaar
    else if(herhaling==="jaarlijks") end.setFullYear(end.getFullYear()+5); // 5 jaar
  } else {
    end=new Date(eindDatum);
  }
  const dates=[startDate];let cur=new Date(startDate);
  for(let i=0;i<500;i++){
    if(herhaling==="dagelijks") cur.setDate(cur.getDate()+1);
    else if(herhaling==="wekelijks") cur.setDate(cur.getDate()+7);
    else if(herhaling==="maandelijks") cur.setMonth(cur.getMonth()+1);
    else if(herhaling==="jaarlijks") cur.setFullYear(cur.getFullYear()+1);
    if(cur>end) break;dates.push(cur.toISOString().split("T")[0]);
  }
  return dates;
}
function nextId(arr){return Math.max(0,...arr.map(x=>x.id))+1;}

/* ── Reducer ── */
function mgmtReducer(s, a) {
  console.log("🔄 mgmtReducer action:", a.type);
  switch(a.type){
    case "LOAD": return {...s, ...a.p};
    case "LOGIN": return {...s, user:a.p, page:"logboek", meerOpen:false};
    case "LOGOUT": return {...s, user:null, page:"public", modal:null, editItem:null, meerOpen:false};
    case "NAV": return {...s, page:a.p, viewSoort:null, viewDier:null, meerOpen:false};
    case "TOGGLE_MEER": return {...s, meerOpen:!s.meerOpen};
    case "CLOSE_MEER": return {...s, meerOpen:false};
    case "VIEW_SOORT": return {...s, viewSoort:a.p, viewDier:null};
    case "VIEW_DIER": return {...s, viewDier:a.p};
    case "VIEW_GEZONDHEID_DIER": return {...s, page:"gezondheid", gezondheid_dier_id:a.p, viewSoort:null, viewDier:null};
    case "BACK_SOORT": return {...s, viewDier:null};
    case "BACK_HOME": return {...s, viewSoort:null, viewDier:null};
    case "MODAL": return {...s, modal:a.p?.type||null, editItem:a.p?.item||null, modalMeta:a.p||null};
    case "CLOSE": return {...s, modal:null, editItem:null};
    case "TOG_FEED": return {...s, feedings:s.feedings.map(f=>f.id===a.p?{...f,gedaan:!f.gedaan,door:!f.gedaan?s.user?.name:null}:f)};
    case "TOG_MED": return {...s, meds:s.meds.map(m=>m.id===a.p?{...m,gedaan:!m.gedaan,door:!m.gedaan?s.user?.name:null}:m)};
    case "ADD_FEED": return {...s, feedings:[...s.feedings,{...a.p,id:nextId(s.feedings),gedaan:false,door:null}], modal:null};
    case "EDIT_FEED": return {...s, feedings:s.feedings.map(f=>f.id===a.p.id?{...f,...a.p}:f), modal:null, editItem:null};
    case "DEL_FEED": return {...s, feedings:s.feedings.filter(f=>f.id!==a.p)};
    case "LOAD_VOEDING_DIEREN": return {...s, voedingDieren:a.p, modal:null, editItem:null};
    case "ADD_VOEDING_DIER": return {...s, voedingDieren:[...s.voedingDieren, a.p], modal:null};
    case "EDIT_VOEDING_DIER": return {...s, voedingDieren:s.voedingDieren.map(v=>v.id===a.p.id?{...v,...a.p}:v), modal:null, editItem:null};
    case "DEL_VOEDING_DIER": return {...s, voedingDieren:s.voedingDieren.filter(v=>v.id!==a.p)};
    case "TOG_MAALTIJD_CHECK": {
      const existing=s.maaltijdChecks.find(c=>c.group_key===a.p.groupKey&&c.dagdeel===a.p.dagdeel);
      const updatedCheck=a.p.check;
      if(existing) return {...s, maaltijdChecks:s.maaltijdChecks.map(c=>c.group_key===a.p.groupKey&&c.dagdeel===a.p.dagdeel?updatedCheck:c)};
      return {...s, maaltijdChecks:[...s.maaltijdChecks, updatedCheck]};
    }
    case "ADD_MED": return {...s, meds:[...s.meds,{...a.p,id:nextId(s.meds),gedaan:false,door:null}], modal:null};
    case "EDIT_MED": return {...s, meds:s.meds.map(m=>m.id===a.p.id?{...m,...a.p}:m), modal:null, editItem:null};
    case "DEL_MED": return {...s, meds:s.meds.filter(m=>m.id!==a.p)};
    case "LOAD_LEVERANCIERS": return {...s, leveranciers:a.p, modal:null, editItem:null};
    case "LOAD_BESTELLINGEN": return {...s, bestellingen:a.p, modal:null, editItem:null};
    case "ADD_TASK": return {...s, tasks:[...s.tasks,{...a.p,id:nextId(s.tasks)}], modal:null};
    case "EDIT_TASK": return {...s, tasks:s.tasks.map(t=>t.id===a.p.id?{...t,...a.p}:t), modal:null, editItem:null};
    case "DEL_TASK": return {...s, tasks:s.tasks.filter(t=>t.id!==a.p), takenToewijzingen:s.takenToewijzingen.filter(tt=>tt.taak_id!==a.p)};
    case "LOAD_TOEWIJZINGEN": return {...s, takenToewijzingen:a.p};
    case "TOG_TAAK_CHECK": {
      const existing=s.takenChecks.find(c=>c.taak_id===a.p.taakId);
      const updatedCheck=a.p.check;
      if(existing) return {...s, takenChecks:s.takenChecks.map(c=>c.taak_id===a.p.taakId?updatedCheck:c)};
      return {...s, takenChecks:[...s.takenChecks, updatedCheck]};
    }
    case "ADD_AGENDA": {
      const {herhaling,herhaalTot,...base}=a.p;
      const dates=genRecurDates(base.datum,herhaling,herhaalTot||"");
      const newItems=dates.map((dt,i)=>({...base,datum:dt,id:nextId(s.agenda)+i,herhaling:herhaling&&herhaling!=="geen"?herhaling:undefined}));
      return {...s, agenda:[...s.agenda,...newItems], modal:null};
    }
    case "EDIT_AGENDA": return {...s, agenda:s.agenda.map(x=>x.id===a.p.id?{...x,...a.p}:x), modal:null, editItem:null};
    case "DEL_AGENDA": return {...s, agenda:s.agenda.filter(x=>x.id!==a.p)};
    case "ADD_MSG": return {...s, msgs:[{id:nextId(s.msgs),auteur:s.user?.name,datum:new Date().toLocaleString("nl-NL"),...a.p},...s.msgs]};
    case "DEL_MSG": return {...s, msgs:s.msgs.filter(m=>m.id!==a.p)};
    case "SET_ROLE": return {...s, users:s.users.map(u=>u.id===a.p.id?{...u,role:a.p.role}:u)};
    case "TOG_ACTIVE": return {...s, users:s.users.map(u=>u.id===a.p?{...u,actief:!u.actief}:u)};
    case "ADD_USER": return {...s, users:[...s.users,{...a.p,id:nextId(s.users),actief:true}], modal:null};
    case "DEL_USER": return {...s, users:s.users.filter(u=>u.id!==a.p)};
    case "ADD_ANIMAL": return {...s, animals:[...s.animals,{...a.p,id:nextId(s.animals),fotos:[]}], modal:null};
    case "EDIT_ANIMAL": return {...s, animals:s.animals.map(x=>x.id===a.p.id?{...x,...a.p}:x), modal:null, editItem:null};
    case "DEL_ANIMAL": return {...s, animals:s.animals.filter(x=>x.id!==a.p)};
    case "ADD_PHOTO": return {...s, animals:s.animals.map(x=>x.id===a.p.id?{...x,fotos:[...x.fotos,...a.p.fotos]}:x)};
    case "DEL_PHOTO": return {...s, animals:s.animals.map(x=>x.id===a.p.animalId?{...x,fotos:x.fotos.filter((_,i)=>i!==a.p.idx)}:x)};
    case "ADD_CONTACT": return {...s, contacten:[...s.contacten,{...a.p,id:nextId(s.contacten)}], modal:null};
    case "ADD_SLEUTEL": return {...s, sleutels:[...s.sleutels,{...a.p,id:nextId(s.sleutels)}], modal:null};
    case "EDIT_SLEUTEL": return {...s, sleutels:s.sleutels.map(k=>k.id===a.p.id?{...k,...a.p}:k), modal:null, editItem:null};
    case "DEL_SLEUTEL": return {...s, sleutels:s.sleutels.filter(k=>k.id!==a.p)};
    case "EDIT_CONTACT": return {...s, contacten:s.contacten.map(c=>c.id===a.p.id?{...c,...a.p}:c), modal:null, editItem:null};
    case "DEL_CONTACT": return {...s, contacten:s.contacten.filter(c=>c.id!==a.p)};
    case "ADD_PLANNING": {
      // items already saved to DB by ds(), just add to local state
      const newItems=a.p.items||[];
      return {...s, planning:[...s.planning,...newItems], modal:null};
    }
    case "EDIT_PLANNING": return {...s, planning:s.planning.map(p=>p.id===a.p.id?{...p,...a.p}:p), modal:null, editItem:null};
    case "EDIT_REEKS": {
      const item=s.planning.find(p=>p.id===a.p.id);
      if(!item?.reeks_id) return {...s, planning:s.planning.map(p=>p.id===a.p.id?{...p,...a.p}:p), modal:null, editItem:null};
      return {...s, modal:null, editItem:null, planning:s.planning.map(p=>
        p.reeks_id===item.reeks_id&&p.datum>=item.datum
          ?{...p,naam:a.p.naam,shift:a.p.shift,notitie:a.p.notitie,userId:a.p.userId,contactId:a.p.contactId,contact_id:a.p.contactId||null,gebruiker_id:a.p.userId||null}
          :p
      )};
    }
    case "DEL_REEKS": {
      const item=s.planning.find(p=>p.id===a.p);
      if(!item?.reeks_id) return {...s, planning:s.planning.filter(p=>p.id!==a.p)};
      return {...s, planning:s.planning.filter(p=>p.reeks_id!==item.reeks_id)};
    }
    case "DEL_PLANNING": return {...s, planning:s.planning.filter(p=>p.id!==a.p)};
    // Vaste taken
    case "ADD_VASTE_TAAK": return {...s, vasteTaken:[...s.vasteTaken, a.p], modal:null};
    case "DEL_VASTE_TAAK": return {...s, vasteTaken:s.vasteTaken.filter(t=>t.id!==a.p)};
    case "TOG_CHECK": {
      const existing=s.checks.find(c=>c.vaste_taak_id===a.p.taakId&&c.dagdeel===a.p.dagdeel);
      const updatedCheck=a.p.check;
      if(existing) return {...s, checks:s.checks.map(c=>c.vaste_taak_id===a.p.taakId&&c.dagdeel===a.p.dagdeel?updatedCheck:c)};
      return {...s, checks:[...s.checks, updatedCheck]};
    }
    // Logboek
    case "ADD_LOGBOEK": return {...s, logboek:[a.p,...s.logboek], modal:null};
    case "DEL_LOGBOEK": return {...s, logboek:s.logboek.filter(l=>l.id!==a.p)};
    case "LOAD_DIERENADVIES": return {...s, dierenadvies:a.p||[]};
    // Waarschuwingen
    case "ADD_WAARSCHUWING": return {...s, waarschuwingen:[a.p,...s.waarschuwingen], modal:null};
    case "DEL_WAARSCHUWING": return {...s, waarschuwingen:s.waarschuwingen.filter(w=>w.id!==a.p)};
    // Protocollen
    case "ADD_PROTOCOL": return {...s, protocollen:[a.p,...s.protocollen], modal:null};
    case "EDIT_PROTOCOL": return {...s, protocollen:s.protocollen.map(p=>p.id===a.p.id?{...p,...a.p}:p), modal:null, editItem:null};
    case "DEL_PROTOCOL": return {...s, protocollen:s.protocollen.filter(p=>p.id!==a.p)};
    case "ADD_RAS": return {...s, rassen:[...s.rassen, a.p]};
    case "EDIT_RAS": return {...s, rassen:s.rassen.map(r=>r.id===a.p.id?{...r,...a.p}:r)};
    case "DEL_RAS": return {...s, rassen:s.rassen.filter(r=>r.id!==a.p)};
    case "EDIT_GEZONDHEID": return {...s, gezondheid:s.gezondheid.map(x=>x.id===a.p.id?{...x,...a.p}:x), modal:null, editItem:null};
    case "DEL_GEZONDHEID": return {...s, gezondheid:s.gezondheid.filter(x=>x.id!==a.p)};
    case "ADD_GEZONDHEID_SUCCESS": return {...s, gezondheid:[a.p,...s.gezondheid], modal:null, editItem:null};
    case "EDIT_GEZONDHEID_SUCCESS": return {...s, gezondheid:s.gezondheid.map(x=>x.id===a.p.id?a.p:x), modal:null, editItem:null};
    default: return s;
  }
}

/* ── Icons ── */
const IC={
  home:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  food:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  med:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>,
  heart:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  pill:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7" cy="12" r="4"/><circle cx="17" cy="12" r="4"/><line x1="11" y1="12" x2="13" y2="12"/></svg>,
  cal:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  tasks:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  chat:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  team:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  planning:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="2" x2="9" y2="6"/><line x1="15" y1="2" x2="15" y2="6"/><circle cx="8" cy="15" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="15" r="1.5" fill="currentColor" stroke="none"/><circle cx="16" cy="15" r="1.5" fill="currentColor" stroke="none"/></svg>,
  meer:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="5" r="1.5" fill="currentColor"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/><circle cx="12" cy="19" r="1.5" fill="currentColor"/></svg>,
  plus:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  chk:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  X:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  lock:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  send:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  shield:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  login:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>,
  logout:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  paw:<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><ellipse cx="8" cy="6" rx="2.2" ry="2.8"/><ellipse cx="16" cy="6" rx="2.2" ry="2.8"/><ellipse cx="4.5" cy="11" rx="2" ry="2.5"/><ellipse cx="19.5" cy="11" rx="2" ry="2.5"/><path d="M12 22c-4 0-7-3.5-7-6.5S9 10 12 10s7 2 7 5.5S16 22 12 22z"/></svg>,
  phone:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  mail:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  pin:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  book:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
  back:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  cam:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  chevL:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  chevR:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>,
  repeat:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>,
  search:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  map:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  clock:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  globe:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  
  // SVG line icons for menu items
  supplier:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>,
  orders:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2l-4 4v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6l-4-4M9 2H7a2 2 0 0 0-2 2v2h12V4a2 2 0 0 0-2-2h-2M9 2v4h6V2M4 8h16M7 12h2M7 16h2M13 12h2M13 16h2"/></svg>,
  prikbord:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3c0 1.1.6 2.1 1.5 2.6L9 22h6l-1.5-14.4A3 3 0 0 0 15 5a3 3 0 0 0-3-3z"/><circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none"/></svg>,
};

function fmtD(d){return new Date(d).toLocaleDateString("nl-NL",{day:"numeric",month:"short"});}
function dayN(d){return new Date(d).getDate();}
function monS(d){return new Date(d).toLocaleDateString("nl-NL",{month:"short"}).toUpperCase();}
function rCol(r){return PERMISSIONS[r]?.color||"#888";}
function rLab(r){return PERMISSIONS[r]?.label||r;}

/* ═══════════════════════════════════════════════════════════
   SHARED UI COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function PhotoModal({photos, initialIndex=0, dierName, onClose, showNav=true}){
  const [currentIndex, setCurrentIndex]=useState(initialIndex);
  const currentPhoto=photos[currentIndex];
  const total=photos.length;
  
  const goNext=()=>setCurrentIndex((currentIndex+1)%total);
  const goPrev=()=>setCurrentIndex((currentIndex-1+total)%total);
  
  return <div className="mov" onClick={onClose}><div className="mod" onClick={e=>e.stopPropagation()} style={{maxWidth:"90vw",maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
    <div className="mt">{dierName} • Foto {currentIndex+1}/{total}<button className="mx" onClick={onClose}>×</button></div>
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"#000",overflow:"hidden"}}>
      <img src={currentPhoto} alt={dierName} style={{width:"auto",maxWidth:"90vw",height:"auto",maxHeight:"calc(90vh - 120px)",objectFit:"contain"}}/>
    </div>
    {showNav&&total>1&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px",background:"#f5f5f5"}}>
      <button onClick={goPrev} style={{padding:"8px 16px",border:"none",borderRadius:8,background:"var(--g)",color:"#fff",cursor:"pointer",fontWeight:600}}>← Vorige</button>
      <div style={{fontSize:12,color:"var(--txtl)"}}>{currentIndex+1} / {total}</div>
      <button onClick={goNext} style={{padding:"8px 16px",border:"none",borderRadius:8,background:"var(--g)",color:"#fff",cursor:"pointer",fontWeight:600}}>Volgende →</button>
    </div>}
  </div></div>;
}

function Modal({title,children,onClose}){
  return <div className="mov" onClick={onClose}><div className="mod" onClick={e=>e.stopPropagation()}>
    <div className="mt">{title}<button className="mx" onClick={onClose}>{IC.X}</button></div>{children}
  </div></div>;
}

/* ═══════════════════════════════════════════════════════════
   PLATFORM: LANDING PAGE
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   BEZOEKER REGISTER - VISITOR SIGNUP
   ═══════════════════════════════════════════════════════════ */

function BezoekerAuthPage({onNavigate, onSuccess, initialMode="register"}){
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError("");
    if(!email.trim() || !password.trim()){
      setError("Vul email en wachtwoord in");
      return;
    }
    setLoading(true);
    try {
      const {data, error: loginError} = await sb.auth.signInWithPassword({email, password});
      if(loginError) throw loginError;
      // Auth state change handler in App will route to mijnparkjes
    } catch(err) {
      console.error("Login fout:", err);
      setError(err.message === "Invalid login credentials" ? "Onbekende combinatie. Probeer opnieuw." : (err.message || "Inloggen mislukt"));
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    setError("");
    if(!name.trim() || !email.trim() || !password.trim()){
      setError("Vul alle velden in");
      return;
    }
    if(password !== confirmPassword){
      setError("Wachtwoorden komen niet overeen");
      return;
    }
    if(password.length < 6){
      setError("Wachtwoord moet minstens 6 karakters zijn");
      return;
    }
    setLoading(true);
    try {
      const {data, error: signupError} = await db.signUp(email, password);
      if(signupError) throw signupError;
      const {data: insertData, error: insertError} = await sb.from("gebruikers").insert({
        auth_id: data.user.id,
        naam: name,
        roepnaam: name,
        email: email,
        is_bezoeker: true,
        type: 'bezoeker'
      }).select().single();
      if(insertError) throw insertError;
      toast.ok("Welkom! Je account is aangemaakt. 🎉");
      // Auth state change handler in App will route to mijnparkjes
    } catch(err) {
      console.error("Registratie fout:", err);
      setError(err.message || "Er ging iets fout bij registratie");
    }
    setLoading(false);
  };

  const inputStyle={width:"100%",padding:"10px 12px",border:"1px solid #ddd",borderRadius:8,fontSize:16,boxSizing:"border-box",fontFamily:"inherit"};

  return <div style={{
    minHeight:"100vh",
    backgroundImage:"url('/bezoekers.png')",
    backgroundSize:"cover",
    backgroundPosition:"center",
    backgroundAttachment:"fixed",
    display:"flex",
    alignItems:"center",
    justifyContent:"center",
    padding:"16px"
  }}>
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.3)",zIndex:0}}/>
    
    <div style={{background:"#fff",borderRadius:16,padding:"32px",maxWidth:400,width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.15)",zIndex:1}}>
      <button onClick={()=>onNavigate("landing")} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",marginBottom:16}}>← Terug</button>
      
      <h1 style={{fontSize:24,fontWeight:700,marginBottom:8,color:"var(--g)"}}>
        🎫 Mijn Dierenparkjes
      </h1>
      <p style={{fontSize:13,color:"var(--txtl)",marginBottom:20}}>
        {mode==="login" ? "Log in om je bezoeken te bekijken" : "Maak een account aan om je bezoeken bij te houden"}
      </p>

      {/* Mode toggle */}
      <div style={{display:"flex",borderRadius:8,border:"1px solid var(--brd)",marginBottom:20,overflow:"hidden"}}>
        <button onClick={()=>{setMode("login");setError("");}} style={{
          flex:1,padding:"10px",border:"none",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",
          background:mode==="login"?"var(--g)":"#fff",color:mode==="login"?"#fff":"var(--txtl)"
        }}>Inloggen</button>
        <button onClick={()=>{setMode("register");setError("");}} style={{
          flex:1,padding:"10px",border:"none",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",
          background:mode==="register"?"var(--g)":"#fff",color:mode==="register"?"#fff":"var(--txtl)"
        }}>Account aanmaken</button>
      </div>

      <form onSubmit={e=>{e.preventDefault();mode==="login"?handleLogin():handleRegister();}} autoComplete={mode==="login"?"on":"off"} style={{display:"flex",flexDirection:"column",gap:12}}>
        {mode==="register"&&<div>
          <label style={{fontSize:12,fontWeight:600,color:"var(--g)",display:"block",marginBottom:6}}>Naam</label>
          <input type="text" placeholder="Je naam" value={name} onChange={(e)=>setName(e.target.value)} style={inputStyle}/>
        </div>}

        <div>
          <label style={{fontSize:12,fontWeight:600,color:"var(--g)",display:"block",marginBottom:6}}>Email</label>
          <input type="email" placeholder="je@email.nl" value={email} onChange={(e)=>setEmail(e.target.value)} autoComplete="email" style={inputStyle}/>
        </div>

        <div>
          <label style={{fontSize:12,fontWeight:600,color:"var(--g)",display:"block",marginBottom:6}}>Wachtwoord</label>
          <input type="password" placeholder={mode==="login"?"Je wachtwoord":"Minimaal 6 karakters"} autoComplete={mode==="login"?"current-password":"new-password"} value={password} onChange={(e)=>setPassword(e.target.value)} style={inputStyle}/>
        </div>

        {mode==="register"&&<div>
          <label style={{fontSize:12,fontWeight:600,color:"var(--g)",display:"block",marginBottom:6}}>Wachtwoord bevestigen</label>
          <input type="password" placeholder="Herhaal wachtwoord" autoComplete="new-password" value={confirmPassword} onChange={(e)=>setConfirmPassword(e.target.value)} style={inputStyle}/>
        </div>}

        {error&&<div style={{background:"#ffebee",color:"#c62828",padding:12,borderRadius:8,fontSize:12,border:"1px solid #ef5350"}}>{error}</div>}

        <PawBtn type="submit" loading={loading} style={{width:"100%",marginTop:4}}>
          {mode==="login" ? "Inloggen" : "Account aanmaken"}
        </PawBtn>
      </form>
    </div>
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   LANDING PAGE
   ═══════════════════════════════════════════════════════════ */

function LandingPage({onNavigate, loggedInUser}){
  return <div className="landing" style={{display:"flex",flexDirection:"column",minHeight:"100vh",margin:0,padding:0,width:"100%",maxWidth:"100vw",backgroundColor:"#2d5016"}}>
    <style>{`
      @keyframes hop {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-20px); }
      }
      .hopping-animal {
        display: inline-block;
        font-size: 40px;
        animation: hop 2s infinite ease-in-out;
        position: absolute;
      }
      html, body {
        height: 100%;
        margin: 0;
        padding: 0;
      }
    `}</style>
    
    {/* Green Hero Section — 2/3 of screen */}
    <div className="l-hero" style={{flex:2,minHeight:320,display:"flex",flexDirection:"column",position:"relative",background:"linear-gradient(135deg, #2d5016 0%, #3a6b1f 100%)",width:"100%",justifyContent:"center",alignItems:"center",overflow:"hidden"}}>
      
      {/* Hopping Animals Background */}
      <div className="hopping-animal" style={{left:"5%",top:"15%",animationDelay:"0s"}}>🐑</div>
      <div className="hopping-animal" style={{left:"15%",top:"25%",animationDelay:"0.3s"}}>🐐</div>
      <div className="hopping-animal" style={{left:"25%",top:"10%",animationDelay:"0.6s"}}>🦌</div>
      <div className="hopping-animal" style={{right:"25%",top:"20%",animationDelay:"0.9s"}}>🪿</div>
      <div className="hopping-animal" style={{right:"15%",top:"30%",animationDelay:"1.2s"}}>🐔</div>
      <div className="hopping-animal" style={{right:"5%",top:"15%",animationDelay:"1.5s"}}>🐇</div>

      {/* Centered Logo and Two Paths */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:40,padding:"40px 20px",maxWidth:"600px",width:"100%",margin:"0 auto",position:"relative",zIndex:5}}>
        {/* Logo */}
        <img src="/dierenparkje-logo.png" alt="Dierenparkje.nl" style={{maxWidth:"500px",width:"100%",height:"auto",objectFit:"contain"}}/>
        
        {/* Two Path Buttons */}
        <div style={{display:"flex",gap:16,width:"100%",maxWidth:400,justifyContent:"center"}}>
          {/* Bezoekers */}
          <button onClick={()=>onNavigate("zoek")} style={{
            flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:10,
            padding:"24px 16px",background:"rgba(255,255,255,0.12)",backdropFilter:"blur(8px)",
            border:"2px solid rgba(255,255,255,0.3)",borderRadius:16,cursor:"pointer",
            transition:"all .2s",color:"#fff"
          }} onMouseEnter={(e)=>{e.currentTarget.style.background="rgba(255,255,255,0.22)";e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={(e)=>{e.currentTarget.style.background="rgba(255,255,255,0.12)";e.currentTarget.style.transform="translateY(0)";}}>
            <span style={{fontSize:40}}>🐾</span>
            <span style={{fontSize:16,fontWeight:700,fontFamily:"'Fraunces',serif"}}>Bezoekers</span>
            <span style={{fontSize:12,opacity:0.8}}>Ontdek een dierenpark</span>
          </button>

          {/* Medewerkers */}
          <button onClick={()=>onNavigate("login")} style={{
            flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:10,
            padding:"24px 16px",background:"rgba(255,255,255,0.95)",
            border:"2px solid #fff",borderRadius:16,cursor:"pointer",
            transition:"all .2s",color:"var(--g)"
          }} onMouseEnter={(e)=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,.2)";}} onMouseLeave={(e)=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}>
            <span style={{fontSize:40}}>🔑</span>
            <span style={{fontSize:16,fontWeight:700,fontFamily:"'Fraunces',serif"}}>Medewerkers</span>
            <span style={{fontSize:12,opacity:0.7}}>Inloggen beheerapp</span>
          </button>
        </div>

        {/* Bezoeker login link */}
        <button onClick={()=>onNavigate(loggedInUser?.is_bezoeker?"mijnparkjes":"bezoekerLogin")} style={{
          background:"none",border:"none",color:"rgba(255,255,255,0.7)",fontSize:13,
          cursor:"pointer",fontFamily:"inherit",marginTop:-20
        }}>
          Al een bezoekersaccount? <span style={{textDecoration:"underline",textUnderlineOffset:3,color:"#fff"}}>Mijn Dierenparkjes</span>
        </button>
      </div>
    </div>

    {/* Invitation section — 1/3 of screen */}
    <div style={{
      flex:1,minHeight:180,overflow:"hidden",position:"relative",
      background:"linear-gradient(160deg,#1a3d0a 0%,#2d5016 50%,#3a6b1f 100%)",
      display:"flex",flexDirection:"column",alignItems:"center",
      justifyContent:"center",gap:8,padding:"12px 20px",
      textAlign:"center",boxSizing:"border-box"
    }}>
      {/* Subtle background animals */}
      {["🐔","🐑","🐰","🦆"].map((a,i)=>(
        <span key={i} style={{
          position:"absolute",fontSize:22,opacity:.08,pointerEvents:"none",
          top:`${[10,55,20,65][i]}%`,left:`${[4,6,90,88][i]}%`,
          transform:`rotate(${[-15,10,-8,12][i]}deg)`
        }}>{a}</span>
      ))}

      {/* Grass blades along top edge */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:14,display:"flex",alignItems:"flex-end",gap:2,paddingLeft:4,overflow:"hidden"}}>
        {Array.from({length:50}).map((_,i)=>(
          <div key={i} style={{
            width:4+Math.sin(i*1.7)*2,
            height:6+Math.sin(i*2.3)*6,
            background:`hsl(${100+Math.sin(i)*20},55%,${26+Math.sin(i*3)*8}%)`,
            borderRadius:"50% 50% 0 0",flexShrink:0,opacity:.65
          }}/>
        ))}
      </div>

      <div style={{fontSize:28,lineHeight:1,filter:"drop-shadow(0 2px 6px rgba(0,0,0,.3))"}}>🐾</div>

      <div>
        <h2 style={{fontFamily:"'Fraunces',serif",fontSize:17,fontWeight:800,color:"#fff",margin:"0 0 4px",lineHeight:1.2,textShadow:"0 2px 8px rgba(0,0,0,.4)"}}>
          Jouw park op Dierenparkje.nl?
        </h2>
        <p style={{fontSize:12,color:"rgba(255,255,255,.75)",margin:0,lineHeight:1.4}}>
          Gratis aansluiten · Meer bezoekers · Alles op één plek
        </p>
      </div>

      <button onClick={()=>onNavigate("aanvraag")} style={{
        background:"linear-gradient(135deg,#a8e64a,#7bc72a)",
        border:"none",color:"#1a3d0a",fontSize:14,fontWeight:800,
        cursor:"pointer",fontFamily:"inherit",padding:"10px 28px",
        borderRadius:50,boxShadow:"0 4px 16px rgba(0,0,0,.3)",
        letterSpacing:.2,transition:"transform .15s,box-shadow .15s",
        whiteSpace:"nowrap"
      }}
        onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 22px rgba(0,0,0,.35)";}}
        onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.3)";}}
      >
        🌿 Aanmelden — gratis
      </button>
    </div>
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   AANVRAAG PAGE - CONTACT FORM FOR PARK APPLICATIONS
   ═══════════════════════════════════════════════════════════ */

function AanvraagPage({onNavigate, db}) {
  const [formData, setFormData] = useState({
    naam_park: '',
    adres_park: '',
    postcode: '',
    plaatsnaam: '',
    contactpersoon: '',
    telefoon: '',
    email: '',
    bericht: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.naam_park.trim()) return 'Naam park is verplicht';
    if (!formData.adres_park.trim()) return 'Adres park is verplicht';
    if (!formData.postcode.trim()) return 'Postcode is verplicht';
    if (!formData.plaatsnaam.trim()) return 'Plaatsnaam is verplicht';
    if (!formData.contactpersoon.trim()) return 'Naam contactpersoon is verplicht';
    if (!formData.telefoon.trim()) return 'Telefoonnummer is verplicht';
    if (!formData.email.trim()) return 'Emailadres is verplicht';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Geldig emailadres nodig';
    return '';
  };

  const sendEmailViaEdgeFunction = async (data) => {
    try {
      // Call the Supabase Edge Function (no CORS issues, API key is secure on server)
      const edgeFunctionUrl = 'https://rbbmfbycrjwcalxaydxh.functions.supabase.co/send-contact-email';
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          naam_park: data.naam_park,
          adres_park: data.adres_park,
          postcode: data.postcode,
          plaatsnaam: data.plaatsnaam,
          contactpersoon: data.contactpersoon,
          telefoon: data.telefoon,
          email: data.email,
          bericht: data.bericht
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Email verzending mislukt');
      }

      const result = await response.json();
      console.log('✅ Edge Function response:', result);
      return result;
    } catch (err) {
      console.error('Edge Function error:', err);
      throw new Error(err.message || 'Email kon niet worden verzonden');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call Edge Function - it handles both email and database saving
      const result = await sendEmailViaEdgeFunction(formData);
      
      console.log('✅ Form submission successful:', result);

      setSuccess(true);
      setFormData({
        naam_park: '',
        adres_park: '',
        postcode: '',
        plaatsnaam: '',
        contactpersoon: '',
        telefoon: '',
        email: '',
        bericht: ''
      });

      // Show success for 3 seconds then redirect
      setTimeout(() => {
        onNavigate('landing');
      }, 3000);

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Er is iets fout gegaan. Probeer alstublieft opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",background:"#f5f5f5",overflowX:"hidden"}}>
    {/* Top Promo Section */}
    <div style={{background:"linear-gradient(135deg, #3a6b1f 0%, #2d5016 100%)",color:"#fff",padding:"40px 20px",textAlign:"center",flex:"0 0 auto",position:"relative"}}>
      <button onClick={()=>onNavigate("landing")} style={{position:"absolute",top:16,left:16,background:"rgba(255,255,255,0.2)",color:"#fff",border:"none",borderRadius:6,padding:"8px 16px",cursor:"pointer",fontSize:13}}>
        ← Terug
      </button>
      <h2 style={{fontSize:24,fontWeight:700,maxWidth:"600px",margin:"0 auto 15px"}}>
        Sluit gratis aan bij Dierenparkje.nl
      </h2>
      <p style={{fontSize:16,lineHeight:1.6,maxWidth:"600px",margin:"0 auto",opacity:0.95}}>
        Bereik duizenden potentiële bezoekers. Geen verborgen kosten, geen ingewikkelde contracten. Gewoon gratis meedoen!
      </p>
    </div>

    {/* Form Section */}
    <div style={{flex:1,padding:"40px 20px",maxWidth:"600px",width:"100%",margin:"0 auto"}}>
      {success ? (
        <div style={{background:"#e8f5e9",border:"2px solid #4caf50",borderRadius:8,padding:20,textAlign:"center"}}>
          <h3 style={{color:"#2e7d32",margin:"0 0 10px 0"}}>✅ Aanvraag ingediend!</h3>
          <p style={{color:"#558b2f",margin:0}}>We hebben uw gegevens ontvangen. U wordt zo teruggepast naar de startpagina.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <h3 style={{color:"var(--g)",fontSize:18,fontWeight:700,marginBottom:20}}>
            Gegevens van uw park
          </h3>

          {error && (
            <div style={{background:"#ffebee",border:"2px solid #f44336",borderRadius:8,padding:12,marginBottom:20,color:"#c62828",fontSize:14}}>
              ⚠️ {error}
            </div>
          )}

          {/* Form Fields */}
          <div style={{marginBottom:15}}>
            <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Naam park *</label>
            <input type="text" name="naam_park" value={formData.naam_park} onChange={handleChange}
              placeholder="bijv. De Kleine Boerderij" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
          </div>

          <div style={{marginBottom:15}}>
            <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Adres park *</label>
            <input type="text" name="adres_park" value={formData.adres_park} onChange={handleChange}
              placeholder="bijv. Dorpsstraat 123" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:15,marginBottom:15}}>
            <div>
              <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Postcode *</label>
              <input type="text" name="postcode" value={formData.postcode} onChange={handleChange}
                placeholder="1234 AB" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
            </div>
            <div>
              <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Plaatsnaam *</label>
              <input type="text" name="plaatsnaam" value={formData.plaatsnaam} onChange={handleChange}
                placeholder="Amsterdam" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
            </div>
          </div>

          <h3 style={{color:"var(--g)",fontSize:16,fontWeight:700,marginTop:25,marginBottom:15}}>
            Contactpersoon
          </h3>

          <div style={{marginBottom:15}}>
            <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Naam contactpersoon *</label>
            <input type="text" name="contactpersoon" value={formData.contactpersoon} onChange={handleChange}
              placeholder="Jan Jansen" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
          </div>

          <div style={{marginBottom:15}}>
            <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Telefoonnummer *</label>
            <input type="tel" name="telefoon" value={formData.telefoon} onChange={handleChange}
              placeholder="06 12345678" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
          </div>

          <div style={{marginBottom:15}}>
            <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Emailadres *</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              placeholder="jan@example.com" style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit"}}/>
          </div>

          <div style={{marginBottom:20}}>
            <label style={{display:"block",fontWeight:600,marginBottom:6,color:"var(--g)",fontSize:14}}>Bericht (optioneel)</label>
            <textarea name="bericht" value={formData.bericht} onChange={handleChange}
              placeholder="Iets extra wat u ons wilt vertellen..." rows="4"
              style={{width:"100%",padding:"12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,fontFamily:"inherit",resize:"vertical"}}/>
          </div>

          <PawBtn type="submit" loading={loading} style={{width:"100%",fontSize:16}}>Versturen</PawBtn>

          <p style={{fontSize:12,color:"#999",marginTop:15,textAlign:"center"}}>
            * Verplichte velden
          </p>
        </form>
      )}
      
      {/* Footer with Privacy Link */}
      <div style={{textAlign:"center",padding:"30px 20px 20px",color:"#999",fontSize:12,borderTop:"1px solid #eee",marginTop:30}}>
        <button onClick={() => onNavigate("privacy")} 
          style={{background:"none",border:"none",color:"var(--g)",cursor:"pointer",textDecoration:"underline",fontSize:12,fontWeight:500}}>
          🔒 Privacybeleid
        </button>
        <p style={{margin:"15px 0 0 0",color:"#bbb",fontSize:11}}>© 2026 Dierenparkje.nl</p>
      </div>
    </div>
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   PLATFORM: INFO / MARKETING PAGE
   ═══════════════════════════════════════════════════════════ */

function InfoPage({onNavigate}){
  const features = [
    {icon:"\uD83D\uDC3E",title:"Dierenbeheer",desc:"Registreer alle dieren met soort, locatie, foto's en medische gegevens."},
    {icon:"\uD83C\uDF7D\uFE0F",title:"Voeding & Medicatie",desc:"Dagelijks afvinken wie gevoerd en behandeld is. Nooit meer iets missen."},
    {icon:"\uD83D\uDCCB",title:"Takenbeheer",desc:"Taken toewijzen, prioriteren en opvolgen. Van reparaties tot schoonmaak."},
    {icon:"\uD83D\uDCC5",title:"Personeelsplanning",desc:"Weekrooster met ochtend-, middag- en avonddiensten. Herhalende diensten instellen."},
    {icon:"\uD83D\uDCAC",title:"Berichten",desc:"Intern berichtensysteem met urgentieniveaus. Privé gemeente-kanaal."},
    {icon:"\uD83D\uDCC7",title:"Contacten",desc:"Dierenarts, gemeente, leveranciers, noodgevallen — alles bij de hand."},
  ];
  const doelgroepen = [
    {icon:"\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67\u200D\uD83D\uDC66",title:"Bezoekers",desc:"Vind een dierenparkje bij jou in de buurt en ontdek welke dieren er zijn."},
    {icon:"\uD83D\uDC69\u200D\uD83C\uDF3E",title:"Medewerkers & Vrijwilligers",desc:"Overzichtelijk zien wat je moet doen: voeding, medicatie, taken en je rooster."},
    {icon:"\uD83C\uDFDB\uFE0F",title:"Gemeentes & Organisaties",desc:"Professioneel beheer en rapportage voor gesubsidieerde kinderboerderijen."},
  ];
  return <div className="landing">
    <div className="zoek-header">
      <button className="zoek-back" onClick={()=>onNavigate("landing")}>{IC.back} Terug</button>
      <h1 className="zoek-title">Parkje aansluiten</h1>
    </div>
    <div className="l-section">
      <h2 className="l-section-title">Voor iedereen die van dieren houdt</h2>
      <div className="l-doelgroepen">
        {doelgroepen.map((d,i)=><div key={i} className="l-doel-card" style={{animationDelay:(i*0.1)+'s'}}>
          <span className="l-doel-icon">{d.icon}</span>
          <div><h3 className="l-doel-title">{d.title}</h3><p className="l-doel-desc">{d.desc}</p></div>
        </div>)}
      </div>
    </div>
    <div className="l-section l-section-dark">
      <h2 className="l-section-title" style={{color:"#fff"}}>Één app, alles geregeld</h2>
      <p className="l-section-sub">Van voedingsschema tot gemeentecorrespondentie — ontworpen voor hoe dierenparkjes echt werken.</p>
      <div className="l-features">
        {features.map((f,i)=><div key={i} className="l-feat-card">
          <span className="l-feat-icon">{f.icon}</span>
          <div><h3 className="l-feat-title">{f.title}</h3><p className="l-feat-desc">{f.desc}</p></div>
        </div>)}
      </div>
    </div>
    <div className="l-section l-cta">
      <h2 className="l-cta-title">Interesse?</h2>
      <p className="l-cta-sub">Neem contact op om jouw dierenparkje aan te sluiten bij het platform.</p>
      <button className="l-btn l-btn-primary" onClick={()=>onNavigate("landing")}>{IC.back} Terug naar home</button>
    </div>
    <div className="l-footer">
      <div className="l-footer-logo">{"\uD83D\uDC3E"} Dierenparkje.nl</div>
      <p className="l-footer-copy">{"\u00A9"} 2026 Dierenparkje.nl</p>
    </div>
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   PLATFORM: ZOEK EEN PARKJE (with Leaflet map)
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   CITY COORDINATES & SEARCH
   ═══════════════════════════════════════════════════════════ */
const CITY_COORDS = {
  'coevorden': { lat: 52.6612, lng: 6.7411 }, 'orvelte': { lat: 52.8411, lng: 6.6183 }, 'assen': { lat: 52.9925, lng: 6.5625 }, 'ruinerwold': { lat: 52.7500, lng: 6.6000 }, 'erica': { lat: 52.7333, lng: 6.6167 }, 'schoonebeek': { lat: 52.6833, lng: 6.8333 }, 'dronten': { lat: 52.5311, lng: 5.8228 }, 'emmeloord': { lat: 52.7100, lng: 5.7822 }, 'urk': { lat: 52.6580, lng: 5.5896 }, 'almere': { lat: 52.3508, lng: 5.2647 }, 'drachten': { lat: 53.1025, lng: 6.3980 }, 'leeuwarderadeel': { lat: 53.2000, lng: 6.1500 }, 'sneek': { lat: 53.0347, lng: 5.6447 }, 'leeuwarden': { lat: 53.2011, lng: 5.7860 }, 'hoorn': { lat: 52.6426, lng: 5.0571 }, 'aalten': { lat: 51.9269, lng: 6.5794 }, 'laren': { lat: 51.9978, lng: 6.4569 }, 'druten': { lat: 51.8178, lng: 5.8061 }, 'lent': { lat: 51.8676, lng: 5.8584 }, 'ammerzoden': { lat: 51.8500, lng: 5.5083 }, 'nijmegen': { lat: 51.8425, lng: 5.8520 }, 'zutphen': { lat: 52.1414, lng: 6.1956 }, 'rheden': { lat: 51.9931, lng: 6.1575 }, 'eerbeek': { lat: 52.0294, lng: 6.2022 }, 'malden': { lat: 51.7950, lng: 5.9061 }, 'tiel': { lat: 51.8767, lng: 5.4244 }, 'voorthuizen': { lat: 52.1458, lng: 6.0233 }, 'culemborg': { lat: 51.9753, lng: 5.2353 }, 'beuningen': { lat: 51.8778, lng: 5.8394 }, 'apeldoorn': { lat: 52.2116, lng: 5.9699 }, 'epe': { lat: 52.4283, lng: 6.0933 }, 'otterloo': { lat: 52.0753, lng: 5.9992 }, 'wilp': { lat: 52.4239, lng: 6.2458 }, 'ede': { lat: 52.0411, lng: 5.6639 }, 'arnhem': { lat: 51.9851, lng: 5.8987 }, 'doetinchem': { lat: 51.9586, lng: 6.2878 }, 'didam': { lat: 51.8319, lng: 6.1364 }, 'bekveld': { lat: 51.9656, lng: 5.7953 }, 'heelsum': { lat: 52.0236, lng: 5.9619 }, 'duiven': { lat: 51.9331, lng: 5.8814 }, 'zevenaar': { lat: 51.9350, lng: 5.9769 }, 'hattem': { lat: 52.4722, lng: 5.8569 }, 'wezep': { lat: 52.5222, lng: 6.0500 }, 'velp': { lat: 51.9856, lng: 5.9858 }, 'elst': { lat: 51.9322, lng: 5.5750 }, 'wijchen': { lat: 51.8550, lng: 5.7289 }, 'heteren': { lat: 51.8917, lng: 5.5944 }, 'groningen': { lat: 53.2194, lng: 6.5669 }, 'eibergen': { lat: 52.0722, lng: 6.5694 }, 'valkenburg': { lat: 50.8656, lng: 5.8317 }, 'maastricht': { lat: 50.8514, lng: 5.6909 }, 'weert': { lat: 51.2500, lng: 5.7167 }, 'venlo': { lat: 51.3633, lng: 6.1697 }, 'sittard': { lat: 50.9906, lng: 5.8531 }, 'heel': { lat: 51.1667, lng: 5.8500 }, 'brunssum': { lat: 50.9564, lng: 5.9844 }, 'wellerlooi': { lat: 51.5333, lng: 5.5833 }, 'breda': { lat: 51.5719, lng: 4.7764 }, 'tilburg': { lat: 51.5586, lng: 5.0853 }, 'eindhoven': { lat: 51.4416, lng: 5.4697 }, "'s-hertogenbosch": { lat: 51.6898, lng: 5.3050 }, 'oss': { lat: 51.7633, lng: 5.5350 }, 'oirschot': { lat: 51.5394, lng: 5.2525 }, 'waspik': { lat: 51.6778, lng: 4.9667 }, 'waalwijk': { lat: 51.6406, lng: 5.0853 }, 'cuijk': { lat: 51.6036, lng: 5.3447 }, 'helmond': { lat: 51.4808, lng: 5.6636 }, 'roosendaal': { lat: 51.5269, lng: 4.4739 }, 'deurne': { lat: 51.6256, lng: 5.0972 }, 'rucphen': { lat: 51.4894, lng: 4.4603 }, 'heeswijk': { lat: 51.6333, lng: 5.3667 }, 'dinther': { lat: 51.6333, lng: 5.3667 }, 'woensdrecht': { lat: 51.3667, lng: 4.5667 }, 'amsterdam': { lat: 52.3676, lng: 4.9041 }, 'castricum': { lat: 52.5461, lng: 4.6736 }, 'alkmaar': { lat: 52.6332, lng: 4.7419 }, 'medemblik': { lat: 52.7757, lng: 5.1128 }, 'zaandam': { lat: 52.4384, lng: 4.8200 }, 'hilversum': { lat: 52.2285, lng: 5.1682 }, 'den burg': { lat: 53.0475, lng: 4.9639 }, 'kampen': { lat: 52.5550, lng: 5.9117 }, 'holten': { lat: 52.2567, lng: 6.3458 }, 'zwolle': { lat: 52.5050, lng: 6.0900 }, 'raalte': { lat: 52.3439, lng: 6.3544 }, 'ommen': { lat: 52.5089, lng: 6.4183 }, 'hengelo': { lat: 52.2700, lng: 6.7975 }, 'zeist': { lat: 52.0835, lng: 5.2667 }, 'utrecht': { lat: 52.0902, lng: 5.1075 }, 'maarssen': { lat: 52.1428, lng: 5.1578 }, 'haarzuilens': { lat: 52.1175, lng: 5.0075 }, 'eemnes': { lat: 52.3061, lng: 5.1639 }, 'zoeterwoude': { lat: 52.1333, lng: 4.5000 }, 'kerkwerve': { lat: 51.6758, lng: 3.8567 }, 'sint maartensdijk': { lat: 51.5278, lng: 3.9667 }, 'sint-maartensdijk': { lat: 51.5278, lng: 3.9667 }, 'clinge': { lat: 51.3667, lng: 3.8833 }, 'goes': { lat: 51.5311, lng: 3.8859 }, 'zierikzee': { lat: 51.6503, lng: 3.9197 }, 'terneuzen': { lat: 51.3322, lng: 3.8394 }, 'waddinxveen': { lat: 52.0447, lng: 4.6514 }, 'delft': { lat: 52.0116, lng: 4.3571 }, 'den haag': { lat: 52.0705, lng: 4.3007 }, 'rotterdam': { lat: 51.9225, lng: 4.4792 }, 'barendrecht': { lat: 51.8522, lng: 4.5142 }, 'schieveen': { lat: 51.9333, lng: 4.4833 }, 'groot-ammers': { lat: 51.9533, lng: 4.6967 }, 'schiedam': { lat: 51.9272, lng: 4.4058 }, 'kralingen': { lat: 51.9333, lng: 4.5167 }, 'terschuur': { lat: 52.1839, lng: 5.2428 }, 'hoogvliet': { lat: 51.8850, lng: 4.4150 }, 'etten-leur': { lat: 51.5667, lng: 4.6167 }, 'etten': { lat: 51.5667, lng: 4.6167 }, 'leur': { lat: 51.5667, lng: 4.6167 }, 'brouwershaven': { lat: 51.6667, lng: 3.8833 }, 'yerseke': { lat: 51.4833, lng: 3.9833 }, 'middelburg': { lat: 51.4981, lng: 3.6109 }, 'vlissingen': { lat: 51.4447, lng: 3.5753 }, 'roermond': { lat: 51.1928, lng: 5.9869 }, 'haarlem': { lat: 52.3909, lng: 4.6345 }, 'leiden': { lat: 52.1601, lng: 4.4970 }, 'dieren': { lat: 52.0483, lng: 6.3639 }, 'arnhem-noord': { lat: 51.9851, lng: 5.8987 }, 'veenendaal': { lat: 52.0256, lng: 5.5619 }, 'nieuwegein': { lat: 52.0261, lng: 5.1181 }, 'vianen': { lat: 51.9889, lng: 5.3286 }, 'gorinchem': { lat: 51.8333, lng: 4.9667 }, 'leerdam': { lat: 51.9089, lng: 5.1289 }, 'doesburg': { lat: 52.0089, lng: 6.2919 }, 'groenlo': { lat: 52.0019, lng: 6.6261 }, 'varsseveld': { lat: 51.9372, lng: 6.3558 }, 'harderwijk': { lat: 52.3373, lng: 5.3744 }, 'lelystad': { lat: 52.5081, lng: 5.4704 }, 'almelo': { lat: 52.3539, lng: 6.6697 }, 'enschede': { lat: 52.2215, lng: 6.8936 }, 'oldenzaal': { lat: 52.3142, lng: 6.9256 }, 'usselmuide': { lat: 52.7033, lng: 6.0364 }, 'stavoren': { lat: 53.0022, lng: 5.3667 },
};
const normalizeCity = (city) => city.toLowerCase().replace(/[àáâäâ]/g, 'a').replace(/[èéêë]/g, 'e').replace(/[ìíîï]/g, 'i').replace(/[òóôö]/g, 'o').replace(/[ùúûü]/g, 'u').replace(/ij/g, 'y').replace(/\s+/g, ' ').trim();
const getCityCoords = (cityName) => {
  const normalized = normalizeCity(cityName);
  if (CITY_COORDS[normalized]) return CITY_COORDS[normalized];
  const key = Object.keys(CITY_COORDS).find(k => {
    const keyNorm = normalizeCity(k);
    return normalized.includes(keyNorm) || keyNorm.includes(normalized) || normalized.replace(/[\s-]/g, '').includes(keyNorm.replace(/[\s-]/g, ''));
  });
  return key ? CITY_COORDS[key] : null;
};

function ZoekPage({onNavigate, onSelectPark, parken, actieveIds, isBezoeker}){
  const [zoek,setZoek]=useState("");
  const [selected,setSelected]=useState(null);
  const [provFilter,setProvFilter]=useState("alle");
  const [userLoc,setUserLoc]=useState(null);
  const [locLoading,setLocLoading]=useState(false);
  const [locError,setLocError]=useState(null);
  const [showNearby,setShowNearby]=useState(false);
  const [searchCity,setSearchCity]=useState("");
  const [citySearchResults,setCitySearchResults]=useState(null);
  const mapRef=useRef(null);
  const mapInstanceRef=useRef(null);
  const markersRef=useRef([]);
  const userMarkerRef=useRef(null);

  const PROV_NL=["Drenthe","Flevoland","Friesland","Gelderland","Groningen","Limburg","Noord-Brabant","Noord-Holland","Overijssel","Utrecht","Zeeland","Zuid-Holland"];
  const PROV_BE=["Antwerpen","Henegouwen","Limburg (B)","Luik","Luxemburg","Namen","Oost-Vlaanderen","Vlaams-Brabant","Waals-Brabant","West-Vlaanderen"];
  const provSet=[...new Set(parken.map(p=>p.provincie).filter(Boolean))];
  const provincies=["alle",...PROV_NL.filter(p=>provSet.includes(p)),...PROV_BE.filter(p=>provSet.includes(p)),...provSet.filter(p=>!PROV_NL.includes(p)&&!PROV_BE.includes(p)).sort()];

  const calcDist=(lat1,lng1,lat2,lng2)=>{
    const R=6371;const dLat=(lat2-lat1)*Math.PI/180;const dLng=(lng2-lng1)*Math.PI/180;
    const a=Math.sin(dLat/2)*Math.sin(dLat/2)+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)*Math.sin(dLng/2);
    return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
  };

  const parksWithDist=userLoc?parken.map(p=>({...p,dist:calcDist(userLoc.lat,userLoc.lng,p.lat,p.lng)})):parken.map(p=>({...p,dist:null}));
  const nearbyParks=userLoc?[...parksWithDist].sort((a,b)=>a.dist-b.dist).slice(0,5):[];

  const filtered=parksWithDist.filter(p=>{
    const matchZoek=!zoek.trim()||p.naam.toLowerCase().includes(zoek.toLowerCase())||p.plaats.toLowerCase().includes(zoek.toLowerCase())||(p.adres||"").toLowerCase().includes(zoek.toLowerCase());
    const matchProv=provFilter==="alle"||p.provincie===provFilter;
    return matchZoek&&matchProv;
  });

  const findMyLocation=()=>{
    if(!navigator.geolocation){setLocError("Locatie niet beschikbaar op dit apparaat.");return;}
    setLocLoading(true);setLocError(null);
    navigator.geolocation.getCurrentPosition(
      (pos)=>{
        const loc={lat:pos.coords.latitude,lng:pos.coords.longitude};
        setUserLoc(loc);setLocLoading(false);setShowNearby(true);setProvFilter("alle");setZoek("");setCitySearchResults(null);setSearchCity("");
        const map=mapInstanceRef.current;
        if(map){
          map.flyTo([loc.lat,loc.lng],10,{duration:1});
          if(userMarkerRef.current) map.removeLayer(userMarkerRef.current);
          const LL=window.L;
          if(LL){
            const userIcon=LL.divIcon({className:'park-marker-icon',html:'<div style="width:16px;height:16px;border-radius:50%;background:#4285f4;border:3px solid #fff;box-shadow:0 0 0 2px #4285f4,0 2px 8px rgba(0,0,0,.3)"></div>',iconSize:[16,16],iconAnchor:[8,8]});
            userMarkerRef.current=LL.marker([loc.lat,loc.lng],{icon:userIcon}).addTo(map).bindTooltip("Jouw locatie",{direction:"top",offset:[0,-10],className:"park-tooltip"});
          }
        }
      },
      (err)=>{setLocLoading(false);setLocError(err.code===1?"Locatietoegang geweigerd. Sta locatie toe in je browser.":"Kon locatie niet bepalen.");},
      {enableHighAccuracy:true,timeout:10000}
    );
  };

  const handleCitySearch = (city) => {
    if (!city.trim()) {
      setCitySearchResults(null);
      return;
    }
    const coords = getCityCoords(city);
    if (!coords) {
      // Not a known city — search by park name instead
      const q = city.toLowerCase();
      const nameMatches = parksWithDist
        .filter(p => p.naam.toLowerCase().includes(q) || p.plaats.toLowerCase().includes(q))
        .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
      if(nameMatches.length > 0){
        setCitySearchResults({ success: true, cityName: city, cityCoords: null, parks: nameMatches.map(p=>({...p, distance: p.dist||0})) });
      } else {
        setCitySearchResults({ success: false, error: `Geen parkjes gevonden voor "${city}"` });
      }
      return;
    }
    // Calculate distances
    const parksWithDist2 = parken
      .filter(p => p.lat && p.lng)
      .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i)
      .map(p => ({
        ...p,
        distance: calcDist(coords.lat, coords.lng, p.lat, p.lng)
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
    
    setCitySearchResults({ success: true, cityName: city, cityCoords: coords, parks: parksWithDist2 });
    
    // Zoom to city
    const map = mapInstanceRef.current;
    if (map && coords) {
      map.flyTo([coords.lat, coords.lng], 9, { duration: 1 });
    }
  };

  useEffect(()=>{
    if(!mapRef.current||mapInstanceRef.current) return;
    const LL=window.L;
    if(!LL) return;
    const map=LL.map(mapRef.current,{zoomControl:true,scrollWheelZoom:true}).setView([52.1,5.3],7);
    LL.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{attribution:'&copy; OpenStreetMap',maxZoom:18}).addTo(map);
    mapInstanceRef.current=map;
    setTimeout(()=>map.invalidateSize(),100);
    return ()=>{map.remove();mapInstanceRef.current=null;};
  },[]);

  useEffect(()=>{
    const LL=window.L;const map=mapInstanceRef.current;
    if(!LL||!map) return;
    markersRef.current.forEach(m=>map.removeLayer(m));
    markersRef.current=[];
    
    // Toon alleen nearby parks als showNearby true is
    const parksToShow=showNearby?nearbyParks:filtered;
    
    parksToShow.forEach(park=>{
      const isActive=actieveIds.includes(park.id);const isSel=selected?.id===park.id;
      const size=isSel?22:isActive?16:10;
      let html;
      if(isSel){
        html='<div style="width:22px;height:22px;border-radius:50%;background:#c44536;border:3px solid #fff;box-shadow:0 0 0 3px #c4453680,0 3px 10px rgba(0,0,0,.35)"></div>';
      } else if(isActive){
        html='<div style="position:relative;width:16px;height:16px">'
          +'<div style="position:absolute;inset:0;border-radius:50%;background:#2d5016;opacity:.25;animation:pulse 2s ease-out infinite"></div>'
          +'<div style="position:absolute;inset:2px;border-radius:50%;background:#3a7a1e;border:2px solid #fff;box-shadow:0 2px 8px rgba(45,80,22,.5)"></div>'
          +'</div>';
      } else {
        html='<div style="width:10px;height:10px;border-radius:50%;background:#aaa;border:1.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.2)"></div>';
      }
      const icon=LL.divIcon({className:'park-marker-icon',html,iconSize:[size,size],iconAnchor:[size/2,size/2]});
      const marker=LL.marker([park.lat,park.lng],{icon}).addTo(map);
      marker.bindTooltip(park.naam,{direction:'top',offset:[0,-8],className:'park-tooltip'});
      marker.on('click',()=>{
        setSelected(park);map.flyTo([park.lat,park.lng],12,{duration:0.8});
        setTimeout(()=>{const el=document.getElementById('park-card-'+park.id);if(el) el.scrollIntoView({behavior:'smooth',block:'center'});},300);
      });
      markersRef.current.push(marker);
    });
    
    // Zoom to fit all shown parks
    if(parksToShow.length>1&&!selected){
      const bounds=LL.latLngBounds(parksToShow.map(p=>[p.lat,p.lng]));
      map.fitBounds(bounds,{padding:[50,50]});
    } else if(parksToShow.length===1&&!selected){
      // Single park - zoom in closer
      map.flyTo([parksToShow[0].lat,parksToShow[0].lng],11,{duration:1});
    }
  },[filtered,selected,showNearby,nearbyParks]);

  const focusPark=(park)=>{
    setSelected(park);setShowNearby(false);
    const map=mapInstanceRef.current;
    if(map) map.flyTo([park.lat,park.lng],12,{duration:0.8});
  };
  const fmtDist=(d)=>d<1?(Math.round(d*1000)+'m'):d<10?(d.toFixed(1)+' km'):(Math.round(d)+' km');

  const renderCard=(park)=>{
    const isActive=actieveIds.includes(park.id);
    const isSel=selected?.id===park.id;
    return <div key={park.id} id={'park-card-'+park.id}
      className={'zoek-card '+(isSel?'selected ':'')+(isActive?'zoek-card-active':'zoek-card-inactive')}
      onClick={()=>focusPark(park)}>
      <div className="zoek-card-top">
        <div className={"zoek-card-avatar"+(isActive?" zoek-card-avatar-active":"")}>{"\uD83D\uDC3E"}</div>
        <div className="zoek-card-info">
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <h3 className="zoek-card-name">{park.naam}</h3>
            {isActive&&<span className="zoek-actief-badge">✓ Aangesloten</span>}
          </div>
          <p className="zoek-card-place">{park.plaats} {"\u00B7"} {park.provincie}</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
          {park.soorten&&park.soorten.length>0&&<div className="zoek-card-badge">{park.soorten.length} soorten</div>}
          {park.google_rating&&<div className="zoek-card-badge" style={{background:"#fff8e1",color:"#e65100"}}>⭐ {park.google_rating} <span style={{fontWeight:400,opacity:.7}}>({park.google_reviews_count})</span></div>}
          {park.dist!=null&&<span className="zoek-dist-badge">{fmtDist(park.dist)}</span>}
        </div>
      </div>
      {isSel&&<div className="zoek-card-detail">
        <p className="zoek-card-desc">{park.beschrijving}</p>
        <div className="zoek-card-meta">
          {park.adres&&<div className="zoek-meta-row">{IC.pin} {park.adres}</div>}
          {park.openingstijden&&<div className="zoek-meta-row">{IC.clock} {park.openingstijden}</div>}
          {park.telefoon&&<div className="zoek-meta-row">{IC.phone} {park.telefoon}</div>}
          {park.dist!=null&&<div className="zoek-meta-row">{IC.map} {fmtDist(park.dist)} bij jou vandaan</div>}
          {(park.website||park.instagram)&&<div style={{display:"flex",gap:8,marginTop:6}}>
            {park.website&&<a href={park.website.startsWith("http")?park.website:"https://"+park.website} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
              style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,background:"#e8f5e9",color:"#2d5016",textDecoration:"none",fontSize:12,fontWeight:700}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              Website
            </a>}
            {park.instagram&&<a href={"https://instagram.com/"+park.instagram.replace(/^@/,"")} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
              style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,background:"linear-gradient(135deg,#f9ce34,#ee2a7b,#6228d7)",color:"#fff",textDecoration:"none",fontSize:12,fontWeight:700}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </a>}
          </div>}
        </div>
        {park.soorten&&park.soorten.length>0&&<div className="zoek-card-soorten">
          {park.soorten.map(s=><span key={s} className="zoek-soort-chip">{emoji[s]||"\uD83D\uDC3E"} {s}</span>)}
        </div>}
        {isActive&&<div style={{display:"flex",gap:8,marginTop:10}}>
          <button className="l-btn l-btn-primary" style={{flex:1}} onClick={(e)=>{e.stopPropagation();onNavigate("welkomPark",park);}}>
            🔍 Bezoek {park.naam}
          </button>
          {park.lat&&park.lng&&<button className="l-btn" style={{flexShrink:0,background:"#fff",border:"1px solid var(--brd)",color:"#1a73e8",fontWeight:600,padding:"0 14px"}}
            onClick={(e)=>{e.stopPropagation();window.open(`https://www.google.com/maps/dir/?api=1&destination=${park.lat},${park.lng}`,"_blank");}}>
            🗺️
          </button>}
        </div>}
        {!isActive&&<div style={{marginTop:10,padding:"10px 12px",borderRadius:10,background:"#f5f5f5",border:"1px dashed #ccc",fontSize:11,color:"var(--txtl)",textAlign:"center"}}>
          📬 Dit parkje is nog niet aangesloten op Dierenparkje.nl. Kom later terug!
        </div>}
      </div>}
    </div>;
  };

  return <div className="zoek-page">
    <div className="zoek-header">
      <button className="zoek-back" onClick={()=>onNavigate(isBezoeker?"mijnparkjes":"landing")}>{IC.back} Terug</button>
      <h1 className="zoek-title">Zoek een dierenparkje</h1>
      <p style={{fontSize:12,opacity:.8,marginTop:4}}>{parken.length} parkjes door heel Nederland</p>
    </div>
    <div className="zoek-search-wrap">
      <div className="zoek-search">
        {IC.search}
        <input placeholder="Zoek op naam, stad of plaatsnaam..." value={zoek} onChange={e=>{
          const v = e.target.value;
          setZoek(v);
          setSelected(null);
          setShowNearby(false);
          setSearchCity(v);
          handleCitySearch(v);
        }}/>
        {zoek&&<button className="zoek-clear" onClick={()=>{setZoek("");setSelected(null);setSearchCity("");setCitySearchResults(null);}}>x</button>}
      </div>
      <div style={{display:"flex",gap:6,marginTop:8,alignItems:"center"}}>
        <button className={'zoek-nearby-btn'+(showNearby?' a':'')} onClick={findMyLocation} disabled={locLoading}>
          {locLoading?<span className="zoek-spinner"/>:IC.pin} {locLoading?"Locatie zoeken...":"Bij mij in de buurt"}
        </button>
        {userLoc&&!showNearby&&<button className="zoek-nearby-btn mini" onClick={()=>setShowNearby(true)}>Top 5 dichtbij</button>}
      </div>
      {locError&&<p className="zoek-loc-error">{locError}</p>}
      {citySearchResults&&!citySearchResults.success&&<p style={{fontSize:13,color:"var(--txtl)",marginTop:8,padding:"0 2px"}}>❌ {citySearchResults.error}</p>}
      {citySearchResults&&citySearchResults.cityCoords&&(
        <div style={{marginTop:12,padding:"12px",background:"rgba(45, 80, 22, 0.05)",borderRadius:8}}>
          {citySearchResults.success?(
            <>
              <p style={{fontSize:12,fontWeight:600,color:"var(--g)",marginBottom:12}}>
                📍 {citySearchResults.parks.length} parkjes dicht bij {citySearchResults.cityName}:
              </p>
              {citySearchResults.parks.map(park=>(
                <div key={park.id} onClick={()=>focusPark(park)} style={{padding:"8px 12px",marginBottom:8,background:"white",borderRadius:6,cursor:"pointer",border:"1px solid var(--bd)",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={(e)=>e.currentTarget.style.background="var(--cream)"} onMouseLeave={(e)=>e.currentTarget.style.background="white"}>
                  <div>
                    <strong>{park.naam}</strong>
                    <div style={{fontSize:11,color:"var(--txtl)"}}>{park.plaats}</div>
                  </div>
                  <div style={{fontWeight:600,color:"var(--g)"}}>{park.distance.toFixed(1)} km</div>
                </div>
              ))}
            </>
          ):(
            <p style={{fontSize:13,color:"var(--txtl)"}}>❌ {citySearchResults.error}</p>
          )}
        </div>
      )}
      <div className="zoek-prov-filter">
        {provincies.map(prov=><button key={prov} className={'zoek-prov-btn '+(provFilter===prov?'a':'')} onClick={()=>{setProvFilter(prov);setSelected(null);setShowNearby(false);}}>{prov==="alle"?"Alle":prov}</button>)}
      </div>
    </div>
    <div className="zoek-map-container" ref={mapRef}/>
    {citySearchResults&&citySearchResults.success&&<div className="zoek-results" style={{paddingBottom:0}}>
      <div className="zoek-nearby-header">
        <span className="zoek-results-count" style={{color:"var(--g)",fontWeight:700}}>{citySearchResults.cityCoords ? "📍" : "🔍"} {citySearchResults.parks.length} {citySearchResults.cityCoords ? "parkjes dicht bij "+citySearchResults.cityName : "parkjes gevonden voor \""+citySearchResults.cityName+"\""}</span>
        <button className="zoek-nearby-close" onClick={()=>{setSearchCity("");setCitySearchResults(null);}}>Toon alles</button>
      </div>
      {citySearchResults.parks.map(renderCard)}
    </div>}
    {showNearby&&nearbyParks.length>0&&<div className="zoek-results" style={{paddingBottom:0}}>
      <div className="zoek-nearby-header">
        <span className="zoek-results-count" style={{color:"var(--g)",fontWeight:700}}>{"📍"} Top 5 bij jou in de buurt</span>
        <button className="zoek-nearby-close" onClick={()=>setShowNearby(false)}>Toon alles</button>
      </div>
      {nearbyParks.map(renderCard)}
    </div>}
    {!showNearby&&!citySearchResults?.success&&<div className="zoek-results">
      <div className="zoek-results-count">{filtered.length} {filtered.length===1?"parkje":"parkjes"} gevonden</div>
      {(userLoc?[...filtered].sort((a,b)=>(a.dist||999)-(b.dist||999)):filtered).map(renderCard)}
      {filtered.length===0&&<div className="zoek-empty">
        <span style={{fontSize:40}}>{"\uD83D\uDD0D"}</span>
        <p>Geen dierenparkjes gevonden</p>
        <p style={{fontSize:12,color:"var(--txtl)"}}>Probeer een andere zoekterm of selecteer alle provincies.</p>
      </div>}
    </div>}
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   MIJN DIERENPARKJES - BEZOEKER PAGINA
   ═══════════════════════════════════════════════════════════ */

function MijnDierenparkjesPage({onNavigate, bezoeken, parken, user, setSelectedPark}){
  const [selectedParkMedia, setSelectedParkMedia] = useState(null);
  const [parkMedia, setParkMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  
  const totalParken = parken.length;
  const bezochteCount = new Set(bezoeken.map(b => b.park_id)).size;
  const progressPercent = Math.round((bezochteCount / totalParken) * 100);
  
  const loadParkMedia = async (parkId) => {
    try {
      setMediaLoading(true);
      const media = await db.getMediaByPark(parkId);
      setParkMedia(media);
      console.log("📸 Park media loaded:", media.length);
    } catch(err) {
      console.error("Error loading park media:", err);
    } finally {
      setMediaLoading(false);
    }
  };
  
  const handleShowParkMedia = (park) => {
    if (selectedParkMedia?.id === park.id) {
      setSelectedParkMedia(null);
    } else {
      setSelectedParkMedia(park);
      loadParkMedia(park.id);
    }
  };
  
  // Group bezoeken by park
  const bezookenPerPark = {};
  bezoeken.forEach(b => {
    if(!bezookenPerPark[b.park_id]) bezookenPerPark[b.park_id] = [];
    bezookenPerPark[b.park_id].push(b);
  });
  
  const getBezoekenVoorPark = (parkId) => bezookenPerPark[parkId] || [];
  const getParkInfo = (parkId) => parken.find(p => p.id === parkId);
  
  return <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#fff9e6 0%,#f0f4ff 100%)",paddingBottom:40}}>
    {/* Header */}
    <div style={{background:"var(--g)",color:"#fff",padding:"16px",display:"flex",alignItems:"center",gap:12}}>
      <button onClick={async()=>{await sb.auth.signOut(); localStorage.clear(); sessionStorage.clear(); window.location.href = "/";}} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,padding:8,cursor:"pointer",color:"#fff",fontSize:18}} title="Naar homepage">🏠</button>
      <button onClick={()=>{toast.confirm("Wil je uitloggen?",()=>onNavigate("logout"));}} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,padding:8,cursor:"pointer",color:"#fff",fontSize:18}} title="Uitloggen">🚪</button>
      <div style={{flex:1}}>
        <h1 style={{margin:0,fontSize:20}}>Mijn Dierenparkjes</h1>
        <p style={{margin:0,fontSize:11,opacity:0.9}}>Jouw bezoeken</p>
      </div>
    </div>

    {/* Progress */}
    <div style={{padding:"16px",background:"#fff",margin:"12px 16px",borderRadius:12,boxShadow:"0 2px 8px rgba(0,0,0,.08)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:12,fontWeight:600,color:"var(--g)"}}>🏆 Voortgang</div>
        <div style={{fontSize:13,fontWeight:600,color:"var(--g)"}}>{bezochteCount} / {totalParken}</div>
      </div>
      <div style={{background:"#e0e0e0",borderRadius:8,height:8,overflow:"hidden"}}>
        <div style={{background:"var(--g)",height:"100%",width:progressPercent+"%",transition:"width .3s"}}/>
      </div>
      <div style={{fontSize:11,color:"var(--txtl)",marginTop:8,textAlign:"center"}}>
        {progressPercent}% van alle parkjes bezocht
      </div>
    </div>

    {/* Bezoeken List */}
    {bezoeken.length === 0 ? (
      <div style={{padding:"40px 16px",textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>🎫</div>
        <p style={{color:"var(--txtl)",marginBottom:8}}>Je hebt nog geen parkjes bezocht</p>
        <button onClick={()=>onNavigate("zoek")} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",cursor:"pointer",fontWeight:600}}>
          Ontdek parkjes →
        </button>
      </div>
    ) : (
      <div style={{padding:"0 16px"}}>
        {/* Discover button */}
        <button onClick={()=>onNavigate("zoek")} style={{
          width:"100%",
          background:"var(--g)",
          color:"#fff",
          border:"none",
          borderRadius:8,
          padding:"12px",
          cursor:"pointer",
          fontWeight:600,
          marginBottom:"24px"
        }}>
          Ontdek parkjes →
        </button>

        {/* Visited parks section */}
        <div style={{marginBottom:"20px"}}>
          <h2 style={{fontSize:14,fontWeight:700,color:"var(--g)",marginBottom:12,marginLeft:0}}>
            🏞️ Parkjes die ik heb bezocht:
          </h2>
          
          {Object.keys(bezookenPerPark).sort().map(parkId => {
            const park = getParkInfo(parkId);
            const visits = bezookenPerPark[parkId];
            const latestVisit = visits[0]; // Most recent
            
            if(!park) return null;
            
            return <div key={parkId} onClick={() => {setSelectedPark(park); onNavigate("welkomPark");}} style={{
              background:"#fff",
              borderRadius:12,
              padding:16,
              marginBottom:12,
              boxShadow:"0 2px 8px rgba(0,0,0,.08)",
              border:"1px solid #e0e0e0",
              cursor:"pointer",
              transition:"all 0.2s",
            }} onMouseEnter={(e) => {e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.12)"; e.currentTarget.style.transform = "translateY(-2px)";}} onMouseLeave={(e) => {e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.08)"; e.currentTarget.style.transform = "translateY(0);"}}>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:15,fontWeight:600,color:"var(--g)",marginBottom:2}}>
                  {park.naam}
                </div>
                <div style={{fontSize:12,color:"var(--txtl)"}}>
                  📍 {park.plaats}
                </div>
              </div>

              {/* Rating */}
              <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:12,fontWeight:600,color:"var(--g)"}}>Rating:</span>
                {latestVisit.rating ? (
                  <div>
                    {[...Array(5)].map((_, i) => (
                      <span key={i} style={{fontSize:14,marginRight:2}}>
                        {i < latestVisit.rating ? "⭐" : "☆"}
                      </span>
                    ))}
                    <span style={{fontSize:12,color:"var(--txtl)",marginLeft:6}}>
                      ({latestVisit.rating}/5)
                    </span>
                  </div>
                ) : (
                  <span style={{fontSize:12,color:"var(--txtl)"}}>Nog niet beoordeeld</span>
                )}
              </div>

              {/* Animals spotted */}
              <div style={{
                background:"#f5f5f5",
                padding:12,
                borderRadius:8,
                fontSize:13,
                fontWeight:600,
                color:"var(--g)",
                textAlign:"center"
              }}>
                🐾 {latestVisit.dieren_gezien?.length || 0} dieren gezien
              </div>

              {/* Media Button */}
              <button onClick={(e) => {e.stopPropagation(); handleShowParkMedia(park);}} style={{
                width: "100%",
                marginTop: 12,
                background: "var(--gp)",
                border: "1px solid var(--g)",
                color: "var(--g)",
                padding: 12,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }} onMouseEnter={(e) => {e.target.style.background = "var(--g)"; e.target.style.color = "#fff";}} onMouseLeave={(e) => {e.target.style.background = "var(--gp)"; e.target.style.color = "var(--g)";}}>
                🎬 Media Bekijken
              </button>

              {/* Visit date */}
              <div style={{
                fontSize:11,
                color:"var(--txtl)",
                marginTop:12,
                paddingTop:12,
                borderTop:"1px solid #e0e0e0",
                textAlign:"right"
              }}>
                Laatste bezoek: {new Date(latestVisit.datum).toLocaleDateString("nl-NL")}
              </div>
            </div>;
          })}
        </div>
      </div>
    )}

    {/* Park Media Modal */}
    {selectedParkMedia && (
      <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 1000,
        padding: "16px"
      }}>
        <div style={{
          background: "#fff",
          borderRadius: "16px 16px 0 0",
          padding: "24px",
          width: "100%",
          maxWidth: "500px",
          maxHeight: "80vh",
          overflow: "auto"
        }}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
            <h3 style={{fontSize: 18, fontWeight: 600, color: "var(--g)", margin: 0}}>
              🎬 {selectedParkMedia.naam}
            </h3>
            <button onClick={() => setSelectedParkMedia(null)} style={{
              background: "none",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "var(--txtl)"
            }}>
              ✕
            </button>
          </div>
          
          {mediaLoading ? (
            <div style={{textAlign: "center", padding: 20}}>
              <div style={{fontSize: 24}}>🔄</div>
              <div style={{fontSize: 12, color: "var(--txtl)", marginTop: 8}}>Laden...</div>
            </div>
          ) : parkMedia.length === 0 ? (
            <div style={{textAlign: "center", padding: 20, background: "#f5f5f5", borderRadius: 12}}>
              <div style={{fontSize: 24, marginBottom: 8}}>📭</div>
              <div style={{fontSize: 12, color: "var(--txtl)"}}>Geen media beschikbaar</div>
            </div>
          ) : (
            <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 12}}>
              {parkMedia.map(m => (
                <div key={m.id} style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#f5f5f5",
                  aspectRatio: "1",
                  position: "relative",
                  cursor: "pointer"
                }}>
                  {m.file_type === "image" ? (
                    <img src={m.file_url} alt={m.file_name} style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }} />
                  ) : (
                    <div style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#333",
                      color: "#fff",
                      fontSize: 32
                    }}>
                      🎬
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
  </div>;
}

/* ─── Instagram Card ─── */
function InstagramCard({park, canEdit=false}){
  const [editing, setEditing]=useState(false);
  const [handle, setHandle]=useState(park.instagram||"");
  const [saving, setSaving]=useState(false);
  const clean=(v)=>v.replace(/^@/,"").replace(/https?:\/\/(www\.)?instagram\.com\//,"").replace(/\/$/,"").trim();
  const url=park.instagram?`https://instagram.com/${clean(park.instagram)}`:null;
  const save=async()=>{
    setSaving(true);
    const val=clean(handle);
    await sb.from("parken").update({instagram:val||null}).eq("id",park.id);
    park.instagram=val||null;
    setSaving(false);
    setEditing(false);
  };
  if(!canEdit&&!url) return null;
  return <div className="card" style={{marginBottom:14}}>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:url&&!editing?10:0}}>
      <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
      </div>
      <div style={{flex:1}}>
        <div style={{fontWeight:700,fontSize:14,color:"var(--txt)"}}>Instagram</div>
        {!editing&&<div style={{fontSize:12,color:"var(--txtl)"}}>{url?`@${clean(park.instagram)}`:"Nog niet ingesteld"}</div>}
      </div>
      {canEdit&&!editing&&<button onClick={()=>{setHandle(park.instagram||"");setEditing(true);}} style={{background:"none",border:"1px solid var(--brd)",borderRadius:8,padding:"5px 10px",fontSize:12,cursor:"pointer",color:"var(--txtl)"}}>✏️ Instellen</button>}
    </div>
    {editing&&<div style={{marginTop:10}}>
      <div style={{display:"flex",gap:8}}>
        <input className="fi" value={handle} onChange={e=>setHandle(e.target.value)} placeholder="@parkjanssen of URL" style={{flex:1}}/>
        <button className="fbtn" style={{padding:"8px 14px"}} onClick={save} disabled={saving}>{saving?"...":"Opslaan"}</button>
        <button onClick={()=>setEditing(false)} style={{background:"none",border:"1px solid var(--brd)",borderRadius:8,padding:"8px 12px",cursor:"pointer",fontSize:13}}>✕</button>
      </div>
      <p style={{fontSize:11,color:"var(--txtl)",marginTop:6}}>Vul je @handle, profielnaam of volledige Instagram URL in.</p>
    </div>}
    {url&&!editing&&<a href={url} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)",color:"#fff",borderRadius:10,padding:"10px 16px",textDecoration:"none",fontWeight:700,fontSize:14}}>
      Bekijk op Instagram →
    </a>}
  </div>;
}

function WelkomParkPage({onNavigate, park, onStartSpotting, user}){
  const [showRating, setShowRating] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showGastWaarschuwing, setShowGastWaarschuwing] = useState(false);
  const [loginMode, setLoginMode] = useState("login");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  // Load media for this park
  const loadMedia = async () => {
    try {
      setMediaLoading(true);
      const mediaList = await db.getMediaByPark(park.id);
      setMedia(mediaList || []);
      console.log("📸 Media loaded:", mediaList?.length);
    } catch(err) {
      console.error("Error loading media:", err);
      setMedia([]);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleShowMedia = () => {
    if (!showMedia) {
      loadMedia();
    }
    setShowMedia(!showMedia);
  };


  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError("Email en wachtwoord invullen");
      return;
    }
    
    try {
      setLoginLoading(true);
      setLoginError("");
      
      const {data, error} = await sb.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword
      });
      
      if (error) throw error;
      console.log("✅ Ingelogd!", data.user.email);
      setShowLogin(false);
    } catch(err) {
      setLoginError(err.message || "Login mislukt");
      console.error("Login error:", err);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError("Email en wachtwoord invullen");
      return;
    }
    
    if (loginPassword.length < 6) {
      setLoginError("Wachtwoord min. 6 tekens");
      return;
    }
    
    try {
      setLoginLoading(true);
      setLoginError("");
      
      const {data, error} = await sb.auth.signUp({
        email: loginEmail,
        password: loginPassword
      });
      
      if (error) throw error;
      console.log("✅ Account aangemaakt!", data.user.email);
      setLoginError("Check je email voor bevestiging!");
      setTimeout(() => setShowLogin(false), 2000);
    } catch(err) {
      setLoginError(err.message || "Registratie mislukt");
      console.error("Register error:", err);
    } finally {
      setLoginLoading(false);
    }
  };

  // Helper: Create or get bezoek record
  const createOrGetBezoek = async () => {
    let bezoekId = sessionStorage.getItem(`bezoek_${park.id}`);
    
    if (!bezoekId) {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        // Check if bezoek for today already exists
        const existingBezoeken = await db.getBezoeken(user.id);
        const todayBezoek = existingBezoeken.find(
          b => b.park_id === park.id && b.datum === today
        );
        
        if (todayBezoek) {
          bezoekId = todayBezoek.id;
          console.log("✅ Bezoek vandaag gevonden:", bezoekId);
        } else {
          // Create new bezoek
          const bezoekData = {
            bezoeker_id: user.id,
            park_id: park.id,
            datum: today,
            dieren_gezien: [],
            notitie: null,
            rating: null
          };
          const newBezoek = await db.addBezoek(bezoekData);
          bezoekId = newBezoek.id;
          console.log("✅ Nieuw bezoek aangemaakt:", bezoekId);
        }
        
        sessionStorage.setItem(`bezoek_${park.id}`, bezoekId);
      } catch(err) {
        console.error("Error in createOrGetBezoek:", err);
        throw err;
      }
    }
    return bezoekId;
  };

  const handleBeginSpotting = async () => {
    setLoading(true);
    try {
      if(user) await createOrGetBezoek();
      onStartSpotting();
      onNavigate("parkVisit");
    } catch(err) {
      toast.err("Kon bezoek niet registreren. Probeer opnieuw.");
    }
    setLoading(false);
  };

  const handleRating = async () => {
    if(rating === 0) {
      toast.warn("Kies een rating alstublieft");
      return;
    }
    setLoading(true);
    try {
      const bezoekId = await createOrGetBezoek();
      await db.updateBezoek(bezoekId, { rating });
      console.log("✅ Rating opgeslagen:", rating);
      toast.ok(`Bedankt voor je ${rating} sterren! ⭐`);
      setShowRating(false);
      setRating(0);
    } catch(err) {
      console.error("Error saving rating:", err);
      toast.err("Kon rating niet opslaan. Probeer opnieuw.");
    }
    setLoading(false);
  };

  const handleMessage = async () => {
    if(!message.trim()) {
      toast.warn("Schrijf een bericht alstublieft");
      return;
    }
    setLoading(true);
    try {
      const bezoekId = await createOrGetBezoek();
      await db.updateBezoek(bezoekId, { notitie: message });
      console.log("✅ Bericht opgeslagen");
      toast.ok("Bedankt voor je bericht! 💬");
      setShowMessage(false);
      setMessage("");
    } catch(err) {
      console.error("Error saving message:", err);
      toast.err("Kon bericht niet opslaan. Probeer opnieuw.");
    }
    setLoading(false);
  };

  return <div style={{
    minHeight: "100vh",
    backgroundImage: `url('https://rbbmfbycrjwcalxaydxh.supabase.co/storage/v1/object/public/DierenBucket/parkplaatje.png')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "16px"
  }}>
    {/* Back button */}
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px"
    }}>
      <button onClick={()=>onNavigate(user?"mijnparkjes":"zoek")} style={{
        background: "rgba(255,255,255,0.9)",
        border: "none",
        borderRadius: 8,
        padding: "10px 16px",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        color: "var(--g)"
      }}>
        ← Terug
      </button>
    </div>

    {/* Park name on wooden board */}
    <div style={{
      position: "relative",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-end",
      paddingBottom: "24px"
    }}>
      {/* Park name - positioned on wooden board */}
      <div style={{
        textAlign: "center",
        zIndex: 2
      }}>
        <div style={{
          fontSize: "24px",
          fontWeight: 700,
          color: "#2d5016",
          textShadow: "2px 2px 4px rgba(0,0,0,0.15)",
          marginBottom: "8px",
          fontFamily: "'Fraunces', serif"
        }}>
          Welkom op
        </div>
        <div style={{
          fontSize: "32px",
          fontWeight: 700,
          color: "#2d5016",
          textShadow: "2px 2px 6px rgba(0,0,0,0.2)",
          fontFamily: "'Fraunces', serif",
          marginBottom: "24px"
        }}>
          {park.naam}
        </div>
      </div>
    </div>

    {/* Action buttons */}
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      paddingBottom: "20px"
    }}>
      {/* Start Spotting Button */}
      <button onClick={user ? handleBeginSpotting : () => setShowGastWaarschuwing(true)} style={{
        background: "var(--g)",
        color: "#fff",
        border: "none",
        borderRadius: 12,
        padding: "16px 24px",
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: "0 4px 12px rgba(45,80,22,0.2)",
        transition: "all 0.2s",
      }} onMouseEnter={(e)=>e.target.style.transform="scale(1.02)"} onMouseLeave={(e)=>e.target.style.transform="scale(1)"} >
        🎮 Dieren Spotten
      </button>

      {/* Rating Button */}
      <button onClick={user ? ()=>setShowRating(!showRating) : () => setShowLogin(true)} style={{
        background: "rgba(255,255,255,0.95)",
        color: user ? "var(--g)" : "#999",
        border: user ? "2px solid var(--g)" : "2px solid #ddd",
        borderRadius: 12,
        padding: "14px 24px",
        fontSize: 14,
        fontWeight: 600,
        cursor: user ? "pointer" : "not-allowed",
        transition: "all 0.2s",
        opacity: user ? 1 : 0.6
      }} onMouseEnter={(e)=>{if(user)e.target.style.background="rgba(255,255,255,1)";}} onMouseLeave={(e)=>{if(user)e.target.style.background="rgba(255,255,255,0.95)";}} >
        {user ? "⭐ Beoordeel Park" : "🔐 Login om te Beoordelen"}
      </button>

      {/* Message Button */}
      <button onClick={user ? ()=>setShowMessage(!showMessage) : () => setShowLogin(true)} style={{
        background: "rgba(255,255,255,0.95)",
        color: user ? "var(--g)" : "#999",
        border: user ? "2px solid var(--g)" : "2px solid #ddd",
        borderRadius: 12,
        padding: "14px 24px",
        fontSize: 14,
        fontWeight: 600,
        cursor: user ? "pointer" : "not-allowed",
        transition: "all 0.2s",
        opacity: user ? 1 : 0.6
      }} onMouseEnter={(e)=>{if(user)e.target.style.background="rgba(255,255,255,1)";}} onMouseLeave={(e)=>{if(user)e.target.style.background="rgba(255,255,255,0.95)";}} >
        {user ? "💬 Bericht Achterlaten" : "🔐 Login voor Bericht"}
      </button>

      {/* Media Button */}
      <button onClick={handleShowMedia} style={{
        background: "rgba(255,255,255,0.95)",
        color: "var(--g)",
        border: "2px solid var(--g)",
        borderRadius: 12,
        padding: "14px 24px",
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s"
      }} onMouseEnter={(e)=>{e.target.style.background="rgba(255,255,255,1)";}} onMouseLeave={(e)=>{e.target.style.background="rgba(255,255,255,0.95)";}}>
        🎬 Media Gallerij
      </button>

      {/* Virtueel Parkje knop */}
      <button onClick={()=>{ window.location.href = db.getVirtueelParkUrl(park.id); }} style={{
        background: "rgba(255,255,255,0.95)",
        color: "var(--g)",
        border: "2px solid var(--g)",
        borderRadius: 12,
        padding: "14px 24px",
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s",
      }} onMouseEnter={e=>e.target.style.background="rgba(255,255,255,1)"} onMouseLeave={e=>e.target.style.background="rgba(255,255,255,0.95)"}>
        🏡 Virtueel Parkje bekijken
      </button>

      {/* Instagram knop */}
      {park.instagram&&<a
        href={`https://instagram.com/${park.instagram.replace(/^@/,"")}`}
        target="_blank" rel="noreferrer"
        style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:"linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)",color:"#fff",border:"none",borderRadius:12,padding:"14px 24px",fontSize:14,fontWeight:600,textDecoration:"none",transition:"all 0.2s"}}
        onMouseEnter={e=>e.currentTarget.style.opacity=".88"}
        onMouseLeave={e=>e.currentTarget.style.opacity="1"}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
        @{park.instagram.replace(/^@/,"")}
      </a>}
    </div>

    {/* Rating Modal */}
    {showRating && (
      <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 1000,
        padding: "16px"
      }}>
        <div style={{
          background: "#fff",
          borderRadius: "16px 16px 0 0",
          padding: "24px",
          width: "100%",
          maxWidth: "500px"
        }}>
          <h3 style={{fontSize: 18, fontWeight: 600, marginBottom: 16, color: "var(--g)"}}>
            Hoe beviel het park?
          </h3>
          
          {/* Star Rating */}
          <div style={{display: "flex", gap: 12, marginBottom: 24, justifyContent: "center"}}>
            {[1,2,3,4,5].map(star => (
              <button key={star} onClick={() => setRating(star)} style={{
                fontSize: 32,
                background: "none",
                border: "none",
                cursor: "pointer",
                opacity: rating >= star ? 1 : 0.3,
                transition: "all 0.2s"
              }} onMouseEnter={(e) => e.target.style.transform = "scale(1.2)"} onMouseLeave={(e) => e.target.style.transform = "scale(1)"}>
                ⭐
              </button>
            ))}
          </div>

          <div style={{display: "flex", gap: 12}}>
            <button onClick={handleRating} style={{
              flex: 1,
              background: "var(--g)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px",
              fontWeight: 600,
              cursor: "pointer"
            }}>
              Bevestigen
            </button>
            <button onClick={() => setShowRating(false)} style={{
              flex: 1,
              background: "#e0e0e0",
              color: "#666",
              border: "none",
              borderRadius: 8,
              padding: "12px",
              fontWeight: 600,
              cursor: "pointer"
            }}>
              Annuleren
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Message Modal */}
    {showMessage && (
      <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 1000,
        padding: "16px"
      }}>
        <div style={{
          background: "#fff",
          borderRadius: "16px 16px 0 0",
          padding: "24px",
          width: "100%",
          maxWidth: "500px"
        }}>
          <h3 style={{fontSize: 18, fontWeight: 600, marginBottom: 16, color: "var(--g)"}}>
            Bericht voor het park
          </h3>
          
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Wat vond je ervan? Geef tips!" style={{
            width: "100%",
            minHeight: "120px",
            padding: "12px",
            border: "1px solid #ddd",
            borderRadius: 8,
            fontSize: 14,
            fontFamily: "inherit",
            marginBottom: 16,
            boxSizing: "border-box",
            resize: "vertical"
          }}/>

          <div style={{display: "flex", gap: 12}}>
            <button onClick={handleMessage} style={{
              flex: 1,
              background: "var(--g)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px",
              fontWeight: 600,
              cursor: "pointer"
            }}>
              Versturen
            </button>
            <button onClick={() => setShowMessage(false)} style={{
              flex: 1,
              background: "#e0e0e0",
              color: "#666",
              border: "none",
              borderRadius: 8,
              padding: "12px",
              fontWeight: 600,
              cursor: "pointer"
            }}>
              Annuleren
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Media Gallery Modal */}
    {showMedia && (
      <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 1000,
        padding: "16px"
      }}>
        <div style={{
          background: "#fff",
          borderRadius: "16px 16px 0 0",
          padding: "24px",
          width: "100%",
          maxWidth: "500px",
          maxHeight: "80vh",
          overflow: "auto"
        }}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
            <h3 style={{fontSize: 18, fontWeight: 600, color: "var(--g)", margin: 0}}>
              🎬 Media Gallerij
            </h3>
            <button onClick={() => setShowMedia(false)} style={{
              background: "none",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "var(--txtl)"
            }}>
              ✕
            </button>
          </div>
          
          {mediaLoading ? (
            <div style={{textAlign: "center", padding: 20}}>
              <div style={{fontSize: 24}}>🔄</div>
              <div style={{fontSize: 12, color: "var(--txtl)", marginTop: 8}}>Laden...</div>
            </div>
          ) : media.length === 0 ? (
            <div style={{textAlign: "center", padding: 20, background: "#f5f5f5", borderRadius: 12}}>
              <div style={{fontSize: 24, marginBottom: 8}}>📭</div>
              <div style={{fontSize: 12, color: "var(--txtl)"}}>Geen media beschikbaar</div>
            </div>
          ) : (
            <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 12}}>
              {media.map(m => (
                <div key={m.id} style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#f5f5f5",
                  aspectRatio: "1",
                  position: "relative",
                  cursor: "pointer"
                }}>
                  {m.file_type === "image" ? (
                    <img src={m.file_url} alt={m.file_name} style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }} />
                  ) : (
                    <div style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#333",
                      color: "#fff",
                      fontSize: 32
                    }}>
                      🎬
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Login/Register Modal */}
    {showGastWaarschuwing&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"flex-end",zIndex:1001,padding:"16px"}}>
      <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"28px 24px 36px",width:"100%",maxWidth:500,animation:"slideUp .25s cubic-bezier(.25,.8,.25,1)"}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:44,marginBottom:10}}>🎮</div>
          <h3 style={{fontSize:18,fontWeight:700,color:"#1a3d0f",margin:"0 0 10px",fontFamily:"'Fraunces',serif"}}>Spel starten als gast</h3>
          <p style={{fontSize:14,color:"#666",lineHeight:1.55,margin:0}}>
            Je kunt het spel spelen zonder in te loggen, maar <strong>je spelvoortgang wordt niet opgeslagen</strong>. Sluit je de pagina, dan ben je je score kwijt.
          </p>
        </div>
        <div style={{display:"grid",gap:10}}>
          <button onClick={()=>{setShowGastWaarschuwing(false); handleBeginSpotting();}}
            style={{padding:"14px",borderRadius:12,border:"none",background:"var(--g)",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer"}}>
            🐾 Doorgaan als gast
          </button>
          <button onClick={()=>{setShowGastWaarschuwing(false); setShowLogin(true); setLoginMode("login");}}
            style={{padding:"14px",borderRadius:12,border:"2px solid var(--g)",background:"#fff",color:"var(--g)",fontSize:15,fontWeight:600,cursor:"pointer"}}>
            🔓 Inloggen
          </button>
          <button onClick={()=>{setShowGastWaarschuwing(false); setShowLogin(true); setLoginMode("register");}}
            style={{padding:"14px",borderRadius:12,border:"2px solid #ddd",background:"#fff",color:"#555",fontSize:15,fontWeight:600,cursor:"pointer"}}>
            🆕 Account aanmaken
          </button>
          <button onClick={()=>setShowGastWaarschuwing(false)}
            style={{padding:"10px",borderRadius:12,border:"none",background:"none",color:"#aaa",fontSize:13,cursor:"pointer"}}>
            Annuleren
          </button>
        </div>
      </div>
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
    </div>}

    {showLogin && (
      <div style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1001, padding: "16px"}}>
        <div style={{background: "#fff", borderRadius: "16px 16px 0 0", padding: "24px", width: "100%", maxWidth: "500px"}}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20}}>
            <h3 style={{fontSize: 18, fontWeight: 600, color: "var(--g)", margin: 0}}>
              {loginMode === "login" ? "🔓 Inloggen" : "🆕 Account Aanmaken"}
            </h3>
            <button onClick={() => {setShowLogin(false); setLoginError("");}} style={{background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "var(--txtl)"}}>✕</button>
          </div>
          <form onSubmit={e=>{e.preventDefault();(loginMode === "login" ? handleLogin : handleRegister)();}} autoComplete={loginMode==="login"?"on":"off"}>
          <div style={{marginBottom: 16}}>
            <label style={{fontSize: 12, fontWeight: 600, color: "var(--g)", display: "block", marginBottom: 6}}>Email</label>
            <input type="email" autoComplete="username" value={loginEmail} onChange={(e) => {setLoginEmail(e.target.value); setLoginError("");}} placeholder="je@email.nl" style={{width: "100%", padding: "12px", border: "1px solid #ddd", borderRadius: 8, fontSize: 16, boxSizing: "border-box", fontFamily: "inherit"}}/>
          </div>
          <div style={{marginBottom: 16}}>
            <label style={{fontSize: 12, fontWeight: 600, color: "var(--g)", display: "block", marginBottom: 6}}>Wachtwoord</label>
            <input type="password" autoComplete={loginMode==="login"?"current-password":"new-password"} value={loginPassword} onChange={(e) => {setLoginPassword(e.target.value); setLoginError("");}} placeholder={loginMode === "login" ? "Je wachtwoord" : "Min. 6 tekens"} style={{width: "100%", padding: "12px", border: "1px solid #ddd", borderRadius: 8, fontSize: 16, boxSizing: "border-box", fontFamily: "inherit"}}/>
          </div>
          {loginError && <div style={{background: loginError.includes("bevestiging") ? "rgba(76, 175, 80, 0.1)" : "rgba(244, 67, 54, 0.1)", color: loginError.includes("bevestiging") ? "var(--g)" : "var(--red)", padding: 12, borderRadius: 8, fontSize: 12, marginBottom: 16}}>{loginError}</div>}
          <PawBtn type="submit" loading={loginLoading} style={{width:"100%",marginBottom:12,fontSize:16}}>
            {loginMode === "login" ? "Inloggen" : "Account Aanmaken"}
          </PawBtn>
          <button type="button" onClick={() => setLoginMode(loginMode === "login" ? "register" : "login")} style={{width: "100%", background: "transparent", color: "var(--g)", border: "1px solid var(--g)", borderRadius: 8, padding: "12px", fontWeight: 600, cursor: "pointer", fontSize: 16}}>{loginMode === "login" ? "Geen account? Maak er een aan" : "Heb je al een account? Inloggen"}</button>
          </form>
        </div>
      </div>
    )}
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   PLATFORM: PARK BEZOEKER PAGINA
   Dieren bekijken, info & spotting game
   ═══════════════════════════════════════════════════════════ */

function ParkVisitorPage({onNavigate, park, parken, animals, loggedInUser, soorten=[], setPhotoModal, photoModal}){
  const [spottedToday, setSpottedToday] = useState(new Set()); // Dieren van vandaag
  const [allSpotted, setAllSpotted] = useState(new Set()); // Alle dieren ooit gezien
  const [expandedSoorten, setExpandedSoorten] = useState(new Set());
  const [viewMode, setViewMode] = useState("weide");
  const [saving, setSaving] = useState(false);
  
  const dieren = animals || [];
  const bezoekId = sessionStorage.getItem(`bezoek_${park.id}`);
  
  // Open all soorten by default in album view (Pokemon collection style!)
  useEffect(() => {
    if (dieren.length > 0) {
      const soort_ids = [...new Set(dieren.map(d => d.soort_id))];
      setExpandedSoorten(new Set(soort_ids));
      console.log("📖 Opened all soorten by default:", soort_ids);
    }
  }, [dieren.length]);
  
  // Load all spotted animals for this park (all time) - ONLY on mount!
  useEffect(() => {
    const loadAllSpottedAnimals = async () => {
      try {
        if (!loggedInUser) {
          console.log("❌ No loggedInUser yet");
          return;
        }
        
        console.log("🔄 Loading all spotted for park:", park.id, "user:", loggedInUser.id);
        const allBezoeken = await db.getBezoeken(loggedInUser.id);
        const parkBezoeken = allBezoeken.filter(b => b.park_id === park.id);
        
        console.log("📊 Park bezoeken:", parkBezoeken.length);
        
        // Collect all dieren_gezien from all visits
        const allSpottedIds = new Set();
        parkBezoeken.forEach(bezoek => {
          if (bezoek.dieren_gezien) {
            let dieren = bezoek.dieren_gezien;
            // Handle if it's stored as string
            if (typeof dieren === 'string') {
              try {
                dieren = JSON.parse(dieren);
              } catch(e) {
                dieren = [];
              }
            }
            if (Array.isArray(dieren)) {
              // Convert strings to numbers!
              dieren.forEach(id => allSpottedIds.add(Number(id)));
            }
          }
        });
        
        setAllSpotted(allSpottedIds);
        
        console.log("🐾 allSpottedIds details:", {
          size: allSpottedIds.size,
          ids: Array.from(allSpottedIds).slice(0, 10),
          firstIdType: Array.from(allSpottedIds)[0] ? typeof Array.from(allSpottedIds)[0] : "empty"
        });
        
        // Load today's spotted animals - use MOST RECENT bezoek
        const today = new Date().toISOString().split('T')[0];
        const todayBezoeken = parkBezoeken.filter(b => b.datum === today);
        let todaySpotted = [];
        
        if (todayBezoeken.length > 0) {
          // Use most recent bezoek (last one, since they're ordered by date)
          const todayBezoek = todayBezoeken[todayBezoeken.length - 1];
          
          if (todayBezoek && todayBezoek.dieren_gezien) {
            let dieren = todayBezoek.dieren_gezien;
            // Handle if it's stored as string
            if (typeof dieren === 'string') {
              try {
                dieren = JSON.parse(dieren);
              } catch(e) {
                dieren = [];
              }
            }
            if (Array.isArray(dieren)) {
              todaySpotted = dieren.map(id => Number(id));
              setSpottedToday(new Set(todaySpotted.map(id => Number(id))));
            }
          }
        }
        
        console.log("✅ Loaded all spotted:", allSpottedIds.size, "Today:", todaySpotted.length);
      } catch(err) {
        console.error("Error loading spotted animals:", err);
      }
    };
    
    loadAllSpottedAnimals();
  }, [park.id]);
  
  // Real-time save when dier is spotted/unspotted
  const toggleSpotted = async (dierId, soort) => {
    try {
      setSaving(true);
      
      const newSpottedToday = new Set(spottedToday);
      if (newSpottedToday.has(dierId)) {
        newSpottedToday.delete(dierId);
      } else {
        newSpottedToday.add(dierId);
      }
      
      // Update state immediately for UI feedback
      setSpottedToday(newSpottedToday);
      
      // Also add to allSpotted if not there
      const newAllSpotted = new Set(allSpotted);
      newAllSpotted.add(dierId);
      setAllSpotted(newAllSpotted);
      
      // Save to database
      if (bezoekId) {
        const spottedArray = Array.from(newSpottedToday);
        console.log("💾 Saving to bezoekId:", bezoekId, "dieren:", spottedArray);
        await db.updateBezoek(bezoekId, { dieren_gezien: spottedArray });
        console.log("✅ Dier opgeslagen:", dierId, "Total today:", spottedArray.length);
      } else {
        console.error("❌ No bezoekId found! sessionStorage:", sessionStorage.getItem(`bezoek_${park.id}`));
      }
      
      // Auto-expand soort
      const newExpanded = new Set(expandedSoorten);
      newExpanded.add(soort);
      setExpandedSoorten(newExpanded);
    } catch(err) {
      console.error("Error saving dier:", err);
      toast.err("Kon dier niet opslaan. Probeer opnieuw.");
    } finally {
      setSaving(false);
    }
  };
  
  // Soort zones op de weide (%, horizontaal, verticaal)
  // Soort colors scheme
  const soortColors = {
    12: { bg: "#e8d4c4", border: "#d4a89a", accent: "#c97a4a" },  // Schaap
    11: { bg: "#d4e8c4", border: "#a8d49a", accent: "#7ac94a" },  // Geit
    13: { bg: "#c4d4e8", border: "#9ab0d4", accent: "#4a7ac9" },  // Konijn
    14: { bg: "#e8c4d4", border: "#d49ab0", accent: "#c94a7a" },  // Hert
    9: { bg: "#e8e8c4", border: "#d4d49a", accent: "#c9c94a" },   // Gans
    10: { bg: "#d4c4e8", border: "#b09ad4", accent: "#7a4ac9" },  // Kip
  };
  
  const soortZones = {
    12: { x: 25, y: 30 },  // Schaap
    11: { x: 75, y: 40 },  // Geit
    13: { x: 45, y: 70 },  // Konijn
    14: { x: 65, y: 20 },  // Hert
    9: { x: 20, y: 75 },   // Gans
    10: { x: 80, y: 75 },  // Kip
  };
  
  const geslachtIcon=(g)=>g==="man"?"♂️":"♀️";
  const berekeenLeeftijd=(born)=>{
    const today=new Date();const bd=new Date(born);
    const age=today.getFullYear()-bd.getFullYear();
    const m=today.getMonth()-bd.getMonth();
    return (m<0||m===0&&today.getDate()<bd.getDate())?age-1:age;
  };
  
  // Groepeer dieren per soort
  const dierenPerSoort={};
  dieren.forEach(d=>{
    if(!dierenPerSoort[d.soort_id]) dierenPerSoort[d.soort_id]=[];
    dierenPerSoort[d.soort_id].push(d);
  });
  const soort_ids=Object.keys(dierenPerSoort).sort();
  
  const toggleExpandSoort=(soort_id)=>{
    const newExpanded=new Set(expandedSoorten);
    if(newExpanded.has(soort_id)) newExpanded.delete(soort_id);
    else newExpanded.add(soort_id);
    setExpandedSoorten(newExpanded);
  };
  
  const spottedTodayCount = spottedToday.size;
  // Filter allSpotted to only include dieren that currently exist
  const allSpottedFiltered = new Set([...allSpotted].filter(id => dieren.some(d => d.id === id)));
  const allSpottedCount = allSpottedFiltered.size;
  const totalCount = dieren.length;
  const progressAll = totalCount > 0 ? Math.round((allSpottedCount / totalCount) * 100) : 0;
  
  // Genereer random posities voor dieren in hun zone
  const getDierPos=(dier,index,soortDieren)=>{
    const zone=soortZones[dier.soort_id] || {x:50,y:50};
    // Spread dieren uit in een circle rond zone center
    const angle=(index/soortDieren.length)*Math.PI*2;
    const radius=35; // Increased from 20 to 35 for even better spacing
    const x=zone.x+Math.cos(angle)*radius;
    const y=zone.y+Math.sin(angle)*radius;
    return {x:Math.max(5,Math.min(95,x)),y:Math.max(5,Math.min(95,y))};
  };
  
  // Weide view render
  if(viewMode==="weide"){
    return <div style={{minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      {/* Header */}
      <div style={{background:"var(--g)",color:"#fff",padding:"16px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={()=>onNavigate("welkomPark",park)} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,padding:8,cursor:"pointer",color:"#fff",fontSize:18}}>←</button>
        <div style={{flex:1}}>
          <h1 style={{margin:0,fontSize:20}}>{park.naam}</h1>
          <p style={{margin:0,fontSize:11,opacity:0.9}}>Dierenweide • {allSpottedCount}/{dieren.length} gevonden {spottedToday.size > 0 ? `(${spottedToday.size} vandaag)` : ""}</p>
        </div>
        <button onClick={()=>setViewMode("album")} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,padding:8,cursor:"pointer",color:"#fff",fontSize:18}} title="Album view">📖</button>
      </div>
      
      {/* Weide Map */}
      <div style={{flex:1,position:"relative",background:"#90EE90",backgroundImage:"url('/dierenweide.png')",backgroundSize:"cover",backgroundPosition:"center",backgroundRepeat:"no-repeat",overflow:"hidden"}}>
        <style>{`
          @keyframes randomWalk {
            0% { left: var(--p0x); top: var(--p0y); }
            20% { left: var(--p1x); top: var(--p1y); }
            40% { left: var(--p2x); top: var(--p2y); }
            60% { left: var(--p3x); top: var(--p3y); }
            80% { left: var(--p4x); top: var(--p4y); }
            100% { left: var(--p5x); top: var(--p5y); }
          }
          .dier-wander { animation: randomWalk var(--duration) linear infinite; }
        `}</style>
        {/* Dieren */}
        {dieren.map((dier,idx)=>{
          const isSpottedToday = spottedToday.has(dier.id);
          const isSpottedBefore = allSpottedFiltered.has(dier.id) && !isSpottedToday;
          const hasFoto=dier.fotos&&dier.fotos.length>0;
          
          // Generate unique random path for each animal based on their ID
          const seed = dier.id * 12345;
          const pseudoRandom = (n) => {
            const x = Math.sin(seed * n) * 10000;
            return x - Math.floor(x);
          };
          
          // Generate 6 waypoints within bounds (5% to 95% of screen)
          const generatePos = (offset) => {
            const rand = pseudoRandom(offset);
            const x = 5 + (rand * 90);
            const rand2 = pseudoRandom(offset + 1);
            const y = 5 + (rand2 * 90);
            return { x: x.toFixed(1), y: y.toFixed(1) };
          };
          
          const p0 = generatePos(0);
          const p1 = generatePos(10);
          const p2 = generatePos(20);
          const p3 = generatePos(30);
          const p4 = generatePos(40);
          const p5 = generatePos(50);
          
          const duration = 25 + (pseudoRandom(100) * 15); // 25-40 seconds per cycle
          
          const dierColor=soortColors[dier.soort_id]||{bg:"#e8e8e8",border:"#999",accent:"#666"};
          
          if (isSpottedBefore) {
            console.log("Found isSpottedBefore dier:", dier.id, "type:", typeof dier.id, dier.name);
          }
          
          return <div key={dier.id} className="dier-wander" onClick={()=>toggleSpotted(dier.id,dier.soort_id)} style={{"--p0x":`${p0.x}%`,"--p0y":`${p0.y}%`,"--p1x":`${p1.x}%`,"--p1y":`${p1.y}%`,"--p2x":`${p2.x}%`,"--p2y":`${p2.y}%`,"--p3x":`${p3.x}%`,"--p3y":`${p3.y}%`,"--p4x":`${p4.x}%`,"--p4y":`${p4.y}%`,"--p5x":`${p5.x}%`,"--p5y":`${p5.y}%`,"--duration":`${duration.toFixed(1)}s`,position:"absolute",transform:"translate(-50%, -50%)",cursor:"pointer",transition:"filter .2s",filter:isSpottedToday?`brightness(1.2) drop-shadow(0 0 8px ${dierColor.accent})`:isSpottedBefore?"brightness(0.8)":"brightness(1)",opacity:1,zIndex:isSpottedToday?10:1,textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{position:"relative"}}>
              {hasFoto?<img src={dier.fotos[0]} alt={dier.naam||dier.name} style={{width:54,height:54,borderRadius:"50%",border:`4px solid ${dierColor.accent}`,boxShadow:`0 2px 8px ${dierColor.accent}40`,objectFit:"cover"}}/>:<div style={{fontSize:42,background:dierColor.bg,borderRadius:"50%",width:54,height:54,display:"flex",alignItems:"center",justifyContent:"center",border:`3px solid ${dierColor.accent}`}}>{soorten.find(s=>s.id===dier.soort_id)?.icon || "🐾"}</div>}
              {isSpottedToday&&<div style={{position:"absolute",bottom:-8,right:-8,background:"var(--g)",color:"#fff",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,boxShadow:"0 4px 8px rgba(45,80,22,0.4)",border:"2px solid #fff"}}>✓</div>}
              {isSpottedBefore&&<div style={{position:"absolute",bottom:-8,right:-8,background:"#ddd",color:"#888",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,boxShadow:"0 3px 8px rgba(0,0,0,0.15)",border:"2px solid #fff"}}>✓</div>}
            </div>
            <div style={{fontSize:11,fontWeight:600,marginTop:8,color:dierColor.accent,maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{dier.naam || dier.name}</div>
          </div>;
        })}
      </div>
      
      {/* Progress Bar */}
      <div style={{padding:"12px 16px",background:"#fff",borderTop:"1px solid #e0e0e0"}}>
        <div style={{background:"#e0e0e0",borderRadius:8,height:6,marginBottom:8,overflow:"hidden"}}>
          <div style={{background:"var(--g)",height:"100%",width:progressAll+"%",transition:"width .3s"}}/>
        </div>
        <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:"var(--g)"}}>
          {allSpottedCount}/{totalCount} gevonden {spottedTodayCount > 0 ? `(${spottedTodayCount} vandaag)` : ""}
        </div>
      </div>
    </div>;
  }
  
  // Album view (origineel)
  
  return <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#fff9e6 0%,#f0f4ff 100%)",paddingBottom:40}}>
    {/* Header */}
    <div style={{background:"var(--g)",color:"#fff",padding:"16px",display:"flex",alignItems:"center",gap:12}}>
      <button onClick={()=>onNavigate("welkomPark",park)} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,padding:8,cursor:"pointer",color:"#fff",fontSize:18}}>←</button>
      <div style={{flex:1}}>
        <h1 style={{margin:0,fontSize:20}}>{park.naam}</h1>
        <p style={{margin:0,fontSize:11,opacity:0.9}}>{park.plaats}</p>
      </div>
      <button onClick={()=>setViewMode("weide")} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,padding:8,cursor:"pointer",color:"#fff",fontSize:18}} title="Weide view">🌾</button>
    </div>
    
    {/* Spotting Game Progress */}
    <div style={{padding:"16px",background:"#fff",margin:"12px 16px",borderRadius:12,boxShadow:"0 2px 8px rgba(0,0,0,.08)"}}>
      <div style={{fontSize:12,fontWeight:600,marginBottom:8,color:"var(--g)"}}>🎮 SPOTTING GAME</div>
      <div style={{background:"#e0e0e0",borderRadius:8,height:8,marginBottom:8,overflow:"hidden"}}>
        <div style={{background:"var(--g)",height:"100%",width:progressAll+"%",transition:"width .3s"}}/>
      </div>
      <div style={{fontSize:13,fontWeight:600,textAlign:"center"}}>
        {allSpottedCount}/{totalCount} gevonden {spottedTodayCount > 0 ? `(${spottedTodayCount} vandaag)` : ""} {allSpottedCount===totalCount&&"🏆"}
      </div>
    </div>

    {/* Instagram */}
    {park.instagram&&<div style={{padding:"0 16px"}}><InstagramCard park={park} canEdit={false}/></div>}
    
    {/* Soorten met Album Kaartjes */}
    {soorten.filter(soort_obj=>dierenPerSoort[soort_obj.id]&&dierenPerSoort[soort_obj.id].length>0).map(soort_obj=>{
      const dierenSoort=dierenPerSoort[soort_obj.id]||[];
      const isExpanded=expandedSoorten.has(soort_obj.id);
      const info=DIER_INFO[soort_obj.naam];
      const cardColor=soortColors[soort_obj.id]||{bg:"#e8e8e8",border:"#999",accent:"#666"};
      
      return <div key={soort_obj.id} style={{padding:"0 16px",marginBottom:12}}>
        {/* Soort Header */}
        <div onClick={()=>toggleExpandSoort(soort_obj.id)} style={{cursor:"pointer",background:cardColor?.bg||"#fff",padding:"12px",borderRadius:12,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:"0 2px 8px rgba(0,0,0,.08)",border:`2px solid ${cardColor?.border||"#e0e0e0"}`}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:24}}>{soort_obj.icon}</span>
            <div>
              <div style={{fontWeight:600,fontSize:13,color:cardColor?.accent||"#333"}}>{soort_obj.naam}</div>
              <div style={{fontSize:10,color:"var(--txtl)"}}>{dierenSoort.length} dieren</div>
            </div>
          </div>
          <div style={{fontSize:12,color:cardColor?.accent||"var(--g)",fontWeight:600}}>{isExpanded?"▼":"▶"}</div>
        </div>
        
        {/* Soort Info */}
        {isExpanded&&info&&<div style={{background:"#fffaf0",padding:"12px",borderRadius:8,marginBottom:8,fontSize:11,lineHeight:1.6,border:"1px solid #ffe6cc"}}>
          <p style={{margin:"0 0 6px 0",fontWeight:600,color:"var(--g)"}}>{soort_obj.icon} Over {soort_obj.naam.toLowerCase()}</p>
          <p style={{margin:"0 0 4px 0",color:"#333"}}>{info.info}</p>
          <p style={{margin:"0",color:"#666"}}>🌾 <strong>Voeding:</strong> {info.diet}</p>
        </div>}
        
        {/* Dieren Kaarten (Album-stijl) */}
        {isExpanded&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:12}}>
          {dierenSoort.map(dier=>{
            console.log("🔍 Dier object keys:", Object.keys(dier), "Full dier:", dier);
            const isSpottedToday=spottedToday.has(dier.id);
            const isSpottedBefore=allSpottedFiltered.has(dier.id)&&!isSpottedToday;
            const isSpotted = isSpottedToday || isSpottedBefore;
            const geboortedatumFormatted=dier.geboortedatum?new Date(dier.geboortedatum).toLocaleDateString("nl-NL"):"-";
            
            const neverSpotted = !allSpottedFiltered.has(dier.id);
            
            return <div key={dier.id} onClick={()=>setPhotoModal({photos:dier.fotos||[],dierName:dier.naam||dier.name,initialIndex:0,showNav:false})} style={{cursor:"pointer",background:isSpotted?cardColor.bg:neverSpotted?"#f0f0f0":"#fff",border:`2px solid ${isSpotted?cardColor.border:neverSpotted?"#ddd":"#e0e0e0"}`,borderRadius:10,padding:10,textAlign:"center",transition:"all .2s",transform:isSpotted?"scale(1.05)":"scale(1)",boxShadow:isSpotted?`0 4px 12px ${cardColor.accent}30`:"0 2px 6px rgba(0,0,0,.08)",display:"flex",flexDirection:"column",opacity:neverSpotted?0.45:1,filter:neverSpotted?"grayscale(0.6)":"none"}}>
              {/* Foto or Icon */}
              <div style={{fontSize:40,marginBottom:8,height:70,display:"flex",alignItems:"center",justifyContent:"center",background:dier.fotos&&dier.fotos.length>0?"#f0f0f0":"transparent",borderRadius:8,overflow:"hidden",flexShrink:0}}>
                {dier.fotos&&dier.fotos.length>0?<img src={dier.fotos[0]} alt={dier.naam||dier.name} style={{height:"100%",width:"100%",objectFit:"contain",borderRadius:6}}/>:<div style={{fontSize:40}}>{soort_obj.icon}</div>}
              </div>
              
              {/* Name with Gender */}
              <div style={{fontSize:13,fontWeight:700,marginBottom:4,lineHeight:1.3,color:"#333",wordBreak:"break-word"}}>
                {(dier.naam || dier.name) || (dier.geslacht==="man"?"♂️":"♀️")}
              </div>
              
              {/* Birth Date */}
              {dier.geboortedatum&&(
                <div style={{fontSize:10,color:"#666",marginBottom:2}}>
                  📅 {geboortedatumFormatted}
                </div>
              )}
              
              {/* Spotted badge */}
              {isSpottedToday&&<div style={{fontSize:9,background:"var(--g)",color:"#fff",padding:"4px 8px",borderRadius:20,fontWeight:600,marginTop:"auto"}}>✓ Vandaag!</div>}
              {isSpottedBefore&&<div style={{fontSize:9,background:"#ccc",color:"#888",padding:"4px 8px",borderRadius:20,fontWeight:600,marginTop:"auto"}}>✓ Eerder</div>}
            </div>;
          })}
        </div>}
      </div>;
    })}
    
{photoModal&&<PhotoModal photos={photoModal.photos} dierName={photoModal.dierName} initialIndex={photoModal.initialIndex} showNav={photoModal.showNav!==false} onClose={()=>setPhotoModal(null)}/>}
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   PLATFORM: UNIVERSEEL LOGIN SCHERM
   Wachtwoord bepaalt bestemming — geen park-selectie nodig
   ═══════════════════════════════════════════════════════════ */

function LoginPage({onBack}){
  const [mode,setMode]=useState("login"); // login | reset | reset_sent
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [guestLoading,setGuestLoading]=useState(false);

  const doLogin=async()=>{
    if(!email.trim()||!pw.trim()){setErr("Vul je e-mail en wachtwoord in.");return;}
    setLoading(true);setErr("");
    const {data,error}=await db.signIn(email.trim(),pw);
    if(error){setErr("Onbekende combinatie. Probeer opnieuw.");setLoading(false);return;}
    setLoading(false);
  };

  const doGuestLogin=async()=>{
    setGuestLoading(true);setErr("");
    const guestEmail=process.env.REACT_APP_GUEST_EMAIL;
    const guestPw=process.env.REACT_APP_GUEST_PASSWORD;
    if(!guestEmail||!guestPw){setErr("Gastlogin is niet beschikbaar.");setGuestLoading(false);return;}
    const {error}=await db.signIn(guestEmail,guestPw);
    if(error){setErr("Gastlogin mislukt. Probeer later opnieuw.");setGuestLoading(false);return;}
    // Log the demo login (fire and forget — don't block the user)
    try {
      fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/log-demo-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": process.env.REACT_APP_SUPABASE_KEY || "" },
      }).catch(()=>{});
    } catch { /* silent fail */ }
    setGuestLoading(false);
  };

  const doReset=async()=>{
    if(!email.trim()){setErr("Vul je e-mailadres in.");return;}
    setLoading(true);setErr("");
    const {error}=await db.resetPassword(email.trim());
    if(error){setErr("Kon reset-email niet versturen.");setLoading(false);return;}
    setMode("reset_sent");setLoading(false);
  };

  return <div className="login-page">
    <div className="login-card">
      <button className="zoek-back" onClick={onBack} style={{marginBottom:12}}>{IC.back} Terug</button>
      <div className="login-park-header">
        <div className="login-park-avatar" style={{background:"linear-gradient(135deg,var(--g),var(--gl))",fontSize:24}}>🐾</div>
        <div>
          <h2 className="login-park-name">Dierenparkje.nl</h2>
          <p className="login-park-place">Beheersysteem</p>
        </div>
      </div>
      <div className="login-divider"/>

      {mode==="reset_sent"&&<div style={{textAlign:"center",padding:"20px 0"}}>
        <div style={{fontSize:40,marginBottom:12}}>📬</div>
        <h3 style={{fontFamily:"'Fraunces',serif",color:"var(--g)",marginBottom:8}}>Check je e-mail!</h3>
        <p style={{fontSize:13,color:"var(--txtl)",lineHeight:1.6}}>We hebben een wachtwoord-reset link gestuurd naar <strong>{email}</strong>.</p>
        <button className="l-btn l-btn-primary" style={{marginTop:16,width:"100%"}} onClick={()=>setMode("login")}>Terug naar inloggen</button>
      </div>}

      {mode==="reset"&&<>
        <h3 className="login-subtitle">Wachtwoord vergeten</h3>
        <p className="login-hint">Vul je e-mailadres in en we sturen je een reset-link.</p>
        <div className="fg"><input className="fi login-pw-input" type="email" placeholder="jouw@email.nl" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} autoFocus/></div>
        {err&&<p className="login-pw-error">{err}</p>}
        <PawBtn className="l-btn l-btn-primary" style={{width:"100%",padding:"12px"}} onClick={doReset} loading={loading}>
          📬 Reset-link versturen
        </PawBtn>
        <button className="login-forgot" onClick={()=>setMode("login")} style={{marginTop:8}}>Terug naar inloggen</button>
      </>}

      {mode==="login"&&<form onSubmit={e=>{e.preventDefault();doLogin();}} autoComplete="on">
        <h3 className="login-subtitle">{IC.lock} Inloggen</h3>
        <div className="fg" style={{marginBottom:8}}>
          <label className="fl">E-mailadres</label>
          <input className="fi" type="email" autoComplete="username" placeholder="jouw@email.nl" value={email}
            onChange={e=>{setEmail(e.target.value);setErr("");}}
            onKeyDown={e=>{if(e.key==="Enter")document.getElementById("pw-input")?.focus();}}
            autoFocus/>
        </div>
        <div className="fg" style={{marginBottom:8}}>
          <label className="fl">Wachtwoord</label>
          <input id="pw-input" className="fi" type="password" name="password" autoComplete="current-password" placeholder="••••••••" value={pw}
            onChange={e=>{setPw(e.target.value);setErr("");}}/>
        </div>
        {err&&<p className="login-pw-error">{err}</p>}
        <PawBtn type="submit" className="l-btn l-btn-primary" style={{width:"100%",padding:"12px",marginTop:4}} loading={loading}>
          {IC.login} Inloggen
        </PawBtn>
        <button type="button" className="login-forgot" onClick={()=>{setMode("reset");setErr("");}}>Wachtwoord vergeten?</button>

        {/* Divider */}
        <div style={{display:"flex",alignItems:"center",gap:10,margin:"16px 0 12px"}}>
          <div style={{flex:1,height:1,background:"var(--brd)"}}/>
          <span style={{fontSize:11,color:"var(--txtl)",fontWeight:600}}>OF</span>
          <div style={{flex:1,height:1,background:"var(--brd)"}}/>
        </div>

        {/* Guest login */}
        <PawBtn type="button" className="l-btn" onClick={doGuestLogin} loading={guestLoading} style={{width:"100%",padding:"12px",background:"var(--gp)",color:"var(--g)",border:"2px solid var(--g)"}}>
          👁️ Inloggen als gast — De Zonnehoeve (DEMO)
        </PawBtn>
        <p style={{fontSize:11,color:"var(--txtl)",textAlign:"center",marginTop:8,lineHeight:1.5}}>
          Bekijk de app zonder account. Alleen lezen, geen wijzigingen mogelijk.
        </p>
      </form>}
    </div>
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   MANAGEMENT APP — CRUD FORMS
   ═══════════════════════════════════════════════════════════ */

function AnimalForm({item,onSave,onClose,defaultSoort,parkId}){
  const [soorten, setSoorten] = useState([]);
  const [rassen, setRassen] = useState([]);
  const [verblijven, setVerblijven] = useState([]);
  const [verblijvenLoaded, setVerblijvenLoaded] = useState(false);
  const [f,setF]=useState(item||{name:"",soort_id:null,ras_id:null,geslacht:"vrouw",geboortedatum:"",park_datum:"",locatie:"",oornummer:"",chipnummer:"",herkomst:"",gewicht:"",notitie:"",is_gecastreerd:false,datum_overlijden:"",datum_rendac:"",verblijf_id:""});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  
  // Fetch soorten, rassen en verblijven on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const queries = [
          sb.from("soorten").select("*").order("naam"),
          sb.from("rassen").select("*").order("ras_naam"),
        ];
        if(parkId) queries.push(sb.from("verblijven").select("id,naam,type").eq("park_id",parkId).eq("actief",true).order("volgorde").order("naam"));
        // Bij bewerken: huidige verblijf ophalen
        if(item?.id) queries.push(sb.from("verblijf_bewoners").select("verblijf_id").eq("dier_id",item.id).limit(1));
        const results = await Promise.all(queries);
        setSoorten(results[0].data || []);
        setRassen(results[1].data || []);
        if(parkId) setVerblijven(results[2].data || []);
        // verblijf_id pre-vullen als gevonden
        const verblijfIdx = parkId ? 3 : 2;
        if(item?.id && results[verblijfIdx]?.data?.[0]?.verblijf_id){
          setF(p=>({...p, verblijf_id: results[verblijfIdx].data[0].verblijf_id}));
        }
        setVerblijvenLoaded(true);
      } catch(err) {
        console.error("Error fetching data:", err);
        setVerblijvenLoaded(true);
      }
    };
    fetchData();
  }, []);
  
  // Reset ras_id when soort changes
  const handleSoortChange = (soort_id) => {
    setF(p=>({...p, soort_id: Number(soort_id), ras_id: null}));
  };

  // Get current soort object
  const currentSoort = soorten.find(s => s.id === f.soort_id);
  const rassenVoorSoort = rassen.filter(r => Number(r.soort_id) === Number(f.soort_id));
  
  // Get display name based on geslacht
  const getDisplayName = () => {
    if (!currentSoort) return f.name;
    const label = f.geslacht === "vrouw" ? currentSoort.naam_vrouw : currentSoort.naam_man;
    return (label || currentSoort.naam) + " " + f.name;
  };
  
  const displayName = getDisplayName();
  
  return <Modal title={item?"Dier bewerken":"Dier toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Naam</label><input className="fi" value={f.name} onChange={e=>u("name",e.target.value)}/></div>
    <div className="fg"><label className="fl">Soort</label><select className="fi" value={f.soort_id||""} onChange={e=>handleSoortChange(e.target.value)}><option value="">-- Kies soort --</option>{soorten.map(s=><option key={s.id} value={s.id}>{s.icon} {s.naam}</option>)}</select></div>
    {f.soort_id&&<div className="fg"><label className="fl">Ras <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><select className="fi" value={f.ras_id||""} onChange={e=>u("ras_id",e.target.value?Number(e.target.value):null)}><option value="">— Onbekend ras —</option>{rassenVoorSoort.map(r=><option key={r.id} value={r.id}>{r.ras_naam}</option>)}</select></div>}
    <div className="fg"><label className="fl">Geslacht</label>
      <div style={{display:"flex",gap:8,marginTop:6}}>
        <button 
          className="fbtn" 
          style={{flex:1,background:f.geslacht==="vrouw"?"var(--g)":"var(--cd)",color:f.geslacht==="vrouw"?"#fff":"var(--g)",border:"none"}}
          onClick={()=>u("geslacht","vrouw")}>
          ♀️ {currentSoort?.naam_vrouw || "Vrouw"}
        </button>
        <button 
          className="fbtn" 
          style={{flex:1,background:f.geslacht==="man"?"var(--g)":"var(--cd)",color:f.geslacht==="man"?"#fff":"var(--g)",border:"none"}}
          onClick={()=>u("geslacht","man")}>
          ♂️ {currentSoort?.naam_man || "Man"}
        </button>
      </div>
    </div>
    <div className="fg"><label className="fl">Display naam</label><input className="fi" value={displayName} disabled style={{background:"var(--cd)",color:"var(--txtl)"}}/></div>
    <div className="fg"><label className="fl">Geboortedatum</label><input className="fi" type="date" value={f.geboortedatum} onChange={e=>u("geboortedatum",e.target.value)}/></div>
    <div className="fg"><label className="fl">Op park sinds <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" type="date" value={f.park_datum||""} onChange={e=>u("park_datum",e.target.value)}/></div>
    {f.geslacht==="man"&&<div className="fg"><label className="fl" style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
      <input type="checkbox" checked={!!f.is_gecastreerd} onChange={e=>u("is_gecastreerd",e.target.checked)} style={{width:18,height:18,accentColor:"var(--g)",cursor:"pointer",flexShrink:0}}/>
      <span>Gecastreerd</span>
    </label></div>}
    <div className="fg"><label className="fl">Verblijf <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label>
      {!verblijvenLoaded ? <div style={{fontSize:13,color:"var(--txtl)"}}>Laden...</div>
      : verblijven.length > 0
        ? <select className="fi" style={{fontSize:16}} value={f.verblijf_id||""} onChange={e=>u("verblijf_id",e.target.value)}>
            <option value="">— Nog niet gekoppeld —</option>
            {verblijven.map(v=><option key={v.id} value={v.id}>{v.naam}</option>)}
          </select>
        : <div style={{fontSize:12,color:"var(--txtl)",padding:"8px 10px",background:"#fff9e6",borderRadius:8,border:"1px solid #f0d060"}}>
            💡 Maak eerst verblijven aan via <strong>Virtueel Park</strong> om dieren te koppelen.
          </div>
      }
    </div>
    <div className="fg"><label className="fl">Locatie <span style={{fontWeight:400,color:"var(--txtl)"}}>(vrij tekstveld, optioneel)</span></label><input className="fi" value={f.locatie} onChange={e=>u("locatie",e.target.value)} placeholder="Bijv. Wei 1, Stal A"/></div>
    <div className="fg"><label className="fl">Oornummer <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" value={f.oornummer||""} onChange={e=>u("oornummer",e.target.value)} placeholder="Bijv. NL 12345"/></div>
    <div className="fg"><label className="fl">Chipnummer <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" style={{fontSize:16}} value={f.chipnummer||""} onChange={e=>u("chipnummer",e.target.value)} placeholder="Bijv. 528210000123456"/></div>
    <div className="fg"><label className="fl">Herkomst <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><select className="fi" style={{fontSize:16}} value={f.herkomst||""} onChange={e=>u("herkomst",e.target.value)}><option value="">— Selecteer herkomst —</option><option value="Fokker">Fokker</option><option value="Opvang / rescue">Opvang / rescue</option><option value="Eigen kweek">Eigen kweek</option><option value="Schenking">Schenking</option><option value="Overig">Overig</option></select></div>
    <div className="fg"><label className="fl">Gewicht <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><div style={{display:"flex",alignItems:"center",gap:6}}><input className="fi" type="number" min="0" step="0.1" style={{flex:1,fontSize:16}} value={f.gewicht||""} onChange={e=>u("gewicht",e.target.value===("")?null:parseFloat(e.target.value))} placeholder="0.0"/><span style={{color:"var(--txtl)",fontWeight:500,whiteSpace:"nowrap"}}>kg</span></div></div>
    <div className="fg"><label className="fl">Notitie</label><textarea className="fi" rows={2} value={f.notitie} onChange={e=>u("notitie",e.target.value)}/></div>
    <div className="fg"><label className="fl">Datum overlijden <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" type="date" value={f.datum_overlijden||""} onChange={e=>u("datum_overlijden",e.target.value)}/></div>
    <div className="fg"><label className="fl">Datum Rendac <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" type="date" value={f.datum_rendac||""} onChange={e=>u("datum_rendac",e.target.value)}/></div>
    <button className="fbtn" onClick={()=>{console.log("💾 Opslaan button clicked! Form data:", f); onSave(f);}}>Opslaan</button>
  </Modal>;
}

function PhotoUpload({animalId,d}){
  const [uploading,setUploading]=useState(false);
  
  // Compress image to max 800px width, 80% quality
  const compressImage=(file)=>new Promise((resolve)=>{
    const reader=new FileReader();
    reader.onload=(e)=>{
      const img=new Image();
      img.onload=()=>{
        const canvas=document.createElement("canvas");
        let width=img.width;
        let height=img.height;
        
        // Scale down if larger than 800px
        if(width>800){
          height=Math.round(height*(800/width));
          width=800;
        }
        
        canvas.width=width;
        canvas.height=height;
        const ctx=canvas.getContext("2d");
        ctx.drawImage(img,0,0,width,height);
        
        // Convert to JPEG at 80% quality
        canvas.toBlob((blob)=>{
          resolve(blob);
        },"image/jpeg",0.8);
      };
      img.src=e.target.result;
    };
    reader.readAsDataURL(file);
  });
  
  const handleUpload=async(e)=>{
    const files=Array.from(e.target.files);
    setUploading(true);
    
    for(const file of files){
      try{
        // Compress afbeelding
        const compressed=await compressImage(file);
        
        // Get token directly from Supabase
        const { data: { session } } = await sb.auth.getSession();
        const token = session?.access_token;
        if(!token) throw new Error("Niet ingelogd");
        
        console.log("Token obtained:",!!token);
        
        // Decode JWT to get user ID
        const tokenParts=token.split('.');
        const payload=JSON.parse(atob(tokenParts[1]));
        const userId=payload.sub;
        console.log("User ID:",userId);
        
        const formData=new FormData();
        formData.append("file",compressed,"photo.jpg");
        formData.append("animalId",animalId.toString());
        
        console.log("Uploading photo for animal ID:",animalId);
        
        const res=await fetch(`${SUPABASE_URL}/functions/v1/upload-animal-photo`,{
          method:"POST",
          headers:{
            "x-user-id":userId,
            "Authorization":`Bearer ${token}`,
            "apikey":SUPABASE_KEY,
          },
          body:formData
        });
        
        console.log("Response status:",res.status);
        const data=await res.json();
        console.log("Response data:",data);
        
        if(!res.ok||data.error) throw new Error(data.error||"Upload failed");
        
        // Get current animal fotos and append new URL
        const { data: currentAnimal } = await sb.from("dieren").select("fotos").eq("id", animalId).single();
        const currentFotos = currentAnimal?.fotos || [];
        const newFotos = [...currentFotos, data.url];
        
        // Update dieren table with new fotos array - but we need db object
        // For now, just dispatch to state and let the handler update DB
        console.log("✅ Photo upload SUCCESS! Animal ID:", animalId, "URL:", data.url);
        d({type:"ADD_PHOTO",p:{id:animalId,fotos:[data.url]}});
        console.log("📣 ADD_PHOTO action dispatched for animal ID:", animalId);
      }catch(err){
        console.error("❌ Upload error:",err);
        toast.err("Fout bij uploaden: "+err.message);
      }
    }
    
    setUploading(false);
    e.target.value="";
  };
  
  return <label className="upload-btn" style={{opacity:uploading?0.5:1,pointerEvents:uploading?"none":"auto"}}>
    {uploading?"Uploaden...":IC.cam+" Foto's uploaden"}
    <input type="file" accept="image/*" multiple onChange={handleUpload} disabled={uploading} style={{display:"none"}}/>
  </label>;
}

function MediaUploadField({foto,onChange}){
  const handleFile=(e)=>{
    const file=e.target.files[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=(ev)=>onChange(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value="";
  };
  return <div className="fg">
    <label className="fl">Foto / Media</label>
    {foto
      ?<div className="mfu-preview">
          <img src={foto} alt="preview" style={{width:"100%",height:100,objectFit:"cover",borderRadius:10,display:"block",marginBottom:6}}/>
          <button className="mfu-del" onClick={()=>onChange(null)}>Foto verwijderen ×</button>
        </div>
      :<label className="upload-btn" style={{marginBottom:0}}>
          {IC.cam} Foto uploaden
          <input type="file" accept="image/*" onChange={handleFile} style={{display:"none"}}/>
        </label>
    }
  </div>;
}

function VoedForm({item,animals,leveranciers=[],onSave,onClose,soorten=[]}){
  const [f,setF]=useState(item||{type:"",merk:"",inhoud:"",omschrijving:"",methode:"",foto:null,link:"",leverancier_id:null});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  return <Modal title={item?"Voeding bewerken":"Voeding toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Voer / Beschrijving *</label><input className="fi" value={f.type} onChange={e=>u("type",e.target.value)} placeholder="Bijv. Hooi, Krachtvoer, Appels"/></div>
    <div className="fg"><label className="fl">Merk</label><input className="fi" value={f.merk||""} onChange={e=>u("merk",e.target.value)} placeholder="Bijv. Agrifirm, DeLaval"/></div>
    <div className="fg"><label className="fl">Inhoud</label><input className="fi" value={f.inhoud||""} onChange={e=>u("inhoud",e.target.value)} placeholder="Bijv. 20kg zak, 10L emmer, 1L fles"/></div>
    <div className="fg"><label className="fl">Omschrijving</label><textarea className="fi" rows={2} value={f.omschrijving||""} onChange={e=>u("omschrijving",e.target.value)} placeholder="Bijv. Ruwvoer, Krachtvoer, Suppletie, Snacks"/></div>
    <div className="fg"><label className="fl">Toedieningswijze</label><textarea className="fi" rows={2} value={f.methode||""} onChange={e=>u("methode",e.target.value)} placeholder="Bijv. Uitstrooien in verblijf, Handje per dier voeren, Ruif vullen"/></div>
    <div className="fg"><label className="fl">Leverancier</label><select className="fi" value={f.leverancier_id||""} onChange={e=>u("leverancier_id",e.target.value?Number(e.target.value):null)}><option value="">- Geen leverancier -</option>{leveranciers.map(l=><option key={l.id} value={l.id}>{l.naam}</option>)}</select></div>
    <MediaUploadField foto={f.foto} onChange={v=>u("foto",v)}/>
    <div className="fg"><label className="fl">🔗 Link (bijv. YouTube instructie)</label><input className="fi" value={f.link||""} onChange={e=>u("link",e.target.value)} placeholder="https://www.youtube.com/..."/></div>
    <button className="fbtn" onClick={()=>{if(!f.type){toast.warn("Voer/beschrijving is verplicht!");return;}onSave(f);}}>Opslaan</button>
  </Modal>;
}

function MaaltijdForm({item,feedings=[],animals=[],soorten=[],onSave,onClose}){
  const [mode,setMode]=useState(item?(item.dier_id?"dier":"soort"):"soort");
  const [voedingId,setVoedingId]=useState(item?.voeding_id||"");
  const [dierId,setDierId]=useState(item?.dier_id||"");
  const [soortId,setSoortId]=useState(item?.soort_id||"");
  const [hoeveelheid,setHoeveelheid]=useState(item?.hoeveelheid||"");
  const [dagdeel,setDagdeel]=useState(item?.dagdeel||"ochtend");
  const save=()=>{
    if(!voedingId){toast.warn("Selecteer een voeding");return;}
    if(mode==="dier"&&!dierId){toast.warn("Selecteer een dier");return;}
    if(mode==="soort"&&!soortId){toast.warn("Selecteer een diersoort");return;}
    onSave({
      voeding_id:Number(voedingId),
      dier_id:mode==="dier"?Number(dierId):null,
      soort_id:mode==="soort"?Number(soortId):null,
      hoeveelheid:hoeveelheid||null,
      dagdeel,
      ...(item?{id:item.id}:{})
    });
  };
  return <Modal title={item?"Maaltijd bewerken":"Maaltijd toewijzen"} onClose={onClose}>
    {/* Mode toggle */}
    <div style={{display:"flex",gap:6,marginBottom:12}}>
      <button type="button" onClick={()=>setMode("soort")} style={{flex:1,padding:"10px",borderRadius:10,
        border:mode==="soort"?"2px solid var(--g)":"2px solid var(--brd)",
        background:mode==="soort"?"var(--g)":"#fff",color:mode==="soort"?"#fff":"var(--txt)",
        fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>🐾 Per diersoort</button>
      <button type="button" onClick={()=>setMode("dier")} style={{flex:1,padding:"10px",borderRadius:10,
        border:mode==="dier"?"2px solid var(--g)":"2px solid var(--brd)",
        background:mode==="dier"?"var(--g)":"#fff",color:mode==="dier"?"#fff":"var(--txt)",
        fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>🏷️ Per dier</button>
    </div>

    {/* Target selection */}
    {mode==="soort"?
      <div className="fg"><label className="fl">Diersoort *</label><select className="fi" value={soortId} onChange={e=>setSoortId(e.target.value)}>
        <option value="">-- Kies soort --</option>
        {soorten.map(s=><option key={s.id} value={s.id}>{s.icon} {s.naam}</option>)}
      </select></div>
    :
      <div className="fg"><label className="fl">Dier *</label><select className="fi" value={dierId} onChange={e=>setDierId(e.target.value)}>
        <option value="">-- Kies dier --</option>
        {animals.map(a=>{const soort=soorten.find(s=>s.id===a.soort_id);return <option key={a.id} value={a.id}>{soort?.icon||"🐾"} {a.name||a.naam}</option>;})}
      </select></div>
    }

    {/* Voeding selection */}
    <div className="fg"><label className="fl">Voeding *</label><select className="fi" value={voedingId} onChange={e=>setVoedingId(e.target.value)}>
      <option value="">-- Kies voeding --</option>
      {feedings.map(f=><option key={f.id} value={f.id}>{f.type}{f.merk?` (${f.merk})`:""}{f.inhoud?` · ${f.inhoud}`:""}</option>)}
    </select></div>

    <div className="fg"><label className="fl">Hoeveelheid</label><input className="fi" value={hoeveelheid} onChange={e=>setHoeveelheid(e.target.value)} placeholder="Bijv. 2 scheppen, 1 plak, 500g"/></div>

    <div className="fg"><label className="fl">Dagdeel *</label><select className="fi" value={dagdeel} onChange={e=>setDagdeel(e.target.value)}>
      <option value="ochtend">🌅 Ochtend</option>
      <option value="middag">☀️ Middag</option>
      <option value="avond">🌙 Avond</option>
    </select></div>

    <button className="fbtn" onClick={save}>Opslaan</button>
  </Modal>;
}

function MedForm({item,animals,onSave,onClose}){
  const VORMEN=["pil","capsule","vloeistof","injectie","creme","prothese","bandage","brace","spray"];
  const [f,setF]=useState(item||{medicijn:"",merknaam:"",vorm:"pil",toediening:"",foto:null,link:""});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  
  // Ensure vorm has a value
  const vormValue = f.vorm || "pil";
  
  return <Modal title={item?"Medicatie bewerken":"Medicatie toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Medicijn *</label><input className="fi" value={f.medicijn} onChange={e=>u("medicijn",e.target.value)} placeholder="Bijv. Ibuprofen"/></div>
    <div className="fg"><label className="fl">Merknaam</label><input className="fi" value={f.merknaam||""} onChange={e=>u("merknaam",e.target.value)} placeholder="Bijv. Brufen"/></div>
    <div className="fg"><label className="fl">Vorm *</label><select className="fi" value={vormValue} onChange={e=>u("vorm",e.target.value)}>
      {VORMEN.map(v=><option key={v} value={v}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
    </select></div>
    <div className="fg"><label className="fl">Toediening</label><textarea className="fi" rows={3} value={f.toediening||""} onChange={e=>u("toediening",e.target.value)} placeholder="Bijv. Intramusculair in de schouder"/></div>
    <MediaUploadField foto={f.foto} onChange={v=>u("foto",v)}/>
    <div className="fg"><label className="fl">🔗 Link (bijv. YouTube instructie)</label><input className="fi" value={f.link||""} onChange={e=>u("link",e.target.value)} placeholder="https://www.youtube.com/..."/></div>
    <button className="fbtn" onClick={()=>{if(!f.medicijn){toast.warn("Medicijn is verplicht!");return;}onSave(f);}}>Opslaan</button>
  </Modal>;
}

function LeverancierForm({item,contacten=[],onSave,onClose}){
  const [f,setF]=useState(item||{naam:"",contact_id:null,foto:null,website:""});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  return <Modal title={item?"Leverancier bewerken":"Leverancier toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Naam leverancier *</label><input className="fi" value={f.naam} onChange={e=>u("naam",e.target.value)} placeholder="Bijv. Agrifirm, DeLaval, Greenfeeds"/></div>
    <div className="fg"><label className="fl">Contact</label><select className="fi" value={f.contact_id||""} onChange={e=>u("contact_id",e.target.value?Number(e.target.value):null)}><option value="">- Geen contact gekoppeld -</option>{contacten.map(c=><option key={c.id} value={c.id}>{c.naam}{c.tel&&` (${c.tel})`}</option>)}</select></div>
    <div className="fg"><label className="fl">Website</label><input className="fi" value={f.website||""} onChange={e=>u("website",e.target.value)} placeholder="https://www.agrifirm.nl"/></div>
    <MediaUploadField foto={f.foto} onChange={v=>u("foto",v)}/>
    <button className="fbtn" onClick={()=>{if(!f.naam.trim()){toast.warn("Naam is verplicht!");return;}onSave(f);}}>Opslaan</button>
  </Modal>;
}

function BestelForm({item,leveranciers=[],feedings=[],onSave,onClose}){
  const [f,setF]=useState(item||{datum:new Date().toISOString().split('T')[0],leverancier_id:null,voeding_id:null,amount:""});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  return <Modal title={item?"Bestelling bewerken":"Bestelling toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Datum *</label><input className="fi" type="date" value={f.datum} onChange={e=>u("datum",e.target.value)}/></div>
    <div className="fg"><label className="fl">Leverancier *</label><select className="fi" value={f.leverancier_id||""} onChange={e=>u("leverancier_id",e.target.value?Number(e.target.value):null)}><option value="">- Selecteer leverancier -</option>{leveranciers.map(l=><option key={l.id} value={l.id}>{l.naam}</option>)}</select></div>
    <div className="fg"><label className="fl">Voeding *</label><select className="fi" value={f.voeding_id||""} onChange={e=>u("voeding_id",e.target.value?Number(e.target.value):null)}><option value="">- Selecteer voeding -</option>{feedings.map(v=><option key={v.id} value={v.id}>{v.type}{v.merk&&` (${v.merk})`}</option>)}</select></div>
    <div className="fg"><label className="fl">Hoeveelheid</label><input className="fi" value={f.amount||""} onChange={e=>u("amount",e.target.value)} placeholder="Bijv. 20kg, 100L, 5 dozen"/></div>
    <button className="fbtn" onClick={()=>{if(!f.datum||!f.leverancier_id||!f.voeding_id){toast.warn("Datum, leverancier en voeding zijn verplicht!");return;}onSave(f);}}>Opslaan</button>
  </Modal>;
}

function AgForm({item,onSave,onClose}){
  const [f,setF]=useState(item||{titel:"",datum:"2026-02-20",tijd:"10:00",desc:"",type:"evenement",herhaling:"geen",herhaalTot:"2026-03-20"});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  return <Modal title={item?"Agenda bewerken":"Afspraak toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Titel</label><input className="fi" value={f.titel} onChange={e=>u("titel",e.target.value)}/></div>
    <div className="fg"><label className="fl">Datum</label><input className="fi" type="date" value={f.datum} onChange={e=>u("datum",e.target.value)}/></div>
    <div className="fg"><label className="fl">Tijd</label><input className="fi" type="time" value={f.tijd} onChange={e=>u("tijd",e.target.value)}/></div>
    <div className="fg"><label className="fl">Type</label><select className="fi" value={f.type} onChange={e=>u("type",e.target.value)}><option value="medisch">Medisch</option><option value="vergadering">Vergadering</option><option value="evenement">Evenement</option><option value="gemeente">Gemeente</option></select></div>
    <div className="fg"><label className="fl">Beschrijving</label><textarea className="fi" rows={2} value={f.desc} onChange={e=>u("desc",e.target.value)}/></div>
    {!item&&<><div className="fg"><label className="fl">Herhaling</label><select className="fi" value={f.herhaling} onChange={e=>u("herhaling",e.target.value)}><option value="geen">Geen</option><option value="dagelijks">Dagelijks</option><option value="wekelijks">Wekelijks</option><option value="maandelijks">Maandelijks</option></select></div>
    {f.herhaling!=="geen"&&<div className="fg"><label className="fl">Herhalen t/m</label><input className="fi" type="date" value={f.herhaalTot} onChange={e=>u("herhaalTot",e.target.value)}/></div>}</>}
    <button className="fbtn" onClick={()=>onSave(f)}>Opslaan</button>
  </Modal>;
}

function TkForm({item,users,onSave,onClose,toewijzingen=[]}){
  const itemToewijzingen=item?toewijzingen.filter(t=>t.taak_id===item.id).map(t=>t.gebruiker_id):[];
  // Determine initial frequentie from existing data
  const initFreq = item?.frequentie || (item?.herhalend ? "wekelijks" : (item?.dagen?.length > 0 ? "wekelijks" : "eenmalig"));
  const [f,setF]=useState(item||{titel:"",cat:"reparatie",prio:"normaal",desc:"",dagen:[],herhalend:false,frequentie:"wekelijks",datum:"",maand_dag:1});
  const [selectedUsers,setSelectedUsers]=useState(itemToewijzingen);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const DAGEN_KORT=["ma","di","wo","do","vr","za","zo"];
  const DAGEN_LANG=["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];
  const dagen=f.dagen||[];
  const freq=f.frequentie||initFreq;
  const toggleDag=(dag)=>{
    const next=dagen.includes(dag)?dagen.filter(d=>d!==dag):[...dagen,dag];
    u("dagen",next);
  };
  const toggleUser=(uid)=>{
    setSelectedUsers(prev=>prev.includes(uid)?prev.filter(id=>id!==uid):[...prev,uid]);
  };
  return <Modal title={item?"Taak bewerken":"Taak toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Titel *</label><input className="fi" value={f.titel} onChange={e=>u("titel",e.target.value)}/></div>
    <div className="fg"><label className="fl">Categorie</label><select className="fi" value={f.cat} onChange={e=>u("cat",e.target.value)}><option value="reparatie">Reparatie</option><option value="schoonmaak">Schoonmaak</option><option value="gemeente">Gemeente</option><option value="voeding">Voeding</option><option value="administratie">Administratie</option><option value="overig">Overig</option></select></div>
    <div className="fg"><label className="fl">Prioriteit</label><select className="fi" value={f.prio} onChange={e=>u("prio",e.target.value)}><option value="hoog">Hoog</option><option value="normaal">Normaal</option><option value="laag">Laag</option></select></div>

    {/* Frequentie */}
    <div className="fg"><label className="fl">Frequentie</label>
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {[{id:"wekelijks",label:"🔁 Wekelijks",desc:"Vaste weekdagen"},{id:"maandelijks",label:"📆 Maandelijks",desc:"Op een vaste dag"},{id:"jaarlijks",label:"🗓️ Jaarlijks",desc:"Op een vaste datum"},{id:"eenmalig",label:"📌 Eenmalig",desc:"Eén keer"}].map(opt=>
          <button key={opt.id} type="button" onClick={()=>u("frequentie",opt.id)} style={{
            flex:"1 1 45%",padding:"10px 8px",border:freq===opt.id?"2px solid var(--g)":"2px solid var(--brd)",
            borderRadius:10,background:freq===opt.id?"var(--gp)":"#fff",cursor:"pointer",textAlign:"center",transition:"all .15s"
          }}>
            <div style={{fontSize:13,fontWeight:600,color:freq===opt.id?"var(--g)":"var(--txt)"}}>{opt.label}</div>
            <div style={{fontSize:10,color:"var(--txtl)",marginTop:2}}>{opt.desc}</div>
          </button>
        )}
      </div>
    </div>

    {/* Weekdagen - only for wekelijks */}
    {freq==="wekelijks"&&<div className="fg"><label className="fl">Dagen</label>
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {DAGEN_KORT.map((dag,i)=><button key={dag} type="button" onClick={()=>toggleDag(dag)} style={{
          padding:"8px 0",width:42,border:dagen.includes(dag)?"2px solid var(--g)":"2px solid var(--brd)",
          borderRadius:10,background:dagen.includes(dag)?"var(--g)":"#fff",color:dagen.includes(dag)?"#fff":"var(--txt)",
          fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"center",transition:"all .15s"
        }}>{DAGEN_LANG[i].slice(0,2)}</button>)}
      </div>
    </div>}

    {/* Maanddag - only for maandelijks */}
    {freq==="maandelijks"&&<div className="fg"><label className="fl">Dag van de maand</label>
      <select className="fi" value={f.maand_dag||1} onChange={e=>u("maand_dag",parseInt(e.target.value))}>
        {Array.from({length:28},(_,i)=>i+1).map(d=><option key={d} value={d}>{d}e van de maand</option>)}
      </select>
    </div>}

    {/* Datum - for eenmalig and jaarlijks */}
    {(freq==="eenmalig"||freq==="jaarlijks")&&<div className="fg"><label className="fl">{freq==="eenmalig"?"Deadline datum":"Datum (jaarlijks terugkerend)"}</label>
      <input className="fi" type="date" value={f.datum||""} onChange={e=>u("datum",e.target.value)}/>
      {freq==="jaarlijks"&&f.datum&&<div style={{marginTop:4,padding:"4px 10px",borderRadius:8,background:"var(--gp)",fontSize:12,color:"var(--g)"}}>
        🗓️ Elk jaar op {new Date(f.datum+"T00:00").toLocaleDateString("nl-NL",{day:"numeric",month:"long"})}
      </div>}
    </div>}

    {/* Multi-person toewijzing */}
    <div className="fg"><label className="fl">Toewijzen aan</label>
      <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:140,overflowY:"auto",border:"1.5px solid var(--brd)",borderRadius:10,padding:8}}>
        {users.filter(u=>u.actief).length===0?<div style={{fontSize:13,color:"var(--txtl)",padding:4}}>Geen actieve teamleden</div>:
        users.filter(u=>u.actief).map(usr=><label key={usr.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 4px",borderRadius:8,cursor:"pointer",
          background:selectedUsers.includes(usr.id)?"var(--gp)":"transparent",transition:"background .15s"}}>
          <input type="checkbox" checked={selectedUsers.includes(usr.id)} onChange={()=>toggleUser(usr.id)}
            style={{width:18,height:18,accentColor:"var(--g)",flexShrink:0}}/>
          <span style={{fontSize:14,fontWeight:selectedUsers.includes(usr.id)?600:400,color:selectedUsers.includes(usr.id)?"var(--g)":"var(--txt)"}}>{usr.name}</span>
          <span style={{fontSize:11,color:"var(--txtl)",marginLeft:"auto"}}>{PERMISSIONS[usr.role]?.label||usr.role}</span>
        </label>)}
      </div>
    </div>

    <div className="fg"><label className="fl">Beschrijving</label><textarea className="fi" rows={6} value={f.desc} onChange={e=>u("desc",e.target.value)} placeholder="Beschrijf de taak zo uitgebreid als nodig..." style={{minHeight:120}}/></div>
    <button className="fbtn" onClick={()=>{if(!f.titel.trim()){toast.warn("Titel is verplicht");return;}onSave({...f,frequentie:freq,herhalend:freq==="wekelijks",toewijzingen:selectedUsers})}}>Opslaan</button>
  </Modal>;
}

function CtForm({item, onSave, onClose, contactCategorieen=[]}){
  const [f, setF] = useState(item || {naam:"", contact_categorie_id: null, functie:"", tel:"", email:"", adres:"", notitie:"", foto:"👤"});
  const u = (k, v) => setF(p => ({...p, [k]: v}));
  
  return <Modal title={item ? "Contact bewerken" : "Contact toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Naam</label><input className="fi" value={f.naam} onChange={e=>u("naam",e.target.value)}/></div>
    <div className="fg"><label className="fl">Categorie</label><select className="fi" value={f.contact_categorie_id || ""} onChange={e=>u("contact_categorie_id", e.target.value ? parseInt(e.target.value) : null)}>
      <option value="">-- Categorie kiezen --</option>
      {(contactCategorieen || []).map(cc => <option key={cc.id} value={cc.id}>{cc.emoji} {cc.categorie}</option>)}
    </select></div>
    <div className="fg"><label className="fl">Functie</label><input className="fi" placeholder="bijv. Dierenarts, Wethouder Groen" value={f.functie} onChange={e=>u("functie",e.target.value)}/></div>
    <div className="fg"><label className="fl">Telefoon</label><input className="fi" type="tel" value={f.tel} onChange={e=>u("tel",e.target.value)}/></div>
    <div className="fg"><label className="fl">E-mail</label><input className="fi" type="email" value={f.email} onChange={e=>u("email",e.target.value)}/></div>
    <div className="fg"><label className="fl">Adres</label><input className="fi" value={f.adres} onChange={e=>u("adres",e.target.value)}/></div>
    <div className="fg"><label className="fl">Notitie</label><textarea className="fi" rows={2} value={f.notitie} onChange={e=>u("notitie",e.target.value)}/></div>
    <button className="fbtn" onClick={()=>onSave(f)}>Opslaan</button>
  </Modal>;
}

function UsrForm({onSave,onClose,parkId,parken,isAppAdmin}){
  const [f,setF]=useState({name:"",roepnaam:"",email:"",role:"vrijwilliger",park_id:parkId||""});
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const [ok,setOk]=useState(false);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));

  const submit=async()=>{
    if(!f.name.trim()||!f.email.trim()||!f.roepnaam.trim()){setErr("Naam, roepnaam en e-mail zijn verplicht.");return;}
    setLoading(true);setErr("");
    try {
      // Haal sessie token op
      const token=await db.getToken();
      console.log("Token aanwezig:", !!token, token?.substring(0,20));
      if(!token){setErr("Sessie verlopen. Log opnieuw in.");setLoading(false);return;}

      const res=await fetch(`${SUPABASE_URL}/functions/v1/invite-user`,{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          "Authorization":`Bearer ${token}`,
          "apikey": SUPABASE_KEY,
        },
        body:JSON.stringify({naam:f.name,roepnaam:f.roepnaam,email:f.email,role:f.role,park_id:f.park_id||null,is_bezoeker:false})
      });
      const data=await res.json();
      if(!res.ok||data.error){setErr(data.error||"Fout bij aanmaken.");setLoading(false);return;}
      setOk(true);
      onSave({...data.user,name:data.user.naam});
    } catch(e){
      setErr("Kon verbinding niet maken met server.");
    }
    setLoading(false);
  };

  if(ok) return <Modal title="Gebruiker uitgenodigd" onClose={onClose}>
    <div style={{textAlign:"center",padding:"16px 0"}}>
      <div style={{fontSize:40,marginBottom:8}}>📬</div>
      <h3 style={{fontFamily:"'Fraunces',serif",color:"var(--g)",marginBottom:8}}>Uitnodiging verstuurd!</h3>
      <p style={{fontSize:13,color:"var(--txtl)",lineHeight:1.6}}><strong>{f.email}</strong> ontvangt een e-mail om een wachtwoord in te stellen.</p>
      <button className="fbtn" style={{marginTop:16}} onClick={onClose}>Sluiten</button>
    </div>
  </Modal>;

  return <Modal title="Medewerker uitnodigen" onClose={onClose}>
    <p style={{fontSize:12,color:"var(--txtl)",marginBottom:12,lineHeight:1.5}}>📬 De medewerker ontvangt een e-mail om een wachtwoord in te stellen.</p>
    <div className="fg"><label className="fl">Naam</label><input className="fi" value={f.name} onChange={e=>u("name",e.target.value)} placeholder="Jan de Vries" autoFocus/></div>
    <div className="fg"><label className="fl">Roepnaam <span style={{fontWeight:400,color:"var(--txtl)"}}>(voor in de app)</span></label><input className="fi" value={f.roepnaam} onChange={e=>u("roepnaam",e.target.value)} placeholder="Jan"/></div>
    <div className="fg"><label className="fl">E-mailadres</label><input className="fi" type="email" value={f.email} onChange={e=>u("email",e.target.value)} placeholder="jan@email.nl"/></div>
    <div className="fg"><label className="fl">Rol</label><select className="fi" value={f.role} onChange={e=>u("role",e.target.value)}>
      {Object.entries(PERMISSIONS).map(([k,v])=><option key={k} value={k}>{v.label} — {v.beschrijving}</option>)}
    </select></div>
    {isAppAdmin&&parken&&<div className="fg"><label className="fl">Park</label><select className="fi" value={f.park_id} onChange={e=>u("park_id",e.target.value)}>
      <option value="">— Kies park —</option>
      {parken.map(p=><option key={p.id} value={p.id}>{p.naam}</option>)}
    </select></div>}
    {err&&<p style={{fontSize:12,color:"var(--red)",marginBottom:8,padding:"6px 10px",background:"var(--rl)",borderRadius:8}}>{err}</p>}
    <PawBtn onClick={submit} loading={loading}>📬 Uitnodiging versturen</PawBtn>
  </Modal>;
}

function PlanForm({item,defaultDatum,users,contacten=[],onSave,onClose}){
  const today=new Date().toISOString().split("T")[0];
  const [f,setF]=useState(item||{userId:"",contactId:"",naam:"",datum:defaultDatum||today,shift:"ochtend",notitie:"",herhaling:"geen",herhaalTot:""});
  const [editMode,setEditMode]=useState(null); // null | "alleen" | "reeks"
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const isEdit=!!item;
  const isReeks=item?.reeks_id&&item?.herhaling&&item.herhaling!=="geen";

  const personValue=f.userId?`user:${f.userId}`:f.contactId?`contact:${f.contactId}`:"";
  const handlePersonChange=(val)=>{
    if(!val){u("userId","");u("contactId","");u("naam","");return;}
    const [type,id]=val.split(":");
    if(type==="user"){
      const usr=users.find(x=>x.id===Number(id));
      u("userId",Number(id));u("contactId","");if(usr)u("naam",usr.name);
    } else {
      const ct=contacten.find(x=>x.id===Number(id));
      u("contactId",Number(id));u("userId","");if(ct)u("naam",ct.naam);
    }
  };

  const handleSave=()=>{
    if(isEdit){
      if(isReeks&&!editMode) return;
      onSave(f, isReeks&&editMode==="reeks"?"reeks":"alleen");
    } else {
      onSave(f);
    }
  };

  return <Modal title={isEdit?"Dienst bewerken":"Dienst toevoegen"} onClose={onClose}>
    {isEdit&&isReeks&&!editMode&&<div style={{marginBottom:12}}>
      <p style={{fontSize:14,fontWeight:600,color:"var(--g)",marginBottom:8}}>🔁 Dit is een herhalende dienst. Wat wil je bewerken?</p>
      <div style={{display:"flex",gap:8}}>
        <button className="fbtn" style={{flex:1,background:"var(--cd)",color:"var(--g)",border:"1px solid var(--brd)"}} onClick={()=>setEditMode("alleen")}>Alleen deze dienst</button>
        <button className="fbtn" style={{flex:1}} onClick={()=>setEditMode("reeks")}>Hele reeks</button>
      </div>
    </div>}

    {(!isEdit||(isEdit&&(!isReeks||editMode)))&&<>
      {isEdit&&editMode&&<div style={{padding:"6px 10px",borderRadius:8,background:"var(--gp)",fontSize:12,color:"var(--g)",marginBottom:10}}>
        {editMode==="reeks"?"✏️ Je bewerkt de hele reeks (deze en toekomstige diensten)":"✏️ Je bewerkt alleen deze dienst"}
      </div>}
      <div className="fg"><label className="fl">Persoon</label><select className="fi" value={personValue} onChange={e=>handlePersonChange(e.target.value)}>
        <option value="">Kies persoon</option>
        <optgroup label="👤 Gebruikers">{users.filter(u=>u.actief).map(u=><option key={`u${u.id}`} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
        {contacten.length>0&&<optgroup label="📋 Contacten">{contacten.map(c=><option key={`c${c.id}`} value={`contact:${c.id}`}>{c.naam}{c.functie?` (${c.functie})`:""}</option>)}</optgroup>}
      </select></div>
      <div className="fg"><label className="fl">Datum</label><input className="fi" type="date" value={f.datum} onChange={e=>u("datum",e.target.value)}/></div>
      <div className="fg"><label className="fl">Dienst</label><select className="fi" value={f.shift} onChange={e=>u("shift",e.target.value)}>{SHIFTS.map(sh=><option key={sh.id} value={sh.id}>{sh.label} ({sh.tijd})</option>)}</select></div>
      <div className="fg"><label className="fl">Notitie</label><input className="fi" value={f.notitie} onChange={e=>u("notitie",e.target.value)} placeholder="Optioneel..."/></div>
      {!isEdit&&<><div className="fg"><label className="fl">Herhaling</label><select className="fi" value={f.herhaling} onChange={e=>u("herhaling",e.target.value)}>
          <option value="geen">🔂 Geen herhaling</option>
          <option value="dagelijks">📅 Dagelijks</option>
          <option value="wekelijks">🔁 Wekelijks</option>
          <option value="maandelijks">📆 Maandelijks</option>
          <option value="jaarlijks">🗓️ Jaarlijks</option>
        </select></div>
      {f.herhaling!=="geen"&&<div className="fg"><label className="fl">Herhalen t/m <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel - zonder datum herhaalt het oneindig)</span></label><input className="fi" type="date" value={f.herhaalTot} onChange={e=>u("herhaalTot",e.target.value)}/></div>}
      {f.herhaling!=="geen"&&f.herhaalTot&&<div style={{padding:"8px 12px",borderRadius:10,background:"var(--gp)",fontSize:12,color:"var(--g)",marginBottom:8}}>
        🔁 <strong>{f.herhaling}</strong> van <strong>{f.datum}</strong> t/m <strong>{f.herhaalTot}</strong>
      </div>}
      {f.herhaling!=="geen"&&!f.herhaalTot&&<div style={{padding:"8px 12px",borderRadius:10,background:"var(--gp)",fontSize:12,color:"var(--g)",marginBottom:8}}>
        🔁 <strong>{f.herhaling}</strong> vanaf <strong>{f.datum}</strong> (oneindig)
      </div>}</>}
      <button className="fbtn" onClick={handleSave}>Opslaan</button>
    </>}
  </Modal>;
}

/* ═══════════════════════════════════════════════════════════
   MANAGEMENT APP — PAGE COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function DierenadviesSection({title,items,canEdit,onAdd,onUpdate,onDelete,emptyText}){
  const [openForm,setOpenForm]=useState(false);
  const [editItem,setEditItem]=useState(null);
  const [titel,setTitel]=useState("");
  const [advies,setAdvies]=useState("");
  const [prioriteit,setPrioriteit]=useState("normaal");

  const reset=()=>{setTitel("");setAdvies("");setPrioriteit("normaal");setEditItem(null);setOpenForm(false);};
  const startEdit=(item)=>{setEditItem(item);setTitel(item.titel||"");setAdvies(item.advies||"");setPrioriteit(item.prioriteit||"normaal");setOpenForm(true);};
  const save=()=>{
    if(!advies.trim()){ toast.warn("Vul advies in"); return; }
    const payload={ ...(editItem||{}), titel:titel.trim(), advies:advies.trim(), prioriteit };
    if(editItem) onUpdate?.(payload); else onAdd?.(payload);
    reset();
  };

  return <div style={{marginTop:14}}>
    <div className="sh" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <span className="st">{title}</span>
      {canEdit&&<button className="fbtn" type="button" onClick={()=>{setOpenForm(v=>!v); if(openForm) reset();}}>+ Advies</button>}
    </div>
    {!items?.length&&<div style={{background:"#fff",border:"1px dashed #d8d8d8",borderRadius:10,padding:12,fontSize:13,color:"var(--txtl)"}}>{emptyText||"Nog geen advies toegevoegd."}</div>}
    {!!items?.length&&<div style={{display:"grid",gap:10}}>
      {items.map(item=><div key={item.id} style={{background:item.prioriteit==="belangrijk"?"#fff7e6":"#fff",border:`1px solid ${item.prioriteit==="belangrijk"?"#ffd591":"#e8e8e8"}`,borderRadius:12,padding:12,boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
              {item.titel&&<strong style={{fontSize:14,color:"var(--g)"}}>{item.titel}</strong>}
              {item.prioriteit==="belangrijk"&&<span style={{fontSize:11,fontWeight:700,background:"#ffe7ba",color:"#ad6800",padding:"2px 8px",borderRadius:999}}>Belangrijk</span>}
            </div>
            <div style={{fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{item.advies}</div>
          </div>
          {canEdit&&<div className="iab">
            <button type="button" onClick={()=>startEdit(item)}>{IC.edit}</button>
            <button type="button" onClick={()=>{ toast.confirm("Weet je zeker dat je dit advies wilt verwijderen?",()=>onDelete?.(item.id)); }}>{IC.trash}</button>
          </div>}
        </div>
      </div>)}
    </div>}

    {canEdit&&openForm&&<div style={{background:"#fff",border:"1px solid #e8e8e8",borderRadius:12,padding:12,marginTop:10}}>
      <div className="fg"><label className="fl">Titel</label><input className="fi" value={titel} onChange={e=>setTitel(e.target.value)} placeholder="Bijv. Voeding, Gedrag, Let op"/></div>
      <div className="fg"><label className="fl">Advies</label><textarea className="fi" rows={4} value={advies} onChange={e=>setAdvies(e.target.value)} placeholder="Schrijf hier het advies..."/></div>
      <div className="fg"><label className="fl">Prioriteit</label><select className="fi" value={prioriteit} onChange={e=>setPrioriteit(e.target.value)}><option value="normaal">Normaal</option><option value="belangrijk">Belangrijk</option></select></div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="fbtn" type="button" onClick={reset}>Annuleren</button>
        <button className="fbtn" type="button" onClick={save}>{editItem?"Opslaan":"Toevoegen"}</button>
      </div>
    </div>}
  </div>;
}

function CombinedDierenadvies({soortAdviezen,dierAdviezen,canEdit,soortNaam,dierNaam,onAddSoort,onAddDier,onUpdate,onDelete}){
  const [openForm,setOpenForm]=useState(false);
  const [scope,setScope]=useState("soort"); // "soort" | "dier"
  const [editItem,setEditItem]=useState(null);
  const [titel,setTitel]=useState("");
  const [advies,setAdvies]=useState("");
  const [prioriteit,setPrioriteit]=useState("normaal");

  const reset=()=>{setTitel("");setAdvies("");setPrioriteit("normaal");setEditItem(null);setOpenForm(false);};
  const startEdit=(item,itemScope)=>{setEditItem(item);setScope(itemScope);setTitel(item.titel||"");setAdvies(item.advies||"");setPrioriteit(item.prioriteit||"normaal");setOpenForm(true);};
  const save=()=>{
    if(!advies.trim()){toast.warn("Vul advies in");return;}
    const payload={...(editItem||{}),titel:titel.trim(),advies:advies.trim(),prioriteit};
    if(editItem) onUpdate?.(payload);
    else if(scope==="soort") onAddSoort?.(payload);
    else onAddDier?.(payload);
    reset();
  };

  const allItems=[
    ...(soortAdviezen||[]).map(a=>({...a,_scope:"soort"})),
    ...(dierAdviezen||[]).map(a=>({...a,_scope:"dier"})),
  ];

  const scopeStyle={
    soort:{bg:"#e8f4fd",color:"#1a6fa8",label:`Soort · ${soortNaam}`},
    dier: {bg:"#f0fdf0",color:"#2d6a2d",label:`Dier · ${dierNaam}`},
  };

  return <div style={{marginTop:14}}>
    <div className="sh" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <span className="st">Advies</span>
      {canEdit&&<button className="fbtn" type="button" onClick={()=>{setOpenForm(v=>!v);if(openForm)reset();}}>+ Advies</button>}
    </div>

    {canEdit&&openForm&&<div style={{background:"#fff",border:"1px solid #e8e8e8",borderRadius:12,padding:12,marginBottom:10}}>
      {!editItem&&<div className="fg">
        <label className="fl">Advies voor</label>
        <div style={{display:"flex",gap:8,marginTop:6}}>
          <button className="fbtn" type="button" style={{flex:1,background:scope==="soort"?"var(--g)":"var(--cd)",color:scope==="soort"?"#fff":"var(--g)",border:"none"}} onClick={()=>setScope("soort")}>🐾 {soortNaam}</button>
          <button className="fbtn" type="button" style={{flex:1,background:scope==="dier"?"var(--g)":"var(--cd)",color:scope==="dier"?"#fff":"var(--g)",border:"none"}} onClick={()=>setScope("dier")}>🏷️ {dierNaam}</button>
        </div>
      </div>}
      <div className="fg"><label className="fl">Titel</label><input className="fi" value={titel} onChange={e=>setTitel(e.target.value)} placeholder="Bijv. Voeding, Gedrag, Let op"/></div>
      <div className="fg"><label className="fl">Advies</label><textarea className="fi" rows={4} value={advies} onChange={e=>setAdvies(e.target.value)} placeholder="Schrijf hier het advies..."/></div>
      <div className="fg"><label className="fl">Prioriteit</label><select className="fi" value={prioriteit} onChange={e=>setPrioriteit(e.target.value)}><option value="normaal">Normaal</option><option value="belangrijk">Belangrijk</option></select></div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="fbtn" type="button" onClick={reset}>Annuleren</button>
        <button className="fbtn" type="button" onClick={save}>{editItem?"Opslaan":"Toevoegen"}</button>
      </div>
    </div>}

    {allItems.length===0&&<div style={{background:"#fff",border:"1px dashed #d8d8d8",borderRadius:10,padding:12,fontSize:13,color:"var(--txtl)"}}>Nog geen advies toegevoegd.</div>}
    {allItems.length>0&&<div style={{display:"grid",gap:8}}>
      {allItems.map(item=>{
        const st=scopeStyle[item._scope];
        return <div key={item.id} style={{background:item.prioriteit==="belangrijk"?"#fff7e6":"#fff",border:`1px solid ${item.prioriteit==="belangrijk"?"#ffd591":"#e8e8e8"}`,borderRadius:12,padding:12,boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>
          <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"flex-start"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:999,background:st.bg,color:st.color}}>{st.label}</span>
                {item.titel&&<strong style={{fontSize:14,color:"var(--g)"}}>{item.titel}</strong>}
                {item.prioriteit==="belangrijk"&&<span style={{fontSize:11,fontWeight:700,background:"#ffe7ba",color:"#ad6800",padding:"2px 8px",borderRadius:999}}>Belangrijk</span>}
              </div>
              <div style={{fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{item.advies}</div>
            </div>
            {canEdit&&<div className="iab">
              <button type="button" onClick={()=>startEdit(item,item._scope)}>{IC.edit}</button>
              <button type="button" onClick={()=>toast.confirm("Advies verwijderen?",()=>onDelete?.(item.id))}>{IC.trash}</button>
            </div>}
          </div>
        </div>;
      })}
    </div>}
  </div>;
}

function DierenadviesForm({item, dier_id, onSave, onClose}){
  const today = new Date().toISOString().split("T")[0];
  const [f, setF] = useState(item || {titel:"", advies:"", dier_id:dier_id||null});
  const u = (k, v) => setF(p=>({...p, [k]:v}));
  
  return <Modal title={item ? "Advies bewerken" : "Advies toevoegen"} onClose={onClose}>
    <div className="fg"><label className="fl">Titel <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" value={f.titel||""} onChange={e=>u("titel",e.target.value)}/></div>
    <div className="fg"><label className="fl">Advies *</label><textarea className="fi" rows={4} value={f.advies||""} onChange={e=>u("advies",e.target.value)} placeholder="Bijv. Dit dier heeft behoefte aan..."/></div>
    <button className="fbtn" onClick={()=>{if(!f.advies?.trim()) {toast.err("Advies is verplicht"); return;} onSave(f);}} style={{marginBottom:8}}>💾 Opslaan</button>
    <button className="fbtn" onClick={onClose} style={{background:"var(--cd)",color:"var(--g)",marginBottom:0}}>Annuleren</button>
  </Modal>;
}

const BEZOEKER_VRAGEN={
  algemeen:[
    {code:"wie_ben_ik",label:"Wie ben ik?",placeholder:"Vertel iets over de persoonlijkheid of het karakter van dit dier..."},
    {code:"waar_vandaan",label:"Waar kom ik vandaan?",placeholder:"Habitat, oorsprong, hoe dit dier bij het park terecht is gekomen..."},
    {code:"kenmerken",label:"Waar herken je mij aan?",placeholder:"Uiterlijke kenmerken, kleur, formaat..."},
    {code:"bijzonder",label:"Wat maakt mij bijzonder?",placeholder:"Een leuk weetje of bijzondere eigenschap..."},
  ],
  voeding:[
    {code:"wat_eet_ik",label:"Wat eet ik?",placeholder:"Soorten voedsel, favorieten..."},
    {code:"hoe_vaak",label:"Hoe vaak word ik gevoerd?",placeholder:"Frequentie, tijden van de dag..."},
    {code:"mag_voeren",label:"Mag jij mij iets geven?",placeholder:"Voederinstructies voor bezoekers, wat mag wel/niet..."},
  ],
  verzorging:[
    {code:"hoe_verzorgd",label:"Hoe word ik verzorgd?",placeholder:"Dagelijkse verzorging, borstelen, schoonhouden..."},
    {code:"gedrag",label:"Hoe gedraag ik mij?",placeholder:"Sociaal gedrag, kudde of solitair, temperament..."},
    {code:"hoe_oud",label:"Hoe oud word ik?",placeholder:"Levensverwachting van deze soort..."},
    {code:"wanneer_actief",label:"Wanneer ben ik het actiefst?",placeholder:"Dag of nacht, seizoensgebonden gedrag..."},
  ],
};
const CAT_LABELS={algemeen:"🌿 Algemeen",voeding:"🥕 Voeding",verzorging:"🧴 Verzorging"};

function BezoekersInfoSection({dierId, soortId, rasId, parkId, soort_obj, dierNaam, isAppAdmin}){
  const [infoRecords,setInfoRecords]=useState([]);
  const [savingKey,setSavingKey]=useState(null);
  const [savedKey,setSavedKey]=useState(null);
  const [aiLoadingKey,setAiLoadingKey]=useState(null);
  const [activeTab,setActiveTab]=useState("algemeen");

  useEffect(()=>{
    if(!dierId&&!soortId) return;
    db.getDierInfo({dierId, soortId, rasId, parkId})
      .then(d=>setInfoRecords(d||[])).catch(()=>{});
  },[dierId,soortId]);

  const getRecord=(cat,vraag)=>infoRecords.find(r=>r.categorie===cat&&r.vraag===vraag
    &&(dierId?r.dier_id===dierId:r.dier_id===null||r.dier_id===undefined));

  const effectiveParkId = dierId ? parkId : (isAppAdmin ? null : parkId);

  const handleBlur=async(cat,vraag,tekst,origTekst)=>{
    if(tekst===origTekst) return;
    const key=`${cat}_${vraag}`;
    setSavingKey(key);
    try{
      const existing=getRecord(cat,vraag);
      if(!tekst.trim()){
        if(existing?.id){ await db.deleteDierInfo(existing.id); setInfoRecords(p=>p.filter(r=>r.id!==existing.id)); }
      } else {
        const payload={
          categorie:cat, vraag, tekst:tekst.trim(),
          ...(dierId?{dier_id:dierId}:{soort_id:soortId}),
          ...(rasId?{ras_id:rasId}:{}),
          park_id:effectiveParkId,
          ...(existing?.id?{id:existing.id}:{}),
        };
        const saved=await db.upsertDierInfo(payload);
        setInfoRecords(p=>{const without=p.filter(r=>!(r.categorie===cat&&r.vraag===vraag&&(dierId?r.dier_id===dierId:!r.dier_id))); return [...without,saved];});
      }
      setSavedKey(key); setTimeout(()=>setSavedKey(k=>k===key?null:k),2000);
    }catch(e){ toast.err("Opslaan mislukt"); }
    finally{ setSavingKey(null); }
  };

  const handleAI=async(cat,vraag,label)=>{
    const key=`${cat}_${vraag}`;
    setAiLoadingKey(key);
    try{
      const isDierNiveau=!!dierId;
      const context=[
        soort_obj?`Diersoort: ${soort_obj.naam}`:"",
        isDierNiveau&&dierNaam?`Naam van het dier: ${dierNaam}`:"",
      ].filter(Boolean).join(". ");
      const perspectief=isDierNiveau
        ?"vanuit het perspectief van het individuele dier (ik-vorm)"
        :"als algemene informatie over deze diersoort (ik-vorm, alsof de soort zelf spreekt)";
      const prompt=`Je schrijft bezoekersinfo voor een kinderdierenpark in het Nederlands. ${context}.\n\nBeantwoord deze vraag in 2-4 zinnen, geschreven ${perspectief}, vrolijk en educatief voor kinderen en ouders:\n\n"${label}"\n\nGeef alleen de antwoordtekst, geen uitleg of opmaak.`;
      const resp=await fetch(`${SUPABASE_URL}/functions/v1/ai-verrijking`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body:JSON.stringify({prompt,max_tokens:300}),
      });
      const data=await resp.json();
      const tekst=data?.tekst?.trim()||"";
      if(tekst){
        const existing=getRecord(cat,vraag);
        const payload={
          categorie:cat, vraag, tekst,
          ...(dierId?{dier_id:dierId}:{soort_id:soortId}),
          ...(rasId?{ras_id:rasId}:{}),
          park_id:effectiveParkId,
          ...(existing?.id?{id:existing.id}:{}),
        };
        const saved=await db.upsertDierInfo(payload);
        setInfoRecords(p=>{const without=p.filter(r=>!(r.categorie===cat&&r.vraag===vraag&&(dierId?r.dier_id===dierId:!r.dier_id))); return [...without,saved];});
        setSavedKey(key); setTimeout(()=>setSavedKey(k=>k===key?null:k),2000);
      }
    }catch(e){ toast.err("AI verrijking mislukt"); }
    finally{ setAiLoadingKey(null); }
  };

  // App admin: promoveer park-specifieke soortrecords naar platformbreed
  const parkRecordsVoorSoort = !dierId && isAppAdmin
    ? infoRecords.filter(r=>!r.dier_id && r.park_id !== null)
    : [];

  const handleKopieerNaarPlatform=async()=>{
    if(!parkRecordsVoorSoort.length) return;
    if(!window.confirm(`${parkRecordsVoorSoort.length} record(s) kopiëren naar platformbreed (park_id=null)? Bestaande platformbrede records worden overschreven.`)) return;
    try{
      for(const r of parkRecordsVoorSoort){
        await db.upsertDierInfo({categorie:r.categorie, vraag:r.vraag, tekst:r.tekst, soort_id:r.soort_id, ...(r.ras_id?{ras_id:r.ras_id}:{}), park_id:null});
      }
      const refreshed=await db.getDierInfo({dierId, soortId, rasId, parkId});
      setInfoRecords(refreshed||[]);
      toast.ok(`${parkRecordsVoorSoort.length} records gepromoveerd naar platformbreed`);
    }catch(e){ toast.err("Kopiëren mislukt"); }
  };

  return <div>
    {/* App admin: banner als er park-specifieke records zijn die gepromoveerd kunnen worden */}
    {parkRecordsVoorSoort.length>0&&<div style={{background:"#fff8e6",border:"1px solid #f0d060",borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
      <div style={{fontSize:12,color:"#7a5c00"}}>
        <strong>⚠️ {parkRecordsVoorSoort.length} record(s)</strong> opgeslagen onder een specifiek park — nog niet platformbreed.
      </div>
      <button onClick={handleKopieerNaarPlatform} style={{fontSize:12,padding:"5px 12px",borderRadius:6,border:"1px solid #d4a800",background:"#fff3cc",color:"#7a5c00",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>
        🌐 Kopieer naar platform
      </button>
    </div>}
    {/* Categorie tabs */}
    <div style={{display:"flex",borderBottom:"1px solid #eee",marginBottom:12}}>
      {Object.keys(BEZOEKER_VRAGEN).map(cat=>{
        const gevuld=infoRecords.filter(r=>r.categorie===cat&&(dierId?r.dier_id===dierId:!r.dier_id)).length;
        return <button key={cat} onClick={()=>setActiveTab(cat)} style={{flex:1,padding:"8px 4px",border:"none",borderBottom:activeTab===cat?"2px solid var(--g)":"2px solid transparent",background:"none",fontSize:12,fontWeight:activeTab===cat?700:400,color:activeTab===cat?"var(--g)":"var(--txtl)",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
          <span>{CAT_LABELS[cat].split(" ")[0]}</span>
          <span style={{fontSize:11}}>{CAT_LABELS[cat].split(" ")[1]}{gevuld>0&&<span style={{marginLeft:3,background:"var(--g)",color:"#fff",borderRadius:8,padding:"0 5px",fontSize:10}}>{gevuld}</span>}</span>
        </button>;
      })}
    </div>
    {/* Vragen */}
    <div style={{display:"grid",gap:12}}>
      {BEZOEKER_VRAGEN[activeTab].map(({code,label,placeholder})=>{
        const rec=getRecord(activeTab,code);
        const key=`${activeTab}_${code}`;
        const isSaving=savingKey===key;
        const isSaved=savedKey===key;
        const isAiLoading=aiLoadingKey===key;
        return <div key={code}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <label style={{fontSize:12,fontWeight:600,color:"var(--g)"}}>{label}</label>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {isSaving&&<span style={{fontSize:11,color:"var(--txtl)"}}>⏳</span>}
              {isSaved&&!isSaving&&<span style={{fontSize:11,color:"#22a05b",fontWeight:600}}>✓ Opgeslagen</span>}
              {isAppAdmin&&<button onClick={()=>handleAI(activeTab,code,label)} disabled={isAiLoading} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:"1px solid #c0d8b0",background:isAiLoading?"#f0f7eb":"#fff",color:"var(--g)",cursor:isAiLoading?"wait":"pointer",fontWeight:500}}>
                {isAiLoading?"⏳ Bezig...":"✨ AI"}
              </button>}
            </div>
          </div>
          <textarea
            className="fi"
            rows={3}
            style={{fontSize:14,resize:"vertical"}}
            defaultValue={rec?.tekst||""}
            key={rec?.id||`${activeTab}_${code}_leeg`}
            placeholder={placeholder}
            onBlur={e=>handleBlur(activeTab,code,e.target.value,rec?.tekst||"")}
          />
        </div>;
      })}
    </div>
  </div>;
}

function DierDetail({dier,s,d,ds,soorten,setPhotoModal,park}){
  const canM=hasPerm(s.user,"voeding_beheren");
  const canAdvice=!!(s.user && (s.user.isAppAdmin || hasPerm(s.user,"voeding_beheren") || hasPerm(s.user,"taken_beheren") || hasPerm(s.user,"medicatie_beheren")));
  const soort_obj = soorten.find(x => x.id === dier.soort_id);
  const ras_obj = dier.ras_id ? (s.rassen||[]).find(r => r.id === dier.ras_id) : null;
  const displayName = `${((dier.geslacht === "vrouw" ? soort_obj?.naam_vrouw : soort_obj?.naam_man) || soort_obj?.naam || "Onbekend")} ${dier.name||dier.naam||""}`.trim();
  const leeftijd=dier.geboortedatum?(() => { const birth=new Date(dier.geboortedatum); const today=new Date(); return (today.getFullYear()-birth.getFullYear())+" jaar"; })():"";
  const dierAdviezen=(s.dierenadvies||[]).filter(ad=>ad.scope_type==="dier" && String(ad.dier_id)===String(dier.id) && ad.actief!==false).sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
  const [adviesOpen, setAdviesOpen] = useState(false);
  const [bezoekersOpen, setBezoekersOpen] = useState(false);
  const [showAdviesForm, setShowAdviesForm] = useState(false);
  const [verblijf, setVerblijf] = useState(undefined); // undefined=laden, null=geen, object=gevonden

  useEffect(()=>{
    db.getVerblijfVoorDier(dier.id).then(v=>setVerblijf(v||null)).catch(()=>setVerblijf(null));
  },[dier.id]);

  return <div>
    <button className="back-btn" onClick={()=>d({type:"BACK_SOORT"})}>{IC.back} Terug naar {soort_obj?.naam || "Dieren"}</button>
    
    {/* ═══ DATA SECTION ═══ */}
    <div style={{background:"#fff",border:"1px solid #ddd",borderRadius:8,padding:16,marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
        <div>
          <h2 className="pt" style={{marginBottom:2}}>{displayName}</h2>
          {dier.geslacht==="man"?"♂️":"♀️"}
        </div>
        {canM&&<div className="iab">
          <button onClick={()=>d({type:"MODAL",p:{type:"editAnimal",item:dier}})} style={{padding:6,fontSize:14}}>{IC.edit}</button>
          <button onClick={()=>toast.confirm(`${dier.naam||"Dit dier"} verwijderen?`,()=>{ds({type:"DEL_ANIMAL",p:dier.id});d({type:"BACK_SOORT"});})} style={{padding:6,fontSize:14}}>{IC.trash}</button>
        </div>}
      </div>

      {/* Basics */}
      <div style={{fontSize:13,color:"var(--g)",fontWeight:500,marginBottom:8}}>{soort_obj?.icon || "🐾"} {soort_obj?.naam || "Onbekend"}{ras_obj&&<span style={{fontWeight:400,color:"var(--txtl)"}}> · {ras_obj.ras_naam}</span>}</div>

      {/* Info grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13,color:"var(--txt)"}}>
        {dier.geboortedatum&&<div>📅 Geboren<br/><span style={{color:"var(--txtl)"}}>{new Date(dier.geboortedatum).toLocaleDateString("nl-NL")} ({leeftijd})</span></div>}
        {dier.geslacht==="man"&&dier.is_gecastreerd&&<div>✂️ Gecastreerd</div>}
        {dier.park_datum&&<div>🏠 Op park<br/><span style={{color:"var(--txtl)"}}>{new Date(dier.park_datum).toLocaleDateString("nl-NL")}</span></div>}
        {dier.oornummer&&<div>🏷️ Oornummer<br/><span style={{color:"var(--txtl)"}}>{dier.oornummer}</span></div>}
        {dier.chipnummer&&<div>💾 Chipnummer<br/><span style={{color:"var(--txtl)"}}>{dier.chipnummer}</span></div>}
        {dier.herkomst&&<div>🌱 Herkomst<br/><span style={{color:"var(--txtl)"}}>{dier.herkomst}</span></div>}
        {dier.gewicht!=null&&dier.gewicht!==""&&<div>⚖️ Gewicht<br/><span style={{color:"var(--txtl)"}}>{Number(dier.gewicht).toLocaleString("nl-NL",{minimumFractionDigits:0,maximumFractionDigits:3})} kg</span></div>}
        {verblijf?<div>🏠 Verblijf<br/><span style={{color:"var(--txtl)"}}>{verblijf.naam}</span></div>:dier.locatie?<div>📍 Locatie<br/><span style={{color:"var(--txtl)"}}>{dier.locatie}</span></div>:null}
        {dier.datum_overlijden&&<div>🕊️ Overleden<br/><span style={{color:"var(--red)"}}>{new Date(dier.datum_overlijden).toLocaleDateString("nl-NL")}</span></div>}
        {dier.datum_rendac&&<div>🚛 Rendac<br/><span style={{color:"var(--txtl)"}}>{new Date(dier.datum_rendac).toLocaleDateString("nl-NL")}</span></div>}
      </div>

      {dier.notitie&&<div style={{marginTop:10,padding:8,background:"var(--cd)",borderRadius:6,fontSize:13,color:"var(--txt)"}}>📝 {dier.notitie}</div>}
    </div>

    {/* ═══ ADVIEZEN + BEZOEKERSINFO ═══ */}
    {canAdvice&&<div style={{background:"#fff",border:"1px solid #ddd",borderRadius:8,overflow:"hidden",marginBottom:12}}>
      {/* Twee knoppen naast elkaar */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",borderBottom:adviesOpen||bezoekersOpen?"1px solid #eee":"none"}}>
        <button onClick={()=>{setAdviesOpen(v=>!v);setBezoekersOpen(false);}}
          style={{padding:"11px 14px",border:"none",borderRight:"1px solid #eee",background:"none",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",fontWeight:600,fontSize:13,color:adviesOpen?"var(--g)":"var(--txt)"}}>
          <span>💡 Adviezen{dierAdviezen.length>0&&<span style={{fontWeight:400,fontSize:11,color:"var(--txtl)",marginLeft:4}}>({dierAdviezen.length})</span>}</span>
          <span style={{fontSize:11,color:"var(--txtl)"}}>{adviesOpen?"▲":"▼"}</span>
        </button>
        <button onClick={()=>{setBezoekersOpen(v=>!v);setAdviesOpen(false);}}
          style={{padding:"11px 14px",border:"none",background:"none",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",fontWeight:600,fontSize:13,color:bezoekersOpen?"var(--g)":"var(--txt)"}}>
          <span>🌿 Bezoekersinfo</span>
          <span style={{fontSize:11,color:"var(--txtl)"}}>{bezoekersOpen?"▲":"▼"}</span>
        </button>
      </div>

      {/* Adviezen inhoud */}
      {adviesOpen&&<div style={{padding:12}}>
        {dierAdviezen.length>0?<div>
          {dierAdviezen.map(a=><div key={a.id} style={{background:"var(--cd)",borderRadius:6,padding:10,marginBottom:8,fontSize:13,borderLeft:"3px solid var(--g)"}}>
            <div style={{fontWeight:600,color:"var(--g)",marginBottom:4}}>{a.titel||"Advies"}</div>
            {a.advies&&<div style={{color:"var(--txt)",lineHeight:1.4,marginBottom:6}}>{a.advies}</div>}
            {a.created_at&&<div style={{fontSize:11,color:"var(--txtl)",marginBottom:4}}>📅 {new Date(a.created_at).toLocaleDateString("nl-NL")}</div>}
            <div style={{display:"flex",gap:4}}>
              <button onClick={()=>d({type:"MODAL",p:{type:"editAdvice",item:a}})} style={{fontSize:12,padding:"4px 8px",background:"var(--g)",color:"#fff",border:"none",borderRadius:4,cursor:"pointer"}}>{IC.edit}</button>
              <button onClick={()=>toast.confirm("Advies verwijderen?",()=>ds({type:"DEL_DIERENADVIES",p:a.id}))} style={{fontSize:12,padding:"4px 8px",background:"var(--red)",color:"#fff",border:"none",borderRadius:4,cursor:"pointer"}}>{IC.trash}</button>
            </div>
          </div>)}
        </div>:<div style={{color:"var(--txtl)",fontSize:13,marginBottom:10}}>Geen adviezen toegevoegd</div>}
        <button onClick={()=>setShowAdviesForm(true)} style={{width:"100%",padding:8,fontSize:12,background:"var(--g)",color:"#fff",border:"none",borderRadius:4,cursor:"pointer",fontWeight:600}}>+ Advies toevoegen</button>
      </div>}

      {/* Bezoekersinfo inhoud */}
      {bezoekersOpen&&<div style={{padding:12}}>
        <BezoekersInfoSection
          dierId={dier.id}
          soortId={dier.soort_id}
          rasId={dier.ras_id||null}
          parkId={park?.id}
          soort_obj={soort_obj}
          dierNaam={dier.naam||dier.name}
          isAppAdmin={!!s.user?.isAppAdmin}
        />
      </div>}
    </div>}

    {/* ═══ FOTO'S SECTION ═══ */}
    {dier.fotos?.length>0&&<>
      <div className="sh"><span className="st">Foto's ({dier.fotos.length})</span></div>
      <div className="photo-grid">{dier.fotos.map((f,i)=>
        <div key={i} className="photo-wrap" onClick={()=>setPhotoModal({photos:dier.fotos,dierName:dier.name,initialIndex:i,showNav:true})} style={{cursor:"pointer"}}>
          <img src={f} alt={dier.name+' '+(i+1)} style={{opacity:0.8,transition:"opacity .2s"}} onMouseOver={(e)=>e.target.style.opacity="1"} onMouseOut={(e)=>e.target.style.opacity="0.8"}/>
          {canM&&<button className="photo-del" onClick={(e)=>{e.stopPropagation();toast.confirm("Foto verwijderen?",()=>ds({type:"DEL_PHOTO",p:{animalId:dier.id,idx:i}}));}} >×</button>}
        </div>
      )}</div>
    </>}
    {canM&&<PhotoUpload animalId={dier.id} d={ds}/>}
    
    {showAdviesForm&&<DierenadviesForm dier_id={dier.id} onClose={()=>setShowAdviesForm(false)} onSave={f=>ds({type:"ADD_DIERENADVIES",p:{...f,scope_type:"dier",dier_id:dier.id,soort_id:dier.soort_id||null}})}/>}
    
    {s.modal==="editAnimal"&&s.editItem?.id===dier.id&&<AnimalForm parkId={park?.id} item={s.editItem} onClose={()=>d({type:"CLOSE"})} onSave={f=>{ds({type:"EDIT_ANIMAL",p:{...f,id:s.editItem.id,fotos:s.editItem.fotos}});d({type:"CLOSE"});}}/>}
  </div>;
}

function SoortDetail({soort,s,d,ds,soorten,setPhotoModal,park}){
  // Convert soort name to soort_id
  const soort_obj = soorten.find(st => st.naam === soort);
  const soort_id = soort_obj?.id;
  const dieren=s.animals.filter(a=>a.soort_id===soort_id&&a.actief!==false&&!a.datum_overlijden);
  const canM=hasPerm(s.user,"voeding_beheren");
  const canAdvice=!!(s.user && (s.user.isAppAdmin || hasPerm(s.user,"voeding_beheren") || hasPerm(s.user,"taken_beheren") || hasPerm(s.user,"medicatie_beheren")));
  const soortAdviezen=(s.dierenadvies||[]).filter(ad=>ad.scope_type==="soort" && String(ad.soort_id)===String(soort_id) && ad.actief!==false);
  
  // Dutch animal gender names
  const geslachtNamen={
    "Kip":{vrouw:"Kip",man:"Haan"},
    "Geit":{vrouw:"Geit",man:"Bok"},
    "Schaap":{vrouw:"Ooi",man:"Ram"},
    "Konijn":{vrouw:"Konijn (vrouw)",man:"Konijn (man)"},
    "Paard":{vrouw:"Merrie",man:"Hengst"},
    "Ezel":{vrouw:"Ezelin",man:"Ezel"},
    "Koe":{vrouw:"Koe",man:"Stier"},
    "Varken":{vrouw:"Zeug",man:"Beer"},
    "Hert":{vrouw:"Hinde",man:"Hert"},
  };
  
  const getGeslachtLabel=(animalSoort,geslacht)=>{
    const namen=geslachtNamen[animalSoort];
    if(!namen) return geslacht==="vrouw"?"Vrouw":"Man";
    return geslacht==="vrouw"?namen.vrouw:namen.man;
  };
  
  const getDisplayName=(animal)=>{
    const animalSoort = soorten.find(st => st.id === animal.soort_id);
    const label = animal.geslacht === "vrouw" ? animalSoort?.naam_vrouw : animalSoort?.naam_man;
    return (label || animalSoort?.naam || "Onbekend") + " " + animal.name;
  };
  
  const [adviesOpen,setAdviesOpen]=useState(false);
  const [bezoekersOpen,setBezoekersOpen]=useState(false);
  const [showAdviesForm,setShowAdviesForm]=useState(false);

  if(s.viewDier){
    console.log("🔍 viewDier:", s.viewDier, "type:", typeof s.viewDier, "animals:", s.animals?.length);
    const dier=s.animals.find(a=>a.id===Number(s.viewDier));
    console.log("🦁 dier found:", dier?.name, "id:", dier?.id);
    if(dier) return <DierDetail dier={dier} s={s} d={d} ds={ds} soorten={soorten} setPhotoModal={setPhotoModal} park={park}/>;
  }
  return <div>
    <button className="back-btn" onClick={()=>d({type:"BACK_HOME"})}>{IC.back} Alle diersoorten</button>
    <h2 className="pt" style={{display:"flex",alignItems:"center",gap:8}}>{soort_obj?.icon || "🐾"} {soort_obj?.naam || "Onbekend"} <span style={{fontSize:16,fontWeight:400,color:"var(--txtl)"}}>({dieren.length})</span></h2>
    {canM&&<button className="fab" onClick={()=>d({type:"MODAL",p:{type:"addAnimal"}})}>{IC.plus}</button>}
    {canAdvice&&<div style={{background:"#fff",border:"1px solid #ddd",borderRadius:8,overflow:"hidden",marginBottom:12}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",borderBottom:adviesOpen||bezoekersOpen?"1px solid #eee":"none"}}>
        <button onClick={()=>{setAdviesOpen(v=>!v);setBezoekersOpen(false);}}
          style={{padding:"11px 14px",border:"none",borderRight:"1px solid #eee",background:"none",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",fontWeight:600,fontSize:13,color:adviesOpen?"var(--g)":"var(--txt)"}}>
          <span>💡 Adviezen{soortAdviezen.length>0&&<span style={{fontWeight:400,fontSize:11,color:"var(--txtl)",marginLeft:4}}>({soortAdviezen.length})</span>}</span>
          <span style={{fontSize:11,color:"var(--txtl)"}}>{adviesOpen?"▲":"▼"}</span>
        </button>
        <button onClick={()=>{setBezoekersOpen(v=>!v);setAdviesOpen(false);}}
          style={{padding:"11px 14px",border:"none",background:"none",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",fontWeight:600,fontSize:13,color:bezoekersOpen?"var(--g)":"var(--txt)"}}>
          <span>🌿 Bezoekersinfo</span>
          <span style={{fontSize:11,color:"var(--txtl)"}}>{bezoekersOpen?"▲":"▼"}</span>
        </button>
      </div>
      {adviesOpen&&<div style={{padding:12}}>
        {soortAdviezen.length>0?<div>
          {soortAdviezen.map(a=><div key={a.id} style={{background:"var(--cd)",borderRadius:6,padding:10,marginBottom:8,fontSize:13,borderLeft:"3px solid var(--g)"}}>
            <div style={{fontWeight:600,color:"var(--g)",marginBottom:4}}>{a.titel||"Advies"}</div>
            {a.advies&&<div style={{color:"var(--txt)",lineHeight:1.4,marginBottom:6}}>{a.advies}</div>}
            {a.created_at&&<div style={{fontSize:11,color:"var(--txtl)",marginBottom:4}}>📅 {new Date(a.created_at).toLocaleDateString("nl-NL")}</div>}
            <div style={{display:"flex",gap:4}}>
              <button onClick={()=>d({type:"MODAL",p:{type:"editAdvice",item:a}})} style={{fontSize:12,padding:"4px 8px",background:"var(--g)",color:"#fff",border:"none",borderRadius:4,cursor:"pointer"}}>{IC.edit}</button>
              <button onClick={()=>toast.confirm("Advies verwijderen?",()=>ds({type:"DEL_DIERENADVIES",p:a.id}))} style={{fontSize:12,padding:"4px 8px",background:"var(--red)",color:"#fff",border:"none",borderRadius:4,cursor:"pointer"}}>{IC.trash}</button>
            </div>
          </div>)}
        </div>:<div style={{color:"var(--txtl)",fontSize:13,marginBottom:10}}>Geen adviezen toegevoegd</div>}
        <button onClick={()=>setShowAdviesForm(true)} style={{width:"100%",padding:8,fontSize:12,background:"var(--g)",color:"#fff",border:"none",borderRadius:4,cursor:"pointer",fontWeight:600}}>+ Advies toevoegen</button>
      </div>}
      {bezoekersOpen&&<div style={{padding:12}}>
        <BezoekersInfoSection
          soortId={soort_id}
          parkId={park?.id}
          soort_obj={soort_obj}
          isAppAdmin={!!s.user?.isAppAdmin}
        />
      </div>}
    </div>}
    {showAdviesForm&&<DierenadviesForm dier_id={null} onClose={()=>setShowAdviesForm(false)} onSave={f=>ds({type:"ADD_DIERENADVIES",p:{...f,scope_type:"soort",soort_id:soort_id,dier_id:null}})}/>}
    {dieren.map(a=>{
      const ras_obj = a.ras_id ? (s.rassen||[]).find(r => r.id === a.ras_id) : null;
      return <button key={a.id} onClick={()=>d({type:"VIEW_DIER",p:a.id})} style={{background:"#fff",border:"1px solid #ddd",borderRadius:8,padding:12,margin:8,cursor:"pointer",textAlign:"left",width:"calc(100% - 16px)",display:"flex",gap:12,alignItems:"flex-start",position:"relative"}}>
        {a.fotos?.length>0?<img src={a.fotos[0]} alt={a.name} style={{width:60,height:60,borderRadius:8,objectFit:"cover",flexShrink:0}}/>:<div style={{width:60,height:60,borderRadius:8,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>{soort_obj?.icon || "🐾"}</div>}
        <div style={{flex:1}}>
          <div style={{fontWeight:600}}>{getDisplayName(a)} {a.geslacht==="man"?"♂️":"♀️"}</div>
          {ras_obj&&<div style={{fontSize:12,color:"var(--g)",fontWeight:500,marginBottom:2}}>🏷️ {ras_obj.ras_naam}</div>}
          <div style={{fontSize:13,color:"var(--txtl)"}}>{a.locatie}{a.geboortedatum ? ` • 📅 ${new Date(a.geboortedatum).toLocaleDateString("nl-NL")}` : ""}</div>
        </div>
        <button 
          onClick={(e)=>{e.stopPropagation(); d({type:"VIEW_GEZONDHEID_DIER",p:a.id});}}
          style={{position:"absolute",top:8,right:8,background:"none",border:"none",cursor:"pointer",fontSize:18,padding:4,borderRadius:4,hover:{background:"rgba(0,0,0,0.05)"}}}
          title="Gezondheidsrecord">
          🩺
        </button>
      </button>;
    })}
    {(s.modal==="addAnimal"||s.modal==="editAnimal")&&<AnimalForm parkId={park?.id} item={s.editItem} defaultSoort={soort} onClose={()=>d({type:"CLOSE"})} onSave={f=>{console.log("🐑 AnimalForm onSave called with:", f); const action = s.editItem?"EDIT_ANIMAL":"ADD_ANIMAL"; console.log("📣 Dispatching action:", action); ds({type:action,p:s.editItem?{...f,id:s.editItem.id,fotos:s.editItem.fotos}:{...f,soort:f.soort||soort}});}}/>}
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   HEALTH RECORDS (GEZONDHEID) COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function GezondheidForm({item, dieren, medicatie, vets=[], verzorgers=[], soorten=[], onSave, onClose, defaultMode}){
  const [f, setF] = useState(item ? {
    ...item,
    veterinarian_id: item.veterinarian_id ?? "",
    verzorger_id: item.verzorger_id ?? "",
    dier_id: item.dier_id ?? "",
    soort_id: item.soort_id ?? "",
    beschrijving: item.beschrijving ?? "",
  } : {
    dier_id: "",
    soort_id: "",
    datum: new Date().toISOString().split("T")[0],
    diagnoses: "",
    beschrijving: "",
    status: "blijft gelijk",
    veterinarian_id: "",
    verzorger_id: ""
  });
  
  const normalizeMeds = (item) => {
    const src = item?.medicatie || item?.gezondheid_medicatie || [];
    return src.map(m => ({
      medicatie_id: m.medicatie_id || m.id || "",
      dosering: m.dosering || "",
      freq: m.freq || "",
      eind: m.eind || "",
    }));
  };
  const [meds, setMeds] = useState(() => normalizeMeds(item));
  const [selectMode, setSelectMode] = useState(item?.soort_id && !item?.dier_id ? "soort" : defaultMode || "dier"); // "dier" or "soort"
  
  const u = (k, v) => setF(p => ({...p, [k]: v}));
  
  const addMed = () => {
    setMeds([...meds, {medicatie_id: "", dosering: "", freq: "", eind: ""}]);
  };
  
  const removeMed = (idx) => {
    setMeds(meds.filter((_, i) => i !== idx));
  };
  
  const updateMed = (idx, k, v) => {
    const newMeds = [...meds];
    newMeds[idx] = {...newMeds[idx], [k]: v};
    setMeds(newMeds);
  };

  const handleModeSwitch = (mode) => {
    setSelectMode(mode);
    if(mode === "dier") u("soort_id", "");
    else u("dier_id", "");
  };

  // Filter dieren by selected soort when in soort mode
  const filteredDieren = selectMode === "soort" && f.soort_id 
    ? dieren.filter(d => d.soort_id === Number(f.soort_id))
    : dieren;
  
  const handleSave = async () => {
    if (selectMode === "dier" && !f.dier_id) {
      toast.warn("Kies een dier!");
      return;
    }
    if (selectMode === "soort" && !f.soort_id) {
      toast.warn("Kies een soort!");
      return;
    }
    const saveData = {...f, medicatie: meds};
    if (selectMode === "soort") {
      saveData.dier_id = null;
      saveData.soort_id = Number(f.soort_id);
    } else {
      saveData.soort_id = null;
    }
    onSave(saveData);
  };

  // Count animals in selected soort
  const soortCount = f.soort_id ? dieren.filter(d => d.soort_id === Number(f.soort_id)).length : 0;

  return <Modal title={item ? "Gezondheidsrecord bewerken" : "Nieuw gezondheidsrecord"} onClose={onClose}>
    {/* Mode toggle: individual vs group */}
    {!item && <div style={{display:"flex",gap:4,marginBottom:12,background:"var(--cd)",borderRadius:8,padding:3}}>
      <button onClick={()=>handleModeSwitch("dier")} style={{flex:1,padding:"8px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
        background:selectMode==="dier"?"var(--g)":"transparent",color:selectMode==="dier"?"#fff":"var(--txtl)"}}>
        🐾 Individueel dier
      </button>
      <button onClick={()=>handleModeSwitch("soort")} style={{flex:1,padding:"8px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
        background:selectMode==="soort"?"var(--g)":"transparent",color:selectMode==="soort"?"#fff":"var(--txtl)"}}>
        🏷️ Hele soort / groep
      </button>
    </div>}

    {selectMode === "dier" ? (
      <div className="fg">
        <label className="fl">Dier</label>
        <select className="fi" value={f.dier_id} onChange={e => u("dier_id", e.target.value)} disabled={!!item}>
          <option value="">— Kies dier —</option>
          {soorten.filter(so => dieren.some(d => d.soort_id === so.id)).map(so => (
            <optgroup key={so.id} label={`${so.icon} ${so.naam}`}>
              {dieren.filter(d => d.soort_id === so.id).map(d => <option key={d.id} value={d.id}>{d.naam}</option>)}
            </optgroup>
          ))}
          {dieren.filter(d => !soorten.some(so => so.id === d.soort_id)).length > 0 && (
            <optgroup label="🐾 Overig">
              {dieren.filter(d => !soorten.some(so => so.id === d.soort_id)).map(d => <option key={d.id} value={d.id}>{d.naam}</option>)}
            </optgroup>
          )}
        </select>
      </div>
    ) : (
      <div className="fg">
        <label className="fl">Soort / groep</label>
        <select className="fi" value={f.soort_id} onChange={e => u("soort_id", e.target.value)} disabled={!!item}>
          <option value="">— Kies soort —</option>
          {soorten.filter(so => dieren.some(d => d.soort_id === so.id)).map(so => {
            const count = dieren.filter(d => d.soort_id === so.id).length;
            return <option key={so.id} value={so.id}>{so.icon} {so.naam} ({count} dieren)</option>;
          })}
        </select>
        {f.soort_id && soortCount > 0 && (
          <div style={{marginTop:6,padding:"6px 10px",borderRadius:8,background:"var(--gp)",fontSize:12,color:"var(--g)"}}>
            🏷️ Dit record geldt voor alle <strong>{soortCount} {soorten.find(so=>so.id===Number(f.soort_id))?.naam || "dieren"}</strong>
          </div>
        )}
      </div>
    )}

    <div className="fg">
      <label className="fl">Datum</label>
      <input className="fi" type="date" value={f.datum} onChange={e => u("datum", e.target.value)} />
    </div>

    <div className="fg">
      <label className="fl">Diagnose</label>
      <input className="fi" value={f.diagnoses} onChange={e => u("diagnoses", e.target.value)} placeholder="Bijv. Hoefontsteking" />
    </div>

    <div className="fg">
      <label className="fl">Beschrijving</label>
      <textarea className="fi" rows={3} value={f.beschrijving} onChange={e => u("beschrijving", e.target.value)} placeholder="Gedetailleerde beschrijving..." />
    </div>

    <div className="fg">
      <label className="fl">Status</label>
      <select className="fi" value={f.status} onChange={e => u("status", e.target.value)}>
        <option value="gaat beter">✨ Gaat beter</option>
        <option value="is verslechterd">⚠️ Is verslechterd</option>
        <option value="blijft gelijk">➡️ Blijft gelijk</option>
        <option value="alert op blijven">👁️ Alert op blijven</option>
        <option value="genezen">✅ Genezen</option>
      </select>
    </div>

    <div className="fg">
      <label className="fl">Dierenarts (optioneel)</label>
      <select className="fi" value={f.veterinarian_id} onChange={e => u("veterinarian_id", e.target.value)}>
        <option value="">— Geen dierenarts —</option>
        {vets.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
      </select>
    </div>

    <div className="fg">
      <label className="fl">Verzorger (optioneel)</label>
      <select className="fi" value={f.verzorger_id||""} onChange={e => u("verzorger_id", e.target.value)}>
        <option value="">— Geen verzorger —</option>
        {verzorgers.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
      </select>
    </div>

    <div className="fg">
      <label className="fl">Medicijnen (optioneel)</label>
      <div style={{border: "1px solid var(--brd)", borderRadius: 8, padding: 12, background: "#f9f9f9"}}>
        {meds.length === 0 ? (
          <EmptyState icon="💊" title="Geen medicijnen" sub="Er zijn geen medicijnen geregistreerd voor dit dier."/>
        ) : (
          meds.map((med, idx) => (
            <div key={idx} style={{marginBottom: 16, padding: 12, background: "#fff", borderRadius: 6, border: "1px solid var(--brd)"}}>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8}}>
                <div style={{fontWeight: 600, fontSize: 12}}>Medicijn {idx + 1}</div>
                <button 
                  onClick={() => removeMed(idx)} 
                  style={{background: "var(--red)", color: "#fff", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer", fontSize: 12}}
                >
                  ❌ Verwijderen
                </button>
              </div>

              <div style={{marginBottom: 8}}>
                <label style={{fontSize: 11, fontWeight: 600, color: "var(--g)", display: "block", marginBottom: 4}}>Medicatie</label>
                <select 
                  className="fi" 
                  value={med.medicatie_id} 
                  onChange={e => updateMed(idx, "medicatie_id", e.target.value)}
                  style={{width: "100%"}}
                >
                  <option value="">— Kies medicatie —</option>
                  {medicatie.map(m => <option key={m.id} value={m.id}>{m.medicijn}</option>)}
                </select>
              </div>

              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8}}>
                <div>
                  <label style={{fontSize: 11, fontWeight: 600, color: "var(--g)", display: "block", marginBottom: 4}}>Dosering</label>
                  <input 
                    className="fi" 
                    value={med.dosering} 
                    onChange={e => updateMed(idx, "dosering", e.target.value)}
                    placeholder="Bijv. 10ml"
                    style={{width: "100%"}}
                  />
                </div>
                <div>
                  <label style={{fontSize: 11, fontWeight: 600, color: "var(--g)", display: "block", marginBottom: 4}}>Frequentie</label>
                  <input 
                    className="fi" 
                    value={med.freq} 
                    onChange={e => updateMed(idx, "freq", e.target.value)}
                    placeholder="Bijv. 2x dagelijks"
                    style={{width: "100%"}}
                  />
                </div>
              </div>

              <div>
                <label style={{fontSize: 11, fontWeight: 600, color: "var(--g)", display: "block", marginBottom: 4}}>Eind datum (optioneel)</label>
                <input 
                  className="fi" 
                  type="date" 
                  value={med.eind || ""}
                  onChange={e => updateMed(idx, "eind", e.target.value)}
                  style={{width: "100%"}}
                />
              </div>
            </div>
          ))
        )}

        <button 
          onClick={addMed}
          style={{width: "100%", padding: "8px", background: "var(--g)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, marginTop: 8}}
        >
          ➕ Medicijn toevoegen
        </button>
      </div>
    </div>

    <div style={{display: "flex", gap: 8}}>
      <button className="fbtn" onClick={handleSave}>💾 Opslaan</button>
      <button className="fbtn" style={{background: "#ccc"}} onClick={onClose}>Annuleren</button>
    </div>
  </Modal>;
}

function MedicatieGegeven({med, gebruikerId}){
  const [gegeven, setGegeven] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toonAlles, setToonAlles] = useState(false);

  const getDagdeel = () => { const h=new Date().getHours(); return h<12?"ochtend":h<18?"middag":"avond"; };
  const today = new Date().toISOString().split("T")[0];

  useEffect(()=>{
    db.getMedicatieGegeven(med.id).then(data=>{ setGegeven(data); setLoading(false); });
  },[med.id]);

  const eigenVandaag = gegeven.find(g =>
    g.gebruiker_id === gebruikerId &&
    g.gegeven_op.split("T")[0] === today
  );
  const isChecked = !!eigenVandaag;

  const toggle = async () => {
    if(saving) return;
    setSaving(true);
    if(isChecked){
      await db.deleteMedicatieGegeven(eigenVandaag.id);
      setGegeven(prev => prev.filter(g => g.id !== eigenVandaag.id));
    } else {
      const nieuw = await db.addMedicatieGegeven(med.id, gebruikerId);
      if(nieuw) setGegeven(prev => [nieuw, ...prev]);
    }
    setSaving(false);
  };

  const dagdIcon = {ochtend:"🌅", middag:"☀️", avond:"🌙"};
  const zichtbaar = toonAlles ? gegeven : gegeven.slice(0, 5);
  const heeftMeer = gegeven.length > 5;

  // Groepeer per dag voor overzichtelijke weergave
  const groepenPerDag = zichtbaar.reduce((acc, g) => {
    const dag = g.gegeven_op.split("T")[0];
    if(!acc[dag]) acc[dag] = [];
    acc[dag].push(g);
    return acc;
  }, {});

  return <div style={{marginTop:8, padding:"10px 12px", background:"#f8fbff", borderRadius:10, border:"1px solid #d0e8ff"}}>
    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6}}>
      <div>
        <div style={{fontWeight:600, fontSize:13}}>{med.medicatie?.medicijn || "Medicijn"}</div>
        <div style={{fontSize:11, color:"var(--txtl)", marginTop:1}}>
          {med.dosering && <span>📏 {med.dosering} · </span>}
          {med.freq && <span>⏰ {med.freq}</span>}
          {med.eind && <span> · 📅 t/m {new Date(med.eind).toLocaleDateString("nl-NL")}</span>}
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        style={{
          width:36, height:36, borderRadius:10, border:"2px solid",
          borderColor: isChecked?"var(--g)":"var(--brd)",
          background: isChecked?"var(--g)":"#fff",
          color: isChecked?"#fff":"var(--txtl)",
          fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          flexShrink:0, transition:"all .15s"
        }}>
        {saving?"⏳": isChecked?"✓":""}
      </button>
    </div>

    {!loading && gegeven.length > 0 && <div style={{borderTop:"1px solid #e0eeff", paddingTop:8, marginTop:4}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
        <div style={{fontSize:10, fontWeight:600, color:"var(--txtl)", textTransform:"uppercase", letterSpacing:0.5}}>
          Historie ({gegeven.length} toedieningen)
        </div>
        {heeftMeer&&<button onClick={()=>setToonAlles(v=>!v)}
          style={{fontSize:11,color:"#1a6fa8",background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600}}>
          {toonAlles?"Minder ▲":"Alles tonen ▼"}
        </button>}
      </div>
      <div style={{display:"flex", flexDirection:"column", gap:6}}>
        {Object.entries(groepenPerDag).map(([dag,records])=>{
          const datum = new Date(dag);
          const isVandaag = dag === today;
          const isGisteren = dag === new Date(Date.now()-86400000).toISOString().split("T")[0];
          const dagLabel = isVandaag?"Vandaag":isGisteren?"Gisteren":datum.toLocaleDateString("nl-NL",{weekday:"short",day:"numeric",month:"short"});
          return <div key={dag}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--txtl)",marginBottom:3,textTransform:"uppercase"}}>{dagLabel}</div>
            {records.map(g=>{
              const isEigen = g.gebruiker_id === gebruikerId;
              return <div key={g.id} style={{display:"flex", alignItems:"center", gap:6, fontSize:12,
                padding:"4px 8px", background: isEigen?"#edf7ed":"#fff", borderRadius:6, marginBottom:2,
                border:`1px solid ${isEigen?"#c3e6cb":"#e8e8e8"}`}}>
                <span>{dagdIcon[g.dagdeel]||"⏰"}</span>
                <span style={{color:"var(--txtl)", fontSize:11}}>
                  {new Date(g.gegeven_op).toLocaleTimeString("nl-NL",{hour:"2-digit",minute:"2-digit"})}
                </span>
                <span style={{fontWeight:600, color: isEigen?"var(--g)":"var(--txt)", flex:1}}>
                  {g.gebruikers?.roepnaam || g.gebruikers?.naam || "?"}
                </span>
                {isEigen && isVandaag && <button onClick={e=>{e.stopPropagation();toast.confirm("Medicatieregistratie verwijderen?",async()=>{await db.deleteMedicatieGegeven(g.id);setGegeven(prev=>prev.filter(x=>x.id!==g.id));});}}
                  style={{background:"none",border:"none",cursor:"pointer",color:"var(--red)",fontSize:14,padding:0,lineHeight:1}}>×</button>}
              </div>;
            })}
          </div>;
        })}
      </div>
    </div>}
    {!loading && gegeven.length === 0 && <div style={{fontSize:11, color:"var(--txtl)", marginTop:4}}>Nog niet toegediend.</div>}
  </div>;
}

function GezondheidDetail({record, dieren, soorten=[], contacten=[], onClose, d, ds, canEdit=false, gebruikerId}){
  const dier = dieren.find(d => d.id === record.dier_id);
  const soort = record.soort_id ? soorten.find(s => s.id === record.soort_id) : null;
  const isGroup = !!record.soort_id && !record.dier_id;
  const soortAnimals = isGroup ? dieren.filter(d => d.soort_id === record.soort_id) : [];
  const vet = record.veterinarian_id ? contacten.find(c => c.id === record.veterinarian_id) : null;
  const verzorger = record.verzorger_id ? contacten.find(c => c.id === record.verzorger_id) : null;
  const statusEmoji = {
    "gaat beter": "✨",
    "is verslechterd": "⚠️",
    "alert op blijven": "👁️",
    "blijft gelijk": "➡️",
    "genezen": "✅"
  };
  
  const titleText = isGroup 
    ? `Groepsrecord - ${soort?.icon||"🏷️"} ${soort?.naam || "Groep"}`
    : `Gezondheidsrecord - ${dier?.naam}`;

  return <Modal title={titleText} onClose={onClose}>
    {isGroup && (
      <div style={{padding:"8px 12px",borderRadius:8,background:"#f0f0ff",border:"1px solid #7c3aed30",marginBottom:12,fontSize:12}}>
        <strong style={{color:"#7c3aed"}}>🏷️ Groepsrecord</strong>
        <span style={{color:"var(--txtl)",marginLeft:6}}>Geldt voor {soortAnimals.length} {soort?.naam || "dieren"}</span>
        {soortAnimals.length>0 && soortAnimals.length<=8 && (
          <div style={{marginTop:6,display:"flex",flexWrap:"wrap",gap:4}}>
            {soortAnimals.map(a=><span key={a.id} style={{padding:"2px 8px",background:"#fff",borderRadius:12,fontSize:11,border:"1px solid var(--brd)"}}>{a.naam}</span>)}
          </div>
        )}
      </div>
    )}
    <div style={{marginBottom: 16}}>
      <div style={{fontSize: 12, color: "var(--txtl)", marginBottom: 4}}>
        {new Date(record.datum).toLocaleDateString("nl-NL")}
      </div>
      <div style={{fontSize: 14, fontWeight: 600, marginBottom: 8}}>
        🩺 {record.diagnoses}
      </div>
      <div style={{fontSize: 12, color: "var(--g)", marginBottom: 12}}>
        {statusEmoji[record.status]} <strong>{record.status}</strong>
      </div>
    </div>

    {record.beschrijving && (
      <div style={{background: "var(--cd)", padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 12, lineHeight: 1.6}}>
        {record.beschrijving}
      </div>
    )}

    {record.gezondheid_medicatie && record.gezondheid_medicatie.length > 0 && (
      <div style={{marginBottom: 12}}>
        <div style={{fontWeight: 600, marginBottom: 8, fontSize: 12, color: "var(--g)"}}>💊 Medicijnen</div>
        {record.gezondheid_medicatie.map((med) => (
          <MedicatieGegeven key={med.id} med={med} gebruikerId={gebruikerId}/>
        ))}
      </div>
    )}

    {record.dieren && (
      <div style={{fontSize: 12, marginBottom: 8}}>
        <strong>Dier:</strong> {record.dieren.naam} ({record.dieren.soort})
      </div>
    )}

    {vet && (
      <div style={{fontSize: 12, marginBottom: 8}}>
        <strong>🩺 Dierenarts:</strong> {vet.naam}
        {vet.tel && <div style={{color: "var(--txtl)"}}>📞 {vet.tel}</div>}
      </div>
    )}

    {verzorger && (
      <div style={{fontSize: 12, marginBottom: 8}}>
        <strong>🐾 Verzorger:</strong> {verzorger.naam}
        {verzorger.tel && <div style={{color: "var(--txtl)"}}>📞 {verzorger.tel}</div>}
      </div>
    )}

    <div style={{marginTop: 16, display: "flex", gap: 8}}>
      {canEdit && <button className="fbtn" style={{flex: 1}} onClick={() => d({type: "MODAL", p: {type: "editGezondheid", item: record}})}>
        ✏️ Bewerken
      </button>}
      {canEdit && <button className="fbtn" style={{flex: 1, background: "#fff0f0", color: "var(--red)", border: "1px solid var(--red)"}} onClick={() => {
        toast.confirm("Weet je zeker dat je dit record wilt verwijderen?", () => {
          ds({type: "DEL_GEZONDHEID", p: record.id});
          onClose();
        });
      }}>
        🗑️ Verwijderen
      </button>}
    </div>
  </Modal>;
}

/* ═══════════════════════════════════════════════════════════
   PROTOCOLLEN
   ═══════════════════════════════════════════════════════════ */

function ProtocolKaart({protocol}){
  const [open,setOpen]=useState(false);
  const p=protocol;
  const einddatum=p.eind_datum?new Date(p.eind_datum).toLocaleDateString("nl-NL"):"doorlopend";
  return <div style={{padding:"10px 14px",borderRadius:12,marginBottom:8,background:"#f0f7ff",borderLeft:"4px solid #2e6da4",cursor:"pointer"}}
    onClick={()=>setOpen(!open)}>
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      <span style={{fontSize:18,flexShrink:0}}>📄</span>
      <div style={{flex:1}}>
        <div style={{fontSize:14,fontWeight:600,color:"#2e6da4"}}>{p.titel}</div>
        <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>Sinds {new Date(p.start_datum).toLocaleDateString("nl-NL")} · {einddatum}</div>
      </div>
      <span style={{fontSize:12,color:"var(--txtl)",transition:"transform .2s",transform:open?"rotate(180deg)":"rotate(0)"}}>▼</span>
    </div>
    {open&&<div style={{marginTop:10,padding:"10px 12px",background:"#fff",borderRadius:8,fontSize:13,color:"var(--txt)",lineHeight:1.6,whiteSpace:"pre-wrap",maxHeight:400,overflowY:"auto"}}
      onClick={e=>e.stopPropagation()}>{p.inhoud}</div>}
  </div>;
}

function ProtocollenPage({s,d,ds}){
  const canEdit=s.user&&(s.user.isAppAdmin||s.user.type==="beheerder");
  const [showForm,setShowForm]=useState(false);
  const [editItem,setEditItem]=useState(null);
  const [f,setF]=useState({titel:"",inhoud:"",start_datum:new Date().toISOString().split("T")[0],eind_datum:"",actief:true});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const vandaag=new Date().toISOString().split("T")[0];

  const openEdit=(p)=>{
    setEditItem(p);
    setF({titel:p.titel,inhoud:p.inhoud,start_datum:p.start_datum||vandaag,eind_datum:p.eind_datum||"",actief:p.actief!==false});
    setShowForm(true);
  };
  const openNew=()=>{
    setEditItem(null);
    setF({titel:"",inhoud:"",start_datum:vandaag,eind_datum:"",actief:true});
    setShowForm(true);
  };
  const save=()=>{
    if(!f.titel.trim()||!f.inhoud.trim()){toast.warn("Titel en inhoud zijn verplicht");return;}
    if(editItem){
      ds({type:"EDIT_PROTOCOL",p:{id:editItem.id,titel:f.titel,inhoud:f.inhoud,start_datum:f.start_datum||null,eind_datum:f.eind_datum||null,actief:f.actief}});
    } else {
      ds({type:"ADD_PROTOCOL",p:{titel:f.titel,inhoud:f.inhoud,start_datum:f.start_datum||null,eind_datum:f.eind_datum||null,actief:f.actief}});
    }
    setShowForm(false);setEditItem(null);
  };

  const isActief=(p)=>p.actief&&p.start_datum<=vandaag&&(!p.eind_datum||p.eind_datum>=vandaag);
  const actieve=(s.protocollen||[]).filter(isActief);
  const inactieve=(s.protocollen||[]).filter(p=>!isActief(p));

  return <div className="pg">
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
      <h1 className="pt">📄 Protocollen</h1>
    </div>

    {showForm&&<Modal title={editItem?"Protocol bewerken":"Nieuw protocol"} onClose={()=>{setShowForm(false);setEditItem(null);}}>
      <div className="fg"><label className="fl">Titel *</label><input className="fi" value={f.titel} onChange={e=>u("titel",e.target.value)} placeholder="Bijv. Vogelgriep protocol"/></div>
      <div className="fg"><label className="fl">Inhoud *</label><textarea className="fi" rows={10} value={f.inhoud} onChange={e=>u("inhoud",e.target.value)} placeholder="Volledige protocoltekst..." style={{fontFamily:"inherit",lineHeight:1.6}}/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">Startdatum</label><input className="fi" type="date" value={f.start_datum} onChange={e=>u("start_datum",e.target.value)}/></div>
        <div className="fg"><label className="fl">Einddatum <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><input className="fi" type="date" value={f.eind_datum} onChange={e=>u("eind_datum",e.target.value)}/></div>
      </div>
      <div className="fg"><label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
        <input type="checkbox" checked={f.actief} onChange={e=>u("actief",e.target.checked)}/> Protocol actief
      </label></div>
      <button className="fbtn" onClick={save}>Opslaan</button>
    </Modal>}

    {actieve.length>0&&<>
      <div className="sh"><span className="st" style={{color:"#16a34a"}}>🟢 Actief ({actieve.length})</span></div>
      {actieve.map(p=><div key={p.id} className="card" style={{marginBottom:8,borderLeft:"4px solid #16a34a"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:600,color:"var(--g)"}}>{p.titel}</div>
            <div style={{fontSize:12,color:"var(--txtl)",marginTop:2}}>Sinds {new Date(p.start_datum).toLocaleDateString("nl-NL")}{p.eind_datum?" · Tot "+new Date(p.eind_datum).toLocaleDateString("nl-NL"):" · Doorlopend"}</div>
          </div>
          {canEdit&&<div className="iab">
            <button onClick={()=>openEdit(p)}>{IC.edit}</button>
            <button onClick={()=>toast.confirm("Protocol verwijderen?",()=>ds({type:"DEL_PROTOCOL",p:p.id}))}>{IC.trash}</button>
          </div>}
        </div>
        <ProtocolInhoudPreview inhoud={p.inhoud}/>
      </div>)}
    </>}

    {inactieve.length>0&&<>
      <div className="sh" style={{marginTop:12}}><span className="st" style={{color:"var(--txtl)"}}>⏸️ Inactief / verlopen ({inactieve.length})</span></div>
      {inactieve.map(p=><div key={p.id} className="card" style={{marginBottom:8,opacity:.6,borderLeft:"4px solid var(--brd)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:600,color:"var(--txtl)"}}>{p.titel}</div>
            <div style={{fontSize:12,color:"var(--txtl)",marginTop:2}}>{p.start_datum?new Date(p.start_datum).toLocaleDateString("nl-NL"):"-"}{p.eind_datum?" · Tot "+new Date(p.eind_datum).toLocaleDateString("nl-NL"):""}{!p.actief?" · Handmatig gestopt":""}</div>
          </div>
          {canEdit&&<div className="iab">
            <button onClick={()=>openEdit(p)}>{IC.edit}</button>
            <button onClick={()=>toast.confirm("Protocol verwijderen?",()=>ds({type:"DEL_PROTOCOL",p:p.id}))}>{IC.trash}</button>
          </div>}
        </div>
      </div>)}
    </>}

    {(s.protocollen||[]).length===0&&<EmptyState icon="📄" title="Nog geen protocollen" sub="Maak een protocol aan dat medewerkers kunnen inzien tijdens hun dienst."/>}
    {canEdit&&<button className="fab" onClick={openNew}>{IC.plus}</button>}
  </div>;
}

function ProtocolInhoudPreview({inhoud}){
  const [open,setOpen]=useState(false);
  const preview=inhoud?.length>150?inhoud.slice(0,150)+"…":inhoud;
  return <div style={{marginTop:8}}>
    {open
      ?<div style={{padding:"10px 12px",background:"var(--cd)",borderRadius:8,fontSize:13,color:"var(--txt)",lineHeight:1.6,whiteSpace:"pre-wrap",maxHeight:500,overflowY:"auto"}}>
        {inhoud}
        <div style={{marginTop:8,textAlign:"right"}}><button onClick={()=>setOpen(false)} style={{fontSize:12,color:"#2e6da4",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>▲ Inklappen</button></div>
      </div>
      :<div onClick={()=>setOpen(true)} style={{padding:"8px 12px",background:"var(--cd)",borderRadius:8,fontSize:13,color:"var(--txtl)",cursor:"pointer",lineHeight:1.5}}>
        {preview} <span style={{color:"#2e6da4",fontWeight:600,marginLeft:4}}>Lees meer ▼</span>
      </div>}
  </div>;
}

function MedicatieHistorie({medId}){
  const [gegeven, setGegeven] = useState([]);
  const [loading, setLoading] = useState(true);
  const dagdIcon = {ochtend:"🌅", middag:"☀️", avond:"🌙"};
  const today = new Date().toISOString().split("T")[0];
  useEffect(()=>{ db.getMedicatieGegeven(medId).then(data=>{ setGegeven(data); setLoading(false); }); },[medId]);
  if(loading) return <div style={{fontSize:11,color:"var(--txtl)",padding:"6px 0"}}>Laden...</div>;
  if(gegeven.length===0) return <div style={{fontSize:11,color:"var(--txtl)",padding:"6px 0"}}>Nog niet toegediend.</div>;
  // Groepeer per dag
  const perDag = gegeven.reduce((acc,g)=>{ const dag=g.gegeven_op.split("T")[0]; if(!acc[dag]) acc[dag]=[]; acc[dag].push(g); return acc; },{});
  return <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:6}}>
    {Object.entries(perDag).map(([dag,records])=>{
      const datum = new Date(dag);
      const isVandaag = dag===today;
      const isGisteren = dag===new Date(Date.now()-86400000).toISOString().split("T")[0];
      const dagLabel = isVandaag?"Vandaag":isGisteren?"Gisteren":datum.toLocaleDateString("nl-NL",{weekday:"short",day:"numeric",month:"short"});
      return <div key={dag}>
        <div style={{fontSize:10,fontWeight:700,color:"var(--txtl)",textTransform:"uppercase",marginBottom:2}}>{dagLabel}</div>
        {records.map(g=><div key={g.id} style={{display:"flex",alignItems:"center",gap:6,fontSize:11,padding:"3px 8px",background:"#f5f9ff",borderRadius:6,marginBottom:2}}>
          <span>{dagdIcon[g.dagdeel]||"⏰"}</span>
          <span style={{color:"var(--txtl)"}}>{new Date(g.gegeven_op).toLocaleTimeString("nl-NL",{hour:"2-digit",minute:"2-digit"})}</span>
          <span style={{fontWeight:600,color:"var(--g)"}}>{g.gebruikers?.roepnaam||g.gebruikers?.naam||"?"}</span>
        </div>)}
      </div>;
    })}
  </div>;
}

function GezondheidTestPg({s, d, ds, park, soorten=[]}){
  const dieren = s.animals.filter(a=>a.actief!==false&&!a.datum_overlijden);
  const [filterStatus, setFilterStatus] = useState("actief");
  const [zoekterm, setZoekterm] = useState("");
  const [viewMode, setViewMode] = useState("soort"); // "soort" | "dier" | "detail" | "soortrecord"
  const [selectedSoort, setSelectedSoort] = useState(null);
  const [selectedDier, setSelectedDier] = useState(null);
  const [selectedSoortRecord, setSelectedSoortRecord] = useState(null); // for groepsrecords
  const [openMedId, setOpenMedId] = useState(null); // welke medicatie historie open
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [fromDieren, setFromDieren] = useState(false);

  // Auto-select dier als gezondheid_dier_id is ingesteld
  useEffect(() => {
    if(s.gezondheid_dier_id) {
      const dier = dieren.find(d => d.id === s.gezondheid_dier_id);
      if(dier) {
        const soort = soorten.find(st => st.id === dier.soort_id);
        setSelectedDier(dier);
        setSelectedSoort(soort);
        setViewMode("detail");
        setFromDieren(true);
      }
    }
  }, [s.gezondheid_dier_id, dieren, soorten]);

  const medicatie = s.meds;
  const vetCat = (s.contactCategorieen||[]).find(cc => cc.categorie === "dierenarts");
  const vets = s.contacten.filter(c => vetCat && c.contact_categorie_id === vetCat.id);
  const verzorgerCat = (s.contactCategorieen||[]).find(cc => cc.categorie === "dierenverzorger");
  const verzorgers = s.contacten.filter(c => verzorgerCat && c.contact_categorie_id === verzorgerCat.id);

  const records = s.gezondheid || [];
  const statusEmoji = {"gaat beter":"✨","is verslechterd":"⚠️","blijft gelijk":"➡️","alert op blijven":"👁️","genezen":"✅"};
  const statusColor = {"gaat beter":"#16a34a","is verslechterd":"#dc2626","blijft gelijk":"#d97706","alert op blijven":"#9333ea","genezen":"#6b7280"};

  // Filter
  const gefilterd = records.filter(r => {
    if(filterStatus === "actief") return r.status !== "genezen";
    if(filterStatus === "genezen") return r.status === "genezen";
    return true;
  });

  const gezocht = zoekterm.trim()
    ? gefilterd.filter(r => {
        const dier = dieren.find(d => d.id === r.dier_id);
        const naam = (dier?.naam || dier?.name || "").toLowerCase();
        const diagnose = (r.diagnoses || "").toLowerCase();
        return naam.includes(zoekterm.toLowerCase()) || diagnose.includes(zoekterm.toLowerCase());
      })
    : gefilterd;

  // Soort tegels — ook groepsrecords (soort_id zonder dier_id) meetellen
  const soortMetRecords = soorten.map(soort => {
    const soortDieren = dieren.filter(a => a.soort_id === soort.id);
    const indivRecords = gezocht.filter(r => !r.soort_id && soortDieren.some(a => a.id === r.dier_id));
    const groepsRecords = gezocht.filter(r => r.soort_id === soort.id && !r.dier_id);
    return { soort, soortDieren, indivRecords, groepsRecords, allRecords: [...indivRecords, ...groepsRecords] };
  }).filter(x => x.allRecords.length > 0);

  const recordsVanDier = selectedDier
    ? gezocht.filter(r => r.dier_id === selectedDier.id).sort((a,b) => new Date(b.datum) - new Date(a.datum))
    : [];

  // Helper: record kaart
  const RecordKaart = ({r, onEdit, onDelete}) => {
    const vet = r.veterinarian_id ? s.contacten.find(c=>c.id===r.veterinarian_id) : null;
    const verzorger = r.verzorger_id ? s.contacten.find(c=>c.id===r.verzorger_id) : null;
    return <div className="card" style={{marginBottom:10,borderLeft:`4px solid ${statusColor[r.status]||"#ccc"}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:700,color:"var(--g)"}}>🩺 {r.diagnoses}</div>
          <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>📅 {new Date(r.datum).toLocaleDateString("nl-NL")}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:20}}>{statusEmoji[r.status]}</span>
          {onEdit&&<button onClick={()=>onEdit(r)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--txtl)",fontSize:16,padding:"2px 4px"}}>{IC.edit}</button>}
          {onDelete&&<button onClick={()=>toast.confirm("Record verwijderen?",()=>onDelete(r.id))} style={{background:"none",border:"none",cursor:"pointer",color:"var(--red)",fontSize:16,padding:"2px 4px"}}>{IC.trash}</button>}
        </div>
      </div>
      <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:statusColor[r.status]+"18",color:statusColor[r.status],fontWeight:600}}>{r.status}</span>
      {r.beschrijving&&<div style={{fontSize:12,color:"var(--txt)",lineHeight:1.5,marginTop:8,padding:"8px 10px",background:"var(--cd)",borderRadius:8}}>{r.beschrijving}</div>}
      {vet&&<div style={{fontSize:11,color:"#2563eb",marginTop:6}}>🩺 {vet.naam}</div>}
      {verzorger&&<div style={{fontSize:11,color:"#16a34a",marginTop:2}}>🐾 {verzorger.naam}</div>}

      {/* Medicatie met uitklapbare historie */}
      {r.gezondheid_medicatie?.length>0&&<div style={{marginTop:10}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--txtl)",textTransform:"uppercase",letterSpacing:.4,marginBottom:6}}>💊 Medicatie</div>
        {r.gezondheid_medicatie.map(med=>{
          const isOpen = openMedId === med.id;
          return <div key={med.id}
            onClick={()=>setOpenMedId(isOpen?null:med.id)}
            style={{background:isOpen?"#f0f8ff":"#f8fbff",border:`1px solid ${isOpen?"#90c4f0":"#d0e8ff"}`,borderRadius:10,padding:"8px 12px",marginBottom:6,cursor:"pointer",transition:"all .15s"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600}}>{med.medicatie?.medicijn||"Medicijn"}</div>
                <div style={{fontSize:11,color:"var(--txtl)",marginTop:1}}>
                  {med.dosering&&<span>📏 {med.dosering} </span>}
                  {med.freq&&<span>⏰ {med.freq} </span>}
                  {med.eind&&<span>📅 t/m {new Date(med.eind).toLocaleDateString("nl-NL")}</span>}
                </div>
              </div>
              <span style={{fontSize:11,color:"#1a6fa8",fontWeight:600}}>{isOpen?"▲ Sluiten":"▼ Historie"}</span>
            </div>
            {isOpen&&<div style={{borderTop:"1px solid #d0e8ff",marginTop:8,paddingTop:8}}>
              <MedicatieHistorie medId={med.id}/>
            </div>}
          </div>;
        })}
      </div>}
    </div>;
  };

  // ── DETAIL VIEW (individueel dier) ──
  if(viewMode === "detail" && selectedDier) {
    return <div className="pg">
      <button className="back-btn" onClick={()=>{if(fromDieren) {d({type:"NAV",p:"dieren"}); setFromDieren(false);} else setViewMode("dier");}}>{IC.back} {fromDieren?"Dieren":selectedSoort?.naam}</button>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
        <div style={{width:56,height:56,borderRadius:12,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0,overflow:"hidden"}}>
          {selectedDier.fotos?.[0]?<img src={selectedDier.fotos[0]} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:12}}/>:selectedSoort?.icon||"🐾"}
        </div>
        <div style={{flex:1}}>
          <h2 style={{margin:0,fontSize:18,fontWeight:700}}>{selectedDier.naam||selectedDier.name}</h2>
          <div style={{fontSize:12,color:"var(--txtl)"}}>{selectedSoort?.icon} {selectedSoort?.naam} · {selectedDier.geslacht==="man"?"♂️":"♀️"}</div>
        </div>
      </div>

      {recordsVanDier.length===0&&<div className="card"><EmptyState icon="🏥" title="Geen records" sub="Nog geen gezondheidsrecords voor dit dier."/></div>}
      {recordsVanDier.map(r=><RecordKaart key={r.id} r={r}
        onEdit={rec=>{setEditRecord(rec);setShowForm(true);}}
        onDelete={id=>{ds({type:"DEL_GEZONDHEID",p:id});}}
      />)}

      {showForm&&<GezondheidForm
        item={editRecord}
        dieren={dieren} medicatie={medicatie} vets={vets} verzorgers={verzorgers} soorten={soorten}
        onClose={()=>{setShowForm(false);setEditRecord(null);}}
        onSave={f=>{
          if(editRecord) ds({type:"EDIT_GEZONDHEID",p:{...f,id:editRecord.id}});
          else ds({type:"ADD_GEZONDHEID",p:{...f,park_id:park.id}});
          setShowForm(false);setEditRecord(null);
        }}
      />}
      <button className="fab" onClick={()=>{setEditRecord(null);setShowForm(true);}}>{IC.plus}</button>
    </div>;
  }

  // ── GROEPSRECORD DETAIL ──
  if(viewMode === "soortrecord" && selectedSoort) {
    const groepsRecords = gezocht.filter(r=>r.soort_id===selectedSoort.id&&!r.dier_id).sort((a,b)=>new Date(b.datum)-new Date(a.datum));
    return <div className="pg">
      <button className="back-btn" onClick={()=>setViewMode("dier")}>{IC.back} {selectedSoort.naam}</button>
      <h2 className="pt">🏷️ Groepsrecords · {selectedSoort.icon} {selectedSoort.naam}</h2>
      {groepsRecords.length===0&&<div className="card"><EmptyState icon="🏷️" title="Geen groepsrecords" sub={`Nog geen records voor alle ${selectedSoort.naam.toLowerCase()}.`}/></div>}
      {groepsRecords.map(r=><RecordKaart key={r.id} r={r}
        onEdit={rec=>{setEditRecord(rec);setShowForm(true);}}
        onDelete={id=>ds({type:"DEL_GEZONDHEID",p:id})}
      />)}
      {showForm&&<GezondheidForm
        item={editRecord || {soort_id: selectedSoort.id}}
        defaultMode="soort"
        dieren={dieren} medicatie={medicatie} vets={vets} verzorgers={verzorgers} soorten={soorten}
        onClose={()=>{setShowForm(false);setEditRecord(null);}}
        onSave={f=>{
          if(editRecord) ds({type:"EDIT_GEZONDHEID",p:{...f,id:editRecord.id}});
          else ds({type:"ADD_GEZONDHEID",p:{...f,park_id:park.id}});
          setShowForm(false);setEditRecord(null);
        }}
      />}
      <button className="fab" onClick={()=>{setEditRecord(null);setShowForm(true);}}>{IC.plus}</button>
    </div>;
  }
  if(viewMode === "dier" && selectedSoort) {
    const groepsRecords = gezocht.filter(r=>r.soort_id===selectedSoort.id&&!r.dier_id);
    const dierenVanSoort = dieren.filter(a=>a.soort_id===selectedSoort.id);
    // Sorteer: dieren met actieve records eerst, dan zonder
    const dierenGesorteerd = [...dierenVanSoort].sort((a,b)=>{
      const aRecords = gezocht.filter(r=>r.dier_id===a.id).length;
      const bRecords = gezocht.filter(r=>r.dier_id===b.id).length;
      return bRecords - aRecords;
    });
    return <div className="pg">
      <button className="back-btn" onClick={()=>setViewMode("soort")}>{IC.back} Alle soorten</button>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
        <h2 className="pt" style={{margin:0}}>{selectedSoort.icon} {selectedSoort.naam}</h2>
      </div>

      {/* Groepsrecord tegel — altijd zichtbaar */}
      <div onClick={()=>setViewMode("soortrecord")}
        className="card" style={{marginBottom:8,cursor:"pointer",display:"flex",alignItems:"center",gap:12,
          borderLeft:`4px solid #7c3aed`,background: groepsRecords.length>0?"#f8f5ff":"#fff"}}>
        <span style={{fontSize:28}}>{selectedSoort.icon}</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:14,color:"#7c3aed"}}>🏷️ Groepsrecords</div>
          <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>
            {groepsRecords.length>0
              ? `${groepsRecords.length} record${groepsRecords.length!==1?"s":""} voor alle ${selectedSoort.naam.toLowerCase()}`
              : `Nog geen groepsrecords — klik om toe te voegen`}
          </div>
        </div>
        <span style={{fontSize:12,color:"var(--txtl)"}}>▶</span>
      </div>

      {/* Individuele dieren gesorteerd */}
      {dierenGesorteerd.map(dier => {
        const dierRecords = gezocht.filter(r => r.dier_id === dier.id);
        const ergsteStatus = dierRecords.find(r=>r.status==="is verslechterd")?"is verslechterd"
          :dierRecords.find(r=>r.status==="blijft gelijk")?"blijft gelijk"
          :dierRecords.find(r=>r.status==="gaat beter")?"gaat beter":"genezen";
        return <div key={dier.id} onClick={()=>{setSelectedDier(dier);setViewMode("detail");}}
          className="card" style={{marginBottom:8,cursor:"pointer",display:"flex",alignItems:"center",gap:12,
            borderLeft:dierRecords.length>0?`4px solid ${statusColor[ergsteStatus]||"#ccc"}`:"4px solid var(--brd)",
            opacity:dierRecords.length===0?0.5:1}}>
          <div style={{width:48,height:48,borderRadius:10,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            {dier.fotos?.[0]?<img src={dier.fotos[0]} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:22}}>{selectedSoort.icon}</span>}
          </div>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,fontSize:14}}>{dier.naam||dier.name}</div>
            <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>
              {dierRecords.length>0?`${dierRecords.length} record${dierRecords.length!==1?"s":""} · ${dierRecords[0]?.diagnoses}`:"Geen records"}
            </div>
          </div>
          <span style={{fontSize:20}}>{dierRecords.length>0?statusEmoji[ergsteStatus]:"➕"}</span>
        </div>;
      })}
      {dierenVanSoort.length===0&&<div className="card"><EmptyState icon="🏥" title="Geen dieren" sub="Geen dieren gevonden voor deze soort."/></div>}
    </div>;
  }

  // ── SOORT OVERZICHT (laag 1) ──
  return <div className="pg">
    <h1 className="pt">🏥 Gezondheid</h1>

    <div style={{display:"flex",gap:6,marginBottom:12}}>
      {["actief","genezen","alle"].map(f=><button key={f} onClick={()=>setFilterStatus(f)}
        className={`fc ${filterStatus===f?"a":""}`} style={{flex:1,fontSize:12,padding:"7px 4px"}}>
        {f==="actief"?"🔴 Actief":f==="genezen"?"✅ Genezen":"📋 Alle"}
      </button>)}
    </div>

    <div style={{position:"relative",marginBottom:14}}>
      <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:14,color:"var(--txtl)"}}>🔍</span>
      <input className="fi" style={{paddingLeft:34,fontSize:14}} placeholder="Zoek op dier of diagnose..."
        value={zoekterm} onChange={e=>setZoekterm(e.target.value)}/>
      {zoekterm&&<button onClick={()=>setZoekterm("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",fontSize:16,cursor:"pointer",color:"var(--txtl)"}}>×</button>}
    </div>

    {soortMetRecords.length===0&&<div className="card"><EmptyState icon="🏥" title="Geen records gevonden" sub="Pas de filters aan."/></div>}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:80}}>
      {soortMetRecords.map(({soort,allRecords:sr,groepsRecords:gr})=>{
        const verslechterd = sr.filter(r=>r.status==="is verslechterd").length;
        const actief = sr.filter(r=>r.status!=="genezen").length;
        return <div key={soort.id} onClick={()=>{setSelectedSoort(soort);setViewMode("dier");}}
          style={{background:"#fff",borderRadius:14,padding:14,border:`2px solid ${verslechterd>0?"#dc262630":"var(--brd)"}`,
            boxShadow:"0 2px 8px rgba(0,0,0,.06)",cursor:"pointer",position:"relative"}}>
          {verslechterd>0&&<div style={{position:"absolute",top:8,right:8,background:"#dc2626",color:"#fff",borderRadius:20,fontSize:10,fontWeight:700,padding:"2px 6px"}}>⚠️ {verslechterd}</div>}
          {gr.length>0&&<div style={{position:"absolute",bottom:8,right:8,background:"#7c3aed",color:"#fff",borderRadius:20,fontSize:10,fontWeight:700,padding:"2px 6px"}}>🏷️ {gr.length}</div>}
          <div style={{fontSize:32,marginBottom:6}}>{soort.icon}</div>
          <div style={{fontWeight:700,fontSize:14,color:"var(--g)"}}>{soort.naam}</div>
          <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>{actief} actief · {sr.length} totaal</div>
        </div>;
      })}
    </div>

    <button className="fab" onClick={()=>{setEditRecord(null);setShowForm(true);}}>{IC.plus}</button>
    {showForm&&<GezondheidForm
      dieren={dieren} medicatie={medicatie} vets={vets} verzorgers={verzorgers} soorten={soorten}
      onClose={()=>setShowForm(false)}
      onSave={f=>{ds({type:"ADD_GEZONDHEID",p:{...f,park_id:park.id}});setShowForm(false);}}
    />}
  </div>;
}
function MediaPage({s, d, ds, park, setPhotoModal}) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Load media on mount
  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      setLoading(true);
      const mediaList = await db.getMediaByPark(park.id);
      setMedia(mediaList);
    } catch (err) {
      console.error("Error loading media:", err);
      toast.err("Kon media niet laden");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) {
      toast.warn("Alleen foto's en video's zijn toegestaan");
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      toast.warn("Bestand is te groot (max 100MB)");
      return;
    }

    try {
      setUploading(true);
      const fileType = isImage ? "image" : "video";
      const userId = s.user.id;
      
      console.log("📸 Uploading media - userId:", userId, "parkId:", park.id, "s.user:", s.user);
      
      await db.uploadMedia(park.id, file, fileType, userId);
      console.log("✅ Media uploaded successfully");
      
      // Reload media
      await loadMedia();
    } catch (err) {
      console.error("Upload error:", err);
      toast.err("Kon bestand niet uploaden: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = ""; // Reset input
    }
  };

  const handleDelete = async (mediaId) => {
    toast.confirm("Wil je dit media verwijderen?", async () => {
      try {
        await db.deleteMedia(mediaId);
        setMedia(media.filter(m => m.id !== mediaId));
        console.log("✅ Media deleted");
    } catch (err) {
      console.error("Delete error:", err);
      toast.err("Kon media niet verwijderen");
    }
    });
  };

  return <div className="pg">
    <h1 className="pt">📸 Media</h1>
    <p style={{fontSize: 12, color: "var(--txtl)", marginBottom: 16}}>
      Upload foto's en video's van {park.naam}
    </p>
    <InstagramCard park={park} canEdit={hasPerm(s.user,"voeding_beheren")||s.user?.isAppAdmin}/>

    {/* Upload Area */}
    <div style={{
      border: "2px dashed var(--g)",
      borderRadius: 12,
      padding: 20,
      textAlign: "center",
      marginBottom: 20,
      background: "var(--gp)",
      cursor: uploading ? "not-allowed" : "pointer",
      opacity: uploading ? 0.6 : 1
    }}>
      <input
        type="file"
        id="mediaInput"
        accept="image/*,video/*"
        onChange={handleFileUpload}
        disabled={uploading}
        style={{display: "none"}}
      />
      <label htmlFor="mediaInput" style={{cursor: "pointer", display: "block"}}>
        <div style={{fontSize: 32, marginBottom: 8}}>
          {uploading ? "⏳" : "📁"}
        </div>
        <div style={{fontWeight: 600, color: "var(--g)", marginBottom: 4}}>
          {uploading ? "Uploaden..." : "Klik of sleep bestand"}
        </div>
        <div style={{fontSize: 12, color: "var(--txtl)"}}>
          Foto's of video's (max 100MB)
        </div>
      </label>
    </div>

    {/* Media Grid */}
    {loading ? (
      <div style={{textAlign: "center", padding: 20}}>
        <div style={{fontSize: 24}}>🔄</div>
        <div style={{fontSize: 12, color: "var(--txtl)", marginTop: 8}}>Laden...</div>
      </div>
    ) : media.length === 0 ? (
      <div style={{textAlign: "center", padding: 20, background: "#f5f5f5", borderRadius: 12}}>
        <div style={{fontSize: 24, marginBottom: 8}}>📭</div>
        <EmptyState icon="📸" title="Nog geen media" sub="Upload foto's en video's van het park."/>
      </div>
    ) : (
      <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12}}>
        {media.map(m => (
          <div key={m.id} style={{
            borderRadius: 12,
            overflow: "hidden",
            background: "#f5f5f5",
            aspectRatio: "1",
            position: "relative",
            cursor: "pointer",
            transition: "all 0.2s"
          }} onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
            e.currentTarget.style.transform = "scale(1)";
          }} onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
            e.currentTarget.style.transform = "scale(1.02)";
          }}>
            {m.file_type === "image" ? (
              <img src={m.file_url} alt={m.file_name} onClick={()=>setPhotoModal({photos:media.filter(x=>x.file_type==="image").map(x=>x.file_url),dierName:m.file_name,initialIndex:media.findIndex(x=>x.id===m.id),showNav:true})} style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "pointer"
              }} />
            ) : (
              <div style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#333",
                color: "#fff",
                fontSize: 32
              }}>
                🎬
              </div>
            )}
            
            {/* Delete button overlay */}
            <button onClick={() => handleDelete(m.id)} style={{
              position: "absolute",
              top: 6,
              right: 6,
              background: "rgba(239,68,68,0.9)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 700,
              opacity: 0,
              transition: "opacity 0.2s",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }} onMouseEnter={(e) => e.target.style.opacity = "1"} onMouseLeave={(e) => e.target.style.opacity = "0"} title="Verwijderen">
              ✕
            </button>

            {/* Info on hover */}
            <div style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
              padding: 8,
              fontSize: 10,
              maxHeight: 40,
              overflow: "hidden",
              opacity: 0,
              transition: "opacity 0.2s"
            }} onMouseEnter={(e) => e.target.style.opacity = "1"} onMouseLeave={(e) => e.target.style.opacity = "0"}>
              <div style={{fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                {m.file_name}
              </div>
              <div style={{fontSize: 9, opacity: 0.8}}>
                {new Date(m.created_at).toLocaleDateString("nl-NL")}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>;
}

function SleutelForm({item, onSave, onClose}) {
  const [omschrijving, setOmschrijving] = useState(item?.omschrijving || '');
  const [aantal, setAantal] = useState(item?.aantal || 1);
  
  const handleSave = () => {
    if (!omschrijving.trim()) { toast.warn('Voer een naam in'); return; }
    onSave({ omschrijving: omschrijving.trim(), aantal: parseInt(aantal) || 1 });
    onClose();
  };
  
  return <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
    <div style={{background:'#fff',borderRadius:8,padding:24,width:'90%',maxWidth:400,boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
      <h3 style={{margin:'0 0 20px 0',color:'var(--g)'}}>{item?'✏️ Sleutel bewerken':'➕ Nieuwe Sleutel'}</h3>
      <div style={{marginBottom:16}}>
        <label style={{display:'block',fontSize:13,fontWeight:600,color:'#333',marginBottom:6}}>Naam</label>
        <input type="text" value={omschrijving} onChange={(e)=>setOmschrijving(e.target.value)} placeholder="bijv. Voordeur, Schuur, etc." style={{width:'100%',padding:10,border:'1px solid #ddd',borderRadius:6,fontSize:16,boxSizing:'border-box'}}/>
      </div>
      <div style={{marginBottom:20}}>
        <label style={{display:'block',fontSize:13,fontWeight:600,color:'#333',marginBottom:6}}>Aantal beschikbaar</label>
        <input type="number" value={aantal} onChange={(e)=>setAantal(e.target.value)} min="1" style={{width:'100%',padding:10,border:'1px solid #ddd',borderRadius:6,fontSize:16,boxSizing:'border-box'}}/>
      </div>
      <div style={{display:'flex',gap:10}}>
        <button onClick={handleSave} style={{flex:1,padding:'10px 20px',background:'var(--g)',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontWeight:600}}>✅ {item?'Update':'Toevoegen'}</button>
        <button onClick={onClose} style={{flex:1,padding:'10px 20px',background:'#ddd',color:'#333',border:'none',borderRadius:6,cursor:'pointer',fontWeight:600}}>✕ Annuleren</button>
      </div>
    </div>
  </div>;
}

/* ── Mijn Park pagina (module-level to prevent remounting) ── */
function MijnParkPg({park, canBeheer, onParkUpdate}){
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    naam: park?.naam||"", roepnaam: park?.roepnaam||"",
    adres: park?.adres||"", plaats: park?.plaats||"", provincie: park?.provincie||"",
    land: park?.land||"Nederland",
    telefoon: park?.telefoon||"", website: park?.website||"",
    instagram: park?.instagram||"", openingstijden: park?.openingstijden||"",
    beschrijving: park?.beschrijving||"",
  });
  const u = (k,v) => setF(p=>({...p,[k]:v}));

  // Reset formulier als modal opent
  const openEdit = () => {
    setF({
      naam: park?.naam||"", roepnaam: park?.roepnaam||"",
      adres: park?.adres||"", plaats: park?.plaats||"", provincie: park?.provincie||"",
      land: park?.land||"Nederland",
      telefoon: park?.telefoon||"", website: park?.website||"",
      instagram: park?.instagram||"", openingstijden: park?.openingstijden||"",
      beschrijving: park?.beschrijving||"",
    });
    setEditOpen(true);
  };

  const save = async () => {
    if(!f.naam.trim() || !f.roepnaam.trim() || !f.plaats.trim()){
      toast.warn("Naam, roepnaam en plaats zijn verplicht"); return;
    }
    setSaving(true);
    try {
      const updates = {
        naam: f.naam.trim(), roepnaam: f.roepnaam.trim(),
        adres: f.adres.trim(), plaats: f.plaats.trim(), provincie: f.provincie.trim(),
        telefoon: f.telefoon.trim()||null,
        website: f.website.trim()||null,
        instagram: (f.instagram||"").replace(/^@/,"").trim()||null,
        openingstijden: f.openingstijden.trim()||null,
        beschrijving: f.beschrijving.trim()||null,
      };

      // Check of adres/plaats gewijzigd is → coördinaten opnieuw ophalen
      const adresChanged = (updates.adres!==(park?.adres||"")) || (updates.plaats!==(park?.plaats||""));
      const land = f.land.trim()||"Nederland";
      let geocoded = null;
      if(adresChanged && (updates.adres || updates.plaats)){
        geocoded = await geocodeAdres(updates.adres, updates.plaats, land);
        if(geocoded){ updates.lat = geocoded.lat; updates.lng = geocoded.lng; }
      }

      await db.updatePark(park.id, updates);
      if(onParkUpdate) onParkUpdate(updates);

      if(adresChanged){
        if(geocoded) toast.ok("Park opgeslagen, locatie bijgewerkt 📍");
        else toast.warn("Park opgeslagen, maar locatie kon niet automatisch worden bepaald");
      } else {
        toast.ok("Park opgeslagen ✅");
      }
      setEditOpen(false);
    } catch(err){
      toast.err("Opslaan mislukt: "+(err.message||"onbekende fout"));
    } finally {
      setSaving(false);
    }
  };

  // Rij-helper voor read-view
  const Row = ({label, value}) => (
    <div style={{display:"flex",padding:"12px 14px",borderBottom:"1px solid var(--brd)",gap:12}}>
      <div style={{flex:"0 0 130px",fontSize:13,color:"var(--txtl)",fontWeight:600}}>{label}</div>
      <div style={{flex:1,fontSize:14,color:"var(--txt)",wordBreak:"break-word"}}>
        {value || <span style={{color:"var(--txtl)",fontStyle:"italic"}}>— niet ingevuld —</span>}
      </div>
    </div>
  );

  return <div className="pg">
    <h1 className="pt">🏛️ Mijn park</h1>
    <p style={{fontSize:13,color:"var(--txtl)",marginBottom:16}}>
      Overzicht van de gegevens van {park?.naam||"je park"}.
      {!canBeheer && " Neem contact op met de beheerder als iets niet klopt."}
    </p>

    <div style={{background:"#fff",border:"1px solid var(--brd)",borderRadius:12,overflow:"hidden",marginBottom:16}}>
      <Row label="Naam" value={park?.naam}/>
      <Row label="Roepnaam" value={park?.roepnaam}/>
      <Row label="Adres" value={park?.adres}/>
      <Row label="Plaats" value={park?.plaats}/>
      <Row label="Provincie" value={park?.provincie}/>
      <Row label="Land" value={park?.land||"Nederland"}/>
      <Row label="Telefoon" value={park?.telefoon}/>
      <Row label="Website" value={park?.website}/>
      <Row label="Instagram" value={park?.instagram ? "@"+park.instagram.replace(/^@/,"") : null}/>
      <Row label="Openingstijden" value={park?.openingstijden}/>
      <Row label="Beschrijving" value={park?.beschrijving}/>
      <Row label="Coördinaten" value={park?.lat&&park?.lng ? `${Number(park.lat).toFixed(5)}, ${Number(park.lng).toFixed(5)}` : null}/>
    </div>

    {canBeheer && (
      <button className="fbtn" onClick={openEdit} style={{width:"100%",padding:"14px",fontSize:15,fontWeight:600}}>
        ✏️ Wijzig parkgegevens
      </button>
    )}

    {editOpen && <div className="mo" onClick={e=>{if(e.target.classList.contains("mo"))setEditOpen(false);}}>
      <div className="mb" style={{maxWidth:560}}>
        <div className="mh"><h3>✏️ Parkgegevens wijzigen</h3><button onClick={()=>setEditOpen(false)}>✕</button></div>
        <div className="mc">
          <div className="fg"><label className="fl">Naam *</label>
            <input className="fi" value={f.naam} onChange={e=>u("naam",e.target.value)}/></div>
          <div className="fg"><label className="fl">Roepnaam * <span style={{fontWeight:400,color:"var(--txtl)"}}>(korte naam in de app)</span></label>
            <input className="fi" value={f.roepnaam} onChange={e=>u("roepnaam",e.target.value)}/></div>
          <div className="fg"><label className="fl">Adres <span style={{fontWeight:400,color:"var(--txtl)"}}>(straat + huisnummer)</span></label>
            <input className="fi" value={f.adres} onChange={e=>u("adres",e.target.value)} placeholder="Dorpsstraat 12"/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div className="fg"><label className="fl">Plaats *</label>
              <input className="fi" value={f.plaats} onChange={e=>u("plaats",e.target.value)}/></div>
            <div className="fg"><label className="fl">Provincie</label>
              <input className="fi" value={f.provincie} onChange={e=>u("provincie",e.target.value)}/></div>
          </div>
          <div className="fg"><label className="fl">Land</label>
            <select className="fi" value={f.land} onChange={e=>u("land",e.target.value)}>
              <option value="Nederland">Nederland</option>
              <option value="België">België</option>
            </select>
          </div>
          <div className="fg"><label className="fl">Telefoon</label>
            <input className="fi" value={f.telefoon} onChange={e=>u("telefoon",e.target.value)}/></div>
          <div className="fg"><label className="fl">Website</label>
            <input className="fi" value={f.website} onChange={e=>u("website",e.target.value)} placeholder="https://..."/></div>
          <div className="fg"><label className="fl">Instagram <span style={{fontWeight:400,color:"var(--txtl)"}}>(zonder @)</span></label>
            <input className="fi" value={f.instagram} onChange={e=>u("instagram",e.target.value)} placeholder="dierenparkje"/></div>
          <div className="fg"><label className="fl">Openingstijden</label>
            <textarea className="fi" rows={3} value={f.openingstijden} onChange={e=>u("openingstijden",e.target.value)} placeholder="Ma–Vr: 09:00–17:00"/></div>
          <div className="fg"><label className="fl">Beschrijving</label>
            <textarea className="fi" rows={4} value={f.beschrijving} onChange={e=>u("beschrijving",e.target.value)}/></div>

          <div style={{background:"#f0f7eb",border:"1px solid #cde3b8",borderRadius:8,padding:10,fontSize:12,color:"#3b5c1a",marginTop:8}}>
            💡 Bij wijziging van adres of plaats worden de kaartcoördinaten automatisch bijgewerkt.
          </div>

          <div style={{display:"flex",gap:8,marginTop:14}}>
            <button className="fbtn" onClick={save} disabled={saving} style={{flex:1}}>
              {saving ? "⏳ Opslaan..." : "✅ Opslaan"}
            </button>
            <button onClick={()=>setEditOpen(false)} disabled={saving} style={{flex:1,padding:"10px",background:"#ddd",border:"none",borderRadius:8,cursor:"pointer",fontWeight:600}}>
              ✕ Annuleren
            </button>
          </div>
        </div>
      </div>
    </div>}
  </div>;
}

function MgmtApp({park, initialUser, onExitPark, onWijzigWachtwoord, isAppAdmin, soorten, setPhotoModal, photoModal, db, onParkUpdate}){
  const [dbLoading, setDbLoading] = useState(true);
  const [phase2Loading, setPhase2Loading] = useState(true);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [s,d]=useReducer(mgmtReducer,{
    user:initialUser, page:"logboek", modal:null, editItem:null,
    viewSoort:null, viewDier:null, meerOpen:false,
    animals:[], users:[], feedings:[], meds:[], tasks:[], agenda:[], msgs:[], contacten:[], planning:[],
    vasteTaken:[], checks:[], logboek:[], waarschuwingen:[], protocollen:[], gezondheid:[], dierenadvies:[], takenToewijzingen:[], takenChecks:[], voedingDieren:[], maaltijdChecks:[],
    leveranciers:[], bestellingen:[], sleutels:[], functiegroepen:[], functiegroepIndeling:[], contactCategorieen:[], prikbord:[], rassen:[],
  });

  // Load all park data from Supabase on mount
	useEffect(()=>{
		// Show timeout message if loading takes too long
		const timer = setTimeout(() => {
		  if (dbLoading) setLoadTimeout(true);
		}, 10000); // Changed from 3000 to 10000 (10 seconds)
		
		return () => clearTimeout(timer);
	  }, [dbLoading]);

  // Load all park data from Supabase on mount
  useEffect(()=>{
    // Helper to convert park_id to string (database expects text type)
    const parkIdStr = (id) => id ? id.toString() : null;
    
    async function load(){
      const today = new Date().toISOString().split("T")[0];

      // AVG: log app_admin toegang tot park (persoonsgegevens inzage)
      if(isAppAdmin){
        try {
          await db.addAuditLog({
            park_id: String(park.id),
            gebruiker_id: initialUser?.id||null,
            gebruiker_naam: initialUser?.naam||initialUser?.name||"app_admin",
            actie: "PARK_TOEGANG",
            tabel: "park",
            record_id: String(park.id),
            omschrijving: `App admin heeft park "${park.naam}" geopend`,
          });
        } catch(e){ console.warn("AVG-log mislukt:",e.message); }
      }

      // Phase 1: Essential data for dashboard + dienst page
      const [users,feedings,meds,tasks,agenda,msgs,vasteTaken,checks,logboek,waarschuwingen,protocollen,takenToewijzingen,takenChecks,voedingDieren,maaltijdChecks] = await Promise.all([
        db.getGebruikers(park.id), db.getVoeding(park.id),
        db.getMedicatie(), db.getTaken(park.id), db.getAgenda(park.id),
        db.getBerichten(park.id),
        db.getVasteTaken(park.id), db.getChecks(park.id, today),
        db.getLogboekAll(park.id), db.getWaarschuwingen(park.id),
        db.getProtocollen(park.id),
        db.getTakenToewijzingen(park.id), db.getTakenChecks(park.id, today),
        db.getVoedingDieren(park.id), db.getMaaltijdChecks(park.id, today),
      ]);
      d({type:"LOAD", p:{
        users: users.map(u=>({...u, name:u.naam, roepnaam:u.roepnaam||u.naam, status:u.status||"actief"})),
        feedings: feedings.map(f=>({...f, dier:f.dier, soort:f.soort||'', door:f.gedaan_door})),
        meds: meds.map(m=>({...m, eind:m.eind, door:m.gedaan_door})),
        tasks: tasks.map(t=>({...t, titel:t.titel, desc:t.beschrijving})),
        agenda: agenda.map(a=>({...a, desc:a.beschrijving})),
        msgs: msgs.map(m=>({...m, auteur:m.auteur, datum:new Date(m.created_at).toLocaleString("nl-NL")})),
        vasteTaken: vasteTaken||[], checks: checks||[], logboek: logboek||[], waarschuwingen: waarschuwingen||[], protocollen: protocollen||[],
        takenToewijzingen: takenToewijzingen||[], takenChecks: takenChecks||[], voedingDieren: voedingDieren||[], maaltijdChecks: maaltijdChecks||[],
      }});
      setDbLoading(false);

      // Phase 2: Secondary data — loads in background after UI is visible
      const [animals,contacten,planning,gezondheid,gezondheidMeds,dierenadvies,leveranciers,bestellingen,sleutels,functiegroepen,functiegroepIndeling,contactCategorieen,prikbord,rassen] = await Promise.all([
        db.getDieren(park.id), db.getContacten(park.id), db.getPlanning(park.id),
        db.getGezondheid(park.id), db.getAllGezondheidMedicatie(park.id), db.getDierenadvies(park.id),
        db.getLeveranciers(park.id), db.getBestellingen(park.id), db.getSleutels(park.id),
        db.getFunctiegroepen(), db.getFunctiegroepIndeling(park.id), db.getContactCategorieen(),
        db.getPrikbord(), db.getRassen(),
      ]);
      // Merge medicatie into gezondheid records
      const gezondheidMedsMap = {};
      (gezondheidMeds||[]).forEach(m => {
        if(!gezondheidMedsMap[m.gezondheid_id]) gezondheidMedsMap[m.gezondheid_id] = [];
        gezondheidMedsMap[m.gezondheid_id].push(m);
      });
      const gezondheidWithMeds = (gezondheid||[]).map(g => ({...g, gezondheid_medicatie: gezondheidMedsMap[g.id]||[]}));
      d({type:"LOAD", p:{
        animals: animals.map(a=>({...a, name:a.naam, fotos:a.fotos||[]})),
        contacten, planning: planning.map(p=>({...p, naam:p.naam, userId:p.gebruiker_id, contactId:p.contact_id, herhaling:p.herhaling||"geen", datum:(p.datum||'').split('T')[0]})),
        gezondheid: gezondheidWithMeds, dierenadvies: dierenadvies||[],
        leveranciers: leveranciers||[], bestellingen: bestellingen||[], sleutels: sleutels||[],
        functiegroepen: functiegroepen||[], functiegroepIndeling: functiegroepIndeling||[], contactCategorieen: contactCategorieen||[],
        prikbord: prikbord||[], rassen: rassen||[],
      }});
      setPhase2Loading(false);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[park.id]);

  /* ── DB-synced dispatch: updates local state AND persists to Supabase ── */
  const ds = useCallback(async (action) => {
    const pid = park.id;
    try {
      switch(action.type){
      case "TOG_FEED": {
        const f = s.feedings.find(x=>x.id===action.p);
        if(f) await db.updateVoeding(f.id, {gedaan:!f.gedaan, gedaan_door:!f.gedaan?s.user.name:null});
        break;
      }
      case "TOG_MED": {
        const m = s.meds.find(x=>x.id===action.p);
        if(m) await db.updateMedicatie(m.id, {gedaan:!m.gedaan, gedaan_door:!m.gedaan?s.user.name:null});
        break;
      }
      case "ADD_FEED": { const nw=await db.addVoeding({type:action.p.type,merk:action.p.merk||null,inhoud:action.p.inhoud||null,omschrijving:action.p.omschrijving||null,methode:action.p.methode||null,foto:action.p.foto||null,link:action.p.link||null,leverancier_id:action.p.leverancier_id||null,park_id:pid}); if(nw) action.p={...nw}; break; }
      case "EDIT_FEED": { await db.updateVoeding(action.p.id, {type:action.p.type,merk:action.p.merk||null,inhoud:action.p.inhoud||null,omschrijving:action.p.omschrijving||null,methode:action.p.methode||null,foto:action.p.foto||null,link:action.p.link||null,leverancier_id:action.p.leverancier_id||null}); break; }
      case "DEL_FEED": { await db.deleteVoeding(action.p); break; }
      case "ADD_VOEDING_DIER": {
        const nw=await db.addVoedingDier({...action.p, park_id:pid});
        if(nw) action.p=nw;
        break;
      }
      case "EDIT_VOEDING_DIER": {
        await db.updateVoedingDier(action.p.id, {voeding_id:action.p.voeding_id,dier_id:action.p.dier_id||null,soort_id:action.p.soort_id||null,hoeveelheid:action.p.hoeveelheid||null,dagdeel:action.p.dagdeel||null});
        break;
      }
      case "DEL_VOEDING_DIER": { await db.deleteVoedingDier(action.p); break; }
      case "ADD_MED": { const nw=await db.addMedicatie({medicijn:action.p.medicijn,merknaam:action.p.merknaam||null,vorm:action.p.vorm,toediening:action.p.toediening||null,foto:action.p.foto||null,link:action.p.link||null}); if(nw) action.p={...nw}; break; }
      case "EDIT_MED": { await db.updateMedicatie(action.p.id, {medicijn:action.p.medicijn,merknaam:action.p.merknaam||null,vorm:action.p.vorm,toediening:action.p.toediening||null,foto:action.p.foto||null,link:action.p.link||null}); break; }
      case "DEL_MED": { await db.deleteMedicatie(action.p); break; }
      case "ADD_LEVERANCIER": { 
        const nw=await db.addLeverancier({naam:action.p.naam,contact_id:action.p.contact_id||null,foto:action.p.foto||null,website:action.p.website||null,park_id:pid}); 
        if(nw) action.p={...nw}; 
        const leveranciers=await db.getLeveranciers(pid);
        d({type:"LOAD_LEVERANCIERS",p:leveranciers});
        break; 
      }
      case "EDIT_LEVERANCIER": { 
        await db.updateLeverancier(action.p.id, {naam:action.p.naam,contact_id:action.p.contact_id||null,foto:action.p.foto||null,website:action.p.website||null}); 
        const leveranciers=await db.getLeveranciers(pid);
        d({type:"LOAD_LEVERANCIERS",p:leveranciers});
        break; 
      }
      case "DEL_LEVERANCIER": { 
        await db.deleteLeverancier(action.p); 
        const leveranciers=await db.getLeveranciers(pid);
        d({type:"LOAD_LEVERANCIERS",p:leveranciers});
        break; 
      }
      case "ADD_BESTELLING": { 
        const nw=await db.addBestelling({datum:action.p.datum,leverancier_id:action.p.leverancier_id,voeding_id:action.p.voeding_id,amount:action.p.amount||null,park_id:pid}); 
        if(nw) action.p={...nw}; 
        const bestellingen=await db.getBestellingen(pid);
        d({type:"LOAD_BESTELLINGEN",p:bestellingen});
        break; 
      }
      case "EDIT_BESTELLING": { 
        await db.updateBestelling(action.p.id, {datum:action.p.datum,leverancier_id:action.p.leverancier_id,voeding_id:action.p.voeding_id,amount:action.p.amount||null}); 
        const bestellingen=await db.getBestellingen(pid);
        d({type:"LOAD_BESTELLINGEN",p:bestellingen});
        break; 
      }
      case "DEL_BESTELLING": { 
        await db.deleteBestelling(action.p); 
        const bestellingen=await db.getBestellingen(pid);
        d({type:"LOAD_BESTELLINGEN",p:bestellingen});
        break; 
      }
      case "ADD_TASK": { 
        const nw=await db.addTaak({titel:action.p.titel,cat:action.p.cat,prio:action.p.prio,beschrijving:action.p.desc,dagen:action.p.dagen||[],herhalend:action.p.herhalend||false,frequentie:action.p.frequentie||"wekelijks",datum:action.p.datum||null,maand_dag:action.p.maand_dag||null,park_id:pid}); 
        if(nw) {
          action.p={...nw,desc:nw.beschrijving};
          // Save toewijzingen
          if(action.p.toewijzingen?.length>0){
            await db.setTaakToewijzingen(nw.id, pid, action.p.toewijzingen);
            const tt=await db.getTakenToewijzingen(pid);
            d({type:"LOAD_TOEWIJZINGEN",p:tt});
          }
        }
        break; 
      }
      case "EDIT_TASK": { 
        await db.updateTaak(action.p.id, {titel:action.p.titel,cat:action.p.cat,prio:action.p.prio,beschrijving:action.p.desc,dagen:action.p.dagen||[],herhalend:action.p.herhalend||false,frequentie:action.p.frequentie||"wekelijks",datum:action.p.datum||null,maand_dag:action.p.maand_dag||null}); 
        // Update toewijzingen
        if(action.p.toewijzingen!==undefined){
          await db.setTaakToewijzingen(action.p.id, pid, action.p.toewijzingen||[]);
          const tt=await db.getTakenToewijzingen(pid);
          d({type:"LOAD_TOEWIJZINGEN",p:tt});
        }
        break; 
      }
      case "DEL_TASK": { await db.deleteTaakToewijzingenByTaak(action.p); await db.deleteTaak(action.p); break; }
      case "ADD_AGENDA": { const nw=await db.addAgendaItem({titel:action.p.titel,datum:action.p.datum,tijd:action.p.tijd,type:action.p.type,beschrijving:action.p.desc,park_id:pid}); if(nw) action.p={...nw,desc:nw.beschrijving}; break; }
      case "EDIT_AGENDA": { await db.updateAgendaItem(action.p.id, {titel:action.p.titel,datum:action.p.datum,tijd:action.p.tijd,type:action.p.type,beschrijving:action.p.desc}); break; }
      case "DEL_AGENDA": { await db.deleteAgendaItem(action.p); break; }
      case "ADD_MSG": { await db.addBericht({auteur:s.user.name,tekst:action.p.tekst,prio:action.p.prio,cat:action.p.cat,park_id:pid}); break; }
      case "DEL_MSG": { await db.deleteBericht(action.p); break; }
      case "ADD_ANIMAL": { 
        console.log("🐑 ADD_ANIMAL handler START", action.p);
        console.log("📍 Park ID (pid):", pid);
        try {
          const soortNaam = action.p.soort || soorten.find(s=>s.id===action.p.soort_id)?.naam || null;
          const newAnimal = await db.addDier({
            naam: action.p.name,
            soort: soortNaam,
            soort_id: action.p.soort_id || null,
            ras_id: action.p.ras_id || null,
            geslacht: action.p.geslacht || "vrouw",
            geboortedatum: action.p.geboortedatum || null,
            park_datum: action.p.park_datum || null,
            locatie: action.p.locatie,
            oornummer: action.p.oornummer || null,
            chipnummer: action.p.chipnummer || null,
            herkomst: action.p.herkomst || null,
            gewicht: action.p.gewicht != null && action.p.gewicht !== "" ? parseFloat(action.p.gewicht) : null,
            is_gecastreerd: action.p.geslacht==="man" ? (action.p.is_gecastreerd||false) : false,
            notitie: action.p.notitie,
            datum_overlijden: action.p.datum_overlijden || null,
            datum_rendac: action.p.datum_rendac || null,
            fotos: [],
            park_id: pid ? pid.toString() : null  // ✅ Convert to string
          }); 
          console.log("✅ db.addDier() returned:", newAnimal);
          if(newAnimal) {
            console.log("🐑 Dier succesvol toegevoegd met ID:", newAnimal.id);
            // Koppel aan verblijf als opgegeven
            if(action.p.verblijf_id && newAnimal.id) {
              try { await db.addBewoner({verblijf_id:action.p.verblijf_id, dier_id:newAnimal.id, volgorde:0}); }
              catch(e) { console.warn("Verblijf koppelen mislukt:", e.message); }
            }
            d({
              type: "ADD_ANIMAL_SUCCESS",
              p: { ...newAnimal, name: newAnimal.naam }
            });
          } else {
            console.error("❌ db.addDier() returned nothing!");
            toast.err("Fout: dier kon niet worden toegevoegd");
          }
        } catch(err) {
          console.error("❌ ADD_ANIMAL error:", err.message);
          toast.err("Fout bij toevoegen dier: " + (err.message || "Onbekende fout"));
        }
        break; 
      }
      case "ADD_ANIMAL_SUCCESS": {
        console.log("🐑 ADD_ANIMAL_SUCCESS - updating state with new animal:", action.p);
        return {
          ...s,
          animals: [...s.animals, {
            id: action.p.id,
            name: action.p.name,
            naam: action.p.naam,
            soort: action.p.soort,
            geslacht: action.p.geslacht,
            geboortedatum: action.p.geboortedatum,
            park_datum: action.p.park_datum,
            locatie: action.p.locatie,
            notitie: action.p.notitie,
            fotos: action.p.fotos || [],
            park_id: action.p.park_id,
            created_at: action.p.created_at
          }],
          modal: null,
          editItem: null
        };
      }
      case "EDIT_ANIMAL": { 
        console.log("🐑 EDIT_ANIMAL handler START", action.p);
        const soortNaamEdit = action.p.soort || soorten.find(s=>s.id===action.p.soort_id)?.naam || null;
        await db.updateDier(action.p.id, {
          naam: action.p.name,
          soort: soortNaamEdit,
          soort_id: action.p.soort_id || null,
          ras_id: action.p.ras_id || null,
          geslacht: action.p.geslacht || "vrouw",
          geboortedatum: action.p.geboortedatum || null,
          park_datum: action.p.park_datum || null,
          locatie: action.p.locatie,
          oornummer: action.p.oornummer || null,
          chipnummer: action.p.chipnummer || null,
          herkomst: action.p.herkomst || null,
          gewicht: action.p.gewicht != null && action.p.gewicht !== "" ? parseFloat(action.p.gewicht) : null,
          is_gecastreerd: action.p.geslacht==="man" ? (action.p.is_gecastreerd||false) : false,
          notitie: action.p.notitie,
          datum_overlijden: action.p.datum_overlijden || null,
          datum_rendac: action.p.datum_rendac || null,
          actief: action.p.datum_overlijden ? false : true,
          fotos: action.p.fotos || []
        });
        console.log("✅ Dier bijgewerkt met ID:", action.p.id);
        // Verblijf bijwerken: alleen als verblijf_id veld aanwezig was in formulier (niet undefined)
        if(action.p.verblijf_id !== undefined) {
          try {
            await db.verwijderDierUitVerblijven(action.p.id);
            if(action.p.verblijf_id) {
              await db.addBewoner({verblijf_id:action.p.verblijf_id, dier_id:action.p.id, volgorde:0});
            }
          } catch(e) { console.warn("Verblijf bijwerken mislukt:", e.message); }
        }
        break;
      }
      case "DEL_ANIMAL": { 
        // Get animal with fotos before deleting
        const animal = s.animals.find(x => x.id === action.p);
        if(animal && animal.fotos && animal.fotos.length > 0) {
          // Delete all fotos from storage
          try {
            // Get token from Supabase directly
            const { data: { session } } = await sb.auth.getSession();
            const token = session?.access_token;
            
            if(token) {
              const tokenParts = token.split('.');
              const payload = JSON.parse(atob(tokenParts[1]));
              const userId = payload.sub;
              
              for(const fotoURL of animal.fotos) {
                if(fotoURL.includes("DierenBucket")) {
                  const filename = fotoURL.split("/").pop();
                  await fetch(`${SUPABASE_URL}/functions/v1/upload-animal-photo`,{
                    method:"DELETE",
                    headers:{
                      "x-user-id":userId,
                      "Authorization":`Bearer ${token}`,
                      "apikey":SUPABASE_KEY,
                      "Content-Type":"application/json"
                    },
                    body:JSON.stringify({filename,animalId:action.p})
                  });
                }
              }
            }
          } catch(err) {
            console.error("Error deleting photos:", err);
          }
        }
        // Now delete the animal from database
        await db.deleteDier(action.p); 
        break; 
      }
      case "ADD_PHOTO": { 
        console.log("📸 ADD_PHOTO handler START", {animalId: action.p.id, fotos: action.p.fotos});
        const a=s.animals.find(x=>x.id===action.p.id); 
        if(a) {
          console.log("🐑 Found animal:", a.name, "ID:", a.id);
          const newFotos = [...(a.fotos||[]),...action.p.fotos];
          console.log("📸 Saving fotos to DB:", newFotos);
          await db.updateDier(a.id,{fotos: newFotos});
          console.log("✅ Fotos saved to DB for animal ID:", a.id);
          d({
            type: "UPDATE_ANIMAL_STATE",
            p: {
              id: a.id,
              fotos: newFotos
            }
          });
        } else {
          console.error("❌ Animal not found for ID:", action.p.id);
          toast.err("Fout: dier niet gevonden");
        }
        break; 
      }
      case "UPDATE_ANIMAL_STATE": {
        console.log("🐑 UPDATE_ANIMAL_STATE:", action.p);
        return {
          ...s,
          animals: s.animals.map(animal =>
            animal.id === action.p.id
              ? { ...animal, fotos: action.p.fotos }
              : animal
          )
        };
      }
      case "DEL_PHOTO": { 
        const a=s.animals.find(x=>x.id===action.p.animalId); 
        if(a){ 
          const fotoURL=a.fotos[action.p.idx];
          // Delete via Edge Function if it's a Supabase URL
          if(fotoURL&&fotoURL.includes("DierenBucket")){
            try{
              // Get token from Supabase directly
              const { data: { session } } = await sb.auth.getSession();
              const token = session?.access_token;
              
              if(token) {
                const tokenParts=token.split('.');
                const payload=JSON.parse(atob(tokenParts[1]));
                const userId=payload.sub;
                
                const filename=fotoURL.split("/").pop();
                const res=await fetch(`${SUPABASE_URL}/functions/v1/upload-animal-photo`,{
                  method:"DELETE",
                  headers:{
                    "x-user-id":userId,
                    "Authorization":`Bearer ${token}`,
                    "apikey":SUPABASE_KEY,
                    "Content-Type":"application/json"
                  },
                  body:JSON.stringify({filename,animalId:action.p.animalId})
                });
                
                if(res.ok){
                  // Delete successful - update database fotos array
                  const newFotos=a.fotos.filter((_,i)=>i!==action.p.idx);
                  await db.updateDier(a.id,{fotos:newFotos});
                  console.log("Photo deleted from storage and database!");
                }
              }
            }catch(err){
              console.error("Error deleting from storage:",err);
            }
          }else{
            // Non-Supabase URL - just remove from database
            const newFotos=a.fotos.filter((_,i)=>i!==action.p.idx);
            await db.updateDier(a.id,{fotos:newFotos});
          }
        } 
        break; 
      }
      case "ADD_CONTACT": { 
        const nw = await db.addContact({...action.p, park_id:pid}); 
        if(nw) action.p = nw; 
        break; 
      }
      case "EDIT_CONTACT": { 
        await db.updateContact(action.p.id, action.p); 
        break; 
      }
      case "DEL_CONTACT": { await db.deleteContact(action.p); break; }
      case "ADD_PLANNING": {
        const {herhaling,herhaalTot,...base}=action.p;
        if(!base.naam||!base.datum||!base.shift) { console.warn("Planning: naam, datum of shift ontbreekt", base); break; }
        const dates=genRecurDates(base.datum,herhaling,herhaalTot||"");
        const saved=[];
        const reeksId=(herhaling&&herhaling!=="geen")?`reeks_${Date.now()}`:null;
        for(const datum of dates){
          const insertData={naam:base.naam,datum,shift:base.shift,notitie:base.notitie||"",park_id:pid,herhaling:herhaling||"geen"};
          if(base.userId) insertData.gebruiker_id=Number(base.userId);
          if(base.contactId) insertData.contact_id=Number(base.contactId);
          if(reeksId) insertData.reeks_id=reeksId;
          const nw=await db.addPlanning(insertData);
          if(nw) saved.push({...nw,userId:nw.gebruiker_id,contactId:nw.contact_id,naam:nw.naam,herhaling:nw.herhaling||"geen",datum:(nw.datum||'').split('T')[0],reeks_id:reeksId});
        }
        action.p={items:saved,herhaling};
        break;
      }
      case "DEL_PLANNING": { await db.deletePlanning(action.p); break; }
      case "DEL_REEKS": {
        // Delete all items in a reeks
        const item=s.planning.find(p=>p.id===action.p);
        if(item?.reeks_id) await sb.from("planning").delete().eq("reeks_id",item.reeks_id).eq("park_id",pid);
        break;
      }
      case "EDIT_PLANNING": { await db.updatePlanning(action.p.id,{naam:action.p.naam,datum:action.p.datum,shift:action.p.shift,notitie:action.p.notitie||"",gebruiker_id:action.p.userId||null,contact_id:action.p.contactId||null}); break; }
      case "EDIT_REEKS": {
        // Update all future items in a reeks
        const item=s.planning.find(p=>p.id===action.p.id);
        if(item?.reeks_id){
          const allInReeks=s.planning.filter(p=>p.reeks_id===item.reeks_id&&p.datum>=item.datum);
          for(const pl of allInReeks){
            await db.updatePlanning(pl.id,{naam:action.p.naam,shift:action.p.shift,notitie:action.p.notitie||"",gebruiker_id:action.p.userId||null,contact_id:action.p.contactId||null});
          }
        }
        break;
      }
      case "SET_ROLE": { await db.updateGebruiker(action.p.id, {role:action.p.role}); break; }
      case "TOG_ACTIVE": { const u=s.users.find(x=>x.id===action.p); if(u) await db.updateGebruiker(u.id,{actief:!u.actief}); break; }
      case "ADD_USER": { /* gebruiker al aangemaakt door Edge Function */ break; }
      case "DEL_USER": {
        const u=s.users.find(x=>x.id===action.p);
        if(u){
          const token=await db.getToken();
          await fetch(`${SUPABASE_URL}/functions/v1/invite-user`,{
            method:"DELETE",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
            body:JSON.stringify({gebruiker_id:u.id, auth_id:u.auth_id||null})
          });
        }
        break;
      }
      // Vaste taken
      case "ADD_VASTE_TAAK": { const nw=await db.addVasteTaak({...action.p,park_id:pid}); if(nw) action.p=nw; break; }
      case "DEL_VASTE_TAAK": { await db.deleteVasteTaak(action.p); break; }
      case "TOG_CHECK": {
        const today=new Date().toISOString().split("T")[0];
        const existing=s.checks.find(c=>c.vaste_taak_id===action.p.taakId&&c.dagdeel===action.p.dagdeel);
        const gedaan=existing?!existing.gedaan:true;
        const nw=await db.upsertCheck({park_id:pid,vaste_taak_id:action.p.taakId,datum:today,dagdeel:action.p.dagdeel,gedaan,gedaan_door:gedaan?s.user.name:null,gedaan_op:gedaan?new Date().toISOString():null});
        if(nw) action.p={...action.p, check:nw};
        break;
      }
      case "TOG_TAAK_CHECK": {
        const today=new Date().toISOString().split("T")[0];
        const existing=s.takenChecks.find(c=>c.taak_id===action.p.taakId);
        const gedaan=existing?!existing.gedaan:true;
        const nw=await db.upsertTaakCheck({park_id:pid,taak_id:action.p.taakId,datum:today,gedaan,gedaan_door:gedaan?s.user.name:null,gedaan_op:gedaan?new Date().toISOString():null});
        if(nw) action.p={...action.p, check:nw};
        break;
      }
      case "TOG_MAALTIJD_CHECK": {
        const today=new Date().toISOString().split("T")[0];
        const existing=s.maaltijdChecks.find(c=>c.group_key===action.p.groupKey&&c.dagdeel===action.p.dagdeel);
        const gedaan=existing?!existing.gedaan:true;
        const nw=await db.upsertMaaltijdCheck({park_id:pid,group_key:action.p.groupKey,dagdeel:action.p.dagdeel,datum:today,gedaan,gedaan_door:gedaan?s.user.name:null,gedaan_op:gedaan?new Date().toISOString():null});
        if(nw) action.p={...action.p, check:nw};
        break;
      }
      case "ADD_LOGBOEK": {
        const today=new Date().toISOString().split("T")[0];
        const nw=await db.addLogboek({park_id:pid,datum:today,dagdeel:action.p.dagdeel,auteur:s.user.name,gebruiker_id:s.user.id,tekst:action.p.tekst,foto:action.p.foto||null});
        if(nw) action.p=nw;
        break;
      }
      case "DEL_LOGBOEK": {
        await db.delLogboek(action.p);
        break;
      }
      case "ADD_DIERENADVIES": {
        await db.addDierenadvies({
          park_id: pid,
          scope_type: action.p.scope_type,
          soort_id: action.p.scope_type === "soort" ? (action.p.soort_id != null ? parseInt(action.p.soort_id) : null) : (action.p.soort_id != null ? parseInt(action.p.soort_id) : null),
          dier_id: action.p.scope_type === "dier" ? (action.p.dier_id != null ? parseInt(action.p.dier_id) : null) : null,
          titel: action.p.titel,
          advies: action.p.advies,
          prioriteit: action.p.prioriteit || "normaal",
          actief: action.p.actief !== false,
        });
        const advies = await db.getDierenadvies(pid);
        d({type:"LOAD_DIERENADVIES", p: advies});
        return;
      }
      case "EDIT_DIERENADVIES": {
        await db.updateDierenadvies(action.p.id, {
          titel: action.p.titel,
          advies: action.p.advies,
          prioriteit: action.p.prioriteit || "normaal",
          actief: action.p.actief !== false,
        });
        const advies = await db.getDierenadvies(pid);
        d({type:"LOAD_DIERENADVIES", p: advies});
        return;
      }
      case "DEL_DIERENADVIES": {
        await db.deleteDierenadvies(action.p);
        const advies = await db.getDierenadvies(pid);
        d({type:"LOAD_DIERENADVIES", p: advies});
        return;
      }
      // Waarschuwingen
      case "ADD_WAARSCHUWING": { const nw=await db.addWaarschuwing({...action.p,park_id:pid,aangemaakt_door:s.user.name}); if(nw) action.p=nw; break; }
      case "DEL_WAARSCHUWING": { await db.deactiveerWaarschuwing(action.p); break; }
      // Protocollen
      case "ADD_PROTOCOL": { const np=await db.addProtocol({...action.p,park_id:pid}); if(np) action.p=np; break; }
      case "EDIT_PROTOCOL": { const up=await db.updateProtocol(action.p.id,action.p); if(up) action.p=up; break; }
      case "DEL_PROTOCOL": { await db.deleteProtocol(action.p); break; }
      case "ADD_RAS": { const nr=await db.addRas(action.p); if(nr) action.p=nr; break; }
      case "EDIT_RAS": { const ur=await db.updateRas(action.p.id,{ras_naam:action.p.ras_naam,soort_id:action.p.soort_id}); if(ur) action.p=ur; break; }
      case "DEL_RAS": { await db.deleteRas(action.p); break; }
      case "ADD_GEZONDHEID": {
        console.log("🏥 ADD_GEZONDHEID handler START", action.p);
        const gezInsert = {
          park_id: action.p.park_id || pid,
          datum: action.p.datum,
          diagnoses: action.p.diagnoses,
          beschrijving: action.p.beschrijving,
          status: action.p.status,
        };
        if(action.p.dier_id) gezInsert.dier_id = parseInt(action.p.dier_id);
        if(action.p.soort_id) gezInsert.soort_id = parseInt(action.p.soort_id);
        if(action.p.veterinarian_id) gezInsert.veterinarian_id = parseInt(action.p.veterinarian_id);
        if(action.p.verzorger_id) gezInsert.verzorger_id = parseInt(action.p.verzorger_id);
        const newRecord = await db.addGezondheid(gezInsert);
        if (newRecord && action.p.medicatie && action.p.medicatie.length > 0) {
          for (const med of action.p.medicatie) {
            await db.addGezondheidsMedicatie(newRecord.id, parseInt(med.medicatie_id), med.dosering, med.freq, med.eind || null);
          }
        }
        if (newRecord) {
          const meds = await db.getGezondheidsMedicaties(newRecord.id);
          d({type: "ADD_GEZONDHEID_SUCCESS", p: {...newRecord, gezondheid_medicatie: meds}});
        }
        console.log("✅ Gezondheidsrecord created:", newRecord?.id);
        break;
      }
      case "EDIT_GEZONDHEID": {
        console.log("🏥 EDIT_GEZONDHEID handler START", action.p);
        const gezUpdate = {
          datum: action.p.datum,
          diagnoses: action.p.diagnoses,
          beschrijving: action.p.beschrijving,
          status: action.p.status,
        };
        if(action.p.soort_id) gezUpdate.soort_id = parseInt(action.p.soort_id);
        else gezUpdate.soort_id = null;
        if(action.p.veterinarian_id) gezUpdate.veterinarian_id = parseInt(action.p.veterinarian_id);
        else gezUpdate.veterinarian_id = null;
        if(action.p.verzorger_id) gezUpdate.verzorger_id = parseInt(action.p.verzorger_id);
        else gezUpdate.verzorger_id = null;
        await db.updateGezondheid(action.p.id, gezUpdate);
        if (action.p.medicatie) {
          const existing = await db.getGezondheidsMedicaties(action.p.id);
          const existingIds = existing.map(e => e.medicatie_id);
          const newMeds = action.p.medicatie.map(m => parseInt(m.medicatie_id));
          
          // Delete removed medications
          for (const existId of existingIds) {
            if (!newMeds.includes(existId)) {
              await db.deleteGezondheidsMedicatie(action.p.id, existId);
            }
          }
          
          // Add or update new medications
          for (const med of action.p.medicatie) {
            const medId = parseInt(med.medicatie_id);
            if (!existingIds.includes(medId)) {
              await db.addGezondheidsMedicatie(action.p.id, medId, med.dosering, med.freq, med.eind || null);
            }
          }
        }
        const updated = await db.getGezondheid(park.id);
        const meds = await db.getGezondheidsMedicaties(action.p.id);
        const record = updated.find(g => g.id === action.p.id);
        if (record) d({type: "EDIT_GEZONDHEID_SUCCESS", p: {...record, gezondheid_medicatie: meds}});
        console.log("✅ Gezondheidsrecord updated");
        break;
      }
      case "DEL_GEZONDHEID": {
        console.log("🏥 DEL_GEZONDHEID handler START", action.p);
        await db.deleteGezondheid(action.p);
        d({type: "DEL_GEZONDHEID", p: action.p});
        console.log("✅ Gezondheidsrecord deleted");
        break;
      }
      default: break;
    }

    // ── Audit logging ──────────────────────────────────────────
    const AUDIT_MAP = {
      ADD_FEED:          p => ({actie:"INSERT", tabel:"voeding",        omschrijving:`Voeding toegevoegd: ${p.type||""} ${p.merk||""}`}),
      EDIT_FEED:         p => ({actie:"UPDATE", tabel:"voeding",        omschrijving:`Voeding gewijzigd: ${p.type||""} ${p.merk||""}`,      record_id:p.id}),
      DEL_FEED:          p => ({actie:"DELETE", tabel:"voeding",        omschrijving:`Voeding verwijderd`,                                   record_id:p}),
      ADD_VOEDING_DIER:  p => ({actie:"INSERT", tabel:"voeding_dieren", omschrijving:`Voedingskoppeling toegevoegd`}),
      EDIT_VOEDING_DIER: p => ({actie:"UPDATE", tabel:"voeding_dieren", omschrijving:`Voedingskoppeling gewijzigd`,                          record_id:p.id}),
      DEL_VOEDING_DIER:  p => ({actie:"DELETE", tabel:"voeding_dieren", omschrijving:`Voedingskoppeling verwijderd`,                         record_id:p}),
      ADD_MED:           p => ({actie:"INSERT", tabel:"medicatie",      omschrijving:`Medicatie toegevoegd: ${p.medicijn||""}`}),
      EDIT_MED:          p => ({actie:"UPDATE", tabel:"medicatie",      omschrijving:`Medicatie gewijzigd: ${p.medicijn||""}`,               record_id:p.id}),
      DEL_MED:           p => ({actie:"DELETE", tabel:"medicatie",      omschrijving:`Medicatie verwijderd`,                                 record_id:p}),
      TOG_MED:           p => ({actie:"UPDATE", tabel:"medicatie",      omschrijving:`Medicatie afgevinkt`,                                  record_id:p}),
      ADD_GEZONDHEID:    p => ({actie:"INSERT", tabel:"gezondheid",     omschrijving:`Gezondheidsrecord toegevoegd: ${p.titel||p.type||""}`}),
      EDIT_GEZONDHEID:   p => ({actie:"UPDATE", tabel:"gezondheid",     omschrijving:`Gezondheidsrecord gewijzigd`,                          record_id:p.id}),
      DEL_GEZONDHEID:    p => ({actie:"DELETE", tabel:"gezondheid",     omschrijving:`Gezondheidsrecord verwijderd`,                         record_id:p}),
      ADD_ANIMAL:        p => ({actie:"INSERT", tabel:"dieren",         omschrijving:`Dier toegevoegd: ${p.naam||p.name||""}`}),
      EDIT_ANIMAL:       p => ({actie:"UPDATE", tabel:"dieren",         omschrijving:`Dier gewijzigd: ${p.naam||p.name||""}`,               record_id:p.id}),
      DEL_ANIMAL:        p => ({actie:"DELETE", tabel:"dieren",         omschrijving:`Dier verwijderd`,                                      record_id:p}),
      ADD_PHOTO:         p => ({actie:"INSERT", tabel:"dieren",         omschrijving:`Foto toegevoegd`,                                      record_id:p.animalId}),
      DEL_PHOTO:         p => ({actie:"DELETE", tabel:"dieren",         omschrijving:`Foto verwijderd`,                                      record_id:p.animalId}),
      ADD_TASK:          p => ({actie:"INSERT", tabel:"taken",          omschrijving:`Taak toegevoegd: ${p.titel||""}`}),
      EDIT_TASK:         p => ({actie:"UPDATE", tabel:"taken",          omschrijving:`Taak gewijzigd: ${p.titel||""}`,                      record_id:p.id}),
      DEL_TASK:          p => ({actie:"DELETE", tabel:"taken",          omschrijving:`Taak verwijderd`,                                      record_id:p}),
      ADD_VASTE_TAAK:    p => ({actie:"INSERT", tabel:"vaste_taken",    omschrijving:`Vaste taak toegevoegd: ${p.naam||""}`}),
      DEL_VASTE_TAAK:    p => ({actie:"DELETE", tabel:"vaste_taken",    omschrijving:`Vaste taak verwijderd`,                                record_id:p}),
      ADD_AGENDA:        p => ({actie:"INSERT", tabel:"agenda",         omschrijving:`Agendaitem toegevoegd: ${p.titel||""}`}),
      EDIT_AGENDA:       p => ({actie:"UPDATE", tabel:"agenda",         omschrijving:`Agendaitem gewijzigd: ${p.titel||""}`,                record_id:p.id}),
      DEL_AGENDA:        p => ({actie:"DELETE", tabel:"agenda",         omschrijving:`Agendaitem verwijderd`,                                record_id:p}),
      ADD_MSG:           p => ({actie:"INSERT", tabel:"berichten",      omschrijving:`Bericht geplaatst`}),
      DEL_MSG:           p => ({actie:"DELETE", tabel:"berichten",      omschrijving:`Bericht verwijderd`,                                   record_id:p}),
      ADD_CONTACT:       p => ({actie:"INSERT", tabel:"contacten",      omschrijving:`Contact toegevoegd: ${p.naam||""}`}),
      EDIT_CONTACT:      p => ({actie:"UPDATE", tabel:"contacten",      omschrijving:`Contact gewijzigd: ${p.naam||""}`,                    record_id:p.id}),
      DEL_CONTACT:       p => ({actie:"DELETE", tabel:"contacten",      omschrijving:`Contact verwijderd`,                                   record_id:p}),
      ADD_PLANNING:      p => ({actie:"INSERT", tabel:"planning",       omschrijving:`Dienst toegevoegd: ${p.naam||""}`}),
      EDIT_PLANNING:     p => ({actie:"UPDATE", tabel:"planning",       omschrijving:`Dienst gewijzigd: ${p.naam||""}`,                     record_id:p.id}),
      EDIT_REEKS:        p => ({actie:"UPDATE", tabel:"planning",       omschrijving:`Dienstrooster reeks gewijzigd`,                        record_id:p.id}),
      DEL_PLANNING:      p => ({actie:"DELETE", tabel:"planning",       omschrijving:`Dienst verwijderd`,                                    record_id:p}),
      DEL_REEKS:         p => ({actie:"DELETE", tabel:"planning",       omschrijving:`Dienstrooster reeks verwijderd`,                       record_id:p}),
      SET_ROLE:          p => ({actie:"UPDATE", tabel:"gebruikers",     omschrijving:`Rol gewijzigd naar: ${p.role}`,                        record_id:p.id}),
      TOG_ACTIVE:        p => ({actie:"UPDATE", tabel:"gebruikers",     omschrijving:`Actief-status gewijzigd`,                              record_id:p}),
      ADD_USER:          p => ({actie:"INSERT", tabel:"gebruikers",     omschrijving:`Gebruiker uitgenodigd: ${p.naam||p.name||""}`}),
      DEL_USER:          p => ({actie:"DELETE", tabel:"gebruikers",     omschrijving:`Gebruiker verwijderd`,                                 record_id:p}),
      ADD_LEVERANCIER:   p => ({actie:"INSERT", tabel:"leveranciers",   omschrijving:`Leverancier toegevoegd: ${p.naam||""}`}),
      EDIT_LEVERANCIER:  p => ({actie:"UPDATE", tabel:"leveranciers",   omschrijving:`Leverancier gewijzigd: ${p.naam||""}`,                record_id:p.id}),
      DEL_LEVERANCIER:   p => ({actie:"DELETE", tabel:"leveranciers",   omschrijving:`Leverancier verwijderd`,                               record_id:p}),
      ADD_BESTELLING:    p => ({actie:"INSERT", tabel:"bestellingen",   omschrijving:`Bestelling toegevoegd: ${p.naam||""}`}),
      EDIT_BESTELLING:   p => ({actie:"UPDATE", tabel:"bestellingen",   omschrijving:`Bestelling gewijzigd: ${p.naam||""}`,                 record_id:p.id}),
      DEL_BESTELLING:    p => ({actie:"DELETE", tabel:"bestellingen",   omschrijving:`Bestelling verwijderd`,                                record_id:p}),
      ADD_LOGBOEK:       p => ({actie:"INSERT", tabel:"dienst_logboek", omschrijving:`Logboekvermelding toegevoegd`}),
      DEL_LOGBOEK:       p => ({actie:"DELETE", tabel:"dienst_logboek", omschrijving:`Logboekvermelding verwijderd`,                         record_id:p}),
      ADD_DIERENADVIES:  p => ({actie:"INSERT", tabel:"dierenadvies",   omschrijving:`Dierenadvies toegevoegd`}),
      EDIT_DIERENADVIES: p => ({actie:"UPDATE", tabel:"dierenadvies",   omschrijving:`Dierenadvies gewijzigd`,                               record_id:p.id}),
      DEL_DIERENADVIES:  p => ({actie:"DELETE", tabel:"dierenadvies",   omschrijving:`Dierenadvies verwijderd`,                              record_id:p}),
      ADD_WAARSCHUWING:  p => ({actie:"INSERT", tabel:"waarschuwingen",  omschrijving:`Waarschuwing toegevoegd`}),
      DEL_WAARSCHUWING:  p => ({actie:"DELETE", tabel:"waarschuwingen",  omschrijving:`Waarschuwing verwijderd`,                              record_id:p}),
      ADD_PROTOCOL:      p => ({actie:"INSERT", tabel:"protocollen",    omschrijving:`Protocol toegevoegd: ${p.naam||""}`}),
      EDIT_PROTOCOL:     p => ({actie:"UPDATE", tabel:"protocollen",    omschrijving:`Protocol gewijzigd: ${p.naam||""}`,                   record_id:p.id}),
      DEL_PROTOCOL:      p => ({actie:"DELETE", tabel:"protocollen",    omschrijving:`Protocol verwijderd`,                                  record_id:p}),
      ADD_RAS:           p => ({actie:"INSERT", tabel:"rassen",         omschrijving:`Ras toegevoegd: ${p.ras_naam||""}`}),
      EDIT_RAS:          p => ({actie:"UPDATE", tabel:"rassen",         omschrijving:`Ras gewijzigd: ${p.ras_naam||""}`,                    record_id:p.id}),
      DEL_RAS:           p => ({actie:"DELETE", tabel:"rassen",         omschrijving:`Ras verwijderd`,                                       record_id:p}),
    };
    const auditDef = AUDIT_MAP[action.type];
    if(auditDef){
      try {
        const {actie,tabel,omschrijving,record_id} = auditDef(action.p);
        await db.addAuditLog({
          park_id: String(pid),
          gebruiker_id: s.user?.id||null,
          gebruiker_naam: s.user?.name||s.user?.naam||"onbekend",
          actie, tabel,
          record_id: record_id!=null?String(record_id):null,
          omschrijving,
        });
      } catch(e){ console.warn("Audit log mislukt:",e.message); }
    }
    // ── Einde audit logging ─────────────────────────────────────

    d(action);
    } catch(err) {
      console.error("❌ Dispatch error for action:", action.type, err);
      toast.err(`Fout: ${err.message || 'Onbekende fout'}`);
    }
  }, [s, park.id]);

  if(dbLoading) return <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--cream)",flexDirection:"column",gap:12}}><style>{CSS}</style><div style={{fontSize:40}}>🐾</div><div style={{fontFamily:"'Fraunces',serif",fontSize:18,color:"var(--g)"}}>Park laden...</div>{loadTimeout&&<div style={{fontSize:13,color:"var(--txtl)",marginTop:12,textAlign:"center"}}><p>Dit duurt langer dan normaal...</p><button style={{marginTop:8,padding:"8px 16px",background:"var(--g)",color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontWeight:600}} onClick={()=>window.location.reload()}>🔄 Opnieuw proberen</button></div>}</div>;

  const Header=()=><div className="hdr">
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <div className="hav" style={{background:rCol(s.user.role)}}>{s.user.name.split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:"var(--g)",lineHeight:1.2}}>{s.user.name.split(" ")[0]}</div>
          <div style={{fontSize:11,color:"var(--txtl)"}}>{rLab(s.user.role)}</div>
        </div>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <button className="hbtn" style={{fontSize:12,padding:"5px 10px"}} onClick={onWijzigWachtwoord} title="Wachtwoord wijzigen">🔑</button>
        <button className="hbtn" onClick={onExitPark}>{IC.logout} {isAppAdmin?"← Admin":"Uitloggen"}</button>
        {phase2Loading&&<div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--txtl)",padding:"3px 8px",background:"var(--cd)",borderRadius:20}}>
          <span style={{animation:"bounce 0.6s infinite alternate"}}>🐾</span>
          <span>Gegevens ophalen</span>
          <style>{`@keyframes bounce{from{transform:translateX(-2px)}to{transform:translateX(2px)}}`}</style>
        </div>}
      </div>
    </div>;

  /* ── Bottom Nav with "Meer" menu ── */
  const BNav=({soorten,setPhotoModal})=>{
    const getDagdeel=()=>{const h=new Date().getHours();return h<12?"ochtend":h<18?"middag":"avond";};
    const [huidigDagdeel,setHuidigDagdeel]=useState(getDagdeel);
    useEffect(()=>{const t=setInterval(()=>setHuidigDagdeel(getDagdeel()),60000);return()=>clearInterval(t);},[]);
    const vandaagBnav=new Date().toISOString().split("T")[0];
    const isProtActiefBnav=(pid)=>{if(!pid) return true; const p=(s.protocollen||[]).find(x=>x.id===pid); return p&&p.actief&&p.start_datum<=vandaagBnav&&(!p.eind_datum||p.eind_datum>=vandaagBnav);};
    const openChecks=s.vasteTaken.filter(t=>t.dagdeel===huidigDagdeel&&isProtActiefBnav(t.protocol_id)&&!s.checks.find(c=>c.vaste_taak_id===t.id&&c.gedaan)).length;
    const pf=s.feedings.filter(f=>!f.gedaan).length;
    const pm=s.meds.filter(m=>!m.gedaan).length;
    const ot=s.tasks.length;
    const canTeam=hasPerm(s.user,"gebruikers_beheren");
    const canBeheer=hasPerm(s.user,"taken_beheren");
    // Module-gebaseerde checks voor deelbeheer
    const canSeeDieren=        canBeheer||hasModuleLezen(s.user,"dieren_verblijven");
    const canBeheerDieren=     canBeheer||hasModuleBeheer(s.user,"dieren_verblijven");
    const canSeeGezondheid=    canBeheer||hasPerm(s.user,"medicatie_lezen")||hasModuleLezen(s.user,"gezondheid");
    const canSeeVoeding=       canBeheer||hasPerm(s.user,"voeding_lezen")||hasModuleLezen(s.user,"voeding");
    const canSeeMedicatie=     canBeheer||hasPerm(s.user,"medicatie_lezen")||hasModuleLezen(s.user,"gezondheid");
    const canSeeTaken=         canBeheer||hasPerm(s.user,"taken_lezen")||hasModuleLezen(s.user,"taken");
    const canSeeAgenda=        canBeheer||hasPerm(s.user,"agenda_lezen")||hasModuleLezen(s.user,"taken");
    const canSeeVasteTaken=    canBeheer||hasPerm(s.user,"taken_lezen")||hasModuleLezen(s.user,"taken");
    const canSeePlanning=      canBeheer||hasPerm(s.user,"planning_lezen")||hasModuleLezen(s.user,"planning");
    const canSeeLeveranciers=  canBeheer||hasPerm(s.user,"leveranciers_lezen")||hasModuleLezen(s.user,"bestellingen");
    const canSeeBestellingen=  canBeheer||hasPerm(s.user,"bestellingen_lezen")||hasModuleLezen(s.user,"bestellingen");
    const canSeeSleutels=      canBeheer||hasPerm(s.user,"sleutels_beheren")||hasModuleLezen(s.user,"sleutelbeheer");
    const canSeeLogboek=       canBeheer||hasPerm(s.user,"logboek_lezen");
    const canSeeVirtueelPark=  canBeheer||hasModuleLezen(s.user,"dieren_verblijven");
    const canSeeMedia=         canBeheer||hasPerm(s.user,"voeding_lezen");
    const canSeeFunctiegroepen=canBeheer;
    const canSeePrikbord=      canBeheer||hasModuleLezen(s.user,"prikbord")||hasPerm(s.user,"berichten_lezen");
    const meerGroups=[
      {label:"🐾 Dieren & Verzorging",items:[
        ...(canSeeDieren?[{id:"dieren",icon:IC.paw,label:"Dieren",color:"#4a7a2e"}]:[]),
        ...(canSeeVirtueelPark?[{id:"virtueelpark",icon:<span style={{fontSize:26,lineHeight:1}}>🏡</span>,label:"Virtueel Park",color:"#1f6b3d"}]:[]),
        ...(canSeeVoeding?[{id:"voeding",icon:IC.food,label:"Voeding",color:"#4a7a2e",b:pf}]:[]),
        ...(canSeeMedicatie?[{id:"medicatie",icon:IC.pill,label:"Medicatie",color:"#c62828",b:pm}]:[]),
        ...(canSeeGezondheid?[{id:"gezondheid",icon:IC.heart,label:"Gezondheid",color:"#c62828"}]:[]),
        ...(canSeePrikbord?[{id:"prikbord",icon:IC.prikbord,label:"Prikbord",color:"#e87c28"}]:[]),
      ]},
      {label:"📋 Taken & Rooster",items:[
        ...(canSeeAgenda?[{id:"agenda",icon:IC.cal,label:"Agenda",color:"#2e6da4"}]:[]),
        ...(canSeeTaken?[{id:"taken",icon:IC.tasks,label:"Taken",color:"#4a7a2e",b:ot}]:[]),
        ...(canSeeVasteTaken?[{id:"vastetaken",icon:IC.tasks,label:"Vaste taken",color:"#7c3aed"}]:[]),
      ]},
      {label:"🏢 Organisatie",items:[
        ...(canBeheer?[{id:"mijn_park",icon:IC.pin,label:"Mijn park",color:"#2d5016"}]:[]),
        ...(canTeam?[{id:"team",icon:IC.team,label:"Toegang",color:"#c62828"}]:[]),
        ...(hasPerm(s.user,"contacten_lezen")?[{id:"contacten",icon:IC.book,label:"Contacten",color:"#7c3aed"}]:[]),
        ...(canSeeFunctiegroepen?[{id:"functiegroepen",icon:IC.team,label:"Functiegroepen",color:"#c62828"}]:[]),
        ...(canSeeSleutels?[{id:"sleutels",icon:IC.pin,label:"Sleutels",color:"#c62828"}]:[]),
        ...(canBeheer?[{id:"protocollen",icon:<span style={{fontSize:20,lineHeight:1}}>📄</span>,label:"Protocollen",color:"#0f766e"}]:[]),
        ...(canSeeLogboek?[{id:"logboek",icon:IC.book,label:"Logboek",color:"#0f766e"}]:[]),
        {id:"directe_berichten",icon:IC.chat,label:"DM's",color:"#8b5cf6"},
        ...(canBeheer?[{id:"dataimport",icon:<span style={{fontSize:20,lineHeight:1}}>📊</span>,label:"Data",color:"#0369a1"}]:[]),
      ]},
      {label:"📦 Voorraad & Media",items:[
        ...(canSeeLeveranciers?[{id:"leveranciers",icon:IC.supplier,label:"Leveranciers",color:"#1f6b3d"}]:[]),
        ...(canSeeBestellingen?[{id:"bestellingen",icon:IC.orders,label:"Bestellingen",color:"#1f6b3d"}]:[]),
        ...(canSeeMedia?[{id:"media",icon:IC.cam,label:"Media",color:"#e97722"}]:[]),
      ]},
    ].filter(g=>g.items.length>0);
    const allMeerIds=meerGroups.flatMap(g=>g.items.map(i=>i.id));
    const meerPages=["dieren","virtueelpark","voeding","medicatie","agenda","directe_berichten","taken","contacten","team","vastetaken","sleutels","functiegroepen","leveranciers","bestellingen","logboek","gezondheid","media","prikbord","protocollen","mijn_park","dataimport"];
    const isMeerPage=meerPages.includes(s.page);
    return <>
      {s.meerOpen&&<div className="meer-overlay" onClick={()=>ds({type:"CLOSE_MEER"})}/>}
      {s.meerOpen&&<div className="meer-menu" style={{padding:"14px 12px",maxHeight:"75vh",overflowY:"auto"}}>
        {meerGroups.map((group,gi)=><div key={gi} style={{marginBottom:gi<meerGroups.length-1?10:0}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--txtl)",textTransform:"uppercase",letterSpacing:".5px",padding:"4px 8px",marginBottom:2}}>{group.label}</div>
          <div className="meer-grid">
            {group.items.map(i=><button key={i.id} className="meer-item" onClick={()=>ds({type:"NAV",p:i.id})}>
              <div style={{position:"relative"}}><div className="meer-ic" style={{background:i.color}}>{i.icon}</div>
              {i.b>0&&<span className="meer-bdg">{i.b}</span>}</div>{i.label}
            </button>)}
          </div>
        </div>)}
      </div>}
      <div className="bnav">
        <button className={`ni ${s.page==="logboek"?"a":""}`} onClick={()=>ds({type:"NAV",p:"logboek"})}>
          {openChecks>0&&<span className="nbg">{openChecks}</span>}{IC.home}Home
        </button>
        <button className={`ni ${s.page==="dienst"?"a":""}`} onClick={()=>ds({type:"NAV",p:"dienst"})}>
          {IC.tasks}Mijn dienst
        </button>
        <button className={`ni ${s.page==="planning"?"a":""}`} onClick={()=>ds({type:"NAV",p:"planning"})}>{IC.planning}Rooster</button>
        <button className={`ni ${isMeerPage&&!s.meerOpen?"a":""}`} onClick={()=>ds({type:"TOGGLE_MEER"})}>
          {(pf+pm)>0&&<span className="nbg">{pf+pm}</span>}{IC.meer}Meer
        </button>
      </div>
    </>;
  };

  /* ── Dashboard ── */
  const Dash=()=>{
    const pf=s.feedings.filter(f=>!f.gedaan).length,pm=s.meds.filter(m=>!m.gedaan).length;
    const ot=s.tasks.length,ut=s.tasks.filter(t=>t.prio==="hoog").length;
    const next=s.agenda[0];
    const getDagdeel=()=>{const h=new Date().getHours();return h<12?"ochtend":h<18?"middag":"avond";};
    const [huidigDagdeel,setHuidigDagdeel]=useState(getDagdeel);
    useEffect(()=>{const t=setInterval(()=>setHuidigDagdeel(getDagdeel()),60000);return()=>clearInterval(t);},[]);
    const dagdIcon={ochtend:"🌅",middag:"☀️",avond:"🌙"};
    const dagdColor={ochtend:"#e87c28",middag:"#2e6da4",avond:"#6b46c1"};
    const vandaag=new Date().toISOString().split("T")[0];
    const isProtActiefDash=(pid)=>{if(!pid) return true; const p=(s.protocollen||[]).find(x=>x.id===pid); return p&&p.actief&&p.start_datum<=vandaag&&(!p.eind_datum||p.eind_datum>=vandaag);};
    const takenVandaag=s.vasteTaken.filter(t=>t.dagdeel===huidigDagdeel&&isProtActiefDash(t.protocol_id));
    const gedaanVandaag=takenVandaag.filter(t=>s.checks.find(c=>c.vaste_taak_id===t.id&&c.datum===vandaag&&c.gedaan)).length;

    return <div className="pg">
      <span style={{fontSize:13,color:"var(--txtl)"}}>Welkom bij {park.naam},</span>
      <h1 className="pt" style={{marginBottom:0}}>{s.user.name.split(" ")[0]}</h1>
      <p style={{fontSize:12,color:"var(--txtl)",marginBottom:12}}>
        <span className="rbdg" style={{background:rCol(s.user.role)+"18",color:rCol(s.user.role)}}>{rLab(s.user.role)}</span>
        {" \u2022 "}{new Date().toLocaleDateString("nl-NL",{weekday:"long",day:"numeric",month:"long"})}
      </p>

      {/* Dienst samenvatting */}
      {takenVandaag.length>0&&<div onClick={()=>ds({type:"NAV",p:"dienst"})} style={{
        padding:"14px 16px",borderRadius:14,marginBottom:14,cursor:"pointer",
        background:`linear-gradient(135deg,${dagdColor[huidigDagdeel]},${dagdColor[huidigDagdeel]}cc)`,color:"#fff",
        boxShadow:`0 4px 12px ${dagdColor[huidigDagdeel]}44`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <span style={{fontWeight:700,fontSize:15}}>{dagdIcon[huidigDagdeel]} {huidigDagdeel.charAt(0).toUpperCase()+huidigDagdeel.slice(1)}dienst</span>
          <span style={{fontSize:14,opacity:.9}}>{gedaanVandaag}/{takenVandaag.length} klaar</span>
        </div>
        <div style={{height:6,background:"rgba(255,255,255,.3)",borderRadius:3,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${takenVandaag.length?(gedaanVandaag/takenVandaag.length)*100:0}%`,background:"#fff",borderRadius:3,transition:"width .3s"}}/>
        </div>
        <div style={{fontSize:12,opacity:.8,marginTop:6}}>Tik om je takenpagina te openen →</div>
      </div>}

      {/* Waarschuwingen */}
      {s.waarschuwingen.slice(0,2).map(w=><div key={w.id} style={{
        padding:"10px 14px",borderRadius:12,marginBottom:8,
        background:w.type==="dringend"?"#fff0f0":w.type==="waarschuwing"?"#fff8e6":"#e8f4ff",
        borderLeft:`4px solid ${w.type==="dringend"?"#c44536":w.type==="waarschuwing"?"#e87c28":"#2e6da4"}`,
      }}>
        <span style={{fontSize:14,fontWeight:600}}>{w.type==="dringend"?"🚨":w.type==="waarschuwing"?"⚠️":w.type==="goed_nieuws"?"💚":"ℹ️"} {w.tekst}</span>
      </div>)}

      <div className="ds">
        <div className={`sc ${pf>3?"al":""}`}><div className="sn">{pf}</div><div className="sl">Voedingen open</div></div>
        <div className={`sc ${pm>0?"al":""}`}><div className="sn">{pm}</div><div className="sl">Medicatie open</div></div>
        <div className="sc"><div className="sn">{ot}</div><div className="sl">Taken</div></div>
        <div className={`sc ${ut>0?"al":""}`}><div className="sn">{ut}</div><div className="sl">Hoge prio</div></div>
      </div>
      {s.msgs.filter(b=>b.prio==="dringend").slice(0,1).map(b=><div key={b.id} className="mc dringend" style={{marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontWeight:600,fontSize:13}}>{b.auteur}</span><span style={{fontSize:11,color:"var(--txtl)"}}>{b.datum}</span></div>
        <div style={{fontSize:14,lineHeight:1.5,marginTop:4}}>{b.tekst}</div>
      </div>)}
      {next&&<><div className="sh"><span className="st">Eerstvolgende</span><button className="slnk" onClick={()=>ds({type:"NAV",p:"agenda"})}>Alle →</button></div>
        <div className="ai"><div className="ad"><div className="add">{dayN(next.datum)}</div><div className="adm">{monS(next.datum)}</div></div>
        <div className={`atd type-${next.type}`}/><div className="ac"><div className="atn">{next.titel}</div><div className="atm">{next.tijd}</div><div className="adsc">{next.desc}</div></div></div></>}
      <div className="sh"><span className="st">Snel naar</span></div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {[{id:"logboek",i:IC.book,l:"Logboek",c:"#0f766e"},{id:"dieren",i:IC.paw,l:"Dieren",c:"var(--g)"},{id:"dienst",i:IC.tasks,l:"Mijn dienst",c:dagdColor[huidigDagdeel]},{id:"planning",i:IC.planning,l:"Rooster",c:"var(--bl)"}].map(q=>
          <button key={q.id} onClick={()=>ds({type:"NAV",p:q.id})} style={{flex:"1 1 45%",padding:"11px 9px",borderRadius:"var(--r)",border:"1px solid var(--brd)",background:"#fff",boxShadow:"var(--sh)",display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:q.c}}>{q.i} {q.l}</button>
        )}
      </div>
    </div>;
  };

  /* ── Dieren ── */
  const DierenPg=({soorten=[],setPhotoModal})=>{ // eslint-disable-line no-undef
    const [addOpen,setAddOpen]=useState(false);
    const [memoriaOpen,setMemoriaOpen]=useState(false);
    const canM=hasPerm(s.user,"voeding_beheren")||s.user?.isAppAdmin;
    if(s.viewSoort) return <div className="pg"><SoortDetail soort={s.viewSoort} s={s} d={d} ds={ds} soorten={soorten} setPhotoModal={setPhotoModal} park={park}/></div>;
    const levendeDieren=s.animals.filter(a=>a.actief!==false&&!a.datum_overlijden);
    const overledenDieren=s.animals.filter(a=>a.actief===false||!!a.datum_overlijden).sort((a,b)=>new Date(b.datum_overlijden)-new Date(a.datum_overlijden));
    const soortCounts=soorten.map(soort_obj=>({soort_obj,count:levendeDieren.filter(a=>a.soort_id===soort_obj.id).length})).filter(x=>x.count>0);
    return <div className="pg">
      <h1 className="pt">Onze Dieren</h1>
      <div className="agrid">{soortCounts.map(({soort_obj,count})=>
        <div key={soort_obj.id} className="soort-tile" onClick={()=>ds({type:"VIEW_SOORT",p:soort_obj.naam})}>
          <div className="soort-em">{soort_obj.icon}</div>
          <div className="soort-nm">{soort_obj.naam}</div>
          <div className="soort-cnt">{count} {count === 1 ? 'dier' : 'dieren'}</div>
        </div>
      )}</div>
      {soortCounts.length===0&&canM&&<div style={{textAlign:"center",padding:"30px 20px",color:"var(--txtl)"}}><p style={{fontSize:14}}>Nog geen dieren toegevoegd.</p><p style={{fontSize:12}}>Gebruik de + knop om het eerste dier toe te voegen.</p></div>}

      {/* ── In memoriam ── */}
      {overledenDieren.length>0&&<div style={{marginTop:20}}>
        <div onClick={()=>setMemoriaOpen(v=>!v)} style={{cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"#f5f5f5",borderRadius:12,border:"1px solid #e0e0e0"}}>
          <span style={{fontSize:14,fontWeight:600,color:"var(--txtl)"}}>🕊️ In memoriam ({overledenDieren.length})</span>
          <span style={{fontSize:12,color:"var(--txtl)"}}>{memoriaOpen?"▲":"▼"}</span>
        </div>
        {memoriaOpen&&<div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
          {overledenDieren.map(dier=>{
            const soort_obj=soorten.find(s=>s.id===dier.soort_id);
            const displayName=`${((dier.geslacht==="vrouw"?soort_obj?.naam_vrouw:soort_obj?.naam_man)||soort_obj?.naam||"")} ${dier.name||dier.naam||""}`.trim();
            return <div key={dier.id} onClick={()=>{const so=soorten.find(s=>s.id===dier.soort_id);if(so)d({type:"VIEW_SOORT",p:so.naam});setTimeout(()=>d({type:"VIEW_DIER",p:dier.id}),0);}} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"#fff",borderRadius:10,border:"1px solid #e0e0e0",opacity:0.7,cursor:"pointer"}}>
              <div style={{width:40,height:40,borderRadius:8,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:"grayscale(1)"}}>
                {dier.fotos?.length>0?<img src={dier.fotos[0]} alt={dier.naam} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:8}}/>:soort_obj?.icon||"🐾"}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:600,color:"var(--txtl)",textDecoration:"line-through"}}>{displayName}</div>
                <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>{soort_obj?.icon} {soort_obj?.naam} · 🕊️ {new Date(dier.datum_overlijden).toLocaleDateString("nl-NL")}{dier.datum_rendac&&<> · 🚛 Rendac {new Date(dier.datum_rendac).toLocaleDateString("nl-NL")}</>}</div>
              </div>
            </div>;
          })}
        </div>}
      </div>}

      {canM&&<button className="fab" onClick={()=>setAddOpen(true)}>{IC.plus}</button>}
      {addOpen&&<AnimalForm parkId={park?.id} onClose={()=>setAddOpen(false)} onSave={f=>{ds({type:"ADD_ANIMAL",p:f});setAddOpen(false);}}/>}
    </div>;
  };

  /* ── Voeding ── */
  const VoedPg=()=>{
    const canM=hasPerm(s.user,"voeding_beheren"),canC=hasPerm(s.user,"voeding_afvinken");
    const [tab,setTab]=useState("bibliotheek"); // "bibliotheek" or "maaltijden"
    const dagdeelIcon={ochtend:"🌅",middag:"☀️",avond:"🌙"};
    const dagdeelLabel={ochtend:"Ochtend",middag:"Middag",avond:"Avond"};

    // Group voedingDieren by soort/dier + dagdeel
    const getVoedingNaam=(vid)=>{const f=s.feedings.find(x=>x.id===vid);return f?`${f.type}${f.merk?` (${f.merk})`:""}`:"-";};
    const getDierNaam=(did)=>{const a=s.animals.find(x=>x.id===did);return a?(a.name||a.naam):"-";};
    const getSoortNaam=(sid)=>{const so=soorten?.find(x=>x.id===sid);return so?`${so.icon} ${so.naam}`:"-";};

    // Group maaltijden by target (soort or dier)
    const grouped={};
    (s.voedingDieren||[]).forEach(vd=>{
      const key=vd.soort_id?`soort-${vd.soort_id}`:vd.dier_id?`dier-${vd.dier_id}`:"onbekend";
      if(!grouped[key]) grouped[key]={label:vd.soort_id?getSoortNaam(vd.soort_id):getDierNaam(vd.dier_id),type:vd.soort_id?"soort":"dier",items:[]};
      grouped[key].items.push(vd);
    });
    // Sort items within each group by dagdeel
    const dagdeelOrder={ochtend:0,middag:1,avond:2};
    Object.values(grouped).forEach(g=>g.items.sort((a,b)=>(dagdeelOrder[a.dagdeel]||0)-(dagdeelOrder[b.dagdeel]||0)));

    return <div className="pg"><h1 className="pt">Voeding</h1>

      {/* Tabs */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        <button onClick={()=>setTab("maaltijden")} style={{flex:1,padding:"9px 12px",borderRadius:10,
          border:tab==="maaltijden"?"2px solid var(--g)":"2px solid var(--brd)",
          background:tab==="maaltijden"?"var(--g)":"#fff",color:tab==="maaltijden"?"#fff":"var(--txt)",
          fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>🍽️ Maaltijdpakketten</button>
        <button onClick={()=>setTab("bibliotheek")} style={{flex:1,padding:"9px 12px",borderRadius:10,
          border:tab==="bibliotheek"?"2px solid var(--g)":"2px solid var(--brd)",
          background:tab==="bibliotheek"?"var(--g)":"#fff",color:tab==="bibliotheek"?"#fff":"var(--txt)",
          fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>📦 Voedingsbibliotheek</button>
      </div>

      {/* ── MAALTIJDPAKKETTEN TAB ── */}
      {tab==="maaltijden"&&<>
        {Object.keys(grouped).length===0?
          <div className="card" style={{textAlign:"center",padding:24,color:"var(--txtl)"}}>
            <div style={{fontSize:32,marginBottom:8}}>🍽️</div>
            <EmptyState icon="🍽️" title="Nog geen maaltijden" sub="Wijs voeding toe aan dieren of soorten via de + knop."/>
            <p style={{fontSize:12,marginTop:4}}>Voeg voeding toe uit de bibliotheek aan diersoorten of individuele dieren.</p>
          </div>
        :
          Object.entries(grouped).map(([key,group])=><div key={key} className="card" style={{marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:15,color:"var(--g)",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              {group.type==="soort"?"🐾":"🏷️"} {group.label}
            </div>
            {["ochtend","middag","avond"].map(dd=>{
              const items=group.items.filter(i=>i.dagdeel===dd);
              if(items.length===0) return null;
              return <div key={dd} style={{marginBottom:8}}>
                <div style={{fontSize:12,fontWeight:600,color:dd==="ochtend"?"#e87c28":dd==="middag"?"#2e6da4":"#6b46c1",marginBottom:4}}>
                  {dagdeelIcon[dd]} {dagdeelLabel[dd]}
                </div>
                {items.map(vd=><div key={vd.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
                  background:"var(--cd)",borderRadius:8,marginBottom:4,fontSize:13}}>
                  <div style={{flex:1}}>
                    <span style={{fontWeight:600}}>{getVoedingNaam(vd.voeding_id)}</span>
                    {vd.hoeveelheid&&<span style={{color:"var(--txtl)"}}> · {vd.hoeveelheid}</span>}
                  </div>
                  {canM&&<div style={{display:"flex",gap:3}}>
                    <button className="ubtn" style={{width:24,height:24}} onClick={()=>ds({type:"MODAL",p:{type:"editMaaltijd",item:vd}})}>{IC.edit}</button>
                    <button className="ubtn" style={{width:24,height:24}} onClick={()=>{toast.confirm("Verwijderen?",()=>ds({type:"DEL_VOEDING_DIER",p:vd.id}))}}>{IC.trash}</button>
                  </div>}
                </div>)}
              </div>;
            })}
          </div>)
        }
        {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addMaaltijd"}})}>{IC.plus}</button>}
      </>}

      {/* ── BIBLIOTHEEK TAB ── */}
      {tab==="bibliotheek"&&<>
        <p style={{fontSize:12,color:"var(--txtl)",marginBottom:10}}>Alle beschikbare voeding in {park?.naam}</p>
        {s.feedings.map(f=>
          <div key={f.id} className="card" style={{marginBottom:8,cursor:canM?"pointer":"default"}} onClick={()=>canM&&ds({type:"MODAL",p:{type:"editV",item:f}})}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",gap:12}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:14}}>{f.type}</div>
                {f.merk&&<div style={{fontSize:13,color:"var(--txtl)",marginTop:2}}>🏷️ {f.merk}</div>}
                {f.inhoud&&<div style={{fontSize:13,color:"var(--g)",marginTop:2}}>📦 {f.inhoud}</div>}
                {f.omschrijving&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:4,lineHeight:1.4}}>{f.omschrijving}</div>}
                {f.methode&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:2}}>🥣 {f.methode}</div>}
              </div>
              {canM&&<div className="iab" style={{flexShrink:0}}>
                <button onClick={e=>{e.stopPropagation();ds({type:"MODAL",p:{type:"editV",item:f}});}}>{IC.edit}</button>
                <button onClick={e=>{e.stopPropagation();toast.confirm("Verwijderen?",()=>ds({type:"DEL_FEED",p:f.id}));}}>{IC.trash}</button>
              </div>}
            </div>
            {f.foto&&<img src={f.foto} alt="voeding" style={{width:"100%",height:120,objectFit:"cover",borderRadius:8,marginTop:8,display:"block"}}/>}
            {f.link&&<a href={f.link} target="_blank" rel="noreferrer" className="item-link" onClick={e=>e.stopPropagation()}>▶ Instructievideo bekijken</a>}
          </div>
        )}
        {s.feedings.length===0&&<div className="card"><EmptyState icon="🌾" title="Nog geen voeding" sub="Voeg voedingsproducten toe aan de bibliotheek via de + knop."/></div>}
        {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addV"}})}>{IC.plus}</button>}
      </>}

      {/* ── All modals rendered outside tabs to prevent reset ── */}
      {(s.modal==="addMaaltijd"||s.modal==="editMaaltijd")&&<MaaltijdForm
        item={s.editItem}
        feedings={s.feedings}
        animals={s.animals}
        soorten={soorten||[]}
        onClose={()=>ds({type:"CLOSE"})}
        onSave={f=>ds({type:s.editItem?"EDIT_VOEDING_DIER":"ADD_VOEDING_DIER",p:f})}
      />}
      {(s.modal==="addV"||s.modal==="editV")&&<VoedForm item={s.editItem} animals={s.animals} leveranciers={s.leveranciers||[]} soorten={soorten||[]} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:s.editItem?"EDIT_FEED":"ADD_FEED",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Medicatie ── */
  const MedPg=()=>{
    const canM=hasPerm(s.user,"medicatie_beheren"),canC=hasPerm(s.user,"medicatie_afvinken");
    return <div className="pg"><h1 className="pt">💊 Medicatie</h1>
      <p style={{fontSize:12,color:"var(--txtl)",marginBottom:10}}>Beschikbare medicijnen (gedeeld)​</p>
      {!s.meds || s.meds.length===0 ? (
        <div className="card"><EmptyState icon="💊" title="Geen medicijnen" sub="Er zijn nog geen medicijnen in de bibliotheek."/></div>
      ) : (
        <div>
          {s.meds.map(m=>
            <div key={m.id} className="card" style={{marginBottom:12,cursor:canM?"pointer":"default"}} onClick={()=>canM&&ds({type:"MODAL",p:{type:"editM",item:m}})}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:14}}>{m.medicijn}</div>
                  {m.merknaam&&<div style={{fontSize:13,color:"var(--txtl)",marginTop:2}}>🏷️ {m.merknaam}</div>}
                  {m.vorm&&<div style={{fontSize:13,color:"var(--g)",marginTop:2}}>📋 {m.vorm.charAt(0).toUpperCase()+m.vorm.slice(1)}</div>}
                  {m.toediening&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:4,lineHeight:1.4}}>💉 <strong>Toediening:</strong> {m.toediening}</div>}
                </div>
                {canM&&<div className="iab" style={{flexShrink:0}}>
                  <button onClick={e=>{e.stopPropagation();ds({type:"MODAL",p:{type:"editM",item:m}});}}>{IC.edit}</button>
                  <button onClick={e=>{e.stopPropagation();toast.confirm("Verwijderen?",()=>ds({type:"DEL_MED",p:m.id}));}}>{IC.trash}</button>
                </div>}
              </div>
              {m.foto&&<img src={m.foto} alt={m.medicijn} style={{width:"100%",height:150,objectFit:"contain",borderRadius:8,marginTop:12,display:"block"}}/>}
              {m.link&&<a href={m.link} target="_blank" rel="noreferrer" className="item-link" onClick={e=>e.stopPropagation()}>▶ Meer informatie</a>}
            </div>
          )}
        </div>
      )}
      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addM"}})}>{IC.plus}</button>}
      {(s.modal==="addM"||s.modal==="editM")&&<MedForm item={s.editItem} animals={s.animals} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:s.editItem?"EDIT_MED":"ADD_MED",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Taken ── */
  const TkPg=()=>{
    const [fil,setFil]=useState("alle");
    const [freqFil,setFreqFil]=useState("alle"); // "alle"|"wekelijks"|"maandelijks"|"jaarlijks"|"eenmalig"
    const [view,setView]=useState("week"); // "week" or "lijst"
    const canM=hasPerm(s.user,"taken_beheren"),canC=hasPerm(s.user,"taken_afvinken"),canGem=hasPerm(s.user,"berichten_gemeente");
    const cats=["alle","reparatie","schoonmaak","gemeente","voeding","administratie","overig"].filter(c=>c!=="gemeente"||canGem);
    let list=(fil==="alle"?s.tasks:s.tasks.filter(t=>t.cat===fil)).filter(t=>t.cat!=="gemeente"||canGem);
    if(freqFil!=="alle") list=list.filter(t=>(t.frequentie||"wekelijks")===freqFil);
    const weekTasks=list.filter(t=>!t.frequentie||t.frequentie==="wekelijks");
    const DAGEN_KORT=["ma","di","wo","do","vr","za","zo"];
    const DAGEN_LANG=["Ma","Di","Wo","Do","Vr","Za","Zo"];
    const getUserNames=(taakId)=>{
      const uids=s.takenToewijzingen.filter(tt=>tt.taak_id===taakId).map(tt=>tt.gebruiker_id);
      return s.users.filter(u=>uids.includes(u.id)).map(u=>u.roepnaam||u.name);
    };
    const catIcon={reparatie:"🔧",schoonmaak:"🧹",gemeente:"🏛️",voeding:"🌾",administratie:"📄",overig:"📋"};
    const freqIcon={wekelijks:"🔁",maandelijks:"📆",jaarlijks:"🗓️",eenmalig:"📌"};
    const freqLabel={wekelijks:"Wekelijks",maandelijks:"Maandelijks",jaarlijks:"Jaarlijks",eenmalig:"Eenmalig"};
    const getFreqBadge=(t)=>{
      const freq=t.frequentie||"wekelijks";
      if(freq==="wekelijks"){
        const dagen=t.dagen||[];
        return dagen.length>0?`🔁 ${dagen.join(", ")}`:"🔁 Wekelijks";
      }
      if(freq==="maandelijks") return `📆 Elke ${t.maand_dag||1}e van de maand`;
      if(freq==="jaarlijks"&&t.datum) return `🗓️ Jaarlijks op ${new Date(t.datum+"T00:00").toLocaleDateString("nl-NL",{day:"numeric",month:"long"})}`;
      if(freq==="eenmalig"&&t.datum) return `📌 Deadline: ${new Date(t.datum+"T00:00").toLocaleDateString("nl-NL")}`;
      return freqIcon[freq]+" "+freqLabel[freq];
    };

    return <div className="pg"><h1 className="pt">Taken</h1>

      {/* View toggle */}
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <button onClick={()=>setView("week")} style={{flex:1,padding:"8px 12px",borderRadius:10,border:view==="week"?"2px solid var(--g)":"2px solid var(--brd)",
          background:view==="week"?"var(--g)":"#fff",color:view==="week"?"#fff":"var(--txt)",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>
          📅 Weekoverzicht
        </button>
        <button onClick={()=>setView("lijst")} style={{flex:1,padding:"8px 12px",borderRadius:10,border:view==="lijst"?"2px solid var(--g)":"2px solid var(--brd)",
          background:view==="lijst"?"var(--g)":"#fff",color:view==="lijst"?"#fff":"var(--txt)",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>
          📋 Lijst
        </button>
      </div>

      {/* Frequency filter */}
      <div style={{display:"flex",gap:4,marginBottom:8,overflowX:"auto"}}>
        {[{id:"alle",label:"Alle"},{id:"wekelijks",label:"🔁 Week"},{id:"maandelijks",label:"📆 Maand"},{id:"jaarlijks",label:"🗓️ Jaar"},{id:"eenmalig",label:"📌 Eenmalig"}].map(opt=>
          <button key={opt.id} className={`fc ${freqFil===opt.id?"a":""}`} onClick={()=>setFreqFil(opt.id)}>{opt.label}</button>
        )}
      </div>

      <div className="fr">{cats.map(c=><button key={c} className={`fc ${fil===c?"a":""}`} onClick={()=>setFil(c)}>{c.charAt(0).toUpperCase()+c.slice(1)}</button>)}</div>

      {/* ── WEEK VIEW ── */}
      {view==="week"&&<>
        <div style={{overflowX:"auto",marginBottom:12}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr>
                <th style={{textAlign:"left",padding:"8px 6px",fontSize:13,fontWeight:700,color:"var(--g)",borderBottom:"2px solid var(--g)",minWidth:120}}>Taak</th>
                {DAGEN_LANG.map(d=><th key={d} style={{padding:"8px 2px",fontSize:12,fontWeight:700,color:"var(--g)",borderBottom:"2px solid var(--g)",textAlign:"center",minWidth:34}}>{d}</th>)}
                {canM&&<th style={{width:60,borderBottom:"2px solid var(--g)"}}></th>}
              </tr>
            </thead>
            <tbody>
              {weekTasks.sort((a,b)=>{const p={hoog:0,normaal:1,laag:2};return p[a.prio]-p[b.prio];}).map(t=>{
                const dagen=t.dagen||[];
                const assigned=getUserNames(t.id);
                return <tr key={t.id} style={{borderBottom:"1px solid var(--brd)"}}>
                  <td style={{padding:"10px 6px",verticalAlign:"top"}}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <span className={`pd pd-${t.prio.charAt(0)}`}/>
                      <span style={{fontWeight:600,fontSize:13,color:"var(--g)"}}>{t.titel}</span>
                    </div>
                    {assigned.length>0&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>{assigned.join(", ")}</div>}
                  </td>
                  {DAGEN_KORT.map(dag=><td key={dag} style={{textAlign:"center",padding:"8px 2px",verticalAlign:"middle"}}>
                    {dagen.includes(dag)?
                      <div style={{width:28,height:28,borderRadius:7,background:"#c44536",
                        margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:14,fontWeight:700}}>
                      </div>
                      :<div style={{width:28,height:28,borderRadius:7,border:"1.5px solid var(--brd)",margin:"0 auto",background:"#fafaf8"}}/>
                    }
                  </td>)}
                  {canM&&<td style={{padding:"6px 2px",verticalAlign:"top"}}>
                    <div style={{display:"flex",gap:2}}>
                      <button className="ubtn" style={{width:26,height:26}} onClick={()=>ds({type:"MODAL",p:{type:"editTk",item:t}})}>{IC.edit}</button>
                      <button className="ubtn" style={{width:26,height:26}} onClick={()=>{toast.confirm("Verwijderen?",()=>ds({type:"DEL_TASK",p:t.id}))}}>{IC.trash}</button>
                    </div>
                  </td>}
                </tr>;
              })}
              {weekTasks.length===0&&<tr><td colSpan={canM?9:8} style={{padding:20,textAlign:"center",color:"var(--txtl)",fontSize:13}}>Geen wekelijkse taken in deze categorie</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Legenda */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:4,fontSize:12,color:"var(--txtl)"}}>
            <div style={{width:16,height:16,borderRadius:4,background:"#c44536"}}/> Ingepland
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4,fontSize:12,color:"var(--txtl)"}}>
            <div style={{width:16,height:16,borderRadius:4,border:"1.5px solid var(--brd)",background:"#fafaf8"}}/> Geen taak
          </div>
        </div>

        {/* Non-weekly tasks section */}
        {(()=>{
          const otherTasks=list.filter(t=>t.frequentie&&t.frequentie!=="wekelijks");
          if(otherTasks.length===0) return null;
          return <div style={{marginTop:4}}>
            <h3 style={{fontSize:13,fontWeight:700,color:"var(--g)",marginBottom:8}}>📌 Overige taken</h3>
            {otherTasks.sort((a,b)=>{const p={hoog:0,normaal:1,laag:2};return p[a.prio]-p[b.prio];}).map(t=>{
              const assigned=getUserNames(t.id);
              return <div key={t.id} className="card" style={{padding:10,marginBottom:6}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:3}}>
                      <span className={`pd pd-${t.prio.charAt(0)}`}/>
                      <span style={{fontWeight:600,fontSize:13}}>{catIcon[t.cat]||"📋"} {t.titel}</span>
                    </div>
                    <span style={{padding:"2px 8px",borderRadius:6,fontSize:10,fontWeight:600,
                      background:t.frequentie==="eenmalig"?"#fef3c7":t.frequentie==="jaarlijks"?"#ede9fe":"#dbeafe",
                      color:t.frequentie==="eenmalig"?"#92400e":t.frequentie==="jaarlijks"?"#5b21b6":"#1e40af"
                    }}>{getFreqBadge(t)}</span>
                    {assigned.length>0&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:3}}>{assigned.join(", ")}</div>}
                  </div>
                  {canM&&<div style={{display:"flex",gap:2}}>
                    <button className="ubtn" style={{width:26,height:26}} onClick={()=>ds({type:"MODAL",p:{type:"editTk",item:t}})}>{IC.edit}</button>
                    <button className="ubtn" style={{width:26,height:26}} onClick={()=>{toast.confirm("Verwijderen?",()=>ds({type:"DEL_TASK",p:t.id}))}}>{IC.trash}</button>
                  </div>}
                </div>
              </div>;
            })}
          </div>;
        })()}
      </>}

      {/* ── LIST VIEW ── */}
      {view==="lijst"&&<>
        {list.sort((a,b)=>{const p={hoog:0,normaal:1,laag:2};return p[a.prio]-p[b.prio];}).map(t=>{
          const assigned=getUserNames(t.id);
          return <div key={t.id} className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:5}}>
              <div style={{display:"flex",alignItems:"center",gap:4}}><span className={`pd pd-${t.prio.charAt(0)}`}/><span style={{fontWeight:600,fontSize:14}}>{catIcon[t.cat]||"📋"} {t.titel}</span></div>
              <div style={{display:"flex",gap:3,alignItems:"center"}}>
                <span className={`bdg ${t.prio==="hoog"?"bdg-urg":"bdg-pen"}`}>{t.prio}</span>
                {canM&&<div className="iab"><button onClick={()=>ds({type:"MODAL",p:{type:"editTk",item:t}})}>{IC.edit}</button><button onClick={()=>{toast.confirm("Verwijderen?",()=>ds({type:"DEL_TASK",p:t.id}))}}>{IC.trash}</button></div>}
              </div>
            </div>
            {t.desc&&<p style={{fontSize:13,color:"var(--txtl)",lineHeight:1.5,marginBottom:6,whiteSpace:"pre-wrap"}}>{t.desc}</p>}

            {/* Frequency badge */}
            <div style={{marginBottom:4}}>
              <span style={{padding:"3px 10px",borderRadius:8,fontSize:11,fontWeight:600,
                background:(t.frequentie||"wekelijks")==="eenmalig"?"#fef3c7":(t.frequentie||"wekelijks")==="jaarlijks"?"#ede9fe":(t.frequentie||"wekelijks")==="maandelijks"?"#dbeafe":"var(--bll)",
                color:(t.frequentie||"wekelijks")==="eenmalig"?"#92400e":(t.frequentie||"wekelijks")==="jaarlijks"?"#5b21b6":(t.frequentie||"wekelijks")==="maandelijks"?"#1e40af":"var(--bl)"
              }}>{getFreqBadge(t)}</span>
            </div>

            {/* Dagen badges - only for weekly */}
            {(t.frequentie||"wekelijks")==="wekelijks"&&(t.dagen||[]).length>0&&<div style={{display:"flex",gap:3,marginBottom:4,flexWrap:"wrap"}}>
              {DAGEN_KORT.map(dag=><span key={dag} style={{
                padding:"2px 7px",borderRadius:6,fontSize:11,fontWeight:600,
                background:(t.dagen||[]).includes(dag)?"var(--g)":"transparent",
                color:(t.dagen||[]).includes(dag)?"#fff":"var(--brd)",
                border:(t.dagen||[]).includes(dag)?"none":"1.5px solid var(--brd)"
              }}>{dag}</span>)}
            </div>}

            <div style={{fontSize:12,color:"var(--txtl)",marginTop:4}}>
              {assigned.length>0?<><strong>Toegewezen:</strong> {assigned.join(", ")}</>:"Niet toegewezen"}
            </div>
          </div>;
        })}
        {list.length===0&&<EmptyState icon="✅" title="Geen taken gevonden" sub="Er zijn geen taken die voldoen aan de huidige filter."/>}
      </>}

      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addTk"}})}>{IC.plus}</button>}
      {(s.modal==="addTk"||s.modal==="editTk")&&<TkForm item={s.editItem} users={s.users} toewijzingen={s.takenToewijzingen} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:s.editItem?"EDIT_TASK":"ADD_TASK",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Leveranciers ── */
  const LeveranciersPg=()=>{
    const canM=hasPerm(s.user,"leveranciers_beheren");
    return <div className="pg"><h1 className="pt">{IC.supplier} Leveranciers</h1>
      {s.leveranciers?.length===0?<div className="card"><EmptyState icon="🚚" title="Nog geen leveranciers" sub="Voeg je eerste leverancier toe via de + knop."/></div>:
      s.leveranciers?.map(l=>{const contact=s.contacten?.find(c=>c.id===l.contact_id);return <div key={l.id} className="card" style={{padding:12,marginBottom:8}}>
        {l.foto&&<img src={l.foto} alt={l.naam} style={{width:"100%",height:120,objectFit:"cover",borderRadius:8,marginBottom:8,display:"block"}}/>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,fontSize:14}}>{l.naam}</div>
            {contact&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:4}}>{contact?.naam}{contact?.tel&&` · ${contact.tel}`}{contact?.email&&` · ${contact.email}`}</div>}
            {l.website&&<a href={l.website} target="_blank" rel="noreferrer" className="item-link" style={{marginTop:6,display:"inline-block"}}>🌐 Website bezoeken</a>}
          </div>
          {canM&&<div style={{display:"flex",gap:4}}>
            <button className="ubtn" onClick={()=>ds({type:"MODAL",p:{type:"editLev",item:l}})}>{IC.edit}</button>
            <button className="ubtn" onClick={()=>{toast.confirm("Verwijderen?",()=>ds({type:"DEL_LEVERANCIER",p:l.id}))}}>{IC.trash}</button>
          </div>}
        </div>
      </div>;})}
      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addLev"}})}>{IC.plus}</button>}
      {(s.modal==="addLev"||s.modal==="editLev")&&<LeverancierForm item={s.editItem} contacten={s.contacten||[]} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:s.editItem?"EDIT_LEVERANCIER":"ADD_LEVERANCIER",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Bestellingen ── */
  const BestellingenPg=()=>{
    const canM=hasPerm(s.user,"bestellingen_beheren");
    return <div className="pg"><h1 className="pt">{IC.orders} Bestellingen</h1>
      {s.bestellingen?.length===0?<div className="card"><EmptyState icon="📦" title="Nog geen bestellingen" sub="Registreer je eerste bestelling via de + knop."/></div>:
      s.bestellingen?.map(b=>{const lev=s.leveranciers?.find(l=>l.id===b.leverancier_id);const voed=s.feedings?.find(f=>f.id===b.voeding_id);return <div key={b.id} className="card" style={{padding:12,marginBottom:8}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,fontSize:14}}>{voed?.type||"Voeding"} van {lev?.naam||"Onbekend"}</div>
            <div style={{fontSize:12,color:"var(--txtl)",marginTop:4}}>Datum: {new Date(b.datum).toLocaleDateString("nl-NL")}{b.amount&&` · Hoeveelheid: ${b.amount}`}</div>
          </div>
          {canM&&<div style={{display:"flex",gap:4}}>
            <button className="ubtn" onClick={()=>ds({type:"MODAL",p:{type:"editBest",item:b}})}>{IC.edit}</button>
            <button className="ubtn" onClick={()=>{toast.confirm("Verwijderen?",()=>ds({type:"DEL_BESTELLING",p:b.id}))}}>{IC.trash}</button>
          </div>}
        </div>
      </div>;})}
      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addBest"}})}>{IC.plus}</button>}
      {(s.modal==="addBest"||s.modal==="editBest")&&<BestelForm item={s.editItem} leveranciers={s.leveranciers||[]} feedings={s.feedings||[]} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:s.editItem?"EDIT_BESTELLING":"ADD_BESTELLING",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Agenda ── */
  const AgPg=()=>{
    const [fil,setFil]=useState("alle");
    const canM=hasPerm(s.user,"agenda_beheren");
    const list=fil==="alle"?s.agenda:s.agenda.filter(a=>a.type===fil);
    return <div className="pg"><h1 className="pt">Agenda</h1>
      <div className="fr">{["alle","medisch","vergadering","evenement","gemeente"].map(f=><button key={f} className={`fc ${fil===f?"a":""}`} onClick={()=>setFil(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>)}</div>
      {list.map(ev=><div key={ev.id} className="ai">
        <div className="ad"><div className="add">{dayN(ev.datum)}</div><div className="adm">{monS(ev.datum)}</div></div>
        <div className={`atd type-${ev.type}`}/><div className="ac"><div className="atn">{ev.titel}</div><div className="atm">{ev.tijd}</div><div className="adsc">{ev.desc}</div>
        <span className={`bdg ${ev.type==="gemeente"?"bdg-gem":"bdg-inf"}`} style={{marginTop:4}}>{ev.type}</span></div>
        {canM&&<div className="iab" style={{flexDirection:"column"}}><button onClick={()=>ds({type:"MODAL",p:{type:"editAg",item:ev}})}>{IC.edit}</button><button onClick={()=>toast.confirm("Agendaitem verwijderen?",()=>ds({type:"DEL_AGENDA",p:ev.id}))}>{IC.trash}</button></div>}
      </div>)}
      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addAg"}})}>{IC.plus}</button>}
      {(s.modal==="addAg"||s.modal==="editAg")&&<AgForm item={s.editItem} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:s.editItem?"EDIT_AGENDA":"ADD_AGENDA",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Berichten ── */
  const MsgPg=()=>{
    const [txt,setTxt]=useState(""),[prio,setPrio]=useState("normaal"),[cat,setCat]=useState("algemeen");
    const canW=hasPerm(s.user,"berichten_schrijven"),canGem=hasPerm(s.user,"berichten_gemeente"),canDel=hasPerm(s.user,"berichten_verwijderen");
    const send=()=>{if(!txt.trim())return;ds({type:"ADD_MSG",p:{tekst:txt.trim(),prio,cat}});setTxt("");setPrio("normaal");};
    const vis=s.msgs.filter(b=>b.cat!=="gemeente"||canGem);
    return <div className="pg"><h1 className="pt">Berichten</h1>
      {canW&&<><div className="miw"><textarea className="mi" placeholder="Schrijf een bericht..." value={txt} onChange={e=>setTxt(e.target.value)} rows={2}/><button className="msb" onClick={send}>{IC.send}</button></div>
      <div className="ps">
        {["normaal","belangrijk","dringend"].map(p=><button key={p} className={`po ${prio===p?"s":""}`} onClick={()=>setPrio(p)}>{p}</button>)}
        {canGem&&<button className={`po ${cat==="gemeente"?"s":""}`} style={cat==="gemeente"?{borderColor:"var(--pu)",background:"var(--pul)",color:"var(--pu)"}:{}} onClick={()=>setCat(c=>c==="gemeente"?"algemeen":"gemeente")}>{IC.lock} gemeente</button>}
      </div></>}
      <div style={{marginTop:14}}>{vis.map(b=>
        <div key={b.id} className={`mc ${b.prio}`}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontWeight:600,fontSize:13}}>{b.auteur}</span>
            <div style={{display:"flex",gap:3,alignItems:"center"}}>
              <span style={{fontSize:11,color:"var(--txtl)"}}>{b.datum}</span>
              {b.cat==="gemeente"&&<span className="bdg bdg-gem" style={{fontSize:8,padding:"1px 5px"}}>Gemeente</span>}
              {canDel&&<button onClick={()=>toast.confirm("Bericht verwijderen?",()=>ds({type:"DEL_MSG",p:b.id}))} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",padding:"0 4px",fontSize:14}} title="Bericht verwijderen">×</button>}
            </div>
          </div>
          <div style={{fontSize:13,lineHeight:1.5,marginTop:4}}>{b.tekst}</div>
          {b.prio!=="normaal"&&<span className={`bdg ${b.prio==="dringend"?"bdg-urg":"bdg-pen"}`} style={{marginTop:5}}>{b.prio}</span>}
        </div>
      )}</div>
    </div>;
  };

  /* ── Directe Berichten ── */
  const DirectMsgPg=()=>{
    const [conversaties,setConversaties]=useState([]);
    const [allUsers,setAllUsers]=useState([]); // Alle mogelijke users voor nieuw bericht
    const [selectedUserId,setSelectedUserId]=useState(null);
    const [msgTxt,setMsgTxt]=useState("");
    const [zoek,setZoek]=useState("");
    const [loading,setLoading]=useState(true);
    const [searchTerm,setSearchTerm]=useState(""); // Debounced search
    const [showEmojiPicker,setShowEmojiPicker]=useState(false);
    const emojis=["😊","😂","😍","🥰","😎","🤔","👍","❤️","🔥","💯","⚡","🎉","🐾","🤷","👏","🙏","💪","✨","🎈","🌟"];

    // Debug logging
    useEffect(()=>{
      console.log("DirectMsgPg loaded. s.user:", s.user?.id, "s.users count:", s.users?.length);
    },[]);

    // Debounce search input (500ms delay)
    useEffect(()=>{
      const timer=setTimeout(()=>setSearchTerm(zoek),500);
      return ()=>clearTimeout(timer);
    },[zoek]);

    // Fuzzy search helper - allows partial matching
    const fuzzyMatch=(str,query)=>{
      if(!str||!query) return false;
      const s=str.toLowerCase();
      const q=query.toLowerCase();
      let qIdx=0;
      for(let i=0;i<s.length;i++){
        if(s[i]===q[qIdx]) qIdx++;
        if(qIdx===q.length) return true;
      }
      return false;
    };

    // Laad alle directe berichten en users - ONLY on mount
    useEffect(()=>{
      (async()=>{
        if(!s.user?.id) return;
        console.log("Loading direct messages for user:", s.user.id);
        const msgs=await db.getBerichten_Directe(s.user.id);
        console.log("Direct messages loaded:", msgs?.length || 0);
        setLoading(false);
        
        // Groepeer berichten per persoon
        const grouped={};
        msgs.forEach(m=>{
          const otherId=m.from_user_id===s.user.id?m.to_user_id:m.from_user_id;
          if(!grouped[otherId]) grouped[otherId]=[];
          grouped[otherId].push(m);
        });
        
        // Zet om naar conversaties met info
        const convos=Object.entries(grouped).map(([userId,msgs])=>{
          const lastMsg=msgs[0];
          const otherUser=s.users.find(u=>u.id==userId);
          return {userId:parseInt(userId),user:otherUser,msgs:msgs.sort((a,b)=>new Date(a.created_at)-new Date(b.created_at)),lastMsg,unread:msgs.filter(m=>!m.gelezen&&m.to_user_id===s.user.id).length};
        }).sort((a,b)=>new Date(b.lastMsg.created_at)-new Date(a.lastMsg.created_at));
        
        setConversaties(convos);
        // Set all other users
        const otherUsers=s.users.filter(u=>u.id!==s.user.id);
        setAllUsers(otherUsers);
      })();
    },[s.user?.id]); // Only depend on user id, not s.users

    const sendMsg=async()=>{
      if(!msgTxt.trim()||!selectedUserId) return;
      const msg={from_user_id:s.user.id,to_user_id:selectedUserId,from_park_id:park.id,to_park_id:park.id,tekst:msgTxt.trim(),gelezen:false};
      await db.addBericht_Directe(msg);
      setMsgTxt("");
      // Herlaad conversaties
      const msgs=await db.getBerichten_Directe(s.user.id);
      const grouped={};
      msgs.forEach(m=>{
        const otherId=m.from_user_id===s.user.id?m.to_user_id:m.from_user_id;
        if(!grouped[otherId]) grouped[otherId]=[];
        grouped[otherId].push(m);
      });
      const convos=Object.entries(grouped).map(([userId,msgs])=>{
        const lastMsg=msgs[0];
        const otherUser=s.users.find(u=>u.id==userId);
        return {userId:parseInt(userId),user:otherUser,msgs:msgs.sort((a,b)=>new Date(a.created_at)-new Date(b.created_at)),lastMsg,unread:msgs.filter(m=>!m.gelezen&&m.to_user_id===s.user.id).length};
      }).sort((a,b)=>new Date(b.lastMsg.created_at)-new Date(a.lastMsg.created_at));
      setConversaties(convos);
    };

    // Mark messages as read when chat is opened
    useEffect(()=>{
      if(selectedUserId&&selectedUser?.msgs){
        selectedUser.msgs.forEach(m=>{
          if(m.to_user_id===s.user.id&&!m.gelezen){
            db.markAsRead(m.id).catch(e=>console.error("Error marking message as read:",e));
          }
        });
      }
    },[selectedUserId]);

    // Fuzzy search with DEBOUNCED searchTerm (not zoek!)
    const filtered=conversaties.filter(c=>{
      if(!searchTerm.trim()) return true;
      const q=searchTerm.trim();
      return fuzzyMatch(c.user?.roepnaam||"", q) || fuzzyMatch(c.user?.name||"", q);
    });

    // Suggest users not yet messaged
    const suggestedUsers=!searchTerm.trim()?[]:allUsers.filter(u=>{
      if(conversaties.find(c=>c.userId===u.id)) return false; // Already have conversation
      const q=searchTerm.trim();
      return fuzzyMatch(u.roepnaam||"", q) || fuzzyMatch(u.name||"", q);
    }).slice(0,5);

    const selected=conversaties.find(c=>c.userId===selectedUserId);
    // If no existing conversation, create a temporary one for display
    const selectedUser=selectedUserId?selected||{userId:selectedUserId,user:allUsers.find(u=>u.id===selectedUserId),msgs:[],lastMsg:null,unread:0}:null;
    
    const getRoepnaam=(u)=>{
      // Try roepnaam first, then first name of full name, then full name
      if(u?.roepnaam) return u.roepnaam;
      if(u?.name) return u.name.split(" ")[0]; // First word of name
      return "?";
    };
    const getParkRoepnaam=()=>{
      // Try roepnaam first, then full naam
      if(park?.roepnaam) return park.roepnaam;
      if(park?.naam) return park.naam.split(" ")[0]; // First word of name
      return "Park";
    };
    const userAppName=(u)=>u?`${getRoepnaam(u)}-${getParkRoepnaam()}`:"";

    // Mobile design: show chat in fullscreen when selected
    if(selectedUser){
      return <div className="pg" style={{display:"flex",height:"calc(100vh - 200px)",flexDirection:"column",gap:0,backgroundColor:"#fff"}}>
        {/* Chat header with back button */}
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--brd)",fontWeight:600,fontSize:14,backgroundColor:"#fafafa",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setSelectedUserId(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",padding:0,color:"var(--g)"}}>←</button>
          <div>{userAppName(selectedUser.user)}</div>
        </div>

        {/* Messages */}
        <div style={{flex:1,overflow:"auto",padding:"12px 16px",display:"flex",flexDirection:"column",gap:8}}>
          {selectedUser.msgs.length===0?<div style={{padding:"20px",textAlign:"center",color:"var(--txtl)",fontSize:13}}>Geen berichten nog. Start een gesprek!</div>:selectedUser.msgs.map(m=>
            <div key={m.id} style={{display:"flex",justifyContent:m.from_user_id===s.user.id?"flex-end":"flex-start"}}>
              <div style={{maxWidth:"85%",background:m.from_user_id===s.user.id?"var(--g)":"#e0e0e0",color:m.from_user_id===s.user.id?"#fff":"#333",padding:"8px 12px",borderRadius:12,fontSize:13,wordWrap:"break-word"}}>
                {m.tekst}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{borderTop:"1px solid var(--brd)",padding:"10px 12px",display:"flex",gap:8,backgroundColor:"#fafafa",alignItems:"flex-end",position:"relative"}}>
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowEmojiPicker(!showEmojiPicker)} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,padding:0}}>😊</button>
            {showEmojiPicker&&<div style={{position:"absolute",bottom:"100%",left:0,background:"#fff",border:"1px solid var(--brd)",borderRadius:8,padding:8,marginBottom:4,display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:4,zIndex:10,boxShadow:"0 4px 12px rgba(0,0,0,.15)"}}>
              {["😊","😂","❤️","👍","🔥","🎉","😍","😭","🤔","😎","🙏","✨","💯","🚀","😘","🤗","😴","🤨","😡","😱"].map(e=><button key={e} onClick={()=>{setMsgTxt(msgTxt+e);setShowEmojiPicker(false);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,padding:4,borderRadius:6,transition:"background .2s",hover:{background:"#f0f0f0"}}}>{e}</button>)}
            </div>}
          </div>
          <textarea value={msgTxt} onChange={e=>setMsgTxt(e.target.value)} onKeyPress={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),sendMsg())} placeholder="Typ bericht..." style={{flex:1,border:"1px solid var(--brd)",borderRadius:6,padding:8,fontFamily:"inherit",fontSize:16,resize:"none",height:40,boxSizing:"border-box"}}/>
          <button onClick={sendMsg} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:6,padding:"8px 12px",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap",fontSize:16}}>→</button>
        </div>
      </div>;
    }

    // Desktop/list view - selecting person
    return <div className="pg" style={{display:"flex",height:"calc(100vh - 200px)",flexDirection:"column",gap:0}}>
      <h1 className="pt" style={{marginBottom:8}}>Directe Berichten</h1>
      <div style={{flex:1,minHeight:0,borderTop:"1px solid var(--brd)",overflow:"hidden",display:"flex",flexDirection:"column"}}>
        {/* Search */}
        <div style={{padding:"8px",flexShrink:0,borderBottom:"1px solid var(--brd)"}}>
          <input 
            placeholder="Zoek persoon..." 
            value={zoek} 
            onChange={e=>setZoek(e.target.value)}
            style={{width:"100%",border:"1px solid var(--brd)",borderRadius:6,padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}
          />
        </div>
        
        {/* User list */}
        <div style={{flex:1,overflow:"auto"}}>
          {loading?<div style={{padding:12,color:"var(--txtl)",fontSize:12,textAlign:"center"}}>Laden...</div>:filtered.length===0&&suggestedUsers.length===0?<div style={{padding:12,color:"var(--txtl)",fontSize:12,textAlign:"center"}}>Geen resultaten</div>:<>
            {/* Existing conversations */}
            {filtered.length>0&&<div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--txtl)",padding:"10px 12px",background:"#f0f0f0",position:"sticky",top:0}}>CONVERSATIES ({filtered.length})</div>
              {filtered.map(c=>
                <div key={c.userId} onClick={()=>setSelectedUserId(c.userId)} style={{padding:"12px 12px",borderBottom:"1px solid var(--brd)",cursor:"pointer",background:"#fff",transition:"background .2s"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:3}}>
                    <span style={{fontWeight:600,fontSize:13,flex:1,overflow:"hidden",textOverflow:"ellipsis"}}>{userAppName(c.user)}</span>
                    {c.unread>0&&<span style={{fontSize:9,background:"var(--red)",color:"#fff",padding:"3px 6px",borderRadius:10,flexShrink:0,marginLeft:6}}>●{c.unread}</span>}
                  </div>
                  <div style={{fontSize:12,color:"var(--txtl)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.lastMsg.tekst}</div>
                </div>
              )}
            </div>}
            
            {/* Suggested users */}
            {suggestedUsers.length>0&&<div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--txtl)",padding:"10px 12px",background:"#f0f0f0",position:"sticky",top:0}}>💬 STARTEN MET... ({suggestedUsers.length})</div>
              {suggestedUsers.map(u=>
                <div key={u.id} onClick={()=>setSelectedUserId(u.id)} style={{padding:"12px 12px",borderBottom:"1px solid var(--brd)",cursor:"pointer",background:"#fff",transition:"background .2s"}}>
                  <div style={{fontWeight:500,fontSize:13,marginBottom:2}}>{userAppName(u)}</div>
                  <div style={{fontSize:11,color:"var(--txtl)"}}>{u.name}</div>
                </div>
              )}
            </div>}
          </>}
        </div>
      </div>
    </div>;
  };

  /* ── Data (Import + Export + Afdrukken) ── */
  const DataPg=()=>{
    const [tab,setTab]=useState("import"); // "import" | "export" | "print"

    // ── Export helpers ──────────────────────────────────────────
    const EXPORT_VIEWS={
      contacten:{
        label:"Contacten",
        kolommen:[
          {key:"naam",         label:"Naam"},
          {key:"categorie",    label:"Categorie"},
          {key:"functie",      label:"Functie"},
          {key:"tel",          label:"Telefoon"},
          {key:"email",        label:"E-mail"},
          {key:"adres",        label:"Adres"},
          {key:"notitie",      label:"Notitie"},
        ],
        getData:()=>s.contacten.map(c=>({
          naam:    c.naam||"",
          categorie:(s.contactCategorieen||[]).find(x=>x.id===c.contact_categorie_id)?.categorie||"",
          functie: c.functie||"",
          tel:     c.tel||"",
          email:   c.email||"",
          adres:   c.adres||"",
          notitie: c.notitie||"",
        })),
      },
      vaste_taken:{
        label:"Vaste taken",
        kolommen:[
          {key:"titel",    label:"Titel"},
          {key:"dagdeel",  label:"Dagdeel"},
          {key:"soort",    label:"Soort"},
          {key:"protocol", label:"Protocol"},
        ],
        getData:()=>s.vasteTaken.map(t=>({
          titel:    t.titel||t.naam||"",
          dagdeel:  t.dagdeel||"",
          soort:    t.soort||"",
          protocol: (s.protocollen||[]).find(p=>p.id===t.protocol_id)?.naam||"",
        })),
      },
      taken:{
        label:"Taken",
        kolommen:[
          {key:"titel",        label:"Titel"},
          {key:"beschrijving", label:"Beschrijving"},
          {key:"prioriteit",   label:"Prioriteit"},
          {key:"frequentie",   label:"Frequentie"},
          {key:"dagen",        label:"Dagen"},
          {key:"datum",        label:"Datum"},
          {key:"toegewezen",   label:"Toegewezen aan"},
        ],
        getData:()=>s.tasks.map(t=>({
          titel:        t.titel||"",
          beschrijving: t.desc||t.beschrijving||"",
          prioriteit:   t.prio||"",
          frequentie:   t.frequentie||"wekelijks",
          dagen:        Array.isArray(t.dagen)&&t.dagen.length>0?t.dagen.join(", "):"",
          datum:        t.datum||"",
          toegewezen:   (s.takenToewijzingen||[]).filter(x=>x.taak_id===t.id)
                          .map(x=>(s.users||[]).find(u=>u.id===x.gebruiker_id)?.name||"").filter(Boolean).join(", "),
        })),
      },
      voeding_schema:{
        label:"Voedingsschema",
        kolommen:[
          {key:"dier",       label:"Dier"},
          {key:"soort",      label:"Soort"},
          {key:"dagdeel",    label:"Dagdeel"},
          {key:"voeding",    label:"Voeding"},
          {key:"merk",       label:"Merk"},
          {key:"hoeveelheid",label:"Hoeveelheid"},
          {key:"methode",    label:"Methode"},
        ],
        getData:()=>s.voedingDieren.map(vd=>{
          const voeding=(s.feedings||[]).find(f=>f.id===vd.voeding_id)||{};
          const dier=(s.animals||[]).find(a=>a.id===vd.dier_id);
          const soort=soorten.find(x=>x.id===(dier?.soort_id||vd.soort_id));
          return {
            dier:        dier?.naam||dier?.name||"(alle)",
            soort:       soort?.naam||"",
            dagdeel:     vd.dagdeel||"",
            voeding:     voeding.type||"",
            merk:        voeding.merk||"",
            hoeveelheid: vd.hoeveelheid||"",
            methode:     voeding.methode||"",
          };
        }),
      },
    };

    const [exportView,setExportView]=useState("contacten");
    const [exporting,setExporting]=useState(false);

    const doExport=()=>{
      setExporting(true);
      const cfg=EXPORT_VIEWS[exportView];
      const data=cfg.getData();
      const now=new Date();
      const datum=now.toISOString().slice(0,10);
      const tijd=now.toTimeString().slice(0,5).replace(":","");
      const filename=`${park.id}_${exportView}_${datum}_${tijd}`;

      // HTML-table export — Excel opent dit native als spreadsheet
      const header=cfg.kolommen.map(k=>`<th style="background:#1a3c2e;color:#fff;padding:6px 10px;font-family:Arial">${k.label}</th>`).join("");
      const rows=data.map((row,i)=>{
        const bg=i%2===0?"#ffffff":"#f5f5f5";
        const cells=cfg.kolommen.map(k=>`<td style="padding:5px 10px;font-family:Arial;border:1px solid #ddd;background:${bg}">${String(row[k.key]||"").replace(/</g,"&lt;")}</td>`).join("");
        return `<tr>${cells}</tr>`;
      }).join("");

      const html=`<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8">
<style>table{border-collapse:collapse}th,td{border:1px solid #ccc}</style>
</head><body>
<h2 style="font-family:Arial;color:#1a3c2e">${cfg.label} — ${park.naam}</h2>
<p style="font-family:Arial;color:#666;font-size:12px">Geëxporteerd op ${datum} ${now.toTimeString().slice(0,5)} · Park: ${park.id}</p>
<table><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table>
<p style="font-family:Arial;color:#aaa;font-size:10px">${data.length} rijen · Dierenparkje.nl</p>
</body></html>`;

      const blob=new Blob([html],{type:"application/vnd.ms-excel;charset=utf-8"});
      const a=document.createElement("a");
      a.href=URL.createObjectURL(blob);
      a.download=`${filename}.xls`;
      a.click();
      setTimeout(()=>URL.revokeObjectURL(a.href),1000);
      setExporting(false);
    };

    // ── Print helpers ──────────────────────────────────────────
    const DAGDELEN=["ochtend","middag","avond"];
    const DAGDEEL_ICON={ochtend:"🌅",middag:"☀️",avond:"🌙"};

    const printVoeding=(dagdeel)=>{
      const items=s.voedingDieren.filter(vd=>vd.dagdeel===dagdeel);
      const nu=new Date().toLocaleDateString("nl-NL",{weekday:"long",year:"numeric",month:"long",day:"numeric"});
      const rows=items.map(vd=>{
        const voeding=(s.feedings||[]).find(f=>f.id===vd.voeding_id)||{};
        const dier=(s.animals||[]).find(a=>a.id===vd.dier_id);
        const soort=soorten.find(x=>x.id===(dier?.soort_id||vd.soort_id));
        return `<tr>
          <td>${soort?.icon||"🐾"} ${soort?.naam||""}</td>
          <td><strong>${dier?.naam||dier?.name||"Alle "+( soort?.naam||"")}</strong></td>
          <td>${voeding.type||""}</td>
          <td>${voeding.merk||""}</td>
          <td style="text-align:center"><strong>${vd.hoeveelheid||""}</strong></td>
          <td>${voeding.methode||""}</td>
        </tr>`;
      }).join("");

      const html=`<!DOCTYPE html><html><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Voedingsschema ${dagdeel} — ${park.naam}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; padding: 24px; color: #1a1a1a; }
  .toolbar { display: flex; gap: 8px; margin-bottom: 16px; }
  .btn { padding: 10px 18px; border-radius: 8px; border: none; font-family: Arial; font-size: 14px; font-weight: 700; cursor: pointer; }
  .btn-print { background: #1a3c2e; color: white; }
  .btn-close { background: #f0f0f0; color: #333; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #1a3c2e; padding-bottom: 12px; }
  h1 { font-size: 22px; color: #1a3c2e; }
  h2 { font-size: 14px; font-weight: normal; color: #555; margin-top: 4px; }
  .datum { font-size: 12px; color: #888; text-align: right; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { background: #1a3c2e; color: white; padding: 8px 12px; text-align: left; font-size: 12px; }
  td { padding: 8px 12px; font-size: 13px; border-bottom: 1px solid #eee; }
  tr:nth-child(even) td { background: #f9f9f9; }
  .footer { margin-top: 20px; font-size: 10px; color: #aaa; text-align: center; border-top: 1px solid #eee; padding-top: 8px; }
  .dagdeel-badge { display: inline-block; background: #e8f5e9; color: #1a3c2e; padding: 3px 12px; border-radius: 20px; font-size: 13px; font-weight: bold; margin-bottom: 8px; }
  @media print { .toolbar { display: none; } body { padding: 12px; } }
</style>
</head><body>
<div class="toolbar">
  <button class="btn btn-print" onclick="window.print()">🖨️ Afdrukken</button>
  <button class="btn btn-close" onclick="window.close()">✕ Sluiten</button>
</div>
<div class="header">
  <div>
    <div class="dagdeel-badge">${DAGDEEL_ICON[dagdeel]} ${dagdeel.charAt(0).toUpperCase()+dagdeel.slice(1)}dienst</div>
    <h1>${park.naam}</h1>
    <h2>Voedingsschema</h2>
  </div>
  <div class="datum">${nu}<br><br>□ Afgetekend door:________________</div>
</div>
<table>
  <thead><tr><th>Soort</th><th>Dier</th><th>Voeding</th><th>Merk</th><th>Hoeveelheid</th><th>Methode</th></tr></thead>
  <tbody>${rows||"<tr><td colspan='6' style='text-align:center;color:#aaa;padding:20px'>Geen voedingsschema voor dit dagdeel</td></tr>"}</tbody>
</table>
<div class="footer">Dierenparkje.nl · ${park.naam} · Afgedrukt op ${nu}</div>
</body></html>`;

      const w=window.open("","_blank","width=900,height=700");
      w.document.write(html);
      w.document.close();
      setTimeout(()=>w.print(),400);
    };

    // ── Import logica (ongewijzigd) ─────────────────────────────
    const [importType,setImportType]=useState("contacten");
    const [csvText,setCsvText]=useState("");
    const [rows,setRows]=useState(null);
    const [errors,setErrors]=useState([]);
    const [importing,setImporting]=useState(false);
    const [result,setResult]=useState(null);
    const fileRef=useRef();
    const cats=s.contactCategorieen||[];

    const IMPORT_TYPES={
      contacten:{
        label:"Contacten",
        uitleg:"Importeer contactpersonen voor dit park. De kolommen 'park_id' en 'naam' zijn verplicht. Alle andere velden zijn optioneel.",
        velden:[
          {col:"park_id",     label:"Park ID",            verplicht:true,  type:"tekst",   uitleg:`Moet exact overeenkomen met het ID van dit park: "${park.id}"`},
          {col:"naam",        label:"Naam",               verplicht:true,  type:"tekst",   uitleg:"Roepnaam of voornaam is voldoende — vul alleen wat nodig is"},
          {col:"email",       label:"E-mail",             verplicht:false, type:"tekst",   uitleg:"E-mailadres"},
          {col:"tel",         label:"Telefoon",           verplicht:false, type:"tekst",   uitleg:"Telefoonnummer, bijv. 06-12345678"},
          {col:"functie",     label:"Functie",            verplicht:false, type:"tekst",   uitleg:"Bijv. Dierenarts, Wethouder Groen"},
          {col:"adres",       label:"Adres",              verplicht:false, type:"tekst",   uitleg:"Alleen invullen als het adres nodig is voor de bedrijfsvoering"},
          {col:"notitie",     label:"Notitie",            verplicht:false, type:"tekst",   uitleg:"Vrij tekstveld voor extra informatie"},
          {col:"contact_categorie",label:"Categorie",     verplicht:false, type:"tekst",   uitleg:"Naam van de categorie (zie lijst hieronder)"},
          {col:"foto",        label:"Foto (emoji)",       verplicht:false, type:"tekst",   uitleg:"Één emoji als avatar, standaard 👤"},
        ],
        demo:(parkId)=>`park_id,naam,email,tel,functie,adres,notitie,contact_categorie,foto\n${parkId},Jan,jan@example.com,06-12345678,Dierenarts,,Vaste dierenarts,Zorg,🩺\n${parkId},Maria,maria@gemeente.nl,020-1234567,Wethouder Groen,,,Gemeente,👩‍💼`,
      },
    };

    const cfg=IMPORT_TYPES[importType];

    const parseCSV=(text)=>{
      const lines=text.trim().split(/\r?\n/);
      if(lines.length<2) return {rows:[],errors:["CSV heeft geen datarijen (minimaal een headerrij + één datarij)."]};
      const header=lines[0].split(",").map(h=>h.trim().replace(/^"|"$/g,""));
      const errs=[];
      const parsed=[];
      for(let i=1;i<lines.length;i++){
        if(!lines[i].trim()) continue;
        const vals=[];
        let cur="",inQ=false;
        for(const ch of lines[i]){
          if(ch==='"'){inQ=!inQ;}
          else if(ch===","&&!inQ){vals.push(cur.trim());cur="";}
          else cur+=ch;
        }
        vals.push(cur.trim());
        const row={};
        header.forEach((h,idx)=>{row[h]=vals[idx]?.replace(/^"|"$/g,"")||"";});
        const rowErrs=[];
        const rowWaarsch=[];
        if(row.park_id&&String(row.park_id).trim()!==String(park.id))
          rowErrs.push(`Rij ${i}: park_id '${row.park_id}' klopt niet — verwacht '${park.id}'`);
        cfg.velden.filter(v=>v.verplicht&&v.col!=="park_id").forEach(v=>{
          if(!row[v.col]?.trim()) rowErrs.push(`Rij ${i}: '${v.col}' is verplicht maar leeg`);
        });
        if(row.email?.trim()&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim()))
          rowErrs.push(`Rij ${i}: e-mail '${row.email}' heeft geen geldig formaat`);
        if(row.contact_categorie?.trim()){
          const gevonden=cats.find(c=>c.categorie.toLowerCase()===row.contact_categorie.trim().toLowerCase());
          if(cats.length>0&&!gevonden)
            rowErrs.push(`Rij ${i}: categorie '${row.contact_categorie}' bestaat niet — kies uit: ${cats.map(c=>c.categorie).join(", ")}`);
        }
        if(row.naam?.trim()){
          const naamLower=row.naam.trim().toLowerCase();
          const emailLower=row.email?.trim().toLowerCase()||"";
          const isDup=s.contacten.some(c=>{
            const cNaam=(c.naam||"").toLowerCase();
            const cEmail=(c.email||"").toLowerCase();
            if(emailLower&&cEmail&&cEmail===emailLower) return true;
            if(cNaam===naamLower) return true;
            return false;
          });
          if(isDup) rowWaarsch.push(`Rij ${i}: '${row.naam}' lijkt al in de contactenlijst te staan`);
        }
        if(rowErrs.length) errs.push(...rowErrs);
        if(rowWaarsch.length) errs.push(...rowWaarsch.map(w=>`⚠️ ${w}`));
        parsed.push({row,rowIndex:i,hasError:rowErrs.length>0,hasWaarsch:rowWaarsch.length>0});
      }
      return {rows:parsed,errors:errs};
    };

    const handleFile=(file)=>{
      if(!file) return;
      const reader=new FileReader();
      reader.onload=e=>{
        const text=e.target.result;
        setCsvText(text);
        const {rows:r,errors:er}=parseCSV(text);
        setRows(r);setErrors(er);setResult(null);
      };
      reader.readAsText(file);
    };

    const downloadDemo=()=>{
      const csv=cfg.demo(park.id);
      const blob=new Blob([csv],{type:"text/csv"});
      const a=document.createElement("a");
      a.href=URL.createObjectURL(blob);
      a.download=`demo_import_${importType}_park_${park.id}.csv`;
      a.click();
    };

    const doImport=async()=>{
      if(!rows||rows.length===0) return;
      const echtefouten=errors.filter(e=>!e.startsWith("⚠️")).length;
      if(echtefouten>0){toast.err("Los eerst de fouten op voor je importeert.");return;}
      const verkeerd=rows.filter(({row})=>row.park_id&&String(row.park_id).trim()!==String(park.id));
      if(verkeerd.length>0){toast.err(`${verkeerd.length} rij(en) hebben een verkeerde park_id. Import geannuleerd.`);return;}
      setImporting(true);
      let ok=0,fail=0;
      for(const {row} of rows){
        try{
          const contact={
            naam: row.naam?.trim()||"",
            email: row.email?.trim()||null,
            tel: row.tel?.trim()||null,
            functie: row.functie?.trim()||null,
            adres: row.adres?.trim()||null,
            notitie: row.notitie?.trim()||null,
            contact_categorie_id: row.contact_categorie?.trim()
              ?(cats.find(c=>c.categorie.toLowerCase()===row.contact_categorie.trim().toLowerCase())?.id||null)
              :null,
            foto: row.foto?.trim()||"👤",
            park_id: park.id,
          };
          const nw=await db.addContact(contact);
          if(nw) d({type:"ADD_CONTACT",p:nw});
          ok++;
        } catch(e){ fail++; }
      }
      setResult({ok,fail});
      setImporting(false);
      if(fail===0){toast.ok(`✅ ${ok} contacten geïmporteerd!`);setRows(null);setCsvText("");}
      else toast.err(`${ok} geïmporteerd, ${fail} mislukt.`);
    };

    // ── Tab styles ──────────────────────────────────────────────
    const tabStyle=(active)=>({
      flex:1,padding:"10px 4px",border:"none",borderBottom:`3px solid ${active?"#1a3c2e":"transparent"}`,
      background:"none",fontFamily:"inherit",fontSize:13,fontWeight:active?700:500,
      color:active?"#1a3c2e":"var(--txtl)",cursor:"pointer",transition:"all .15s",
    });

    return <div className="pg">
      <h1 className="pt">📊 Data</h1>

      {/* Tabs */}
      <div style={{display:"flex",borderBottom:"1px solid var(--brd)",marginBottom:14}}>
        <button style={tabStyle(tab==="import")} onClick={()=>setTab("import")}>📥 Import</button>
        <button style={tabStyle(tab==="export")} onClick={()=>setTab("export")}>📤 Export</button>
        <button style={tabStyle(tab==="print")}  onClick={()=>setTab("print")}>🖨️ Afdrukken</button>
      </div>

      {/* ── IMPORT TAB ── */}
      {tab==="import"&&<>
        <div className="card" style={{marginBottom:12,borderLeft:"3px solid #0369a1",background:"#f0f7ff"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#0369a1",marginBottom:4}}>🔒 Privacy — AVG richtlijn</div>
          <p style={{fontSize:12,color:"#1e40af",lineHeight:1.6,margin:0}}>
            Vul alleen de noodzakelijke NAW-gegevens in voor gebruik van het park! Als adressen en achternamen niet nodig zijn voor de praktische bedrijfsvoering, laat ze dan achterwege.<br/><br/>
            Deze app is goed beveiligd met de nieuwste technieken, maar wat we niet opslaan kan ook niet lekken. 👍
          </p>
        </div>

        <div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--txtl)",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>Stap 1 — Kies importtype</div>
          <select className="fi" value={importType} onChange={e=>{setImportType(e.target.value);setRows(null);setCsvText("");setErrors([]);setResult(null);}}>
            <option value="contacten">👥 Contacten</option>
            <option value="vastetaken" disabled>🗂 Dagelijkse vaste taken (binnenkort)</option>
            <option value="taken" disabled>✅ Overige taken (binnenkort)</option>
          </select>
        </div>

        <div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--txtl)",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>Stap 2 — Velden</div>
          <p style={{fontSize:13,color:"var(--txtl)",marginBottom:10,lineHeight:1.5}}>{cfg.uitleg}</p>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
            {cfg.velden.map(v=><div key={v.col} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"6px 8px",borderRadius:8,background:v.verplicht?"#fff5f5":"var(--cd)",border:`1px solid ${v.verplicht?"#fca5a5":"var(--brd)"}`}}>
              <div style={{minWidth:140}}>
                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:v.verplicht?"var(--red)":"var(--g)"}}>{v.col}</span>
                {v.verplicht&&<span style={{fontSize:10,background:"var(--red)",color:"#fff",borderRadius:4,padding:"1px 5px",marginLeft:4,fontWeight:700}}>VERPLICHT</span>}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600}}>{v.label} <span style={{fontWeight:400,color:"var(--txtl)"}}>({v.type})</span></div>
                <div style={{fontSize:11,color:"var(--txtl)",marginTop:1}}>{v.uitleg}</div>
              </div>
            </div>)}
          </div>
          {importType==="contacten"&&cats.length>0&&<div style={{marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:"var(--txtl)",marginBottom:4}}>Beschikbare categorieën voor dit park:</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
              {cats.map(c=><span key={c.id} style={{fontSize:11,padding:"2px 10px",borderRadius:12,background:"var(--cd)",border:"1px solid var(--brd)"}}>{c.categorie}</span>)}
            </div>
          </div>}
          <button onClick={downloadDemo} style={{fontSize:12,padding:"6px 14px",borderRadius:8,border:"1px solid var(--g)",background:"#fff",color:"var(--g)",cursor:"pointer",fontWeight:600,fontFamily:"inherit"}}>
            ⬇️ Download demo CSV
          </button>
        </div>

        <div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--txtl)",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>Stap 3 — Upload CSV</div>
          <div onClick={()=>fileRef.current?.click()} style={{border:"2px dashed var(--brd)",borderRadius:10,padding:"24px 16px",textAlign:"center",cursor:"pointer",background:"var(--cd)",marginBottom:8}}>
            <div style={{fontSize:28,marginBottom:4}}>📂</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--g)"}}>Klik om een CSV te kiezen</div>
            <div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>of sleep het bestand hierheen</div>
            {csvText&&<div style={{fontSize:11,color:"#16a34a",marginTop:6,fontWeight:600}}>✅ Bestand geladen</div>}
          </div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
        </div>

        {errors.length>0&&<div className="card" style={{marginBottom:12}}>
          {errors.filter(e=>!e.startsWith("⚠️")).length>0&&<div style={{borderLeft:"3px solid var(--red)",paddingLeft:8,marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:"var(--red)",marginBottom:4}}>❌ {errors.filter(e=>!e.startsWith("⚠️")).length} fout(en) — moet opgelost worden</div>
            {errors.filter(e=>!e.startsWith("⚠️")).map((e,i)=><div key={i} style={{fontSize:12,color:"var(--red)",marginBottom:2}}>• {e}</div>)}
          </div>}
          {errors.filter(e=>e.startsWith("⚠️")).length>0&&<div style={{borderLeft:"3px solid #d97706",paddingLeft:8}}>
            <div style={{fontSize:12,fontWeight:700,color:"#d97706",marginBottom:4}}>⚠️ {errors.filter(e=>e.startsWith("⚠️")).length} mogelijke dubbel(en) — controleer voor import</div>
            {errors.filter(e=>e.startsWith("⚠️")).map((e,i)=><div key={i} style={{fontSize:12,color:"#d97706",marginBottom:2}}>• {e.replace("⚠️ ","")}</div>)}
          </div>}
        </div>}

        {rows&&rows.length>0&&<div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--txtl)",marginBottom:8}}>{rows.length} rij(en) gevonden — preview:</div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead>
                <tr style={{background:"var(--cd)"}}>
                  {cfg.velden.map(v=><th key={v.col} style={{padding:"4px 8px",textAlign:"left",borderBottom:"1px solid var(--brd)",whiteSpace:"nowrap",color:v.verplicht?"var(--red)":"var(--g)",fontWeight:700}}>{v.col}{v.verplicht?" *":""}</th>)}
                  <th style={{padding:"4px 8px"}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({row,rowIndex,hasError,hasWaarsch})=><tr key={rowIndex} style={{background:hasError?"#fff5f5":hasWaarsch?"#fffbeb":"#fff",borderBottom:"1px solid var(--brd)"}}>
                  {cfg.velden.map(v=><td key={v.col} style={{padding:"4px 8px",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:hasError&&v.verplicht&&!row[v.col]?"var(--red)":"inherit"}}>
                    {row[v.col]||<span style={{color:"var(--txtl)"}}>—</span>}
                  </td>)}
                  <td style={{padding:"4px 8px",whiteSpace:"nowrap"}}>
                    {hasError&&<span style={{fontSize:10,color:"var(--red)",fontWeight:700}}>❌ Fout</span>}
                    {!hasError&&hasWaarsch&&<span style={{fontSize:10,color:"#d97706",fontWeight:700}}>⚠️ Check</span>}
                    {!hasError&&!hasWaarsch&&<span style={{fontSize:10,color:"#16a34a"}}>✅</span>}
                  </td>
                </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

        {rows&&rows.length>0&&!result&&(()=>{
          const echtefouten=errors.filter(e=>!e.startsWith("⚠️")).length;
          const waarsch=errors.filter(e=>e.startsWith("⚠️")).length;
          return <button onClick={doImport} disabled={importing||echtefouten>0}
            style={{width:"100%",padding:"12px",borderRadius:10,border:"none",background:echtefouten>0?"#ccc":waarsch>0?"#d97706":"var(--g)",color:"#fff",fontSize:14,fontWeight:700,cursor:echtefouten>0?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:16}}>
            {importing?"Importeren...":echtefouten>0?`❌ Los ${echtefouten} fout(en) op eerst`:waarsch>0?`⚠️ Importeer ${rows.length} contacten (met ${waarsch} waarschuwing(en))`:`📥 Importeer ${rows.length} contacten`}
          </button>;
        })()}

        {result&&<div className="card" style={{borderLeft:`3px solid ${result.fail===0?"#16a34a":"var(--red)"}`}}>
          <div style={{fontWeight:700,color:result.fail===0?"#16a34a":"var(--red)",marginBottom:4}}>
            {result.fail===0?"✅ Import geslaagd!":"⚠️ Import deels geslaagd"}
          </div>
          <div style={{fontSize:13}}>✅ {result.ok} contacten toegevoegd{result.fail>0&&` · ❌ ${result.fail} mislukt`}</div>
          {result.fail===0&&<button onClick={()=>{setResult(null);setRows(null);setCsvText("");}} style={{marginTop:8,fontSize:12,padding:"4px 12px",borderRadius:6,border:"1px solid var(--brd)",background:"var(--cd)",cursor:"pointer",fontFamily:"inherit"}}>Nog een import</button>}
        </div>}
      </>}

      {/* ── EXPORT TAB ── */}
      {tab==="export"&&<>
        <div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--txtl)",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>Kies wat je wilt exporteren</div>
          <select className="fi" value={exportView} onChange={e=>setExportView(e.target.value)} style={{marginBottom:12}}>
            {Object.entries(EXPORT_VIEWS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>

          {/* Preview kolommen */}
          <div style={{fontSize:12,fontWeight:600,color:"var(--txtl)",marginBottom:6}}>Kolommen in dit export:</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:12}}>
            {EXPORT_VIEWS[exportView].kolommen.map(k=><span key={k.key} style={{fontSize:11,padding:"2px 10px",borderRadius:12,background:"var(--cd)",border:"1px solid var(--brd)"}}>{k.label}</span>)}
          </div>

          {/* Aantal rijen */}
          <div style={{fontSize:12,color:"var(--txtl)",marginBottom:12}}>
            {(()=>{const n=EXPORT_VIEWS[exportView].getData().length;return `${n} rij${n!==1?"en":""} beschikbaar`;})()}
          </div>

          <button onClick={doExport} disabled={exporting}
            style={{width:"100%",padding:"12px",borderRadius:10,border:"none",background:"#1a3c2e",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {exporting?"Exporteren...":"📤 Exporteer naar XLS"}
          </button>
          <div style={{fontSize:11,color:"var(--txtl)",marginTop:6,textAlign:"center"}}>
            Bestandsnaam: {park.id}_{exportView}_{new Date().toISOString().slice(0,10)}.xls
          </div>
        </div>
      </>}

      {/* ── PRINT TAB ── */}
      {tab==="print"&&<>
        <div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:13,color:"var(--txtl)",lineHeight:1.6,marginBottom:12}}>
            Print het voedingsschema per dagdeel als overzichtelijk werkblad. Inclusief aftekenregel.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {DAGDELEN.map(d=>{
              const n=s.voedingDieren.filter(vd=>vd.dagdeel===d).length;
              return <button key={d} onClick={()=>printVoeding(d)}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderRadius:10,border:"1px solid var(--brd)",background:"#fff",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                <span style={{fontSize:15,fontWeight:600}}>{DAGDEEL_ICON[d]} {d.charAt(0).toUpperCase()+d.slice(1)}dienst voedingsschema</span>
                <span style={{fontSize:12,color:"var(--txtl)",background:"var(--cd)",padding:"2px 8px",borderRadius:8}}>{n} items</span>
              </button>;
            })}
          </div>
          {s.voedingDieren.length===0&&<div style={{textAlign:"center",color:"var(--txtl)",fontSize:13,marginTop:12}}>
            Geen voedingsschema ingesteld. Voeg voedingskoppelingen toe via de Voeding pagina.
          </div>}
        </div>
      </>}
    </div>;
  };

  /* ── Contacten ── */
  const CtPg=()=>{
    const [fil,setFil]=useState("alle");const [zoek,setZoek]=useState("");
    const canM=hasPerm(s.user,"contacten_beheren");
    const ccList = s.contactCategorieen || [];
    let list=s.contacten;
    if(fil!=="alle") list=list.filter(c=>c.contact_categorie_id===Number(fil));
    if(zoek.trim()) list=list.filter(c=>c.naam.toLowerCase().includes(zoek.toLowerCase())||(c.functie||"").toLowerCase().includes(zoek.toLowerCase())||(c.tel||"").includes(zoek));
    return <div className="pg"><h1 className="pt">Contacten</h1>
      <input className="search-bar" placeholder="Zoek op naam, functie of nummer..." value={zoek} onChange={e=>setZoek(e.target.value)}/>
      <div className="fr">
        <button className={`fc ${fil==="alle"?"a":""}`} onClick={()=>setFil("alle")}>Alle</button>
        {ccList.map(cc=><button key={cc.id} className={`fc ${fil===String(cc.id)?"a":""}`} onClick={()=>setFil(String(cc.id))}>{cc.emoji} {cc.categorie}</button>)}
      </div>
      {list.length===0&&<EmptyState icon="📋" title="Geen contacten gevonden" sub="Voeg contactpersonen toe via de + knop."/>}
      {list.map(c=>{
        const categorie = (s.contactCategorieen||[]).find(cc => cc.id === c.contact_categorie_id);
        const functiegroepen = (s.functiegroepIndeling || []).filter(x => x.contact_id === c.id);
        
        return <div key={c.id} className="cc">
          <div className="cc-top">
            <div className={`cc-av`} style={{fontSize:24}}>{categorie?.emoji || '📇'}</div>
            <div style={{flex:1,minWidth:0}}>
              <div className="cc-nm">{c.naam}</div>
              {functiegroepen.length > 0 ? (
                <div className="cc-fn">{functiegroepen.map(fg => (s.functiegroepen||[]).find(g => g.id === fg.functiegroep_id)?.naam).join(', ')}</div>
              ) : (
                c.functie && <div className="cc-fn">{c.functie}</div>
              )}
              <div style={{marginTop:6, display:'flex', flexWrap:'wrap', gap:6}}>
                {categorie && <span style={{display:'inline-block', padding:'4px 10px', background:'#f0f0f0', borderRadius:4, fontSize:12, color:'#555', fontWeight:500}}>{categorie.emoji} {categorie.categorie}</span>}
                {functiegroepen.map(fg => {
                  const group = (s.functiegroepen || []).find(g => g.id === fg.functiegroep_id);
                  return <span key={fg.id} style={{
                    display:'inline-block',
                    padding:'4px 10px',
                    background:group?.kleur || '#ddd',
                    color:'#fff',
                    borderRadius:4,
                    fontSize:12,
                    fontWeight:500
                  }}>● {group?.naam}</span>;
                })}
              </div>
            </div>
            {canM&&<div className="iab" style={{flexDirection:"column"}}><button onClick={()=>ds({type:"MODAL",p:{type:"editCt",item:c}})}>{IC.edit}</button><button onClick={()=>toast.confirm(`${c.naam||"Contact"} verwijderen?`,()=>ds({type:"DEL_CONTACT",p:c.id}))}>{IC.trash}</button></div>}
          </div>
          <div className="cc-details">
            {c.tel&&<div className="cc-row">{IC.phone}<a href={`tel:${c.tel}`}>{c.tel}</a></div>}
            {c.email&&<div className="cc-row">{IC.mail}<a href={`mailto:${c.email}`}>{c.email}</a></div>}
            {c.adres&&<div className="cc-row">{IC.pin}<span>{c.adres}</span></div>}
          </div>
          {c.notitie&&<div className="cc-note">{c.notitie}</div>}
        </div>;
      })}
      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addCt"}})}>{IC.plus}</button>}
      {(s.modal==="addCt"||s.modal==="editCt")&&<CtForm item={s.editItem} onClose={()=>ds({type:"CLOSE"})} contactCategorieen={s.contactCategorieen} onSave={f=>ds({type:s.editItem?"EDIT_CONTACT":"ADD_CONTACT",p:s.editItem?{...f,id:s.editItem.id}:f})}/>}
    </div>;
  };

  /* ── Planning ── */
  const PlanPg=()=>{
    const [weekOffset,setWeekOffset]=useState(0);
    const canM=hasPerm(s.user,"planning_beheren");
    const weekDates=getWeekDates(weekOffset);
    const today=new Date().toISOString().split("T")[0];
    return <div className="pg">
      <h1 className="pt" style={{marginBottom:8}}>Rooster</h1>
      <div className="plan-week-nav">
        <button onClick={()=>setWeekOffset(w=>w-1)}>{IC.chevL}</button>
        <div style={{textAlign:"center"}}>
          <div className="plan-week-label">{fmtD(weekDates[0])} – {fmtD(weekDates[6])}</div>
          {weekOffset!==0&&<button className="plan-today-btn" onClick={()=>setWeekOffset(0)} style={{marginTop:4}}>Vandaag</button>}
        </div>
        <button onClick={()=>setWeekOffset(w=>w+1)}>{IC.chevR}</button>
      </div>
      <div className="plan-legend">{SHIFTS.map(sh=><div key={sh.id} className="plan-legend-item"><div className="plan-legend-dot" style={{background:sh.color}}/>{sh.label}</div>)}</div>
      <div className="plan-grid">{weekDates.map((datum,di)=>{
        const dayPlan=s.planning.filter(p=>p.datum===datum);
        const isToday=datum===today;
        return <div key={datum} className={`plan-day ${isToday?"today":""}`}>
          <div className="plan-day-hdr"><span>{DAGEN_LANG[di]}{isToday&&<span style={{fontSize:9,marginLeft:6,padding:"1px 6px",background:"var(--g)",color:"#fff",borderRadius:6}}>vandaag</span>}</span>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span className="plan-day-date">{new Date(datum).toLocaleDateString("nl-NL",{day:"numeric",month:"short"})}</span>
              {canM&&<button style={{width:20,height:20,borderRadius:6,border:"1px solid var(--brd)",background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--g)",padding:0}} onClick={()=>ds({type:"MODAL",p:{type:"addPlan",defaultDatum:datum}})}>{IC.plus}</button>}
            </div>
          </div>
          <div className="plan-shifts">{SHIFTS.map(sh=>{
            const people=dayPlan.filter(p=>p.shift===sh.id);
            return <div key={sh.id} className="plan-shift-row">
              <span className="plan-shift-label" style={{background:sh.bg,color:sh.color}}>{sh.label}</span>
              <div className="plan-people">{people.length===0?<span className="plan-empty">—</span>:
                people.map(p=>{
                  const isContact=!!p.contact_id&&!p.gebruiker_id;
                  const matchedUser=s.users.find(u=>u.name===p.naam);
                  const bgColor=isContact?"#7c3aed15":rCol(matchedUser?.role)+"15";
                  const txtColor=isContact?"#7c3aed":rCol(matchedUser?.role);
                  return <span key={p.id} className="plan-person" style={{background:bgColor,color:txtColor,cursor:canM?"pointer":"default",position:"relative"}}
                  onClick={canM?()=>ds({type:"MODAL",p:{type:"editPlan",item:p}}):undefined}>
                  {p.herhaling&&p.herhaling!=="geen"&&<span title={p.herhaling} style={{marginRight:2}}>🔁</span>}
                  {isContact&&<span title="Contact" style={{marginRight:2}}>📋</span>}
                  {p.naam.split(" ")[0]}
                  {canM&&<button style={{border:"none",background:"none",cursor:"pointer",color:"inherit",padding:0,marginLeft:3,fontSize:12}} onClick={e=>{e.stopPropagation();if(p.reeks_id&&p.herhaling&&p.herhaling!=="geen"){ds({type:"MODAL",p:{type:"deletePlan",item:p}});}else{toast.confirm("Dienst verwijderen?",()=>ds({type:"DEL_PLANNING",p:p.id}));}}}>×</button>}
                </span>;})
              }</div>
            </div>;
          })}</div>
        </div>;
      })}</div>
      {(s.modal==="addPlan"||s.modal==="editPlan")&&<PlanForm
        item={s.editItem} defaultDatum={s.modalMeta?.defaultDatum} users={s.users} contacten={s.contacten||[]}
        onClose={()=>ds({type:"CLOSE"})}
        onSave={(f,mode)=>{
          if(s.editItem){
            ds({type:mode==="reeks"?"EDIT_REEKS":"EDIT_PLANNING",p:{...f,id:s.editItem.id}});
          } else {
            ds({type:"ADD_PLANNING",p:f});
          }
        }}
      />}
      {s.modal==="deletePlan"&&s.editItem&&<Modal title="Dienst verwijderen" onClose={()=>ds({type:"CLOSE"})}>
        <p style={{fontSize:14,color:"var(--txt)",marginBottom:12}}>🔁 Dit is een herhalende dienst van <strong>{s.editItem.naam}</strong>. Wat wil je verwijderen?</p>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <button className="fbtn" style={{background:"#fff0f0",color:"var(--red)",border:"1px solid var(--red)"}} onClick={()=>{ds({type:"DEL_PLANNING",p:s.editItem.id});ds({type:"CLOSE"});}}>🗑️ Alleen deze dienst</button>
          <button className="fbtn" style={{background:"#fff0f0",color:"var(--red)",border:"1px solid var(--red)"}} onClick={()=>{ds({type:"DEL_REEKS",p:s.editItem.id});ds({type:"CLOSE"});}}>🗑️ Hele reeks verwijderen</button>
          <button className="fbtn" style={{background:"var(--cd)",color:"var(--txt)",border:"1px solid var(--brd)"}} onClick={()=>ds({type:"CLOSE"})}>Annuleren</button>
        </div>
      </Modal>}
      {canM&&<button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addPlan"}})}>{IC.plus}</button>}
    </div>;
  };

  /* ── Team ── */
  const TeamPg=()=>{
    const [resending,setResending]=useState(null);
    const [pendingRoles,setPendingRoles]=useState({});
    const [savingRole,setSavingRole]=useState(null);
    const [openModules,setOpenModules]=useState(null); // geselecteerde gebruiker of null
    const [moduleState,setModuleState]=useState({}); // gebruiker_id -> {module->niveau}
    const [savingModule,setSavingModule]=useState({}); // gebruiker_id_module -> bool

    // Laad modules voor een deelbeheer-gebruiker
    const loadModules=async(userId)=>{
      const recs=await db.getGebruikerModules(userId);
      const map={};
      recs.forEach(r=>{ map[r.module]=r.niveau; });
      setModuleState(p=>({...p,[userId]:map}));
    };

    const toggleModules=async(u)=>{
      if(!moduleState[u.id]) await loadModules(u.id);
      setOpenModules(u);
    };

    const setModule=async(u, module, niveau)=>{
      const key=`${u.id}_${module}`;
      setSavingModule(p=>({...p,[key]:true}));
      try{
        await db.saveGebruikerModule(u.id, park.id, module, niveau);
        setModuleState(p=>({...p,[u.id]:{...p[u.id],[module]:niveau}}));
      }catch(e){ toast.err("Opslaan mislukt"); }
      setSavingModule(p=>({...p,[key]:false}));
    };

    const ModuleModal=({u, onClose})=>{
      const mods=moduleState[u.id]||{};
      const niveaus=["beheer","lezen","verborgen"];
      const niveauLabel={beheer:"Beheer",lezen:"Lezen",verborgen:"Verborgen"};
      const niveauColor={beheer:"#7a4fb5",lezen:"#2e6da4",verborgen:"#bbb"};
      const niveauBg={beheer:"#f5f0fc",lezen:"#f0f4ff",verborgen:"#f5f5f5"};
      return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:2000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"16px",overflowY:"auto"}}>
        <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:560,marginTop:8,boxShadow:"0 8px 40px rgba(0,0,0,.2)"}}>
          {/* Header */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px",borderBottom:"1px solid #eee"}}>
            <div>
              <div style={{fontWeight:700,fontSize:16,color:"#1a1a1a"}}>🎛️ Moduletoegang</div>
              <div style={{fontSize:12,color:"#888",marginTop:2}}>{u.name} · Deelbeheer</div>
            </div>
            <button onClick={onClose} style={{width:32,height:32,borderRadius:16,border:"1px solid #ddd",background:"#f5f5f5",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#666"}}>✕</button>
          </div>
          {/* Legenda */}
          <div style={{display:"flex",gap:8,padding:"10px 18px",background:"#fafafa",borderBottom:"1px solid #eee"}}>
            {niveaus.map(n=><span key={n} style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:niveauBg[n],color:niveauColor[n],fontWeight:600}}>● {niveauLabel[n]}</span>)}
          </div>
          {/* Module rijen */}
          <div style={{padding:"8px 0"}}>
            {MODULES.map((m,i)=>{
              const huidig=mods[m.code]||"verborgen";
              const key=`${u.id}_${m.code}`;
              const saving=!!savingModule[key];
              return <div key={m.code} style={{display:"flex",alignItems:"center",padding:"10px 18px",borderBottom:i<MODULES.length-1?"1px solid #f0f0f0":"none",gap:12}}>
                <div style={{flex:1,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:20}}>{m.icon}</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:"#222"}}>{m.label}</div>
                    <div style={{fontSize:11,color:niveauColor[huidig],fontWeight:500,marginTop:1}}>● {niveauLabel[huidig]}</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  {niveaus.map(n=><button key={n} onClick={()=>setModule(u,m.code,n)} disabled={saving}
                    style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${huidig===n?niveauColor[n]:"#ddd"}`,background:huidig===n?niveauColor[n]:"#fff",color:huidig===n?"#fff":niveauColor[n],fontSize:11,fontWeight:600,cursor:saving?"wait":"pointer",transition:"all .15s",opacity:saving?.6:1,whiteSpace:"nowrap"}}>
                    {saving&&huidig!==n?"…":niveauLabel[n]}
                  </button>)}
                </div>
              </div>;
            })}
          </div>
          {/* Footer */}
          <div style={{padding:"12px 18px",borderTop:"1px solid #eee",display:"flex",justifyContent:"flex-end"}}>
            <button onClick={onClose} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"var(--g)",color:"#fff",fontWeight:600,fontSize:13,cursor:"pointer"}}>Klaar</button>
          </div>
        </div>
      </div>;
    };

    const saveRole=async(u)=>{
      const newRole=pendingRoles[u.id];
      if(!newRole) return;
      setSavingRole(u.id);
      try {
        await ds({type:"SET_ROLE",p:{id:u.id,role:newRole}});
        setPendingRoles(prev=>{const n={...prev};delete n[u.id];return n;});
        toast.ok(`✅ Rol van ${u.name} opgeslagen als ${PERMISSIONS[newRole]?.label||newRole}`);
      } catch(e) {
        toast.err("Kon rol niet opslaan: "+(e?.message||e));
      }
      setSavingRole(null);
    };
    if(!hasPerm(s.user,"gebruikers_beheren")) return <div className="pg"><h1 className="pt">Toegang</h1><div style={{textAlign:"center",padding:"30px 20px",color:"var(--txtl)"}}>{IC.lock}<p style={{fontSize:14,marginTop:8}}>Alleen beheerders kunnen toegang beheren.</p></div></div>;

    const statusInfo={
      actief:   {label:"Actief",      icon:"🟢", bg:"#f0fff4", color:"#16a34a"},
      uitgenodigd:{label:"Uitgenodigd",icon:"📬", bg:"#fffbeb", color:"#d97706"},
      geen_account:{label:"Geen account",icon:"⚪", bg:"#f5f5f5", color:"#6b7280"},
      inactief: {label:"Inactief",    icon:"🔴", bg:"#fff0f0", color:"#dc2626"},
    };

    const resendInvite=async(u)=>{
      setResending(u.id);
      try {
        const token=await db.getToken();
        const res=await fetch(`${SUPABASE_URL}/functions/v1/invite-user`,{
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
          body:JSON.stringify({naam:u.name,email:u.email,type:u.role,park_id:park.id,resend:true})
        });
        const data=await res.json();
        if(data.error) toast.err(data.error);
        else toast.ok(`📬 Uitnodiging opnieuw verstuurd naar ${u.email}`);
      } catch(e){ toast.err("Kon uitnodiging niet versturen."); }
      setResending(null);
    };

    const sendPasswordReset=async(u)=>{
      toast.confirm(`Wachtwoord reset sturen naar ${u.email}?`, async()=>{
        const {error}=await db.resetPassword(u.email);
        if(error) toast.err("Kon reset-email niet versturen: "+error.message);
        else toast.ok(`Reset-email verstuurd naar ${u.email}`);
      });
    };

    return <div className="pg"><h1 className="pt">Toegangsbeheer</h1>
      {isAppAdmin&&<div className="card" style={{borderLeft:"3px solid #ffd700",marginBottom:12}}>
        <div style={{fontSize:12,fontWeight:700,color:"#8B7500",marginBottom:4}}>⚙️ App Admin modus</div>
        <p style={{fontSize:12,color:"var(--txtl)",lineHeight:1.5}}>Je bekijkt dit park als App Admin. Ga terug naar het Admin Panel voor platform-brede instellingen.</p>
      </div>}

      <div className="sh"><span className="st">Gebruikers ({s.users.length})</span></div>
      {s.users.map(u=>{
        const st=statusInfo[u.status||"actief"]||statusInfo.actief;
        const isDeelbeheer=(pendingRoles[u.id]??u.role)==="deelbeheer";
        return <div key={u.id} className="card" style={{padding:12,marginBottom:8,opacity:u.actief===false?.5:1}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div className="ua" style={{background:rCol(u.role),flexShrink:0}}>{u.name.split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontWeight:600,fontSize:14}}>{u.name}</span>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:st.bg,color:st.color,fontWeight:600}}>
                  {st.icon} {st.label}
                </span>
              </div>
              <div style={{fontSize:12,color:"var(--txtl)",marginTop:2}}>{u.email}</div>
              {u.laatste_login&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>
                Laatste login: {new Date(u.laatste_login).toLocaleDateString("nl-NL")}
              </div>}
              {u.uitgenodigd_op&&u.status==="uitgenodigd"&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>
                Uitgenodigd: {new Date(u.uitgenodigd_op).toLocaleDateString("nl-NL")}
              </div>}
              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,flexWrap:"wrap"}}>
                <select value={pendingRoles[u.id]??u.role} onChange={e=>setPendingRoles(prev=>({...prev,[u.id]:e.target.value}))}
                  style={{fontSize:11,padding:"2px 6px",borderRadius:7,border:`1px solid ${pendingRoles[u.id]?"#f59e0b":"var(--brd)"}`,fontFamily:"inherit",background:rCol(pendingRoles[u.id]??u.role)+"12",color:rCol(pendingRoles[u.id]??u.role),fontWeight:600}}>
                  {Object.entries(PERMISSIONS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
                {pendingRoles[u.id]&&<button onClick={()=>saveRole(u)} disabled={savingRole===u.id}
                  style={{fontSize:11,padding:"2px 10px",borderRadius:7,border:"none",background:"#16a34a",color:"#fff",fontWeight:700,cursor:"pointer",opacity:savingRole===u.id?.6:1}}>
                  {savingRole===u.id?"...":"Opslaan"}
                </button>}
                {pendingRoles[u.id]&&<button onClick={()=>setPendingRoles(prev=>{const n={...prev};delete n[u.id];return n;})}
                  style={{fontSize:11,padding:"2px 8px",borderRadius:7,border:"1px solid var(--brd)",background:"var(--cd)",color:"var(--txtl)",cursor:"pointer"}}>
                  ✕
                </button>}
              </div>
              {/* Deelbeheer module knop */}
              {(isDeelbeheer||u.role==="deelbeheer")&&(hasPerm(s.user,"gebruikers_beheren")||isAppAdmin)&&<button onClick={()=>toggleModules(u)} style={{marginTop:8,fontSize:11,padding:"4px 10px",borderRadius:7,border:"1px solid #c9b0e8",background:"#f5f0fc",color:"#7a4fb5",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                🎛️ Moduletoegang instellen
              </button>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
              {u.status==="uitgenodigd"&&<button className="ubtn" style={{fontSize:11,padding:"3px 7px",background:"#fffbeb",color:"#d97706",border:"1px solid #d97706",borderRadius:6}}
                onClick={()=>resendInvite(u)} disabled={resending===u.id} title="Uitnodiging opnieuw sturen">
                {resending===u.id?"...":"📬"}
              </button>}
              {u.status==="actief"&&<button className="ubtn" style={{fontSize:11,padding:"3px 7px",background:"#f0f4ff",color:"#4a6cf7",border:"1px solid #4a6cf7",borderRadius:6}}
                onClick={()=>sendPasswordReset(u)} title="Wachtwoord reset sturen">
                🔑
              </button>}
              <button className="ubtn" onClick={()=>ds({type:"TOG_ACTIVE",p:u.id})} title={u.actief?"Deactiveren":"Activeren"}>{u.actief?IC.chk:IC.X}</button>
              <button className="ubtn" onClick={()=>toast.confirm(`${u.name} verwijderen uit het park?`,()=>ds({type:"DEL_USER",p:u.id}))} title="Verwijderen">{IC.trash}</button>
            </div>
          </div>
        </div>;
      })}
      {s.users.length===0&&<div className="card"><EmptyState icon="👥" title="Nog geen medewerkers" sub="Nodig je eerste teamlid uit via de + knop."/></div>}

      <div className="sh" style={{marginTop:16}}><span className="st">Rollen & Rechten</span></div>
      {Object.entries(PERMISSIONS).map(([k,role])=>
        <div key={k} className="card" style={{padding:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
            <span className="rbdg" style={{background:role.color+"18",color:role.color,padding:"2px 8px"}}>{role.label}</span>
            <span style={{fontSize:12,color:"var(--txtl)"}}>{role.beschrijving}</span>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:2}}>{role.can.map(p=><span key={p} style={{fontSize:8,padding:"1px 5px",background:"var(--cd)",borderRadius:6,color:"var(--txtl)"}}>{p.replace(/_/g," ")}</span>)}</div>
        </div>
      )}

      <button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addUsr"}})}>{IC.plus}</button>
      {s.modal==="addUsr"&&<UsrForm parkId={park.id} isAppAdmin={isAppAdmin} onClose={()=>ds({type:"CLOSE"})} onSave={f=>ds({type:"ADD_USER",p:f})}/>}
      {openModules&&<ModuleModal u={openModules} onClose={()=>setOpenModules(null)}/>}
    </div>;
  };

  // ═══════════════════════════════════════════════════════════
  // SLEUTELS (KEYS) MANAGEMENT - Grid with checkboxes
  // ═══════════════════════════════════════════════════════════
  const FunctiegroepPage = () => {
    const canM = hasPerm(s.user, "rollen_beheren") || s.user?.isAppAdmin;
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedPerson, setSelectedPerson] = useState(null);

    const addToGroup = async () => {
      if (!selectedGroup || !selectedPerson) return;
      try {
        const [type, id] = selectedPerson.split(':');
        await db.addToFunctiegroep(
          selectedGroup.id,
          park.id,
          type === 'user' ? parseInt(id) : null,
          type === 'contact' ? parseInt(id) : null
        );
        // Reload
        const updated = await db.getFunctiegroepIndeling(park.id);
        d({type: "LOAD", p: {functiegroepIndeling: updated}});
        setShowAddModal(false);
        setSelectedGroup(null);
        setSelectedPerson(null);
        toast.ok('Persoon toegevoegd! ✅');
      } catch (err) { console.error('Error:', err); toast.err('Fout: ' + err.message); }
    };

    const removeFromGroup = async (indelingId) => {
      toast.confirm('Verwijderen?', async()=>{
        try {
          await db.removeFromFunctiegroep(indelingId);
          const updated = await db.getFunctiegroepIndeling(park.id);
          d({type: "LOAD", p: {functiegroepIndeling: updated}});
          toast.ok('Persoon verwijderd');
        } catch (err) { console.error('Error:', err); toast.err('Fout: ' + err.message); }
      });
    };

    return <div className="pg"><h1 className="pt">👥 Functiegroepen</h1>
      {!canM && <div style={{textAlign:"center", padding:"30px 20px", color:"var(--txtl)"}}>{IC.lock}<p style={{fontSize:14, marginTop:8}}>Alleen beheerders kunnen functiegroepen beheren.</p></div>}
      
      {canM && <>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginTop:20}}>
          {(s.functiegroepen||[]).map(group => {
            const members = (s.functiegroepIndeling||[]).filter(x => x.functiegroep_id === group.id);
            return <div key={group.id} style={{
              background:'#fff',
              border:`3px solid ${group.kleur}`,
              borderRadius:12,
              padding:20,
              boxShadow:'0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{margin:'0 0 15px 0', color:group.kleur, fontSize:16, fontWeight:700}}>{group.naam}</h3>
              
              {members.length > 0 ? (
                <div style={{marginBottom:15}}>
                  {members.map(member => (
                    <div key={member.id} style={{
                      display:'flex',
                      justifyContent:'space-between',
                      alignItems:'center',
                      padding:8,
                      background:'#f9f9f9',
                      borderRadius:6,
                      marginBottom:8,
                      fontSize:14
                    }}>
                      <div>
                        {member.gebruiker_id ? (
                          <div>
                            <strong>{(s.users||[]).find(u => u.id === member.gebruiker_id)?.name}</strong>
                            <div style={{fontSize:12, color:'#999'}}>Teamlid</div>
                          </div>
                        ) : (
                          <div>
                            <strong>{(s.contacten||[]).find(c => c.id === member.contact_id)?.naam}</strong>
                            <div style={{fontSize:12, color:'#999'}}>Contact</div>
                          </div>
                        )}
                      </div>
                      {canM && <button onClick={() => removeFromGroup(member.id)} style={{
                        background:'#ff6b6b',
                        color:'#fff',
                        border:'none',
                        borderRadius:4,
                        padding:'4px 8px',
                        cursor:'pointer',
                        fontSize:13
                      }}>✕</button>}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{fontSize:13, color:'#999', padding:'10px 0', textAlign:'center'}}>Niemand toegewezen</div>
              )}
              
              {canM && <button onClick={() => {setSelectedGroup(group); setShowAddModal(true);}} style={{
                width:'100%',
                padding:10,
                background:group.kleur,
                color:'#fff',
                border:'none',
                borderRadius:6,
                cursor:'pointer',
                fontWeight:600,
                fontSize:14
              }}>➕ Persoon toevoegen</button>}
            </div>;
          })}
        </div>

        {showAddModal && selectedGroup && <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
          <div style={{background:'#fff', borderRadius:8, padding:24, width:'90%', maxWidth:400, boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
            <h3 style={{margin:'0 0 20px 0', color:selectedGroup.kleur}}>Persoon toevoegen aan {selectedGroup.naam}</h3>
            
            <label style={{display:'block', fontSize:13, fontWeight:600, color:'#333', marginBottom:8}}>Teamleden</label>
            <select value={selectedPerson?.startsWith('user:') ? selectedPerson : ''} onChange={(e) => setSelectedPerson(e.target.value)} style={{width:'100%', padding:10, border:'1px solid #ddd', borderRadius:6, marginBottom:15, fontSize:16, boxSizing:'border-box'}}>
              <option value="">-- Teamlid kiezen --</option>
              {(s.users||[]).map(u => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}
            </select>

            <label style={{display:'block', fontSize:13, fontWeight:600, color:'#333', marginBottom:8}}>OF Contacten</label>
            <select value={selectedPerson?.startsWith('contact:') ? selectedPerson : ''} onChange={(e) => setSelectedPerson(e.target.value)} style={{width:'100%', padding:10, border:'1px solid #ddd', borderRadius:6, marginBottom:20, fontSize:16, boxSizing:'border-box'}}>
              <option value="">-- Contact kiezen --</option>
              {(s.contacten||[]).map(c => <option key={c.id} value={`contact:${c.id}`}>{c.naam}</option>)}
            </select>

            <div style={{display:'flex', gap:10}}>
              <button onClick={addToGroup} disabled={!selectedPerson} style={{flex:1, padding:'10px 20px', background:selectedGroup.kleur, color:'#fff', border:'none', borderRadius:6, cursor:selectedPerson?'pointer':'not-allowed', fontWeight:600, opacity:selectedPerson?1:0.5}}>✅ Toevoegen</button>
              <button onClick={() => {setShowAddModal(false); setSelectedGroup(null); setSelectedPerson(null);}} style={{flex:1, padding:'10px 20px', background:'#ddd', color:'#333', border:'none', borderRadius:6, cursor:'pointer', fontWeight:600}}>✕ Annuleren</button>
            </div>
          </div>
        </div>}
      </>}
    </div>;
  };

  const SleUtelsGrid = () => {
    const canM = hasPerm(s.user, "sleutels_beheren");
    const [selectedSleutel, setSelectedSleutel] = useState(null);
    const [assignments, setAssignments] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [originalAssignments, setOriginalAssignments] = useState({});
    const [issuedCounts, setIssuedCounts] = useState({});

    // Load issued counts for all sleutels
    useEffect(() => {
      const loadIssuedCounts = async () => {
        const counts = {};
        for (const sleutel of (s.sleutels || [])) {
          const assignmentData = await db.getSleutelInBezit(park.id, sleutel.id);
          counts[sleutel.id] = assignmentData ? assignmentData.length : 0;
        }
        setIssuedCounts(counts);
      };
      if (s.sleutels && s.sleutels.length > 0 && canM) {
        loadIssuedCounts();
      }
    }, [s.sleutels, park.id, canM]);

    const handleSelectSleutel = async (sleutel) => {
      setSelectedSleutel(sleutel);
      try {
        const assignmentData = await db.getSleutelInBezit(park.id, sleutel.id);
        const assignMap = {};
        assignmentData?.forEach(a => {
          if (a.gebruiker_id) assignMap[`u${a.gebruiker_id}`] = true;
          else if (a.contact_id) assignMap[`c${a.contact_id}`] = true;
          else if (a.op_park) assignMap['p_park'] = true;
        });
        
        setAssignments(assignMap);
        setOriginalAssignments({ ...assignMap });
        setEditMode(false);
      } catch (err) { console.error('Error loading assignments:', err); }
    };

    const handleCheckboxChange = (type, personId) => {
      const key = type === 'p' ? 'p_park' : `${type}${personId}`;
      setAssignments(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async () => {
      if (!selectedSleutel) return;
      setSaving(true);
      try {
        const keysToAdd = [], keysToRemove = [];
        for (const key in assignments) {
          if (assignments[key] && !originalAssignments[key]) keysToAdd.push(key);
          else if (!assignments[key] && originalAssignments[key]) keysToRemove.push(key);
        }

        for (const key of keysToAdd) {
          if (key.startsWith('u')) {
            const personId = parseInt(key.slice(1));
            await db.issueSleutelToUser({
              p_gebruiker_id: personId,
              p_sleutel_id: selectedSleutel.id,
              p_park_id: park.id,
              p_datum_uitgegeven: new Date().toISOString().split('T')[0]
            });
          } else if (key.startsWith('c')) {
            const personId = parseInt(key.slice(1));
            await db.issueSleutelToContact({
              p_contact_id: personId,
              p_sleutel_id: selectedSleutel.id,
              p_park_id: park.id,
              p_datum_uitgegeven: new Date().toISOString().split('T')[0]
            });
          } else if (key === 'p_park') {
            await db.issueSleutelToPark({
              p_sleutel_id: selectedSleutel.id,
              p_park_id: park.id,
              p_datum_uitgegeven: new Date().toISOString().split('T')[0]
            });
          }
        }

        for (const key of keysToRemove) {
          let assignmentData = null;
          
          if (key.startsWith('u')) {
            const personId = parseInt(key.slice(1));
            assignmentData = await db.getSleutelInBezitByUser(park.id, selectedSleutel.id, personId);
          } else if (key.startsWith('c')) {
            const personId = parseInt(key.slice(1));
            assignmentData = await db.getSleutelInBezitByContact(park.id, selectedSleutel.id, personId);
          } else if (key === 'p_park') {
            assignmentData = await db.getSleutelInBezitByPark(park.id, selectedSleutel.id);
          }
          
          if (assignmentData) {
            await db.returnSleutel({
              p_assignment_id: assignmentData.id,
              p_datum_teruggebracht: new Date().toISOString().split('T')[0]
            });
          }
        }

        await handleSelectSleutel(selectedSleutel);
        toast.ok('Sleutels bijgewerkt! ✅');
      } catch (err) { console.error('Error saving:', err); toast.err('Fout: ' + err.message); }
      finally { setSaving(false); }
    };

    const handleCancel = () => {
      setAssignments({ ...originalAssignments });
      setEditMode(false);
    };

    // LEVEL 1: Keys List
    if (!selectedSleutel) {
      return <div className="pg"><h1 className="pt">🔑 Sleutels</h1>
        {!canM && <div style={{textAlign:"center", padding:"30px 20px", color:"var(--txtl)"}}>{IC.lock}<p style={{fontSize:14, marginTop:8}}>Alleen beheerders kunnen sleutels beheren.</p></div>}
        {canM && <>
          <p style={{fontSize:12, color:"var(--txtl)", marginBottom:15}}>Totaal {s.sleutels?.length||0} sleuteltypen</p>
          {(s.sleutels||[]).map(k => {
            const issuedCount = issuedCounts[k.id] || 0;
            const countColor = issuedCount === k.aantal ? '#4caf50' : '#ff6b6b';
            
            return <div key={k.id} className="ci-wrap" onClick={()=>handleSelectSleutel(k)} style={{cursor:'pointer'}}>
              <div className="ci" style={{cursor:"pointer", marginBottom:0, boxShadow:"none", border:"none", padding:"10px 0 6px", display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <div className="cic">
                  <div className="cit" style={{fontWeight:700, color:"var(--g)", fontSize:14}}>
                    {k.omschrijving} <span style={{fontSize:13, color:countColor}}>({issuedCount}/{k.aantal})</span>
                  </div>
                </div>
                {canM && <div className="iab" onClick={(e)=>e.stopPropagation()} style={{marginLeft:'auto'}}>
                  <button onClick={e=>{e.stopPropagation();ds({type:"MODAL",p:{type:"editS",item:k}});}}>{IC.edit}</button>
                  <button onClick={e=>{e.stopPropagation();toast.confirm("Sleutel verwijderen?",()=>ds({type:"DEL_SLEUTEL",p:k.id}));}}>{IC.trash}</button>
                </div>}
              </div>
            </div>;
          })}
          {(!s.sleutels || s.sleutels.length===0) && <div className="card" style={{textAlign:"center", padding:20, color:"var(--txtl)"}}>Geen sleutels aangemaakt</div>}
          {canM && <button className="fab" onClick={()=>ds({type:"MODAL",p:{type:"addS"}})}>{IC.plus}</button>}
          {(s.modal==="addS" || s.modal==="editS") && <SleutelForm item={s.editItem} onClose={()=>ds({type:"CLOSE"})} onSave={k=>ds({type:s.editItem?"EDIT_SLEUTEL":"ADD_SLEUTEL",p:s.editItem?{...k,id:s.editItem.id}:k})}/>}
        </>}
      </div>;
    }

    // LEVEL 2: Assign to Users/Contacts
    return <div className="pg"><h1 className="pt">🔑 {selectedSleutel.omschrijving}</h1>
      <button onClick={()=>setSelectedSleutel(null)} style={{background:"none", border:"none", color:"var(--g)", cursor:"pointer", fontSize:16, padding:"8px 0", marginBottom:10, fontWeight:600}}>← Terug naar sleutels</button>
      
      {!editMode && <div style={{display:'flex', justifyContent:'flex-end', marginBottom:20}}><button onClick={()=>setEditMode(true)} style={{padding:'10px 20px', background:'var(--g)', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontWeight:600}}>✏️ Bewerken</button></div>}
      
      {editMode && <div style={{display:'flex', gap:10, marginBottom:20}}>
        <PawBtn onClick={handleSave} loading={saving}>💾 Opslaan</PawBtn>
        <button onClick={handleCancel} style={{padding:'10px 20px', background:'#999', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontWeight:600}}>✕ Annuleren</button>
      </div>}

      <div style={{marginBottom:30}}>
        <h3 style={{color:"var(--g)", fontSize:14, fontWeight:700, marginBottom:12}}>📍 Park Locatie</h3>
        <label style={{display:'flex', alignItems:'center', padding:12, background:'#f9f9f9', borderRadius:6, cursor:editMode?'pointer':'default', opacity:editMode?1:0.7}}>
          <input type="checkbox" checked={assignments['p_park']||false} onChange={()=>handleCheckboxChange('p','park')} disabled={!editMode} style={{width:18, height:18, cursor:editMode?'pointer':'not-allowed', marginRight:12}}/>
          <div style={{flex:1}}>
            <div style={{fontWeight:600, fontSize:14}}>Op park</div>
            <div style={{fontSize:12, color:'#999'}}>Sleutel bevindt zich fysiek op het parkterrein</div>
          </div>
        </label>
      </div>

      <div style={{marginBottom:30}}>
        <h3 style={{color:"var(--g)", fontSize:14, fontWeight:700, marginBottom:12}}>👥 Teamleden</h3>
        <div style={{display:'flex', flexDirection:'column', gap:10}}>
          {(s.users||[]).map(tm => (
            <label key={`u${tm.id}`} style={{display:'flex', alignItems:'center', padding:12, background:'#f9f9f9', borderRadius:6, cursor:editMode?'pointer':'default', opacity:editMode?1:0.7}}>
              <input type="checkbox" checked={assignments[`u${tm.id}`]||false} onChange={()=>handleCheckboxChange('u',tm.id)} disabled={!editMode} style={{width:18, height:18, cursor:editMode?'pointer':'not-allowed', marginRight:12}}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:600, fontSize:14}}>{tm.name}</div>
                <div style={{fontSize:12, color:'#999'}}>{tm.rol||'Teamlid'}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 style={{color:"var(--g)", fontSize:14, fontWeight:700, marginBottom:12}}>🤝 Contacten</h3>
        <div style={{display:'flex', flexDirection:'column', gap:10}}>
          {(s.contacten||[]).map(c => (
            <label key={`c${c.id}`} style={{display:'flex', alignItems:'center', padding:12, background:'#f9f9f9', borderRadius:6, cursor:editMode?'pointer':'default', opacity:editMode?1:0.7}}>
              <input type="checkbox" checked={assignments[`c${c.id}`]||false} onChange={()=>handleCheckboxChange('c',c.id)} disabled={!editMode} style={{width:18, height:18, cursor:editMode?'pointer':'not-allowed', marginRight:12}}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:600, fontSize:14}}>{c.naam}</div>
                <div style={{fontSize:12, color:'#999'}}>{(s.contactCategorieen||[]).find(cc=>cc.id===c.contact_categorie_id)?.categorie || 'Contact'}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>;
  };

  const VirtueelParkPg=()=>{
    const canManage=s.user?.isAppAdmin||s.user?.role==="beheerder";
    const TYPE_ICON={"voliere":"🐦","stal":"🐄","aquarium":"🐠","terrarium":"🦎","wei":"🌿","ren":"🐔","hok":"🪵","afdak":"⛺","bak":"🪣","overig":"🏠"};
    const TYPE_LABELS={"voliere":"Volière","stal":"Stal","aquarium":"Aquarium","terrarium":"Terrarium","wei":"Wei","ren":"Ren","hok":"Hok","afdak":"Afdak","bak":"Bak","overig":"Overig"};
    const emptyVerblijf={naam:"",type:"overig",omschrijving:"",volgorde:0,actief:true,icoon:""};
    const ICOON_OPTIES=["🐄","🐑","🐐","🐖","🐴","🐇","🐔","🦆","🦢","🦜","🐦","🐠","🦎","🐢","🐍","🐾","🌿","🏠","⛺","🪣","🪵","🦙","🦘","🦬","🦫","🦦","🦡"];
    const [verblijven,setVerblijven]=useState([]);
    const [loading,setLoading]=useState(true);
    const [expanded,setExpanded]=useState(null);
    const [bewoners,setBewoners]=useState({});
    const [bewonersLoading,setBewonersLoading]=useState({});
    const [showForm,setShowForm]=useState(false);
    const [editVerblijf,setEditVerblijf]=useState(null);
    const [form,setForm]=useState(emptyVerblijf);
    const [saving,setSaving]=useState(false);
    const [parkDieren,setParkDieren]=useState([]);
    const [bewonerTab,setBewonerTab]=useState("dier"); // "dier" | "groep"
    const [gekozenSoortId,setGekozenSoortId]=useState(null);
    const [geselecteerdeDieren,setGeselecteerdeDieren]=useState(new Set());
    const [groepForm,setGroepForm]=useState({groep_naam:"",groep_aantal:"",groep_subsoorten:[],groep_foto_url:"",groep_geluid_url:""});
    const [subsoortInput,setSubsoortInput]=useState({naam:"",aantal:""});
    const [addingBewoner,setAddingBewoner]=useState(false);
    const [mediaPicker,setMediaPicker]=useState(null);
    const [showQr,setShowQr]=useState(false);
    const [mediaResults,setMediaResults]=useState([]);
    const [mediaLoading,setMediaLoading]=useState(false);
    const [mediaError,setMediaError]=useState("");
    const [mediaQuery,setMediaQuery]=useState("");
    const uf=(k,v)=>setForm(p=>({...p,[k]:v}));
    const ug=(k,v)=>setGroepForm(p=>({...p,[k]:v}));

    useEffect(()=>{
      db.getVerblijvenByPark(park.id,false).then(async d=>{
        setVerblijven(d);
        setLoading(false);
        // Laad bewoners voor alle verblijven meteen zodat icoontjes kloppen
        const bMap={};
        await Promise.all((d||[]).map(async v=>{
          try{ bMap[v.id]=await db.getBewoners(v.id); }catch{ bMap[v.id]=[]; }
        }));
        setBewoners(bMap);
      }).catch(()=>setLoading(false));
      db.getDieren(park.id).then(d=>setParkDieren(d||[])).catch(()=>{});
    },[]);

    const loadBewoners=async(verblijfId)=>{
      setBewonersLoading(p=>({...p,[verblijfId]:true}));
      try{ const data=await db.getBewoners(verblijfId); setBewoners(p=>({...p,[verblijfId]:data})); }
      catch(e){ toast.err("Bewoners laden mislukt"); }
      finally{ setBewonersLoading(p=>({...p,[verblijfId]:false})); }
    };

    const toggleExpand=async(id)=>{
      if(expanded===id){ setExpanded(null); return; }
      setExpanded(id);
      if(!bewoners[id]) await loadBewoners(id);
    };

    const saveVerblijf=async()=>{
      if(!form.naam.trim()){ toast.warn("Naam is verplicht"); return; }
      setSaving(true);
      try{
        if(editVerblijf){
          const updated=await db.updateVerblijf(editVerblijf.id,{naam:form.naam.trim(),type:form.type,omschrijving:form.omschrijving||null,volgorde:parseInt(form.volgorde)||0,actief:form.actief,icoon:form.icoon||null});
          setVerblijven(p=>p.map(v=>v.id===editVerblijf.id?updated:v));
          toast.ok("Verblijf bijgewerkt");
        } else {
          const created=await db.addVerblijf({naam:form.naam.trim(),type:form.type,omschrijving:form.omschrijving||null,volgorde:parseInt(form.volgorde)||0,actief:form.actief,park_id:park.id,icoon:form.icoon||null});
          setVerblijven(p=>[...p,created]);
          toast.ok("Verblijf aangemaakt 🏠");
        }
        setShowForm(false); setEditVerblijf(null); setForm(emptyVerblijf);
      }catch(e){ toast.err("Opslaan mislukt: "+e.message); }
      finally{ setSaving(false); }
    };

    const handleDelete=async(v)=>{
      if(!window.confirm(`"${v.naam}" verwijderen? Alle bewoners worden ook verwijderd.`)) return;
      try{ await db.deleteVerblijf(v.id); setVerblijven(p=>p.filter(x=>x.id!==v.id)); toast.ok("Verwijderd"); }
      catch(e){ toast.err("Verwijderen mislukt"); }
    };

    const deleteBewoner=async(verblijfId,bewonerIds)=>{
      try{
        await db.deleteBewoner(bewonerIds);
        setBewoners(p=>({...p,[verblijfId]:(p[verblijfId]||[]).filter(b=>b.id!==bewonerIds)}));
      }catch(e){ toast.err("Verwijderen mislukt"); }
    };

    const addBewonerDieren=async(verblijfId)=>{
      if(geselecteerdeDieren.size===0){ toast.warn("Selecteer minstens één dier"); return; }
      setAddingBewoner(true);
      try{
        const alInVerblijf=new Set((bewoners[verblijfId]||[]).filter(b=>b.dier_id).map(b=>b.dier_id));
        const nieuweIds=[...geselecteerdeDieren].filter(id=>!alInVerblijf.has(id));
        if(nieuweIds.length===0){ toast.warn("Alle geselecteerde dieren zitten al in dit verblijf"); return; }
        const toegevoegd=[];
        for(const dierId of nieuweIds){
          const created=await db.addBewoner({verblijf_id:verblijfId,dier_id:dierId,volgorde:(bewoners[verblijfId]||[]).length+toegevoegd.length});
          toegevoegd.push(created);
        }
        setBewoners(p=>({...p,[verblijfId]:[...(p[verblijfId]||[]),...toegevoegd]}));
        setGekozenSoortId(null);
        setGeselecteerdeDieren(new Set());
        toast.ok(`${toegevoegd.length} dier${toegevoegd.length!==1?"en":""} toegevoegd`);
      }catch(e){ toast.err("Toevoegen mislukt: "+e.message); }
      finally{ setAddingBewoner(false); }
    };

    const addBewonerGroep=async(verblijfId)=>{
      if(!groepForm.groep_naam.trim()){ toast.warn("Naam is verplicht"); return; }
      setAddingBewoner(true);
      try{
        const created=await db.addBewoner({verblijf_id:verblijfId,groep_naam:groepForm.groep_naam.trim(),groep_aantal:groepForm.groep_aantal||null,groep_subsoorten:groepForm.groep_subsoorten,groep_foto_url:groepForm.groep_foto_url||null,groep_geluid_url:groepForm.groep_geluid_url||null,volgorde:(bewoners[verblijfId]||[]).length});
        setBewoners(p=>({...p,[verblijfId]:[...(p[verblijfId]||[]),created]}));
        setGroepForm({groep_naam:"",groep_aantal:"",groep_subsoorten:[],groep_foto_url:"",groep_geluid_url:""});
        setSubsoortInput({naam:"",aantal:""});
        toast.ok("Groep toegevoegd");
      }catch(e){ toast.err("Toevoegen mislukt: "+e.message); }
      finally{ setAddingBewoner(false); }
    };

    const stripHtml=(v)=>String(v||"").replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim();
    const getLicenseBucket=(raw)=>{
      const v=String(raw||"").trim().toLowerCase();
      if(!v) return null;
      if(v.includes("public domain")||v==="pd"||v==="cc0"||v.includes("cc0 1.0")) return "PD/CC0";
      if(v.includes("by-sa")&&!v.includes("nc")&&!v.includes("nd")) return "CC BY-SA";
      if((v.includes("cc by")||v==="by")&&!v.includes("by-sa")&&!v.includes("nc")&&!v.includes("nd")) return "CC BY";
      return null;
    };

    const fetchMedia=async(kind,query)=>{
      setMediaLoading(true); setMediaResults([]); setMediaError("");
      try{
        if(kind==="image"){
          const res=await fetch(`https://commons.wikimedia.org/w/api.php?action=query&format=json&origin=*&generator=search&gsrnamespace=6&gsrsearch=${encodeURIComponent(query)}&gsrlimit=12&prop=imageinfo&iiprop=url|extmetadata&iiurlwidth=800`);
          const json=await res.json();
          const pages=Object.values(json?.query?.pages||{});
          const items=pages.map(page=>{
            const info=page?.imageinfo?.[0]||{};
            const meta=info?.extmetadata||{};
            const licenseRaw=stripHtml(meta.LicenseShortName?.value||meta.UsageTerms?.value||"");
            const bucket=getLicenseBucket(licenseRaw);
            return {id:page.pageid,kind:"image",preview:info.thumburl||info.url||"",fileUrl:info.url||"",creator:stripHtml(meta.Artist?.value||"Onbekend"),license:licenseRaw||"?",licenseBucket:bucket,pageUrl:`https://commons.wikimedia.org/wiki/${encodeURIComponent(page.title||"")}`};
          }).filter(i=>i.fileUrl&&i.licenseBucket);
          setMediaResults(items);
          if(!items.length) setMediaError("Geen rechten-vrije foto\'s gevonden.");
        } else {
          const res=await fetch(`https://xeno-canto.org/api/2/recordings?query=${encodeURIComponent(query)}`);
          const json=await res.json();
          const items=(json?.recordings||[]).slice(0,15).map(rec=>{
            const licenseRaw=String(rec.lic||"").replace(/^https?:\/\//,"");
            const bucket=getLicenseBucket(licenseRaw.replace(/\//g," "));
            const fileUrl=rec.file?(String(rec.file).startsWith("http")?rec.file:`https:${rec.file}`):"";
            return {id:rec.id,kind:"sound",preview:fileUrl,fileUrl,creator:rec.rec||"Onbekend",license:licenseRaw||"?",licenseBucket:bucket,pageUrl:`https://xeno-canto.org/${rec.id}`,title:rec.en||rec.gen||"Geluid"};
          }).filter(i=>i.fileUrl&&i.licenseBucket);
          setMediaResults(items);
          if(!items.length) setMediaError("Geen rechten-vrije geluiden gevonden.");
        }
      }catch(e){ setMediaError("Zoeken mislukt: "+e.message); }
      finally{ setMediaLoading(false); }
    };

    const openMediaPicker=(kind)=>{
      setMediaPicker(kind);
      const q=groepForm.groep_naam||"";
      setMediaQuery(q);
      if(q) fetchMedia(kind,q);
    };

    const applyMedia=(item)=>{
      if(mediaPicker==="image") ug("groep_foto_url",item.fileUrl);
      else ug("groep_geluid_url",item.fileUrl);
      setMediaPicker(null); setMediaResults([]);
    };

    const qrUrl=db.getVirtueelParkUrl(park.id);
    const qrPreview=getQrImageUrl(qrUrl,220);

    const downloadQr=()=>{
      const link=document.createElement("a");
      link.href=getQrImageUrl(qrUrl,1200); link.download=`${park.id}-virtueel-parkje-qr.png`; link.click();
    };
    const printPoster=()=>{
      const qrSrc=getQrImageUrl(qrUrl,700);
      const win=window.open("","_blank","width=900,height=1200");
      if(!win) return;
      win.document.write(`<!doctype html>
        <html lang="nl">
          <head>
            <meta charset="utf-8" />
            <title>QR poster - ${park.naam}</title>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link href="https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800&display=swap" rel="stylesheet" />
            <style>
              @page{size:A4 portrait;margin:0;}
              html,body{width:210mm;height:297mm;margin:0;padding:0;}
              *{box-sizing:border-box;}
              body{font-family:'Nunito',Arial,sans-serif;background:#2d7a2d;-webkit-print-color-adjust:exact;print-color-adjust:exact;overflow:hidden;}
              .poster{width:210mm;height:297mm;background:linear-gradient(160deg,#2d7a2d 0%,#3a8f3a 30%,#2d6e1a 70%,#245c12 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative;overflow:hidden;padding:10mm 10mm 28mm;gap:0;}
              .glow{position:absolute;top:38%;left:50%;transform:translate(-50%,-50%);width:200mm;height:200mm;background:radial-gradient(circle,rgba(255,255,255,.08) 0%,transparent 70%);pointer-events:none;}
              .animal{position:absolute;filter:drop-shadow(0 3px 6px rgba(0,0,0,.4));animation:float 5s ease-in-out infinite;}
              .a1{top:5%;left:5%;animation-delay:0s;font-size:38pt;}.a2{top:5%;right:4%;animation-delay:1.5s;font-size:34pt;}.a3{top:32%;left:2%;animation-delay:.8s;font-size:30pt;}.a4{top:32%;right:2%;animation-delay:2.2s;font-size:32pt;}.a5{bottom:16%;left:3%;animation-delay:1s;font-size:30pt;}.a6{bottom:16%;right:3%;animation-delay:.4s;font-size:32pt;}.a7{bottom:5%;left:14%;animation-delay:1.8s;font-size:30pt;z-index:3;}.a8{bottom:5%;right:12%;animation-delay:.9s;font-size:32pt;z-index:3;}
              .leaf{position:absolute;opacity:.4;animation:sway 6s ease-in-out infinite;}
              .l1{top:6%;left:22%;font-size:22pt;animation-delay:0s;}.l2{top:12%;right:22%;font-size:18pt;animation-delay:1.2s;}.l3{top:2%;left:48%;font-size:15pt;animation-delay:2.1s;}.l4{top:22%;left:16%;font-size:16pt;animation-delay:.7s;}.l5{top:3%;right:36%;font-size:20pt;animation-delay:3s;}
              @keyframes float{0%,100%{transform:translateY(0) rotate(-3deg);}50%{transform:translateY(-9px) rotate(3deg);}}
              @keyframes sway{0%,100%{transform:rotate(-8deg) scale(1);}50%{transform:rotate(8deg) scale(1.05);}}
              .header{position:relative;z-index:5;text-align:center;margin-bottom:9mm;}
              .logo-emoji{font-size:46pt;display:block;margin-bottom:3mm;filter:drop-shadow(0 4px 8px rgba(0,0,0,.35));}
              .title{font-family:'Fredoka One',cursive;font-size:42pt;color:#ffffff;text-shadow:0 3px 10px rgba(0,0,0,.45);line-height:1.05;}
              .title span{color:#a8e64a;}
              .tagline{font-size:13pt;color:rgba(255,255,255,.85);font-weight:700;margin-top:5px;letter-spacing:1.5px;text-transform:uppercase;}
              .parkname{font-family:'Fredoka One',cursive;font-size:24pt;color:#a8e64a;text-shadow:0 2px 8px rgba(0,0,0,.4);margin-top:6px;}
              .qr-frame{position:relative;z-index:5;background:#ffffff;border-radius:20px;padding:18px;box-shadow:0 12px 48px rgba(0,0,0,.4);margin-bottom:9mm;}
              .qr-frame .corner{position:absolute;font-size:13pt;opacity:.55;}
              .qr-frame .tl{top:6px;left:10px;}.qr-frame .tr{top:6px;right:10px;transform:scaleX(-1);}.qr-frame .bl{bottom:6px;left:10px;}.qr-frame .br{bottom:6px;right:10px;transform:scaleX(-1);}
              .qr-frame img{display:block;border-radius:6px;}
              .cta{position:relative;z-index:5;background:rgba(255,255,255,.14);border:2px solid rgba(255,255,255,.28);border-radius:50px;padding:11px 32px;text-align:center;}
              .cta-main{font-family:'Fredoka One',cursive;font-size:18pt;color:#fff;display:block;}
              .cta-url{font-size:11pt;color:#a8e64a;font-weight:700;letter-spacing:1.2px;margin-top:3px;display:block;}
              .grass{position:absolute;bottom:0;left:0;right:0;height:30mm;z-index:4;pointer-events:none;}
              .blade{position:absolute;bottom:0;border-radius:50% 50% 0 0;transform-origin:bottom center;}
              .close-btn{position:fixed;top:12px;right:12px;background:#245c12;color:#fff;border:none;border-radius:50%;width:40px;height:40px;font-size:22px;cursor:pointer;z-index:999;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.3);}
              .close-btn:hover{background:#c44536;}
              @media print{.close-btn{display:none!important;}}
            </style>
          </head>
          <body>
            <button class="close-btn" onclick="window.close()" title="Sluiten">&times;</button>
            <div class="poster">
              <div class="glow"></div>
              <div class="leaf l1">🍃</div><div class="leaf l2">🌿</div><div class="leaf l3">🍀</div><div class="leaf l4">🌱</div><div class="leaf l5">🍃</div>
              <div class="animal a1">🐔</div><div class="animal a2">🐰</div><div class="animal a3">🐑</div><div class="animal a4">🦆</div>
              <div class="animal a5">🐐</div><div class="animal a6">🐷</div><div class="animal a7">🐓</div><div class="animal a8">🦔</div>
              <div class="header">
                <span class="logo-emoji">🐾</span>
                <div class="title">Dieren<span>parkje</span>.nl</div>
                <div class="tagline">Ontdek ons Virtueel Parkje</div>
                <div class="parkname">${park.naam}</div>
              </div>
              <div class="qr-frame">
                <span class="corner tl">🐾</span><span class="corner tr">🐾</span><span class="corner bl">🐾</span><span class="corner br">🐾</span>
                <img src="${qrSrc}" alt="QR code" width="380" height="380" />
              </div>
              <div class="cta">
                <span class="cta-main">Scan mij! 📱</span>
                <span class="cta-url">dierenparkje.nl</span>
              </div>
              <div class="grass" id="grass"></div>
            </div>
            <script>
              var grass=document.getElementById("grass");
              var colors=["#2d8a1a","#3aa020","#258015","#45b528","#1e6e10"];
              for(var i=0;i<42;i++){var b=document.createElement("div");b.className="blade";var c=colors[Math.floor(Math.random()*colors.length)];b.style.cssText="left:"+((i/42)*100+(Math.random()-.5)*3)+"%;height:"+(30+Math.random()*55)+"px;background:"+c+";width:"+(6+Math.random()*9)+"px;opacity:"+(0.7+Math.random()*.3)+";";grass.appendChild(b);}
            <\/script>
          </body>
        </html>`);
      win.document.close();
      win.focus();
      setTimeout(()=>win.print(),800);
    };

    return <div className="pg-wrap">
      <h2 style={{fontFamily:"'Fraunces',serif",color:"var(--g)",marginBottom:4}}>🏡 Virtueel Parkje</h2>
      <p style={{fontSize:13,color:"var(--txtl)",marginBottom:16}}>Beheer virtuele verblijven en hun bewoners. Bezoekers scannen de QR-code om het parkje te ontdekken.</p>

      {/* QR sectie */}
      {canManage&&<div className="card" style={{padding:12,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontWeight:600,fontSize:13,color:"var(--g)"}}>📱 QR-code Virtueel Parkje</div>
          <button onClick={()=>setShowQr(p=>!p)} style={{padding:"5px 12px",borderRadius:8,border:"1px solid var(--brd)",background:"#fff",fontSize:12,cursor:"pointer",fontWeight:600}}>
            {showQr?"Verbergen":"Toon QR"}
          </button>
        </div>
        {showQr&&<div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",marginTop:12}}>
          <img src={qrPreview} alt="QR Virtueel Parkje" style={{width:90,borderRadius:8,border:"1px solid var(--brd)"}}/>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontSize:11,color:"var(--txtl)",wordBreak:"break-all",marginBottom:8}}>{qrUrl}</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button className="l-btn l-btn-primary" onClick={()=>window.open(qrUrl,"_blank")} style={{padding:"8px 12px",fontSize:12}}>🔗 Open pagina</button>
              <button className="l-btn" onClick={downloadQr} style={{padding:"8px 12px",fontSize:12}}>⬇️ Download QR</button>
              <button className="l-btn" onClick={printPoster} style={{padding:"8px 12px",fontSize:12}}>🖨️ Print poster</button>
            </div>
          </div>
        </div>}
      </div>}

      {loading&&<div style={{textAlign:"center",padding:32,color:"var(--txtl)"}}>Laden...</div>}

      {/* Verblijf formulier */}
      {canManage&&showForm&&<div className="card" style={{padding:16,marginBottom:16,border:"2px solid var(--g)"}}>
        <h3 style={{marginBottom:12,fontSize:15,color:"var(--g)"}}>{editVerblijf?"✏️ Verblijf bewerken":"🏠 Nieuw verblijf"}</h3>
        <div className="fg"><label className="fl">Naam *</label><input className="fi" style={{fontSize:16}} value={form.naam} onChange={e=>uf("naam",e.target.value)} placeholder="Bijv. Schapenstal 1"/></div>
        <div className="fg"><label className="fl">Type</label>
          <select className="fi" style={{fontSize:16}} value={form.type} onChange={e=>uf("type",e.target.value)}>
            {Object.entries(TYPE_LABELS).map(([k,v])=><option key={k} value={k}>{TYPE_ICON[k]} {v}</option>)}
          </select>
        </div>
        <div className="fg"><label className="fl">Omschrijving <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label><textarea className="fi" rows={2} style={{fontSize:16}} value={form.omschrijving||""} onChange={e=>uf("omschrijving",e.target.value)}/></div>
        <div className="fg">
          <label className="fl">Icoontje <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel — anders automatisch op soort)</span></label>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <div style={{width:44,height:44,borderRadius:10,border:"2px solid var(--g)",background:"#f8f5ee",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>
              {form.icoon||TYPE_ICON[form.type]||"🏠"}
            </div>
            <div style={{flex:1}}>
              {form.icoon?<button onClick={()=>uf("icoon","")} style={{fontSize:11,color:"var(--red)",background:"none",border:"none",cursor:"pointer",padding:0}}>✕ Terug naar automatisch</button>:<span style={{fontSize:11,color:"var(--txtl)"}}>Automatisch op basis van diersoort</span>}
            </div>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {ICOON_OPTIES.map(ic=><button key={ic} onClick={()=>uf("icoon",form.icoon===ic?"":ic)} style={{width:36,height:36,borderRadius:8,border:`2px solid ${form.icoon===ic?"var(--g)":"var(--brd)"}`,background:form.icoon===ic?"var(--cd)":"#fff",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{ic}</button>)}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <div className="fg"><label className="fl">Volgorde</label><input className="fi" type="number" style={{fontSize:16}} value={form.volgorde} onChange={e=>uf("volgorde",e.target.value)}/></div>
          <div className="fg"><label className="fl" style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
            <input type="checkbox" checked={!!form.actief} onChange={e=>uf("actief",e.target.checked)} style={{width:18,height:18,accentColor:"var(--g)"}}/> Actief
          </label></div>
        </div>
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <PawBtn onClick={saveVerblijf} loading={saving}>{editVerblijf?"💾 Opslaan":"🏠 Aanmaken"}</PawBtn>
          <button className="l-btn" onClick={()=>{setShowForm(false);setEditVerblijf(null);setForm(emptyVerblijf);}} style={{padding:"10px 16px"}}>Annuleren</button>
        </div>
      </div>}

      {/* Verblijven lijst */}
      {!loading&&verblijven.length===0&&!showForm&&
        <div className="card"><EmptyState icon="🏡" title="Nog geen verblijven" sub="Voeg verblijven toe zoals een schapenstal, volière of aquarium."/></div>}

      <div style={{display:"grid",gap:12,paddingBottom:100}}>
        {verblijven.map(v=>{
          const isOpen=expanded===v.id;
          const bList=bewoners[v.id]||[];
          const bLoading=bewonersLoading[v.id];
          const vIcon=(()=>{
                if(v.icoon) return v.icoon;
                const tally={};
                (bewoners[v.id]||[]).forEach(b=>{ if(b.dieren?.soort_id) tally[b.dieren.soort_id]=(tally[b.dieren.soort_id]||0)+1; });
                const topId=Object.entries(tally).sort((a,b)=>b[1]-a[1])[0]?.[0];
                if(topId){ const sv=soorten?.find(x=>String(x.id)===String(topId)); if(sv?.icon) return sv.icon; }
                return TYPE_ICON[v.type]||"🏠";
              })();
          return <div key={v.id} className="card" style={{padding:0,overflow:"hidden",border:isOpen?"2px solid var(--g)":"1px solid var(--brd)"}}>
            {/* Verblijf header */}
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",cursor:"pointer"}} onClick={()=>toggleExpand(v.id)}>
              <span style={{fontSize:24}}>{vIcon}</span>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:15,color:"var(--g)"}}>{v.naam}</div>
                <div style={{fontSize:12,color:"var(--txtl)"}}>{TYPE_LABELS[v.type]||v.type}{v.omschrijving&&` · ${v.omschrijving.slice(0,60)}`}</div>
              </div>
              {!v.actief&&<span className="rbdg" style={{background:"#eee",color:"#666"}}>Inactief</span>}
              {canManage&&<button onClick={e=>{e.stopPropagation();setEditVerblijf(v);setForm({naam:v.naam,type:v.type,omschrijving:v.omschrijving||"",volgorde:v.volgorde||0,actief:v.actief!==false,icoon:v.icoon||""});setShowForm(true);}} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--brd)",background:"#fff",fontSize:12,cursor:"pointer"}}>✏️</button>}
              {canManage&&<button onClick={e=>{e.stopPropagation();handleDelete(v);}} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--red)",background:"#fff0f0",fontSize:12,cursor:"pointer",color:"var(--red)"}}>🗑</button>}
              <span style={{fontSize:18,color:"var(--txtl)",transition:"transform .2s",display:"inline-block",transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>

            {/* Bewoners sectie */}
            {isOpen&&<div style={{borderTop:"1px solid var(--brd)",padding:"12px 14px",background:"var(--cd)"}}>
              {bLoading&&<div style={{color:"var(--txtl)",fontSize:13}}>Laden...</div>}

              {/* Huidige bewoners */}
              {!bLoading&&bList.length===0&&<div style={{color:"var(--txtl)",fontSize:13,marginBottom:12}}>Nog geen bewoners toegevoegd.</div>}
              {!bLoading&&bList.length>0&&<div style={{display:"grid",gap:8,marginBottom:12}}>
                {bList.map(b=>{
                  if(b.dier_id&&b.dieren){
                    const d=b.dieren;
                    const foto=d.fotos?.[0];
                    return <div key={b.id} style={{display:"flex",alignItems:"center",gap:10,background:"#fff",borderRadius:8,padding:"8px 10px",border:"1px solid var(--brd)"}}>
                      {foto?<img src={foto} alt={d.naam} style={{width:36,height:36,borderRadius:6,objectFit:"cover"}}/>:<span style={{fontSize:24,width:36,textAlign:"center"}}>🐾</span>}
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:13}}>{d.naam}</div>
                        <div style={{fontSize:11,color:"var(--txtl)"}}>{d.soort}</div>
                      </div>
                      {canManage&&<button onClick={()=>deleteBewoner(v.id,b.id)} style={{padding:"3px 8px",borderRadius:6,border:"1px solid var(--red)",background:"#fff0f0",fontSize:11,cursor:"pointer",color:"var(--red)"}}>🗑</button>}
                    </div>;
                  } else {
                    const subs=b.groep_subsoorten||[];
                    return <div key={b.id} style={{display:"flex",alignItems:"center",gap:10,background:"#fff",borderRadius:8,padding:"8px 10px",border:"1px solid var(--brd)"}}>
                      {b.groep_foto_url?<img src={b.groep_foto_url} alt={b.groep_naam} style={{width:36,height:36,borderRadius:6,objectFit:"cover"}}/>:<span style={{fontSize:24,width:36,textAlign:"center"}}>🐾</span>}
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:13}}>{b.groep_naam}{b.groep_aantal&&<span style={{fontWeight:400,color:"var(--txtl)"}}> · {b.groep_aantal}</span>}</div>
                        {subs.length>0&&<div style={{fontSize:11,color:"var(--txtl)"}}>{subs.map(s=>`${s.naam}${s.aantal?` (${s.aantal})`:""}`).join(", ")}</div>}
                      </div>
                      {b.groep_geluid_url&&<span style={{fontSize:16}}>🔊</span>}
                      {canManage&&<button onClick={()=>deleteBewoner(v.id,b.id)} style={{padding:"3px 8px",borderRadius:6,border:"1px solid var(--red)",background:"#fff0f0",fontSize:11,cursor:"pointer",color:"var(--red)"}}>🗑</button>}
                    </div>;
                  }
                })}
              </div>}

              {/* Bewoner toevoegen */}
              {canManage&&<div style={{background:"#fff",borderRadius:8,padding:12,border:"1px solid var(--brd)"}}>
                <div style={{fontWeight:600,fontSize:13,marginBottom:10,color:"var(--g)"}}>+ Bewoner toevoegen</div>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  <button onClick={()=>setBewonerTab("dier")} style={{padding:"6px 12px",borderRadius:8,border:"none",background:bewonerTab==="dier"?"var(--g)":"var(--cd)",color:bewonerTab==="dier"?"#fff":"var(--txt)",fontSize:12,fontWeight:600,cursor:"pointer"}}>🐾 Dier uit park</button>
                  <button onClick={()=>setBewonerTab("groep")} style={{padding:"6px 12px",borderRadius:8,border:"none",background:bewonerTab==="groep"?"var(--g)":"var(--cd)",color:bewonerTab==="groep"?"#fff":"var(--txt)",fontSize:12,fontWeight:600,cursor:"pointer"}}>👥 Groep</button>
                </div>

                {bewonerTab==="dier"&&<div>
                  {/* Stap 1: soort kiezen */}
                  {!gekozenSoortId&&(()=>{
                    const beschikbareDieren=parkDieren.filter(d=>!(bewoners[v.id]||[]).some(b=>b.dier_id===d.id));
                    const soortGroepen=[...new Map(beschikbareDieren.map(d=>[d.soort_id||d.soort,{soort_id:d.soort_id,soort:d.soort,count:beschikbareDieren.filter(x=>(x.soort_id||x.soort)===(d.soort_id||d.soort)).length}])).values()];
                    if(soortGroepen.length===0) return <div style={{color:"var(--txtl)",fontSize:13}}>Alle dieren zitten al in dit verblijf.</div>;
                    return <div>
                      <div style={{fontSize:12,color:"var(--txtl)",marginBottom:8}}>Kies een soort:</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {soortGroepen.map(sg=><button key={sg.soort_id||sg.soort} onClick={()=>{
                          const dieren=beschikbareDieren.filter(d=>(d.soort_id||d.soort)===(sg.soort_id||sg.soort));
                          setGekozenSoortId(sg.soort_id||sg.soort);
                          setGeselecteerdeDieren(new Set(dieren.map(d=>d.id)));
                        }} style={{padding:"6px 12px",borderRadius:20,border:"1px solid var(--g)",background:"#f0f7eb",color:"var(--g)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                          {sg.soort} <span style={{fontWeight:400,color:"var(--txtl)"}}>({sg.count})</span>
                        </button>)}
                      </div>
                    </div>;
                  })()}

                  {/* Stap 2: dieren aanvinken */}
                  {gekozenSoortId&&(()=>{
                    const dierenVanSoort=parkDieren.filter(d=>(d.soort_id||d.soort)===gekozenSoortId&&!(bewoners[v.id]||[]).some(b=>b.dier_id===d.id));
                    return <div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                        <button onClick={()=>{setGekozenSoortId(null);setGeselecteerdeDieren(new Set());}} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--brd)",background:"#fff",fontSize:11,cursor:"pointer"}}>← Terug</button>
                        <span style={{fontSize:12,fontWeight:600,color:"var(--g)"}}>{dierenVanSoort[0]?.soort}</span>
                        <button onClick={()=>setGeselecteerdeDieren(new Set(dierenVanSoort.map(d=>d.id)))} style={{marginLeft:"auto",padding:"4px 8px",borderRadius:6,border:"1px solid var(--brd)",background:"#fff",fontSize:11,cursor:"pointer"}}>Alles</button>
                        <button onClick={()=>setGeselecteerdeDieren(new Set())} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--brd)",background:"#fff",fontSize:11,cursor:"pointer"}}>Geen</button>
                      </div>
                      <div style={{display:"grid",gap:4,marginBottom:10}}>
                        {dierenVanSoort.map(d=>{
                          const checked=geselecteerdeDieren.has(d.id);
                          return <label key={d.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,background:checked?"#f0f7eb":"#fafafa",border:`1px solid ${checked?"var(--g)":"var(--brd)"}`,cursor:"pointer",transition:"all .15s"}}>
                            <input type="checkbox" checked={checked} onChange={e=>{
                              const next=new Set(geselecteerdeDieren);
                              e.target.checked?next.add(d.id):next.delete(d.id);
                              setGeselecteerdeDieren(next);
                            }} style={{width:16,height:16,accentColor:"var(--g)",cursor:"pointer",flexShrink:0}}/>
                            <span style={{fontSize:13,fontWeight:500}}>{d.naam}</span>
                            {d.geslacht&&<span style={{fontSize:11,color:"var(--txtl)"}}>{d.geslacht==="man"?"♂️":"♀️"}</span>}
                          </label>;
                        })}
                      </div>
                      <PawBtn onClick={()=>addBewonerDieren(v.id)} loading={addingBewoner} disabled={geselecteerdeDieren.size===0}>
                        {geselecteerdeDieren.size} dier{geselecteerdeDieren.size!==1?"en":""} toevoegen
                      </PawBtn>
                    </div>;
                  })()}
                </div>}

                {bewonerTab==="groep"&&<div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <div className="fg"><label className="fl">Naam *</label><input className="fi" style={{fontSize:16}} value={groepForm.groep_naam} onChange={e=>ug("groep_naam",e.target.value)} placeholder="Bijv. Wandelende takken"/></div>
                    <div className="fg"><label className="fl">Aantal</label><input className="fi" style={{fontSize:16}} value={groepForm.groep_aantal} onChange={e=>ug("groep_aantal",e.target.value)} placeholder="Bijv. ca. 15"/></div>
                  </div>
                  {/* Subsoorten */}
                  <div className="fg"><label className="fl">Subsoorten</label>
                    <div style={{display:"flex",gap:6,marginBottom:6}}>
                      <input className="fi" style={{flex:2,fontSize:16}} value={subsoortInput.naam} onChange={e=>setSubsoortInput(p=>({...p,naam:e.target.value}))} placeholder="Soort naam"/>
                      <input className="fi" style={{flex:1,fontSize:16}} value={subsoortInput.aantal} onChange={e=>setSubsoortInput(p=>({...p,aantal:e.target.value}))} placeholder="Aantal"/>
                      <button className="fbtn" onClick={()=>{if(!subsoortInput.naam.trim())return;ug("groep_subsoorten",[...groepForm.groep_subsoorten,{naam:subsoortInput.naam.trim(),aantal:subsoortInput.aantal}]);setSubsoortInput({naam:"",aantal:""});}}>+</button>
                    </div>
                    {groepForm.groep_subsoorten.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {groepForm.groep_subsoorten.map((s,i)=><span key={i} style={{background:"var(--cd)",borderRadius:20,padding:"3px 8px",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                        {s.naam}{s.aantal&&` (${s.aantal})`}
                        <button onClick={()=>ug("groep_subsoorten",groepForm.groep_subsoorten.filter((_,j)=>j!==i))} style={{border:"none",background:"none",cursor:"pointer",color:"var(--red)",padding:0,fontSize:14,lineHeight:1}}>×</button>
                      </span>)}
                    </div>}
                  </div>
                  {/* Foto */}
                  <div className="fg"><label className="fl">Foto</label>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <input className="fi" style={{flex:1,fontSize:14}} value={groepForm.groep_foto_url} onChange={e=>ug("groep_foto_url",e.target.value)} placeholder="URL of zoek hieronder"/>
                      <button className="fbtn" onClick={()=>openMediaPicker("image")}>🔍 Zoek</button>
                    </div>
                    {groepForm.groep_foto_url&&<img src={groepForm.groep_foto_url} alt="preview" style={{height:60,borderRadius:6,marginTop:6,objectFit:"cover"}}/>}
                  </div>
                  {/* Geluid */}
                  <div className="fg"><label className="fl">Geluid</label>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <input className="fi" style={{flex:1,fontSize:14}} value={groepForm.groep_geluid_url} onChange={e=>ug("groep_geluid_url",e.target.value)} placeholder="URL of zoek hieronder"/>
                      <button className="fbtn" onClick={()=>openMediaPicker("sound")}>🔍 Zoek</button>
                    </div>
                    {groepForm.groep_geluid_url&&<audio controls src={groepForm.groep_geluid_url} style={{width:"100%",marginTop:6}}/>}
                  </div>
                  <PawBtn onClick={()=>addBewonerGroep(v.id)} loading={addingBewoner}>👥 Groep toevoegen</PawBtn>
                </div>}
              </div>}
            </div>}
          </div>;
        })}
      </div>

      {canManage&&!showForm&&<button className="fab" onClick={()=>{setShowForm(true);setEditVerblijf(null);setForm(emptyVerblijf);}}>{IC.plus}</button>}

      {/* Media picker modal */}
      {mediaPicker&&<div className="mo" onClick={e=>{if(e.target.classList.contains("mo"))setMediaPicker(null);}}>
        <div className="mb" style={{maxWidth:600}}>
          <div className="mh"><h3>{mediaPicker==="image"?"📷 Foto zoeken":"🔊 Geluid zoeken"}</h3><button onClick={()=>setMediaPicker(null)}>✕</button></div>
          <div className="mc">
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input className="fi" style={{flex:1,fontSize:16}} value={mediaQuery} onChange={e=>setMediaQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&fetchMedia(mediaPicker,mediaQuery)} placeholder="Zoekterm..."/>
              <button className="fbtn" onClick={()=>fetchMedia(mediaPicker,mediaQuery)}>Zoeken</button>
            </div>
            {mediaLoading&&<div style={{textAlign:"center",padding:24,color:"var(--txtl)"}}>Zoeken...</div>}
            {mediaError&&<div style={{color:"var(--red)",fontSize:13,marginBottom:8}}>{mediaError}</div>}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
              {mediaResults.map(item=><div key={item.id} onClick={()=>applyMedia(item)} style={{cursor:"pointer",borderRadius:8,overflow:"hidden",border:"2px solid transparent",transition:"border .15s"}} onMouseEnter={e=>e.currentTarget.style.borderColor="var(--g)"} onMouseLeave={e=>e.currentTarget.style.borderColor="transparent"}>
                {item.kind==="image"?<img src={item.preview} alt={item.title} style={{width:"100%",height:90,objectFit:"cover",display:"block"}}/>:<div style={{background:"var(--cd)",height:90,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}><span style={{fontSize:28}}>🎵</span><span style={{fontSize:10,textAlign:"center",padding:"0 4px",color:"var(--txtl)"}}>{item.title?.slice(0,30)}</span></div>}
                <div style={{padding:"4px 6px",fontSize:10,color:"var(--txtl)",background:"#fff"}}>{item.licenseBucket} · {item.creator?.slice(0,20)}</div>
              </div>)}
            </div>
          </div>
        </div>
      </div>}
    </div>;
  };

  // eslint-disable-next-line no-undef
  const pg=()=>{switch(s.page){
    case "dashboard":return <Dash/>;
    case "dienst":return <DienstPg/>;
    case "dieren":return <DierenPg soorten={soorten||[]} setPhotoModal={setPhotoModal}/>; // eslint-disable-line no-undef
    case "virtueelpark":return <VirtueelParkPg/>;
    case "voeding":return <VoedPg/>;
    case "medicatie":return <MedPg/>;
    case "leveranciers":return <LeveranciersPg/>;
    case "bestellingen":return <BestellingenPg/>;
    case "agenda":return <AgPg/>;
    case "taken":return <TkPg/>;
    case "berichten":return <MsgPg/>;
    case "directe_berichten":return <DirectMsgPg/>;
    case "contacten":return <CtPg/>;
    case "team":return <TeamPg/>;
    case "planning":return <PlanPg/>;
    case "vastetaken":return <VasteTakenPg/>;
    case "logboek":return <LogboekPg/>;
    case "gezondheid":return <GezondheidTestPg s={s} d={d} ds={ds} park={park} soorten={soorten}/>;
    case "media":return <MediaPage s={s} d={d} ds={ds} park={park} setPhotoModal={setPhotoModal}/>;
    case "sleutels":return <SleUtelsGrid/>;
    case "functiegroepen":return <FunctiegroepPage/>;
    case "prikbord":return <PrikbordPg/>;
    case "protocollen":return <ProtocollenPage s={s} d={d} ds={ds}/>;
    case "mijn_park":return <MijnParkPg park={park} canBeheer={hasPerm(s.user,"taken_beheren")} onParkUpdate={onParkUpdate}/>;
    case "dataimport":return <DataPg/>;
    default:return <Dash/>;
  }};

  /* ══════════════════════════════════════════════════
     DIENST PAGINA — Jan's takenpagina voor vandaag
     ══════════════════════════════════════════════════ */
  const DienstPg=()=>{
    const DAGDELEN=[
      {id:"ochtend",label:"Ochtend",tijd:"07:00–12:00",icon:"🌅",color:"#e87c28",bg:"#fff3e6"},
      {id:"middag",label:"Middag",tijd:"12:00–17:00",icon:"☀️",color:"#2e6da4",bg:"#e8f0f8"},
      {id:"avond",label:"Avond",tijd:"17:00–22:00",icon:"🌙",color:"#6b46c1",bg:"#f3eeff"},
    ];
    const now=new Date();
    const vandaag=now.toISOString().split("T")[0];
    const [activeDagdeel,setActiveDagdeel]=useState("ochtend");
    const [logTekst,setLogTekst]=useState("");
    const [logFoto,setLogFoto]=useState(null);
    const [showLogForm,setShowLogForm]=useState(false);
    const [showWaarForm,setShowWaarForm]=useState(false);
    const [waarTekst,setWaarTekst]=useState("");
    const [waarType,setWaarType]=useState("waarschuwing");
    const canBeheer=hasPerm(s.user,"taken_beheren");
    const canLogboek=hasPerm(s.user,"logboek_schrijven");

    const dagInfo=DAGDELEN.find(d=>d.id===activeDagdeel);

    // ── Vaste taken ──
    const vandaagISO2=vandaag;
    const isProtocolActief=(pid)=>{if(!pid) return true; const p=(s.protocollen||[]).find(x=>x.id===pid); return p&&p.actief&&p.start_datum<=vandaagISO2&&(!p.eind_datum||p.eind_datum>=vandaagISO2);};
    const takenVandaag=s.vasteTaken.filter(t=>t.dagdeel===activeDagdeel&&isProtocolActief(t.protocol_id));
    const getProtocolNaam=(pid)=>{if(!pid) return null; const p=(s.protocollen||[]).find(x=>x.id===pid); return p?.titel||null;};
    const isGedaan=(taakId)=>s.checks.find(c=>c.vaste_taak_id===taakId&&c.datum===vandaag)?.gedaan||false;
    const gedaanDoor=(taakId)=>s.checks.find(c=>c.vaste_taak_id===taakId&&c.datum===vandaag)?.gedaan_door||null;
    const soortIcon={voeding:"🌾",medicatie:"💊",schoonmaak:"🧹",controle:"👁️",verzorging:"🤲",overig:"📋"};

    // ── Voeding vandaag voor dit dagdeel ──
    const voedingVandaag=s.feedings.filter(f=>f.dagdeel===activeDagdeel);

    // ── Maaltijdpakketten voor dit dagdeel ──
    const maaltijdenDagdeel=(s.voedingDieren||[]).filter(vd=>vd.dagdeel===activeDagdeel);
    const getVoedingNaam=(vid)=>{const f=s.feedings.find(x=>x.id===vid);return f?`${f.type}${f.merk?` (${f.merk})`:""}`:"-";};
    const getMaaltijdTarget=(vd)=>{
      if(vd.dier_id){const a=s.animals.find(x=>x.id===vd.dier_id);return a?(a.name||a.naam):"Dier";}
      if(vd.soort_id){const so=soorten?.find(x=>x.id===vd.soort_id);return so?`${so.icon} ${so.naam}`:"Soort";}
      return "-";
    };
    // Group maaltijden by target
    const maaltijdGroepen={};
    maaltijdenDagdeel.forEach(vd=>{
      const key=vd.soort_id?`soort-${vd.soort_id}`:vd.dier_id?`dier-${vd.dier_id}`:"onbekend";
      if(!maaltijdGroepen[key]) maaltijdGroepen[key]={key,label:getMaaltijdTarget(vd),items:[]};
      maaltijdGroepen[key].items.push(vd);
    });
    const maaltijdGroepenList=Object.values(maaltijdGroepen);
    const isMaaltijdGedaan=(groupKey)=>{
      const check=s.maaltijdChecks.find(c=>c.group_key===groupKey&&c.dagdeel===activeDagdeel);
      return check?.gedaan||false;
    };
    const maaltijdGedaanDoor=(groupKey)=>{
      const check=s.maaltijdChecks.find(c=>c.group_key===groupKey&&c.dagdeel===activeDagdeel);
      return check?.gedaan_door||null;
    };

    // ── Medicatie vandaag voor dit dagdeel ──
    const medicatieVandaag=s.meds.filter(m=>{
      if(m.gedaan) return false;
      if(m.eind&&m.eind<vandaag) return false;
      return m.dagdeel===activeDagdeel;
    });

    // ── Gezondheid medicatie (uit health records) ──
    const actieveGezondheidMeds=(s.gezondheid||[])
      .filter(g=>g.status!=="genezen" && g.gezondheid_medicatie?.length>0)
      .flatMap(g=>g.gezondheid_medicatie
        .filter(m=>!m.eind||m.eind>=vandaag)
        .map(m=>({...m, _dier: s.animals.find(a=>a.id===g.dier_id), _soort: soorten?.find(so=>so.id===g.soort_id), _diagnose: g.diagnoses}))
      );


    const DAGDEEL_DAG={"ochtend":"ma","middag":"ma","avond":"ma"}; // placeholder
    const dagVandaag=["zo","ma","di","wo","do","vr","za"][now.getDay()===0?0:now.getDay()];
    const takenVandaagLijst=s.tasks.filter(t=>(t.dagen||[]).includes(dagVandaag));

    // ── Agenda vandaag ──
    const agendaVandaag=s.agenda.filter(a=>(a.datum||"").split("T")[0]===vandaag);

    // ── Logboek ──
    const logVandaag=s.logboek.filter(l=>l.datum===vandaag&&l.dagdeel===activeDagdeel);

    // ── Voortgang (alleen vaste taken tellen mee) ──
    const totaal=takenVandaag.length;
    const gedaan=takenVandaag.filter(t=>isGedaan(t.id)).length;
    const pct=totaal>0?Math.round((gedaan/totaal)*100):0;

    const addLog=()=>{
      if(!logTekst.trim()) return;
      ds({type:"ADD_LOGBOEK",p:{dagdeel:activeDagdeel,tekst:logTekst,foto:logFoto}});
      setLogTekst("");setLogFoto(null);setShowLogForm(false);
    };
    const addWaarschuwing=()=>{
      if(!waarTekst.trim()) return;
      ds({type:"ADD_WAARSCHUWING",p:{tekst:waarTekst,type:waarType}});
      setWaarTekst("");setShowWaarForm(false);
    };
    const fotoUpload=(e)=>{
      const file=e.target.files[0]; if(!file) return;
      const reader=new FileReader();
      reader.onload=ev=>setLogFoto(ev.target.result);
      reader.readAsDataURL(file);
    };
    const dagNaam=now.toLocaleDateString("nl-NL",{weekday:"long",day:"numeric",month:"long"});

    // Helper: check card voor voeding/medicatie
    const CheckKaart=({icon,titel,sub,gedaan,onToggle,color})=>(
      <div onClick={onToggle} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
        background:"#fff",borderRadius:12,marginBottom:6,
        boxShadow:"0 1px 4px rgba(0,0,0,.06)",
        border:`1px solid ${gedaan?color+"44":"var(--brd)"}`,
        cursor:"pointer",opacity:gedaan?.7:1,transition:"all .2s"}}>
        <div style={{width:24,height:24,borderRadius:6,border:`2px solid ${gedaan?color:"var(--brd)"}`,
          background:gedaan?color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
          color:"#fff",fontSize:14,flexShrink:0,transition:"all .2s"}}>{gedaan?"✓":""}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:gedaan?400:600,color:gedaan?"var(--txtl)":"var(--g)",
            textDecoration:gedaan?"line-through":"none"}}>{icon} {titel}</div>
          {sub&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>{sub}</div>}
        </div>
      </div>
    );

    return <div className="pg">
      {/* Waarschuwingen */}
      {s.waarschuwingen.length>0&&<div style={{marginBottom:16}}>
        {s.waarschuwingen.map(w=><div key={w.id} style={{
          padding:"10px 14px",borderRadius:12,marginBottom:8,display:"flex",gap:10,alignItems:"flex-start",
          background:w.type==="dringend"?"#fff0f0":w.type==="waarschuwing"?"#fff8e6":w.type==="goed_nieuws"?"#f0fff4":"#e8f4ff",
          borderLeft:`4px solid ${w.type==="dringend"?"#c44536":w.type==="waarschuwing"?"#e87c28":w.type==="goed_nieuws"?"#16a34a":"#2e6da4"}`,
        }}>
          <span style={{fontSize:18,flexShrink:0}}>{w.type==="dringend"?"🚨":w.type==="waarschuwing"?"⚠️":w.type==="goed_nieuws"?"💚":"ℹ️"}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:600,color:"var(--g)",lineHeight:1.4}}>{w.tekst}</div>
            <div style={{fontSize:11,color:"var(--txtl)",marginTop:3}}>— {w.aangemaakt_door}</div>
          </div>
          {canBeheer&&<button onClick={e=>{e.stopPropagation();toast.confirm("Waarschuwing verwijderen?",()=>ds({type:"DEL_WAARSCHUWING",p:w.id}));}}
            style={{background:"none",border:"none",cursor:"pointer",color:"var(--txtl)",fontSize:16,padding:0,flexShrink:0}}>×</button>}
        </div>)}
      </div>}

      {/* Actieve protocollen */}
      {(()=>{
        const vandaagISO=new Date().toISOString().split("T")[0];
        const actieveProtocollen=(s.protocollen||[]).filter(p=>p.actief&&p.start_datum<=vandaagISO&&(!p.eind_datum||p.eind_datum>=vandaagISO));
        if(actieveProtocollen.length===0) return null;
        return <div style={{marginBottom:16}}>
          {actieveProtocollen.map(p=><ProtocolKaart key={p.id} protocol={p}/>)}
        </div>;
      })()}

      {/* Header */}
      <div style={{marginBottom:12}}>
        <h1 className="pt" style={{marginBottom:2}}>Mijn Dienst (geheugensteuntje)</h1>
        <p style={{fontSize:13,color:"var(--txtl)",textTransform:"capitalize"}}>{dagNaam}</p>
      </div>

      {/* Dagdeel tabs */}
      <div className="fr" style={{marginBottom:14}}>
        {DAGDELEN.map(d=><button key={d.id} className={`fc ${activeDagdeel===d.id?"a":""}`}
          onClick={()=>setActiveDagdeel(d.id)}
          style={activeDagdeel===d.id?{background:d.color,color:"#fff",borderColor:d.color}:{}}>
          {d.icon} {d.label}
        </button>)}
      </div>

      {/* Voortgang vaste taken */}
      <div className="card" style={{marginBottom:12,background:dagInfo.bg,border:`1px solid ${dagInfo.color}22`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <span style={{fontSize:14,fontWeight:600,color:dagInfo.color}}>{dagInfo.icon} {dagInfo.label}dienst</span>
          <span style={{fontSize:14,fontWeight:700,color:dagInfo.color}}>{gedaan}/{totaal} klaar</span>
        </div>
        <div style={{height:8,background:"rgba(0,0,0,.08)",borderRadius:4,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${pct}%`,background:dagInfo.color,borderRadius:4,transition:"width .3s"}}/>
        </div>
        {totaal>0&&pct===100&&<div style={{marginTop:8,textAlign:"center",fontSize:13,fontWeight:600,color:dagInfo.color}}>✅ Alle vaste taken afgerond!</div>}
      </div>

      {/* ── Vaste taken ── */}
      {takenVandaag.length>0&&<>
        <div className="sh"><span className="st">📋 Vaste taken</span></div>
        {takenVandaag.map(taak=>{
          const done=isGedaan(taak.id);
          const door=gedaanDoor(taak.id);
          return <div key={taak.id} onClick={()=>ds({type:"TOG_CHECK",p:{taakId:taak.id,dagdeel:activeDagdeel}})}
            style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"#fff",borderRadius:12,
              marginBottom:6,boxShadow:"0 1px 4px rgba(0,0,0,.06)",border:`1px solid ${done?dagInfo.color+"44":"var(--brd)"}`,
              cursor:"pointer",opacity:done?.7:1,transition:"all .2s"}}>
            <div style={{width:24,height:24,borderRadius:6,border:`2px solid ${done?dagInfo.color:"var(--brd)"}`,
              background:done?dagInfo.color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
              color:"#fff",fontSize:14,flexShrink:0,transition:"all .2s"}}>{done?"✓":""}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:done?400:600,color:done?"var(--txtl)":"var(--g)",textDecoration:done?"line-through":"none"}}>
                {soortIcon[taak.soort]||"📋"} {taak.titel}
              </div>
              {getProtocolNaam(taak.protocol_id)&&<div style={{fontSize:11,color:"#2e6da4",marginTop:2}}>📄 {getProtocolNaam(taak.protocol_id)}</div>}
              {done&&door&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>✓ {door}</div>}
            </div>
          </div>;
        })}
      </>}

      {/* ── Voeding ── */}
      {voedingVandaag.length>0&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">🌾 Voeding</span></div>
        {voedingVandaag.map(f=><CheckKaart key={f.id}
          icon="🌾" titel={f.dier||(f.soort?"Groep: "+f.soort:"")||"Voeding"}
          sub={[f.type,f.soort].filter(Boolean).join(" · ")}
          gedaan={!!f.gedaan} color={dagInfo.color}
          onToggle={()=>ds({type:"TOG_FEED",p:f.id})}
        />)}
      </>}

      {/* ── Maaltijdpakketten ── */}
      {maaltijdGroepenList.length>0&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">🍽️ Maaltijdpakketten</span></div>
        {maaltijdGroepenList.map(group=>{
          const done=isMaaltijdGedaan(group.key);
          const door=maaltijdGedaanDoor(group.key);
          return <div key={group.key} onClick={()=>ds({type:"TOG_MAALTIJD_CHECK",p:{groupKey:group.key,dagdeel:activeDagdeel}})}
            style={{display:"flex",alignItems:"flex-start",gap:12,padding:"12px 14px",background:"#fff",borderRadius:12,
              marginBottom:6,boxShadow:"0 1px 4px rgba(0,0,0,.06)",border:`1px solid ${done?dagInfo.color+"44":"var(--brd)"}`,
              cursor:"pointer",opacity:done?.7:1,transition:"all .2s"}}>
            <div style={{width:24,height:24,borderRadius:6,border:`2px solid ${done?dagInfo.color:"var(--brd)"}`,
              background:done?dagInfo.color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
              color:"#fff",fontSize:14,flexShrink:0,transition:"all .2s",marginTop:2}}>{done?"✓":""}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:done?400:600,color:done?"var(--txtl)":"var(--g)",textDecoration:done?"line-through":"none"}}>
                🍽️ {group.label}
              </div>
              <div style={{marginTop:4}}>
                {group.items.map(vd=><div key={vd.id} style={{fontSize:12,color:"var(--txtl)",lineHeight:1.6,paddingLeft:4}}>
                  • {getVoedingNaam(vd.voeding_id)}{vd.hoeveelheid&&<> — <strong>{vd.hoeveelheid}</strong></>}
                </div>)}
              </div>
              {done&&door&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:4}}>✓ {door}</div>}
            </div>
          </div>;
        })}
      </>}

      {/* ── Medicatie ── */}
      {medicatieVandaag.length>0&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">💊 Medicatie</span></div>
        {medicatieVandaag.map(m=><CheckKaart key={m.id}
          icon="💊" titel={m.dier}
          sub={[m.medicijn,m.dosering,m.freq].filter(Boolean).join(" · ")}
          gedaan={!!m.gedaan} color="#e53e3e"
          onToggle={()=>ds({type:"TOG_MED",p:m.id})}
        />)}
      </>}

      {/* ── Gezondheid medicatie ── */}
      {actieveGezondheidMeds.length>0&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">💉 Gezondheid medicatie</span></div>
        {actieveGezondheidMeds.map(med=>{
          const target=med._dier?(med._dier.name||med._dier.naam):med._soort?`${med._soort.icon} ${med._soort.naam}`:"Onbekend";
          return <div key={med.id} style={{background:"#fff",borderRadius:12,marginBottom:6,boxShadow:"0 1px 4px rgba(0,0,0,.06)",border:"1px solid #d0e8ff",overflow:"hidden"}}>
            <div style={{padding:"8px 12px",background:"#f0f8ff",borderBottom:"1px solid #d0e8ff",display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:13,fontWeight:600,color:"#1a6fa8"}}>💉 {target}</span>
              <span style={{fontSize:11,color:"var(--txtl)"}}>· {med._diagnose}</span>
            </div>
            <div style={{padding:"0 8px 8px"}}>
              <MedicatieGegeven med={med} gebruikerId={s.user?.id}/>
            </div>
          </div>;
        })}
      </>}

      {/* ── Taken vandaag (weekplanning) ── */}
      {takenVandaagLijst.length>0&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">📋 Taken vandaag</span></div>
        {takenVandaagLijst.map(t=>{
          const taakCheck=s.takenChecks.find(c=>c.taak_id===t.id);
          const done=taakCheck?.gedaan||false;
          const door=taakCheck?.gedaan_door||null;
          const taakUsers=s.takenToewijzingen.filter(tt=>tt.taak_id===t.id).map(tt=>tt.gebruiker_id);
          const assigned=s.users.filter(u=>taakUsers.includes(u.id)).map(u=>u.roepnaam||u.name);
          return <div key={t.id} onClick={()=>ds({type:"TOG_TAAK_CHECK",p:{taakId:t.id}})}
            style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"#fff",borderRadius:12,
              marginBottom:6,boxShadow:"0 1px 4px rgba(0,0,0,.06)",border:`1px solid ${done?dagInfo.color+"44":"var(--brd)"}`,
              cursor:"pointer",opacity:done?.7:1,transition:"all .2s"}}>
            <div style={{width:24,height:24,borderRadius:6,border:`2px solid ${done?dagInfo.color:"var(--brd)"}`,
              background:done?dagInfo.color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
              color:"#fff",fontSize:14,flexShrink:0,transition:"all .2s"}}>{done?"✓":""}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:done?400:600,color:done?"var(--txtl)":"var(--g)",textDecoration:done?"line-through":"none"}}>
                {t.prio==="hoog"?"🔴 ":""}{t.titel}
              </div>
              {assigned.length>0&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:1}}>{assigned.join(", ")}</div>}
              {done&&door&&<div style={{fontSize:11,color:"var(--txtl)",marginTop:2}}>✓ {door}</div>}
            </div>
          </div>;
        })}
      </>}

      {/* ── Agenda vandaag ── */}
      {agendaVandaag.length>0&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">📅 Agenda vandaag</span></div>
        {agendaVandaag.map(a=><div key={a.id} style={{padding:"10px 14px",background:"#fff",borderRadius:12,marginBottom:6,
          boxShadow:"0 1px 4px rgba(0,0,0,.06)",borderLeft:"3px solid var(--g)",display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:18,flexShrink:0}}>📅</span>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:600,color:"var(--g)"}}>{a.titel}</div>
            {a.tijd&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:2}}>🕐 {a.tijd}</div>}
            {a.desc&&<div style={{fontSize:12,color:"var(--txtl)",marginTop:2}}>{a.desc}</div>}
          </div>
        </div>)}
      </>}

      {/* Lege staat */}
      {takenVandaag.length===0&&voedingVandaag.length===0&&maaltijdGroepenList.length===0&&medicatieVandaag.length===0&&actieveGezondheidMeds.length===0&&takenVandaagLijst.length===0&&agendaVandaag.length===0&&
        <div className="card"><EmptyState icon="🌿" title={`Geen taken voor de ${dagInfo.label.toLowerCase()}dienst`} sub="Geniet van de rust — of plan nieuwe taken via het takenbeheer."/></div>}

      {/* ── Logboek ── */}
      <div className="sh" style={{marginTop:12}}><span className="st">📝 Bevindingen {dagInfo.label.toLowerCase()}</span></div>
      {logVandaag.length===0&&!showLogForm&&<EmptyState icon="📝" title="Nog geen bevindingen" sub={`Noteer je eerste bevinding van de ${dagInfo.label.toLowerCase()}dienst.`}/>}
      {logVandaag.map(l=><div key={l.id} className="card" style={{marginBottom:8,borderLeft:`3px solid ${dagInfo.color}`}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:600,color:"var(--g)"}}>{l.auteur}</span>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:11,color:"var(--txtl)"}}>{new Date(l.created_at).toLocaleTimeString("nl-NL",{hour:"2-digit",minute:"2-digit"})}</span>
            {(l.gebruiker_id===s.user.id||canBeheer)&&<button onClick={()=>{toast.confirm("Bevinding verwijderen?",()=>ds({type:"DEL_LOGBOEK",p:l.id}))}}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"var(--red)",padding:"2px 4px"}}>🗑</button>}
          </div>
        </div>
        <p style={{fontSize:14,color:"var(--g)",margin:0,lineHeight:1.5}}>{l.tekst}</p>
        {l.foto&&<img src={l.foto} alt="bevinding" style={{width:"100%",borderRadius:8,marginTop:8,maxHeight:200,objectFit:"cover"}}/>}
      </div>)}

      {showLogForm&&<div className="card" style={{marginBottom:8}}>
        <textarea className="fi" rows={3} value={logTekst} onChange={e=>setLogTekst(e.target.value)}
          placeholder="Beschrijf je bevinding..." style={{marginBottom:8}}/>
        {logFoto&&<img src={logFoto} alt="preview" style={{width:"100%",borderRadius:8,marginBottom:8,maxHeight:150,objectFit:"cover"}}/>}
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <label style={{flex:1,padding:"8px",borderRadius:10,border:"1px dashed var(--brd)",background:"var(--cd)",
            textAlign:"center",fontSize:13,cursor:"pointer",color:"var(--txtl)"}}>
            📷 Foto toevoegen
            <input type="file" accept="image/*" capture="environment" onChange={fotoUpload} style={{display:"none"}}/>
          </label>
          {logFoto&&<button onClick={()=>setLogFoto(null)} style={{padding:"8px 12px",borderRadius:10,border:"1px solid var(--brd)",
            background:"#fff",fontSize:13,cursor:"pointer",color:"var(--red)"}}>Verwijder foto</button>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="fbtn" onClick={addLog}>Opslaan</button>
          <button className="fbtn" style={{background:"var(--cd)",color:"var(--txtl)"}} onClick={()=>{setShowLogForm(false);setLogTekst("");setLogFoto(null);}}>Annuleren</button>
        </div>
      </div>}
      {!showLogForm&&canLogboek&&<button className="fbtn" style={{background:"var(--cd)",color:"var(--g)",marginBottom:8}}
        onClick={()=>setShowLogForm(true)}>+ Bevinding toevoegen</button>}

      {/* Waarschuwing toevoegen */}
      {canBeheer&&<>
        <div className="sh" style={{marginTop:8}}><span className="st">Waarschuwing plaatsen</span></div>
        {!showWaarForm&&<button className="fbtn" style={{background:"#fff3e6",color:"#e87c28",border:"1px solid #e87c28"}}
          onClick={()=>setShowWaarForm(true)}>⚠️ Waarschuwing voor team</button>}
        {showWaarForm&&<div className="card">
          <div className="fg"><label className="fl">Type</label>
            <select className="fi" value={waarType} onChange={e=>setWaarType(e.target.value)}>
              <option value="goed_nieuws">💚 Goed nieuws</option>
              <option value="info">ℹ️ Info</option>
              <option value="waarschuwing">⚠️ Waarschuwing</option>
              <option value="dringend">🚨 Dringend</option>
            </select>
          </div>
          <div className="fg"><label className="fl">Bericht</label>
            <textarea className="fi" rows={2} value={waarTekst} onChange={e=>setWaarTekst(e.target.value)}
              placeholder="Bijv: Deur volière is kapot, niet ophokken!"/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button className="fbtn" onClick={addWaarschuwing}>Plaatsen</button>
            <button className="fbtn" style={{background:"var(--cd)",color:"var(--txtl)"}} onClick={()=>setShowWaarForm(false)}>Annuleren</button>
          </div>
        </div>}
      </>}

      <div style={{height:80}}/>
    </div>;
  };

  /* ══════════════════════════════════════════════════
     VASTE TAKEN BEHEER — voor beheerders
     ══════════════════════════════════════════════════ */
  const VasteTakenPg=()=>{
    const canM=hasPerm(s.user,"taken_beheren");
    const [activeDagdeel,setActiveDagdeel]=useState("ochtend");
    const [showAdd,setShowAdd]=useState(false);
    const [f,setF]=useState({titel:"",soort:"voeding",dagdeel:"ochtend",protocol_id:""});
    const DAGDELEN=["ochtend","middag","avond"];
    const SOORTEN_VT=["voeding","medicatie","schoonmaak","controle","verzorging","overig"];
    const soortIcon={voeding:"🌾",medicatie:"💊",schoonmaak:"🧹",controle:"👁️",verzorging:"🤲",overig:"📋"};
    const dagIcon={ochtend:"🌅",middag:"☀️",avond:"🌙"};

    const taken=s.vasteTaken.filter(t=>t.dagdeel===activeDagdeel);
    const getProtNaam=(pid)=>{if(!pid) return null; const p=(s.protocollen||[]).find(x=>x.id===pid); return p?.titel||null;};

    const save=()=>{
      if(!f.titel.trim()) return;
      ds({type:"ADD_VASTE_TAAK",p:{dagdeel:activeDagdeel,titel:f.titel,soort:f.soort,protocol_id:f.protocol_id?Number(f.protocol_id):null,volgorde:taken.length+1}});
      setF({titel:"",soort:"voeding",dagdeel:activeDagdeel,protocol_id:""});
      setShowAdd(false);
    };

    return <div className="pg">
      <h1 className="pt">Vaste Taken</h1>
      <p style={{fontSize:13,color:"var(--txtl)",marginBottom:14}}>Taken die elke dag terugkeren per dagdeel.</p>

      <div className="fr" style={{marginBottom:14}}>
        {DAGDELEN.map(d=><button key={d} className={`fc ${activeDagdeel===d?"a":""}`} onClick={()=>{setActiveDagdeel(d);setShowAdd(false);}}>
          {dagIcon[d]} {d.charAt(0).toUpperCase()+d.slice(1)}
        </button>)}
      </div>

      {taken.map(t=><div key={t.id} className="card" style={{marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:20}}>{soortIcon[t.soort]||"📋"}</span>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:600,color:"var(--g)"}}>{t.titel}</div>
          <div style={{fontSize:11,color:"var(--txtl)",marginTop:2,textTransform:"capitalize"}}>{t.soort}{getProtNaam(t.protocol_id)?<span style={{color:"#2e6da4",textTransform:"none"}}> · 📄 {getProtNaam(t.protocol_id)}</span>:""}</div>
        </div>
        {canM&&<button onClick={()=>toast.confirm(`"${t.naam||"Vaste taak"}" verwijderen?`,()=>ds({type:"DEL_VASTE_TAAK",p:t.id}))}
          style={{width:30,height:30,borderRadius:8,border:"1px solid var(--brd)",background:"#fff",
            cursor:"pointer",color:"var(--red)",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>}
      </div>)}

      {taken.length===0&&<div className="card"><EmptyState icon={dagIcon[activeDagdeel]} title="Nog geen vaste taken" sub={`Er zijn nog geen vaste taken voor de ${activeDagdeel}dienst.`}/></div>}

      {canM&&!showAdd&&<button className="fbtn" style={{marginTop:8}} onClick={()=>setShowAdd(true)}>+ Taak toevoegen</button>}

      {showAdd&&<div className="card" style={{marginTop:8}}>
        <div className="fg"><label className="fl">Taakomschrijving</label>
          <input className="fi" value={f.titel} onChange={e=>setF(p=>({...p,titel:e.target.value}))} placeholder="Bijv: Schapen voeren"/>
        </div>
        <div className="fg"><label className="fl">Soort</label>
          <select className="fi" value={f.soort} onChange={e=>setF(p=>({...p,soort:e.target.value}))}>
            {SOORTEN_VT.map(s=><option key={s} value={s}>{soortIcon[s]} {s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>
        <div className="fg"><label className="fl">Protocol <span style={{fontWeight:400,color:"var(--txtl)"}}>(optioneel)</span></label>
          <select className="fi" value={f.protocol_id} onChange={e=>setF(p=>({...p,protocol_id:e.target.value}))}>
            <option value="">— Geen protocol (altijd zichtbaar)</option>
            {(s.protocollen||[]).map(p=><option key={p.id} value={p.id}>📄 {p.titel}</option>)}
          </select>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="fbtn" onClick={save}>Toevoegen</button>
          <button className="fbtn" style={{background:"var(--cd)",color:"var(--txtl)"}} onClick={()=>setShowAdd(false)}>Annuleren</button>
        </div>
      </div>}
      <div style={{height:80}}/>
    </div>;
  };

  /* ══════════════════════════════════════════════════
     PRIKBORD — cross-park bulletin board
     ══════════════════════════════════════════════════ */
  const PrikbordPg=()=>{
    const CATS={dier_aanbod:{label:"Dier aanbod",icon:"🐐",color:"#4a7a2e"},dier_gezocht:{label:"Dier gezocht",icon:"🔍",color:"#2e6da4"},materiaal:{label:"Materiaal",icon:"📦",color:"#7c3aed"},vraag:{label:"Vraag",icon:"❓",color:"#e87c28"},algemeen:{label:"Algemeen",icon:"📌",color:"#666"}};
    const [showNew,setShowNew]=useState(false);
    const [newPost,setNewPost]=useState({bericht:"",categorie:"dier_aanbod"});
    const [saving,setSaving]=useState(false);
    const [openPost,setOpenPost]=useState(null); // expanded post id
    const [reacties,setReacties]=useState({}); // {postId: [reacties]}
    const [loadingReacties,setLoadingReacties]=useState({});
    const [newReactie,setNewReactie]=useState("");
    const [savingReactie,setSavingReactie]=useState(false);
    const [filter,setFilter]=useState("alle");
    const isBeheerder=s.user.role==="beheerder"||s.user.isAppAdmin;

    const handlePost=async()=>{
      if(!newPost.bericht.trim()) return;
      setSaving(true);
      try {
        const post=await db.addPrikbordPost({park_id:park.id,gebruiker_id:s.user.id,bericht:newPost.bericht.trim(),categorie:newPost.categorie});
        d({type:"LOAD",p:{prikbord:[post,...s.prikbord]}});
        setNewPost({bericht:"",categorie:"dier_aanbod"});
        setShowNew(false);
      } catch(e){ toast.err("Kon bericht niet plaatsen: "+e.message); }
      setSaving(false);
    };

    const handleDelete=async(id)=>{
      toast.confirm("Bericht verwijderen van het prikbord?", async()=>{
        try {
          await db.deletePrikbordPost(id);
          d({type:"LOAD",p:{prikbord:s.prikbord.filter(p=>p.id!==id)}});
          if(openPost===id) setOpenPost(null);
        } catch(e){ toast.err("Kon niet verwijderen: "+e.message); }
      });
    };

    const toggleReacties=async(postId)=>{
      if(openPost===postId){setOpenPost(null);return;}
      setOpenPost(postId);
      if(!reacties[postId]){
        setLoadingReacties(p=>({...p,[postId]:true}));
        try {
          const r=await db.getPrikbordReacties(postId);
          setReacties(p=>({...p,[postId]:r}));
        } catch(e){ console.error("Reacties laden:",e); }
        setLoadingReacties(p=>({...p,[postId]:false}));
      }
    };

    const handleReactie=async(postId)=>{
      if(!newReactie.trim()) return;
      setSavingReactie(true);
      try {
        const r=await db.addPrikbordReactie({prikbord_id:postId,park_id:park.id,gebruiker_id:s.user.id,bericht:newReactie.trim()});
        setReacties(p=>({...p,[postId]:[...(p[postId]||[]),r]}));
        setNewReactie("");
      } catch(e){ toast.err("Kon reactie niet plaatsen: "+e.message); }
      setSavingReactie(false);
    };

    const handleDeleteReactie=async(postId,reactieId)=>{
      toast.confirm("Reactie verwijderen?",async()=>{
        try {
          await db.deletePrikbordReactie(reactieId);
          setReacties(p=>({...p,[postId]:(p[postId]||[]).filter(r=>r.id!==reactieId)}));
        } catch(e){ toast.err("Kon reactie niet verwijderen: "+e.message); }
      });
    };

    const filtered=filter==="alle"?s.prikbord:s.prikbord.filter(p=>p.categorie===filter);
    const isOwnPark=(p)=>String(p.park_id)===String(park.id);

    return <div className="pg">
      <h1 className="pt">Prikbord</h1>
      <p style={{fontSize:13,color:"var(--txtl)",marginBottom:14}}>Berichten tussen dierenparken — vraag &amp; aanbod van dieren, materiaal en meer.</p>

      {/* Filter chips */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
        <button onClick={()=>setFilter("alle")} style={{padding:"5px 12px",borderRadius:20,border:"1.5px solid",borderColor:filter==="alle"?"var(--g)":"var(--brd)",background:filter==="alle"?"var(--gp)":"#fff",color:filter==="alle"?"var(--g)":"var(--txtl)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Alle</button>
        {Object.entries(CATS).map(([k,v])=><button key={k} onClick={()=>setFilter(k)} style={{padding:"5px 12px",borderRadius:20,border:"1.5px solid",borderColor:filter===k?v.color:"var(--brd)",background:filter===k?v.color+"18":"#fff",color:filter===k?v.color:"var(--txtl)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{v.icon} {v.label}</button>)}
      </div>

      {/* New post form */}
      {showNew&&hasPerm(s.user,"berichten_schrijven")&&<div className="card" style={{marginBottom:16,padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <strong style={{fontSize:15}}>📌 Nieuw bericht</strong>
          <button onClick={()=>setShowNew(false)} style={{background:"none",border:"none",cursor:"pointer"}}>{IC.X}</button>
        </div>
        <div className="fg" style={{marginBottom:8}}>
          <label className="fl">Categorie</label>
          <select className="fi" value={newPost.categorie} onChange={e=>setNewPost(p=>({...p,categorie:e.target.value}))}>
            {Object.entries(CATS).map(([k,v])=><option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
        </div>
        <div className="fg" style={{marginBottom:10}}>
          <label className="fl">Bericht</label>
          <textarea className="fi" rows={4} value={newPost.bericht} onChange={e=>setNewPost(p=>({...p,bericht:e.target.value}))} placeholder="Bijv: Pasgeboren geitje zoekt nieuw thuis. Wie heeft ruimte?" style={{fontSize:16}}/>
        </div>
        <button className="fbtn" onClick={handlePost} disabled={saving||!newPost.bericht.trim()}>{saving?"Plaatsen...":"📌 Plaatsen op prikbord"}</button>
      </div>}

      {/* Posts list */}
      {filtered.length===0&&<div className="card"><EmptyState icon="📌" title="Nog geen berichten" sub="Wees de eerste — deel nieuws, vraag om hulp of zet een dier te plaatsing."/></div>}
      {filtered.map(post=>{
        const cat=CATS[post.categorie]||CATS.algemeen;
        const own=isOwnPark(post);
        const postReacties=reacties[post.id]||[];
        const isOpen=openPost===post.id;
        const parkNaam=post.parken?.naam||post.park_id;
        const auteurNaam=post.gebruikers?.roepnaam||post.gebruikers?.naam||"Onbekend";
        const datum=new Date(post.created_at).toLocaleDateString("nl-NL",{day:"numeric",month:"short",year:"numeric"});
        return <div key={post.id} className="card" style={{marginBottom:12,borderLeft:`3px solid ${cat.color}`,padding:0,overflow:"hidden"}}>
          <div style={{padding:"14px 16px"}}>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flex:1}}>
                <span style={{fontSize:13,background:cat.color+"18",color:cat.color,padding:"2px 10px",borderRadius:20,fontWeight:600,whiteSpace:"nowrap"}}>{cat.icon} {cat.label}</span>
                {own&&<span style={{fontSize:11,background:"var(--gp)",color:"var(--g)",padding:"2px 8px",borderRadius:20,fontWeight:600}}>Eigen park</span>}
              </div>
              {own&&isBeheerder&&<button onClick={()=>handleDelete(post.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"var(--red)",padding:"2px 4px",flexShrink:0}}>🗑</button>}
            </div>
            {/* Body */}
            <p style={{fontSize:14,lineHeight:1.6,color:"var(--txt)",marginBottom:10,whiteSpace:"pre-wrap"}}>{post.bericht}</p>
            {/* Meta */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}}>
              <div style={{fontSize:12,color:"var(--txtl)"}}>
                <strong style={{color:"var(--g)"}}>{parkNaam}</strong> · {auteurNaam} · {datum}
              </div>
              <button onClick={()=>toggleReacties(post.id)} style={{fontSize:12,color:"var(--bl)",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600,padding:"2px 6px"}}>
                💬 Reageren{postReacties.length>0?` (${postReacties.length})`:""}
              </button>
            </div>
          </div>

          {/* Reacties panel */}
          {isOpen&&<div style={{borderTop:"1px solid var(--brd)",background:"var(--cream)",padding:"12px 16px"}}>
            {loadingReacties[post.id]&&<p style={{fontSize:12,color:"var(--txtl)"}}>Reacties laden...</p>}
            {!loadingReacties[post.id]&&postReacties.length===0&&<p style={{fontSize:12,color:"var(--txtl)",marginBottom:8}}>Nog geen reacties.{!own?" Alleen jouw park en het park van de plaatser kunnen reacties zien.":""}</p>}
            {postReacties.map(r=>{
              const rPark=r.parken?.naam||r.park_id;
              const rAuteur=r.gebruikers?.roepnaam||r.gebruikers?.naam||"Onbekend";
              const rDatum=new Date(r.created_at).toLocaleDateString("nl-NL",{day:"numeric",month:"short"});
              const canDel=isBeheerder&&String(r.park_id)===String(park.id);
              return <div key={r.id} style={{padding:"8px 0",borderBottom:"1px solid var(--brd)",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div>
                  <div style={{fontSize:12,color:"var(--txtl)",marginBottom:2}}><strong style={{color:"var(--g)"}}>{rPark}</strong> · {rAuteur} · {rDatum}</div>
                  <p style={{fontSize:13,lineHeight:1.5,margin:0,whiteSpace:"pre-wrap"}}>{r.bericht}</p>
                </div>
                {canDel&&<button onClick={()=>handleDeleteReactie(post.id,r.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"var(--red)",flexShrink:0}}>🗑</button>}
              </div>;
            })}
            {/* Reply input */}
            <div style={{marginTop:10,display:"flex",gap:8}}>
              <textarea className="fi" rows={2} value={newReactie} onChange={e=>setNewReactie(e.target.value)} placeholder="Schrijf een reactie..." style={{flex:1,fontSize:16}}/>
              <button onClick={()=>handleReactie(post.id)} disabled={savingReactie||!newReactie.trim()} style={{padding:"8px 14px",borderRadius:10,border:"none",background:"var(--g)",color:"#fff",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",alignSelf:"flex-end",opacity:(!newReactie.trim()||savingReactie)?.5:1}}>{savingReactie?"...":"💬"}</button>
            </div>
            <p style={{fontSize:11,color:"var(--txtl)",marginTop:6}}>🔒 Reacties zijn alleen zichtbaar voor jouw park en het park van de plaatser.</p>
          </div>}
        </div>;
      })}
      {!showNew&&hasPerm(s.user,"berichten_schrijven")&&<button className="fab" onClick={()=>setShowNew(true)}>{IC.plus}</button>}
      <div style={{height:80}}/>
    </div>;
  };

  /* ══════════════════════════════════════════════════
     LOGBOEK OVERZICHT — voor beheerders
     ══════════════════════════════════════════════════ */
  const LogboekPg=()=>{
    const dagIcon={ochtend:"🌅",middag:"☀️",avond:"🌙"};
    const dagColor={ochtend:"#e87c28",middag:"#2e6da4",avond:"#6b46c1"};
    // Group by date
    const byDate={};
    s.logboek.forEach(l=>{
      if(!byDate[l.datum]) byDate[l.datum]=[];
      byDate[l.datum].push(l);
    });
    const dates=Object.keys(byDate).sort((a,b)=>b.localeCompare(a));

    return <div className="pg">
      <h1 className="pt">Logboek</h1>
      <p style={{fontSize:13,color:"var(--txtl)",marginBottom:14}}>Bevindingen van alle diensten.</p>
      {dates.length===0&&<div className="card" style={{textAlign:"center",padding:24,color:"var(--txtl)"}}>
        <div style={{fontSize:32,marginBottom:8}}>📓</div>
        <p>Nog geen logboek-entries</p>
      </div>}
      {dates.map(datum=>{
        const entries=byDate[datum];
        const d=new Date(datum);
        const label=d.toLocaleDateString("nl-NL",{weekday:"long",day:"numeric",month:"long"});
        return <div key={datum} style={{marginBottom:16}}>
          <div className="sh"><span className="st" style={{textTransform:"capitalize"}}>{label}</span></div>
          {entries.map(l=><div key={l.id} className="card" style={{marginBottom:8,borderLeft:`3px solid ${dagColor[l.dagdeel]||"var(--g)"}`}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,alignItems:"center"}}>
              <span style={{fontSize:13}}>{dagIcon[l.dagdeel]} <strong>{l.auteur}</strong></span>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:11,background:dagColor[l.dagdeel]+"18",
                  color:dagColor[l.dagdeel],padding:"2px 8px",borderRadius:20,fontWeight:600}}>
                  {l.dagdeel}
                </span>
                {(l.gebruiker_id===s.user.id||hasPerm(s.user,"taken_beheren"))&&<button onClick={()=>{toast.confirm("Bevinding verwijderen?",()=>ds({type:"DEL_LOGBOEK",p:l.id}))}}
                  style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"var(--red)",padding:"2px 4px"}}>🗑</button>}
              </div>
            </div>
            <p style={{fontSize:14,margin:0,lineHeight:1.5,color:"var(--g)"}}>{l.tekst}</p>
            {l.foto&&<img src={l.foto} alt="bevinding" style={{width:"100%",borderRadius:8,marginTop:8,maxHeight:200,objectFit:"cover"}}/>}
          </div>)}
        </div>;
      })}
      <div style={{height:80}}/>
    </div>;
  };

  // eslint-disable-next-line no-undef
  return <div className="app"><Header/>{pg()}<BNav soorten={soorten} setPhotoModal={setPhotoModal}/>{photoModal&&<PhotoModal photos={photoModal.photos} dierName={photoModal.dierName} initialIndex={photoModal.initialIndex} showNav={photoModal.showNav!==false} onClose={()=>setPhotoModal(null)}/>}</div>;
}

/* ═══════════════════════════════════════════════════════════
   CSS — Platform + Management combined
   ═══════════════════════════════════════════════════════════ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&display=swap');
:root{--g:#2d5016;--gl:#4a7a2e;--gp:#e8f0e0;--cream:#faf7f0;--cd:#f0ead6;--red:#c44536;--rl:#fde8e5;--or:#e87c28;--ol:#fff3e6;--bl:#2e6da4;--bll:#e6f0fa;--pu:#6b4c8a;--pul:#f0e6fa;--txt:#1a1a1a;--txtl:#666;--brd:#e0ddd4;--sh:0 2px 12px rgba(45,80,22,.08);--shl:0 8px 32px rgba(45,80,22,.12);--r:14px}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'DM Sans',sans-serif;background:var(--cream);color:var(--txt);-webkit-font-smoothing:antialiased;overscroll-behavior-y:contain}
html{overscroll-behavior-y:contain}

/* ── Landing ── */
.landing{max-width:800px;margin:0 auto;min-height:100vh}
.l-hero{position:relative;overflow:hidden;padding:60px 24px 40px;text-align:center}
.l-hero-bg{position:absolute;inset:0;background:linear-gradient(160deg,#1a3a0a 0%,var(--g) 40%,var(--gl) 100%);z-index:0}
.l-hero-bg::after{content:'';position:absolute;inset:0;background:radial-gradient(circle at 30% 80%,rgba(255,255,255,.06) 0%,transparent 50%)}
.l-hero-content{position:relative;z-index:2}
.l-hero-title{font-family:'Fraunces',serif;font-size:48px;font-weight:700;color:#fff;line-height:1.1;margin-bottom:12px}
.l-hero-dot{color:var(--or)}
.l-hero-sub{font-size:16px;color:rgba(255,255,255,.8);line-height:1.6;max-width:500px;margin:0 auto 28px;font-weight:300}
.l-hero-actions{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
.l-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 24px;border-radius:12px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;border:none;transition:all .2s}
.l-btn:active{transform:scale(.96)}
.l-btn-primary{background:#fff;color:var(--g);box-shadow:0 4px 16px rgba(0,0,0,.15)}
.l-btn-primary:hover{background:var(--gp)}
.l-btn-secondary{background:rgba(255,255,255,.15);color:#fff;border:1.5px solid rgba(255,255,255,.3);backdrop-filter:blur(8px)}
.l-hero-visual{position:relative;height:80px;z-index:1}
.l-hero-animals{position:relative;width:100%;height:80px}
.l-float-emoji{position:absolute;font-size:32px;animation:floatEmoji 4s ease-in-out infinite}
@keyframes floatEmoji{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
.l-stats{display:flex;justify-content:center;align-items:center;gap:20px;padding:20px;background:#fff;border-bottom:1px solid var(--brd)}
.l-stat{text-align:center}.l-stat-n{font-family:'Fraunces',serif;font-size:22px;font-weight:700;color:var(--g);display:block}.l-stat-l{font-size:10px;color:var(--txtl);font-weight:500}
.l-stat-div{width:1px;height:30px;background:var(--brd)}
.l-section{padding:40px 24px;text-align:center}
.l-section-dark{background:linear-gradient(160deg,#1a3a0a,var(--g))}
.l-section-title{font-family:'Fraunces',serif;font-size:26px;font-weight:700;margin-bottom:8px;color:var(--g)}
.l-section-sub{font-size:14px;color:rgba(255,255,255,.7);margin-bottom:24px;line-height:1.5}
.l-doelgroepen{display:flex;flex-direction:column;gap:10px;max-width:500px;margin:20px auto 0}
.l-doel-card{background:#fff;border-radius:var(--r);padding:18px;box-shadow:var(--sh);border:1px solid var(--brd);text-align:left;display:flex;gap:14px;align-items:flex-start;animation:fi .3s ease both}
.l-doel-icon{font-size:28px;flex-shrink:0;margin-top:2px}
.l-doel-title{font-weight:700;font-size:14px;margin-bottom:3px}
.l-doel-desc{font-size:12px;color:var(--txtl);line-height:1.5}
.l-features{display:grid;grid-template-columns:1fr;gap:8px;max-width:500px;margin:0 auto}
.l-feat-card{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);border-radius:var(--r);padding:14px 16px;display:flex;gap:12px;align-items:flex-start;text-align:left;backdrop-filter:blur(4px)}
.l-feat-icon{font-size:24px;flex-shrink:0}
.l-feat-title{font-weight:700;font-size:13px;color:#fff;margin-bottom:2px}
.l-feat-desc{font-size:11px;color:rgba(255,255,255,.7);line-height:1.5}
.l-cta{padding:50px 24px;background:var(--cd)}
.l-cta-title{font-family:'Fraunces',serif;font-size:28px;font-weight:700;color:var(--g);margin-bottom:8px}
.l-cta-sub{font-size:14px;color:var(--txtl);margin-bottom:20px}
.l-footer{padding:30px 24px;text-align:center;border-top:1px solid var(--brd)}
.l-footer-logo{font-family:'Fraunces',serif;font-size:18px;font-weight:700;color:var(--g);margin-bottom:6px}
.l-footer-copy{font-size:10px;color:var(--brd);margin-top:8px}
.l-aansluiten-btn{display:inline-block;margin:10px 0;padding:6px 14px;border-radius:8px;border:1px solid var(--brd);background:#fff;font-family:inherit;font-size:11px;font-weight:500;color:var(--txtl);cursor:pointer;transition:all .2s}
.l-aansluiten-btn:hover{color:var(--g);border-color:var(--g)}
@keyframes fi{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}

/* ── Zoek page ── */
.zoek-page{max-width:800px;margin:0 auto;min-height:100vh;background:var(--cream)}
.zoek-header{padding:16px 20px;background:linear-gradient(135deg,var(--g),var(--gl));color:#fff}
.zoek-back{display:flex;align-items:center;gap:5px;font-size:13px;color:#fff;font-weight:500;cursor:pointer;border:none;background:none;font-family:inherit;padding:0;margin-bottom:8px;opacity:.8}
.zoek-title{font-family:'Fraunces',serif;font-size:22px;font-weight:700}
.zoek-search-wrap{padding:16px 20px 0}
.zoek-search{display:flex;align-items:center;gap:8px;padding:10px 14px;background:#fff;border-radius:12px;border:1.5px solid var(--brd);box-shadow:var(--sh)}
.zoek-search input{flex:1;border:none;outline:none;font-family:inherit;font-size:16px;background:none}
.zoek-search svg{color:var(--txtl);flex-shrink:0}
.zoek-clear{width:24px;height:24px;border-radius:12px;border:none;background:var(--cd);font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--txtl)}
.zoek-prov-filter{display:flex;gap:4px;flex-wrap:wrap;margin-top:10px}
.zoek-prov-btn{padding:5px 10px;border-radius:10px;border:1.5px solid var(--brd);background:#fff;font-family:inherit;font-size:10px;font-weight:500;cursor:pointer;color:var(--txtl);white-space:nowrap}
.zoek-prov-btn.a{background:var(--g);border-color:var(--g);color:#fff}
.zoek-nearby-btn{display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:12px;border:2px solid var(--g);background:var(--gp);font-family:inherit;font-size:12px;font-weight:600;color:var(--g);cursor:pointer;white-space:nowrap;transition:all .2s}
.zoek-nearby-btn.a{background:var(--g);color:#fff}
.zoek-nearby-btn.mini{padding:6px 10px;font-size:11px;border-width:1.5px}
.zoek-nearby-btn:disabled{opacity:.6;cursor:wait}
.zoek-spinner{display:inline-block;width:14px;height:14px;border:2px solid var(--g);border-top-color:transparent;border-radius:50%;animation:spin .6s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes pulse{0%{transform:scale(1);opacity:.25}70%{transform:scale(2.8);opacity:0}100%{transform:scale(2.8);opacity:0}}
.zoek-loc-error{font-size:11px;color:var(--red);margin-top:6px;padding:6px 10px;background:var(--rl);border-radius:8px}
.zoek-dist-badge{padding:2px 7px;border-radius:8px;background:var(--bll);font-size:10px;font-weight:600;color:var(--bl);white-space:nowrap}
.zoek-nearby-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.zoek-nearby-close{font-size:12px;color:var(--g);font-weight:600;cursor:pointer;border:none;background:none;font-family:inherit;text-decoration:underline}
.zoek-map-container{height:350px;margin:0 20px 16px;border-radius:14px;overflow:hidden;border:2px solid var(--brd);box-shadow:var(--sh)}
.park-marker-icon{background:none!important;border:none!important}
.park-tooltip{font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;color:#2d5016;background:#fff;border:1.5px solid #2d5016;border-radius:8px;padding:4px 10px;box-shadow:0 2px 8px rgba(0,0,0,.15)}
.zoek-results{padding:0 20px 30px}
.zoek-results-count{font-size:11px;color:var(--txtl);font-weight:500;margin-bottom:8px}
.zoek-card{background:#fff;border-radius:var(--r);padding:14px;margin-bottom:8px;box-shadow:var(--sh);border:2px solid var(--brd);cursor:pointer;transition:all .2s}
.zoek-card:active{transform:scale(.98)}
.zoek-card.selected{border-color:var(--g)}
.zoek-card-active{background:linear-gradient(135deg,#f0faf0 0%,#e8f5e8 100%);border-color:#b8ddb8}
.zoek-card-active.selected{border-color:var(--g);background:linear-gradient(135deg,#e4f5e4 0%,#d6efd6 100%)}
.zoek-card-inactive{opacity:.85}
.zoek-card-inactive .zoek-card-name{color:var(--txtl)}
.zoek-actief-badge{display:inline-flex;align-items:center;padding:2px 7px;border-radius:20px;background:#2d5016;color:#fff;font-size:10px;font-weight:700;letter-spacing:.3px;flex-shrink:0}
.zoek-card-avatar-active{background:linear-gradient(135deg,#2d5016,#4a8a28)!important;color:#fff}
.zoek-card:active{transform:scale(.98)}
.zoek-card.selected{border-color:var(--g)}
.zoek-card-top{display:flex;align-items:center;gap:12px}
.zoek-card-avatar{width:42px;height:42px;border-radius:12px;background:var(--gp);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
.zoek-card-info{flex:1;min-width:0}
.zoek-card-name{font-weight:700;font-size:15px;color:var(--g)}
.zoek-card-place{font-size:12px;color:var(--txtl)}
.zoek-card-badge{padding:4px 10px;border-radius:10px;background:var(--gp);font-size:11px;font-weight:600;color:var(--g);flex-shrink:0}
.zoek-card-detail{margin-top:12px;padding-top:12px;border-top:1px solid var(--brd);animation:fi .2s ease}
.zoek-card-desc{font-size:12px;color:var(--txtl);line-height:1.5;margin-bottom:10px}
.zoek-card-meta{display:flex;flex-direction:column;gap:5px;margin-bottom:10px}
.zoek-meta-row{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--txt)}
.zoek-meta-row svg{color:var(--g);flex-shrink:0}
.zoek-card-soorten{display:flex;flex-wrap:wrap;gap:4px}
.zoek-soort-chip{padding:3px 8px;border-radius:8px;background:var(--cd);font-size:10px;font-weight:500}
.zoek-empty{text-align:center;padding:40px 20px;color:var(--txtl)}

/* ── Login page ── */
.login-page{max-width:800px;margin:0 auto;min-height:100vh;background:var(--cream);display:flex;align-items:center;justify-content:center;padding:20px}
.login-card{background:#fff;border-radius:20px;padding:30px 24px;width:100%;max-width:400px;box-shadow:var(--shl);animation:fi .3s ease}
.login-park-header{display:flex;align-items:center;gap:14px;margin-bottom:16px}
.login-park-avatar{width:50px;height:50px;border-radius:14px;background:var(--gp);display:flex;align-items:center;justify-content:center;font-size:26px;flex-shrink:0}
.login-park-name{font-family:'Fraunces',serif;font-size:20px;font-weight:700;color:var(--g)}
.login-park-place{font-size:13px;color:var(--txtl)}
.login-divider{height:1px;background:var(--brd);margin:16px 0}
.login-subtitle{font-family:'Fraunces',serif;font-size:16px;font-weight:600;color:var(--g);margin-bottom:4px}
.login-hint{font-size:11px;color:var(--txtl);margin-bottom:14px}
.login-user-btn{width:100%;padding:12px 14px;border:2px solid var(--brd);border-radius:14px;background:#fff;cursor:pointer;display:flex;align-items:center;gap:12px;margin-bottom:8px;font-family:inherit;transition:all .2s}
.login-user-btn:hover{border-color:var(--g);background:var(--gp)}
.login-user-btn:active{transform:scale(.98)}
.login-user-av{width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;color:#fff;flex-shrink:0}
.login-user-info{flex:1;text-align:left}
.login-user-name{font-weight:600;font-size:14px;display:block}
.login-user-role{font-size:11px;font-weight:500}
.login-demo-note{font-size:10px;color:var(--txtl);text-align:center;margin-top:12px;font-style:italic}
.login-pw-wrap{display:flex;gap:8px;margin-top:10px}
.login-pw-input{flex:1;padding:10px 14px;border-radius:12px;border:2px solid var(--brd);font-family:inherit;font-size:16px;outline:none;background:#fff;transition:border-color .2s}
.login-pw-input:focus{border-color:var(--g)}
.login-pw-error{font-size:12px;color:var(--red);margin-top:8px;padding:6px 10px;background:var(--rl);border-radius:8px;text-align:center}
.login-forgot{display:block;width:100%;margin-top:12px;background:none;border:none;color:var(--txtl);font-size:12px;cursor:pointer;text-align:center;font-family:inherit;text-decoration:underline;padding:4px}
.login-forgot:hover{color:var(--g)}

/* ── Management App ── */
.app{min-height:100vh;max-width:480px;margin:0 auto;background:var(--cream)}
.hdr{background:linear-gradient(135deg,var(--g),var(--gl));color:#fff;padding:14px 20px;position:sticky;top:0;z-index:100;box-shadow:0 4px 20px rgba(45,80,22,.25)}
.hdr-top{display:flex;align-items:center;justify-content:space-between}
.hdr-logo{display:flex;align-items:center;gap:10px}
.hdr-t{font-family:'Fraunces',serif;font-size:18px;font-weight:700}
.hdr-s{font-size:11px;opacity:.7;font-weight:300;letter-spacing:.5px}
.hbtn{background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.25);color:#fff;border-radius:10px;padding:7px 12px;font-size:13px;font-family:inherit;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:5px;backdrop-filter:blur(8px)}
.bnav{position:fixed;bottom:0;left:50%;transform:translateX(-50%);max-width:480px;width:100%;background:#fff;border-top:1px solid var(--brd);display:flex;justify-content:space-around;padding:4px 0 max(env(safe-area-inset-bottom),6px);z-index:100;box-shadow:0 -4px 20px rgba(0,0,0,.06)}
.ni{display:flex;flex-direction:column;align-items:center;padding:4px 2px;border:none;background:none;color:var(--txtl);font-size:10px;font-family:inherit;font-weight:500;cursor:pointer;position:relative;min-width:48px}
.ni.a{color:var(--g)}.ni.a::after{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:18px;height:3px;background:var(--g);border-radius:0 0 3px 3px}
.ni svg{margin-bottom:2px}
.nbg{position:absolute;top:0;right:4px;background:var(--red);color:#fff;font-size:9px;font-weight:700;min-width:14px;height:14px;border-radius:7px;display:flex;align-items:center;justify-content:center;padding:0 3px}
.pg{padding:16px;padding-bottom:85px;animation:fi .2s ease}
@keyframes fi{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
.pt{font-family:'Fraunces',serif;font-size:22px;font-weight:700;margin-bottom:14px;color:var(--g)}
.card{background:#fff;border-radius:var(--r);padding:13px;margin-bottom:8px;box-shadow:var(--sh);border:1px solid var(--brd)}
.bdg{display:inline-flex;padding:3px 9px;border-radius:18px;font-size:11px;font-weight:600;letter-spacing:.3px}
.bdg-ok{background:var(--gp);color:var(--g)}.bdg-pen{background:var(--ol);color:var(--or)}.bdg-urg{background:var(--rl);color:var(--red)}.bdg-inf{background:var(--bll);color:var(--bl)}.bdg-gem{background:var(--pul);color:var(--pu)}
.ci{display:flex;align-items:center;padding:11px 13px;background:#fff;border-radius:var(--r);margin-bottom:7px;box-shadow:var(--sh);border:1px solid var(--brd);cursor:pointer;gap:10px;transition:transform .15s}
.ci:active{transform:scale(.98)}.ci.dn{opacity:.5}.ci.dn .cit{text-decoration:line-through}
.cb{width:24px;height:24px;border-radius:7px;border:2px solid var(--brd);display:flex;align-items:center;justify-content:center;flex-shrink:0;background:#fff}
.cb.ck{background:var(--g);border-color:var(--g);color:#fff}
.cic{flex:1;min-width:0}.cit{font-weight:600;font-size:14px}.cis{font-size:12px;color:var(--txtl);margin-top:1px}
.ds{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px}
.sc{background:#fff;border-radius:var(--r);padding:12px;box-shadow:var(--sh);border:1px solid var(--brd);text-align:center}
.sn{font-family:'Fraunces',serif;font-size:24px;font-weight:700;color:var(--g);line-height:1}
.sl{font-size:11px;color:var(--txtl);margin-top:3px;font-weight:500}.sc.al .sn{color:var(--red)}
.sh{display:flex;justify-content:space-between;align-items:center;margin:16px 0 8px}
.st{font-family:'Fraunces',serif;font-size:16px;font-weight:600}
.slnk{font-size:12px;color:var(--g);font-weight:600;cursor:pointer;border:none;background:none;font-family:inherit}
.mov{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:flex-end;justify-content:center;z-index:200;backdrop-filter:blur(4px)}
.mod{background:#fff;border-radius:20px 20px 0 0;padding:22px 18px max(env(safe-area-inset-bottom),18px);width:100%;max-width:480px;box-shadow:var(--shl);animation:su .3s ease;max-height:85vh;overflow-y:auto}
@keyframes su{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
.mt{font-family:'Fraunces',serif;font-size:18px;font-weight:700;color:var(--g);display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
.mx{width:34px;height:34px;border-radius:10px;border:none;background:var(--cd);cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--txtl)}
.rbdg{display:inline-flex;padding:2px 7px;border-radius:10px;font-size:11px;font-weight:600}
.fg{margin-bottom:10px}.fl{font-size:12px;font-weight:600;color:var(--txtl);margin-bottom:3px;display:block;text-transform:uppercase;letter-spacing:.5px}
.fi{width:100%;padding:9px 11px;border:1.5px solid var(--brd);border-radius:10px;font-family:inherit;font-size:16px;outline:none;background:#fff}
.fi:focus{border-color:var(--g)}
select.fi{appearance:none;background:url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23666' viewBox='0 0 24 24'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e") no-repeat right 10px center #fff}
.fbtn{width:100%;padding:11px;border:none;border-radius:12px;background:var(--g);color:#fff;font-family:inherit;font-size:16px;font-weight:600;cursor:pointer;margin-top:6px}
.fbtn:active{transform:scale(.97)}
.fab{position:fixed;bottom:72px;right:max(calc((100vw - 480px)/2 + 14px),14px);width:48px;height:48px;border-radius:15px;background:var(--g);color:#fff;border:none;box-shadow:0 4px 16px rgba(45,80,22,.3);cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:90}
.fab:active{transform:scale(.9)}
.fr{display:flex;gap:5px;margin-bottom:10px;overflow-x:auto;scrollbar-width:none;padding-bottom:2px}
.fr::-webkit-scrollbar{display:none}
.fc{padding:5px 11px;border-radius:16px;border:1.5px solid var(--brd);background:#fff;font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;white-space:nowrap}
.fc.a{background:var(--g);border-color:var(--g);color:#fff}
.ai{display:flex;gap:10px;padding:11px 13px;background:#fff;border-radius:var(--r);margin-bottom:7px;box-shadow:var(--sh);border:1px solid var(--brd)}
.ad{text-align:center;min-width:40px}.add{font-family:'Fraunces',serif;font-size:20px;font-weight:700;color:var(--g);line-height:1}
.adm{font-size:10px;color:var(--txtl);font-weight:500;text-transform:uppercase}
.atd{width:4px;border-radius:2px;flex-shrink:0}
.type-medisch{background:var(--red)}.type-vergadering{background:var(--bl)}.type-evenement{background:var(--or)}.type-gemeente{background:var(--pu)}
.ac{flex:1}.atn{font-weight:600;font-size:14px}.atm{font-size:12px;color:var(--g);font-weight:500;margin-top:1px}.adsc{font-size:12px;color:var(--txtl);margin-top:2px;line-height:1.3}
.sbs{display:flex;gap:4px;margin-top:7px;flex-wrap:wrap}
.sb{padding:4px 9px;border-radius:12px;border:1.5px solid var(--brd);background:#fff;font-family:inherit;font-size:11px;font-weight:600;cursor:pointer}
.sb.a{background:var(--g);border-color:var(--g);color:#fff}
.mc{background:#fff;border-radius:var(--r);padding:11px 13px;margin-bottom:7px;box-shadow:var(--sh);border:1px solid var(--brd)}
.mc.dringend{border-left:4px solid var(--red)}.mc.belangrijk{border-left:4px solid var(--or)}
.miw{display:flex;gap:7px;margin-top:4px;padding:10px;background:#fff;border-radius:var(--r);box-shadow:var(--sh);border:1px solid var(--brd)}
.mi{flex:1;border:none;font-family:inherit;font-size:14px;outline:none;background:none;resize:none;min-height:34px}
.msb{width:34px;height:34px;border-radius:10px;border:none;background:var(--g);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.msb:active{transform:scale(.9)}
.ps{display:flex;gap:4px;margin-top:5px;flex-wrap:wrap}
.po{padding:3px 9px;border-radius:12px;border:1.5px solid var(--brd);background:#fff;font-family:inherit;font-size:11px;font-weight:500;cursor:pointer}
.po.s{border-color:var(--g);background:var(--gp);color:var(--g)}
.agrid{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-bottom:12px}
.soort-tile{background:#fff;border-radius:var(--r);padding:18px 14px;box-shadow:var(--sh);border:1px solid var(--brd);text-align:center;cursor:pointer;transition:all .2s}
.soort-tile:active{transform:scale(.96)}
.soort-em{font-size:40px;margin-bottom:4px}
.soort-nm{font-family:'Fraunces',serif;font-size:15px;font-weight:600;color:var(--g)}
.soort-cnt{font-size:12px;color:var(--txtl);margin-top:2px}
.dier-card{background:#fff;border-radius:var(--r);overflow:hidden;margin-bottom:10px;box-shadow:var(--sh);border:1px solid var(--brd);cursor:pointer;transition:all .2s}
.dier-card:active{transform:scale(.98)}
.dier-img{width:100%;height:120px;object-fit:cover;display:block}
.dier-placeholder{width:100%;height:120px;display:flex;align-items:center;justify-content:center;font-size:48px;background:linear-gradient(135deg,var(--gp),var(--cd))}
.dier-body{padding:12px}
.dier-nm{font-weight:600;font-size:15px}.dier-loc{font-size:12px;color:var(--g);font-weight:500}
.dier-detail-img{width:100%;height:220px;object-fit:contain;border-radius:var(--r);margin-bottom:10px}
.dier-detail-ph{width:100%;height:180px;display:flex;align-items:center;justify-content:center;font-size:70px;background:linear-gradient(135deg,var(--gp),var(--cd));border-radius:var(--r);margin-bottom:10px}
.photo-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;margin:10px 0}
.photo-grid img{width:100%;height:80px;object-fit:cover;border-radius:8px}
.photo-del{position:absolute;top:3px;right:3px;width:20px;height:20px;border-radius:10px;background:rgba(0,0,0,.6);color:#fff;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px}
.photo-wrap{position:relative}
.upload-btn{width:100%;padding:12px;border:2px dashed var(--brd);border-radius:var(--r);background:var(--cd);font-family:inherit;font-size:13px;color:var(--g);font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;margin:8px 0}
.upload-btn:active{background:var(--gp)}
.back-btn{display:flex;align-items:center;gap:5px;font-size:13px;color:var(--g);font-weight:600;cursor:pointer;border:none;background:none;font-family:inherit;padding:0;margin-bottom:12px}
.cc{background:#fff;border-radius:var(--r);padding:14px;margin-bottom:8px;box-shadow:var(--sh);border:1px solid var(--brd)}
.cc-top{display:flex;align-items:center;gap:11px;margin-bottom:8px}
.cc-av{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0;background:var(--gp)}
.cc-av.medewerker{background:var(--gp)}.cc-av.gemeente{background:var(--pul)}.cc-av.dierenarts{background:var(--bll)}.cc-av.leverancier{background:var(--ol)}.cc-av.noodgeval{background:var(--rl)}.cc-av.overig{background:var(--cd)}
.cc-nm{font-weight:600;font-size:15px}.cc-fn{font-size:12px;color:var(--txtl)}
.cc-details{display:flex;flex-direction:column;gap:5px;margin-top:6px;padding-top:8px;border-top:1px solid var(--brd)}
.cc-row{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--txt)}
.cc-row svg{color:var(--g);flex-shrink:0}.cc-row a{color:var(--g);text-decoration:none;font-weight:500}
.cc-note{font-size:12px;color:var(--txtl);font-style:italic;margin-top:4px;line-height:1.4;padding:6px 8px;background:var(--cd);border-radius:8px}
.search-bar{width:100%;padding:9px 12px 9px 34px;border:1.5px solid var(--brd);border-radius:12px;font-family:inherit;font-size:13px;outline:none;background:#fff url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%23999' stroke-width='2' viewBox='0 0 24 24'%3e%3ccircle cx='11' cy='11' r='8'/%3e%3cline x1='21' y1='21' x2='16.65' y2='16.65'/%3e%3c/svg%3e") no-repeat 10px center;margin-bottom:10px}
.search-bar:focus{border-color:var(--g)}
.uc{background:#fff;border-radius:var(--r);padding:12px;margin-bottom:7px;box-shadow:var(--sh);border:1px solid var(--brd);display:flex;align-items:center;gap:10px}
.ua{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;color:#fff;flex-shrink:0}
.uinf{flex:1;min-width:0}.unm{font-weight:600;font-size:14px}.uem{font-size:11px;color:var(--txtl)}
.uab{display:flex;gap:4px}.ubtn{width:30px;height:30px;border-radius:8px;border:1px solid var(--brd);background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--txtl)}
.iab{display:flex;gap:3px;flex-shrink:0}.iab button{width:26px;height:26px;border-radius:6px;border:1px solid var(--brd);background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--txtl);padding:0}
.pd{width:7px;height:7px;border-radius:50%;display:inline-block;margin-right:5px;flex-shrink:0}.pd-h{background:var(--red)}.pd-n{background:var(--or)}.pd-l{background:var(--bl)}
.meer-overlay{position:fixed;inset:0;z-index:150}
.meer-menu{position:fixed;bottom:58px;left:50%;transform:translateX(-50%);max-width:460px;width:calc(100% - 20px);background:#fff;border-radius:18px;box-shadow:0 -8px 40px rgba(0,0,0,.18);padding:10px;z-index:151;animation:su .2s ease}
.meer-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:2px}
.meer-item{display:flex;flex-direction:column;align-items:center;gap:4px;padding:10px 6px;border-radius:14px;border:none;background:none;font-family:inherit;font-size:11px;font-weight:500;color:var(--txt);cursor:pointer}
.meer-item:active{background:var(--gp)}
.meer-item .meer-ic{width:36px;height:36px;border-radius:11px;display:flex;align-items:center;justify-content:center;color:#fff}
.meer-item .meer-bdg{position:absolute;top:-2px;right:-2px;background:var(--red);color:#fff;font-size:9px;font-weight:700;min-width:14px;height:14px;border-radius:7px;display:flex;align-items:center;justify-content:center;padding:0 3px}
.plan-week-nav{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.plan-week-nav button{width:34px;height:34px;border-radius:10px;border:1px solid var(--brd);background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--g)}
.plan-week-label{font-family:'Fraunces',serif;font-size:14px;font-weight:600;color:var(--g)}
.plan-today-btn{padding:4px 10px;border-radius:10px;border:1.5px solid var(--g);background:var(--gp);font-family:inherit;font-size:12px;font-weight:600;color:var(--g);cursor:pointer}
.plan-grid{display:flex;flex-direction:column;gap:6px}
.plan-day{background:#fff;border-radius:var(--r);box-shadow:var(--sh);border:1px solid var(--brd);overflow:hidden}
.plan-day.today{border-color:var(--g);border-width:2px}
.plan-day-hdr{display:flex;justify-content:space-between;align-items:center;padding:10px 12px;background:var(--cd);font-size:13px;font-weight:600}
.plan-day.today .plan-day-hdr{background:var(--gp);color:var(--g)}
.plan-day-date{font-size:11px;color:var(--txtl);font-weight:400}
.plan-shifts{padding:6px 10px 10px}
.plan-shift-row{display:flex;align-items:flex-start;gap:8px;padding:5px 0;border-bottom:1px solid var(--brd)}
.plan-shift-row:last-child{border-bottom:none}
.plan-shift-label{font-size:11px;font-weight:600;min-width:54px;padding:3px 7px;border-radius:8px;text-align:center;flex-shrink:0}
.plan-people{display:flex;flex-wrap:wrap;gap:4px;flex:1;min-height:22px;align-items:center}
.plan-person{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:8px;font-size:12px;font-weight:500}
.plan-empty{font-size:12px;color:var(--txtl);font-style:italic}
.plan-legend{display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap}
.plan-legend-item{display:flex;align-items:center;gap:4px;font-size:11px;color:var(--txtl)}
.ci-wrap{background:#fff;border-radius:var(--r);margin-bottom:7px;box-shadow:var(--sh);border:1px solid var(--brd);padding:0 13px 10px;transition:opacity .2s}
.ci-wrap.dn{opacity:.5}
.ci-wrap .ci{background:none!important;box-shadow:none!important;border:none!important}
.item-link{display:flex;align-items:center;gap:6px;padding:7px 10px;background:linear-gradient(135deg,#ff0000,#cc0000);color:#fff;border-radius:10px;font-size:12px;font-weight:600;text-decoration:none;width:100%;justify-content:center;margin-top:2px}
.item-link:hover{opacity:.9}
.mfu-del{display:block;width:100%;padding:5px;border:1px solid var(--brd);border-radius:8px;background:#fff;font-family:inherit;font-size:12px;color:var(--red);cursor:pointer;text-align:center}
.mfu-preview{margin-top:4px}

/* ── Toast ───────────────────────────────────────────────── */
.toast-ctr{position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:9999;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;width:min(92vw,420px)}
.toast{display:flex;align-items:center;gap:10px;padding:12px 14px;border-radius:14px;box-shadow:0 4px 20px rgba(0,0,0,.18);font-family:inherit;font-size:14px;font-weight:600;pointer-events:all;animation:toastIn .25s ease;width:100%}
@keyframes toastIn{from{opacity:0;transform:translateY(-12px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}
.toast-success{background:#2d7a2d;color:#fff}
.toast-error{background:#c62828;color:#fff}
.toast-warn{background:#e65100;color:#fff}
.toast-info{background:#1565c0;color:#fff}
.toast-confirm{background:#fff;color:var(--txt);border:2px solid var(--brd);flex-wrap:wrap}
.toast-icon{font-size:16px;flex-shrink:0}
.toast-msg{flex:1;line-height:1.35}
.toast-x{background:rgba(255,255,255,.25);border:none;color:inherit;width:24px;height:24px;border-radius:50%;cursor:pointer;font-size:16px;flex-shrink:0;display:flex;align-items:center;justify-content:center;padding:0}
.toast-btns{display:flex;gap:8px;width:100%;margin-top:8px}
.toast-yes{flex:1;padding:8px;border:none;border-radius:8px;background:var(--g);color:#fff;font-weight:700;font-family:inherit;cursor:pointer;font-size:13px}
.toast-no{flex:1;padding:8px;border:1px solid var(--brd);border-radius:8px;background:#f5f5f5;color:var(--txt);font-weight:600;font-family:inherit;cursor:pointer;font-size:13px}

/* ── Paw spinner ─────────────────────────────────────────── */
.paw-spin{display:inline-block;animation:pawBounce .45s ease-in-out infinite alternate;transform-origin:center}
@keyframes pawBounce{from{transform:scale(.75) rotate(-12deg);opacity:.6}to{transform:scale(1.1) rotate(12deg);opacity:1}}
.paw-loading{opacity:.75!important;pointer-events:none!important}

/* ── Empty state ─────────────────────────────────────────── */
.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px 24px;text-align:center}
.empty-icon{font-size:52px;margin-bottom:12px;opacity:.85}
.empty-title{font-size:16px;font-weight:700;color:var(--txt);margin-bottom:6px}
.empty-sub{font-size:13px;color:var(--txtl);line-height:1.5;max-width:260px}
`;

/* ═══════════════════════════════════════════════════════════
   APP ADMIN PANEL
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   SLEUTELS (KEYS) MANAGEMENT - GRID WITH CHECKBOXES
   ═══════════════════════════════════════════════════════════ */

function PrivacyPolicy({onClose}) {
  return <div style={{minHeight:"100vh",background:"#f5f5f5",paddingBottom:100}}>
    {/* Header */}
    <div style={{background:"linear-gradient(135deg, #3a6b1f 0%, #2d5016 100%)",color:"#fff",padding:"30px 20px",textAlign:"center"}}>
      <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",color:"#fff",border:"none",borderRadius:6,padding:"8px 16px",cursor:"pointer",marginBottom:16,fontSize:14}}>
        ← Terug
      </button>
      <h1 style={{margin:"10px 0",fontSize:28,fontWeight:700}}>🐾 Privacybeleid</h1>
      <p style={{margin:0,opacity:0.9,fontSize:14}}>Effectief vanaf 5 maart 2026</p>
    </div>

    {/* Content */}
    <div style={{maxWidth:"900px",margin:"0 auto",padding:"20px",background:"white",marginTop:20,marginBottom:20,borderRadius:12}}>
      
      <p style={{color:"#999",fontSize:13,fontStyle:"italic",marginBottom:20}}>Laatst bijgewerkt: 5 maart 2026</p>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>1. Inleiding</h2>
      <p style={{color:"#555",lineHeight:1.6,marginBottom:15}}>
        Dierenparkje respecteert uw privacy. Dit privacybeleid beschrijft hoe we persoonlijke gegevens verzamelen, gebruiken, delen en beschermen wanneer u onze webapplicatie en mobiele webapp gebruikt.
      </p>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>2. Welke informatie verzamelen we?</h2>
      
      <h3 style={{color:"#2d5016",fontSize:16,fontWeight:600,marginTop:20,marginBottom:10}}>2.1 Informatie die u zelf verstrekt:</h3>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Accountgegevens:</strong> Naam, e-mailadres, telefoonnummer (optioneel)</li>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Parkbeheersgegevens:</strong> Parknaam, adres, contact informatie (voor parkbeheerders)</li>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Dierengegevens:</strong> Dieren in het park (naam, soort, locatie)</li>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Foto's en media:</strong> Foto's die u uploaded van dieren en parken</li>
      </ul>

      <h3 style={{color:"#2d5016",fontSize:16,fontWeight:600,marginTop:20,marginBottom:10}}>2.2 Automatisch verzamelde informatie:</h3>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Locatiegegevens:</strong> Uw GPS-locatie (alleen wanneer u dit toestaat)</li>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Gebruiksgegevens:</strong> Welke parkjes u bezoekt, hoe u de app gebruikt</li>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Apparaatinformatie:</strong> Type apparaat, besturingssysteem, versie</li>
        <li style={{margin:"8px 0",color:"#555"}}><strong>Technische gegevens:</strong> IP-adres, browser type, pagina's die u bezoekt</li>
      </ul>

      <div style={{background:"#f0f8ff",borderLeft:"4px solid #2196F3",padding:15,margin:"20px 0",borderRadius:4}}>
        <strong style={{color:"#1976d2"}}>ℹ️ Let op:</strong> <span style={{color:"#555"}}>We gebruiken lokale opslag (browser cache) voor gebruikersinstellingen. Dit gebeurt alleen op uw apparaat en wordt niet naar onze servers verzonden.</span>
      </div>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>3. Waarom verzamelen we deze informatie?</h2>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>App-functionaliteit:</strong> De app te laten werken (parkjes zoeken, dieren bekijken)</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Personalisatie:</strong> Uw favoriete parkjes onthouden</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Parkbeheer:</strong> Parkbeheerders hun dieren en bezoeken beheren</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Verbetering:</strong> De app beter te maken op basis van hoe u het gebruikt</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Support:</strong> U te helpen wanneer u contact opneemt</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Veiligheid:</strong> Misbruik en fraude te voorkomen</li>
      </ul>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>4. Delen we uw informatie?</h2>
      
      <p style={{color:"#555",fontWeight:600,marginBottom:10}}>We geven uw persoonlijke informatie NIET door aan derde partijen voor:</p>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}>❌ Marketing doeleinden</li>
        <li style={{margin:"8px 0",color:"#555"}}>❌ Verkoop aan andere bedrijven</li>
        <li style={{margin:"8px 0",color:"#555"}}>❌ Advertentiedoeleinden</li>
      </ul>

      <p style={{color:"#555",fontWeight:600,marginBottom:10}}>We delen informatie WEL met:</p>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Supabase:</strong> Onze database provider (in EU)</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Resend:</strong> Onze email service provider</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Google Maps:</strong> Voor locatie-services (optioneel)</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Andere gebruikers:</strong> Uw foto's van parkjes kunnen zichtbaar zijn voor anderen (met uw toestemming)</li>
      </ul>

      <div style={{background:"#fff3cd",borderLeft:"4px solid #ff9800",padding:15,margin:"20px 0",borderRadius:4}}>
        <strong style={{color:"#f57c00"}}>⚠️ Belangrijk:</strong> <span style={{color:"#555"}}>Alle service providers zijn onderworpen aan strenge data-verwerkingsovereenkomsten en mogen uw gegevens niet verder delen.</span>
      </div>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>5. Hoe beveiligen we uw informatie?</h2>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}>🔐 <strong>HTTPS-versleuteling:</strong> Alle communicatie is versleuteld</li>
        <li style={{margin:"8px 0",color:"#555"}}>🔐 <strong>Supabase-beveiliging:</strong> Enterprise-grade database beveiliging</li>
        <li style={{margin:"8px 0",color:"#555"}}>🔐 <strong>Row Level Security:</strong> U kunt alleen uw eigen gegevens zien</li>
        <li style={{margin:"8px 0",color:"#555"}}>🔐 <strong>Wachtwoordbeveiliging:</strong> Wachtwoorden worden gehashed opgeslagen</li>
      </ul>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>6. Uw rechten</h2>
      <p style={{color:"#555",lineHeight:1.6,marginBottom:10}}>U hebt de volgende rechten onder de AVG:</p>
      <ul style={{marginLeft:20,marginBottom:15}}>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Recht op inzage:</strong> Zien wat we over u weten</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Recht op rectificatie:</strong> Onjuiste gegevens corrigeren</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Recht op verwijdering:</strong> Uw gegevens wissen</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Recht op beperking:</strong> Verwerking van uw gegevens beperken</li>
        <li style={{margin:"8px 0",color:"#555"}}>✅ <strong>Recht op portabiliteit:</strong> Uw gegevens in ander format ontvangen</li>
      </ul>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>7. Kinderen</h2>
      <p style={{color:"#555",lineHeight:1.6,marginBottom:15}}>
        Dierenparkje is niet bedoeld voor kinderen onder de 13 jaar. We verzamelen bewust geen gegevens van kinderen. 
        Als we ontdekken dat we gegevens van een kind hebben verzameld, verwijderen we deze onmiddellijk.
      </p>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>8. Contact</h2>
      <div style={{background:"#e8f5e9",padding:20,borderRadius:8,margin:"20px 0"}}>
        <h3 style={{color:"#2d5016",fontSize:16,marginBottom:10}}>Vragen over privacy?</h3>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Contact:</strong> Via het contactformulier op onze website</p>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Website:</strong> https://dierenparkje.nl</p>
      </div>

      <h2 style={{color:"#2d5016",fontSize:20,fontWeight:700,marginTop:30,marginBottom:15,borderLeft:"4px solid #3a6b1f",paddingLeft:15}}>9. Samenvatting</h2>
      <div style={{background:"#f0f8ff",borderLeft:"4px solid #2196F3",padding:15,margin:"20px 0",borderRadius:4}}>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Wat verzamelen we?</strong> Uw naam, e-mail, locatie (optioneel) en wat u in de app doet.</p>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Waarom?</strong> Om de app te laten werken en hem beter te maken.</p>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Aan wie geven we het?</strong> Alleen onze tech-partners. Niet aan adverteerders.</p>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Hoe beveiligen we het?</strong> Met encryptie en sterke beveiligingsmaatregelen.</p>
        <p style={{color:"#555",margin:"8px 0"}}><strong>Hoe lang bewaren we het?</strong> Zolang u het nodig hebt. U kunt het op elk moment verwijderen.</p>
      </div>

      <p style={{color:"#999",fontSize:13,textAlign:"center",marginTop:40,paddingTop:20,borderTop:"1px solid #ddd"}}>
        © 2026 Dierenparkje. Alle rechten voorbehouden.
      </p>
    </div>
  </div>;
}

/* ── Nieuw park aanmaken (module-level to prevent remounting) ── */
async function lookupProvincie(plaats) {
  if(!plaats||plaats.length<2) return [];
  try {
    const res = await fetch(
      "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q="+encodeURIComponent(plaats)+"&fq=type:woonplaats&rows=5",
      {headers:{"Accept":"application/json"}}
    );
    const data = await res.json();
    const docs = data?.response?.docs||[];
    // Dedupe provincienaam
    const provs = [...new Set(docs.map(d=>d.provincienaam).filter(Boolean))];
    return provs;
  } catch { return []; }
}

/* ── Geocode helper: PDOK voor NL, Nominatim fallback (ook voor BE) ── */
async function geocodeAdres(adres, plaats, land){
  const isBE = (land||"").toLowerCase().startsWith("be") || (land||"").toLowerCase()==="belgië" || (land||"").toLowerCase()==="belgie";
  // 1. PDOK (alleen voor NL)
  if(!isBE){
    try {
      const q = [adres, plaats].filter(Boolean).join(" ");
      if(q){
        const r = await fetch(
          "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q="+encodeURIComponent(q)+"&fq=type:adres&rows=1",
          {headers:{"Accept":"application/json"}}
        );
        const d = await r.json();
        const doc = d?.response?.docs?.[0];
        if(doc?.centroide_ll){
          const m = doc.centroide_ll.match(/POINT\(([^ ]+) ([^ )]+)\)/);
          if(m) return {lat: parseFloat(m[2]), lng: parseFloat(m[1])};
        }
      }
    } catch {}
  }
  // 2. Nominatim fallback (werkt voor NL én BE)
  try {
    const landNaam = isBE ? "België" : "Nederland";
    const adresZonderPc = (adres||"").replace(/\b\d{4}\s*[A-Z]{2}\b/g,"").trim();
    const queries = [
      [adres, plaats, landNaam].filter(Boolean).join(", "),
      [adresZonderPc, plaats, landNaam].filter(Boolean).join(", "),
      plaats ? plaats+", "+landNaam : null,
    ].filter(Boolean).filter((q,i,a)=>a.indexOf(q)===i);
    const cc = isBE ? "be" : "nl";
    for(const query of queries){
      const res = await fetch(
        "https://nominatim.openstreetmap.org/search?q="+encodeURIComponent(query)+"&format=json&limit=1&countrycodes="+cc,
        {headers:{"Accept-Language":"nl","User-Agent":"Dierenparkje/1.0"}}
      );
      const data = await res.json();
      if(data.length) return {lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon)};
      await new Promise(r=>setTimeout(r,300));
    }
  } catch {}
  return null;
}

function NieuwParkForm({setParken, setActieveIds, setModal}){
    const provincies=["Drenthe","Flevoland","Friesland","Gelderland","Groningen","Limburg","Noord-Brabant","Noord-Holland","Overijssel","Utrecht","Zeeland","Zuid-Holland"];
    const [f,setF]=useState({naam:"",roepnaam:"",plaats:"",provincie:"",adres:"",lat:"52.0",lng:"5.1",beschrijving:"",website:"",instagram:""});
    const [saving,setSaving]=useState(false);
    const [provLoading,setProvLoading]=useState(false);
    const [provOpties,setProvOpties]=useState([]);
    const u=(k,v)=>setF(p=>({...p,[k]:v}));

    const onPlaatsBlur=async()=>{
      if(!f.plaats.trim()||f.provincie) return;
      setProvLoading(true);
      const provs=await lookupProvincie(f.plaats);
      setProvLoading(false);
      if(provs.length===1){ setF(p=>({...p,provincie:provs[0]})); toast.ok("Provincie automatisch ingevuld: "+provs[0]); }
      else if(provs.length>1){ setProvOpties(provs); }
      else toast.warn("Provincie niet automatisch gevonden — kies handmatig");
    };

    const save=async()=>{
      if(!f.naam.trim()||!f.roepnaam.trim()||!f.plaats.trim()){toast.warn("Naam, roepnaam en plaats zijn verplicht");return;}
      if(!f.provincie){toast.warn("Vul eerst de provincie in");return;}
      setSaving(true);
      const id=f.naam.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
      const newPark={id,naam:f.naam,roepnaam:f.roepnaam,plaats:f.plaats,provincie:f.provincie,adres:f.adres,lat:parseFloat(f.lat)||52.0,lng:parseFloat(f.lng)||5.1,beschrijving:f.beschrijving,website:f.website||null,instagram:f.instagram.replace(/^@/,"")||null,actief:true};
      const result=await db.addPark(newPark);
      if(result){
        setParken(prev=>[...prev.filter(p=>p.id!==id),result]);
        setActieveIds(prev=>[...prev,id]);
        toast.ok(f.naam+" aangemaakt! ✅");
      }
      setSaving(false);
      setModal(null);
    };
    return <Modal title="Nieuw park aanmaken" onClose={()=>setModal(null)}>
      <div className="fg"><label className="fl">Naam van het park *</label><input className="fi" value={f.naam} onChange={e=>u("naam",e.target.value)} placeholder="Bijv. Kinderboerderij De Hoeve"/></div>
      <div className="fg"><label className="fl">Roepnaam park <span style={{fontWeight:400,color:"var(--txtl)"}}>(voor in de app)</span> *</label><input className="fi" value={f.roepnaam} onChange={e=>u("roepnaam",e.target.value)} placeholder="Bijv. De Hoeve"/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">Plaats *</label><input className="fi" value={f.plaats} onChange={e=>u("plaats",e.target.value)} onBlur={onPlaatsBlur} placeholder="Bijv. Utrecht"/></div>
        <div className="fg">
          <label className="fl">Provincie {provLoading&&<span style={{fontWeight:400,color:"var(--txtl)"}}>⏳</span>}</label>
          {provOpties.length>1
            ? <select className="fi" value={f.provincie} onChange={e=>{u("provincie",e.target.value);setProvOpties([]);}}>
                <option value="">— kies provincie —</option>
                {provOpties.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            : <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input className="fi" value={f.provincie} readOnly={!!f.provincie} onChange={e=>u("provincie",e.target.value)}
                  style={{background:f.provincie?"#f0f7eb":"#fff",flex:1}} placeholder="Wordt automatisch ingevuld"/>
                {f.provincie&&<button onClick={()=>u("provincie","")} style={{padding:"6px 10px",borderRadius:8,border:"1px solid var(--brd)",background:"#fff",cursor:"pointer",fontSize:12,whiteSpace:"nowrap"}}>✏️ Wijzig</button>}
              </div>
          }
        </div>
      </div>
      <div className="fg"><label className="fl">Adres</label><input className="fi" value={f.adres} onChange={e=>u("adres",e.target.value)} placeholder="Bijv. Hoofdstraat 1"/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">Breedtegraad (lat)</label><input className="fi" type="number" step="0.0001" value={f.lat} onChange={e=>u("lat",e.target.value)}/></div>
        <div className="fg"><label className="fl">Lengtegraad (lng)</label><input className="fi" type="number" step="0.0001" value={f.lng} onChange={e=>u("lng",e.target.value)}/></div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">🌐 Website</label><input className="fi" value={f.website} onChange={e=>u("website",e.target.value)} placeholder="https://..."/></div>
        <div className="fg"><label className="fl">📸 Instagram</label><input className="fi" value={f.instagram} onChange={e=>u("instagram",e.target.value)} placeholder="@gebruikersnaam"/></div>
      </div>
      <div className="fg"><label className="fl">Beschrijving</label><textarea className="fi" rows={2} value={f.beschrijving} onChange={e=>u("beschrijving",e.target.value)}/></div>
      <PawBtn onClick={save} loading={saving}>Park aanmaken</PawBtn>
    </Modal>;
}

/* ═══════════════════════════════════════════════════════════
   RASSEN BEHEER (admin only)
   ═══════════════════════════════════════════════════════════ */
function RassenBeheer(){
  const [soorten,setSoorten]=useState([]);
  const [rassen,setRassen]=useState([]);
  const [selectedSoortId,setSelectedSoortId]=useState("");
  const [nieuwRas,setNieuwRas]=useState("");
  const [loading,setLoading]=useState(false);

  useEffect(()=>{
    const load=async()=>{
      const [{data:sd},{data:rd}]=await Promise.all([
        sb.from("soorten").select("*").order("naam"),
        sb.from("rassen").select("*").order("ras_naam"),
      ]);
      setSoorten(sd||[]);
      setRassen(rd||[]);
    };
    load();
  },[]);

  const rassenVoorSoort=rassen.filter(r=>r.soort_id===Number(selectedSoortId));

  const addRas=async()=>{
    if(!nieuwRas.trim()||!selectedSoortId) return;
    setLoading(true);
    const {data,error}=await sb.from("rassen").insert({ras_naam:nieuwRas.trim(),soort_id:Number(selectedSoortId)}).select().single();
    if(!error&&data){ setRassen(prev=>[...prev,data].sort((a,b)=>a.ras_naam.localeCompare(b.ras_naam))); setNieuwRas(""); }
    else toast.err("Fout bij toevoegen ras");
    setLoading(false);
  };

  const deleteRas=async(id)=>{
    toast.confirm("Ras verwijderen?",async()=>{
      const {error}=await sb.from("rassen").delete().eq("id",id);
      if(!error) setRassen(prev=>prev.filter(r=>r.id!==id));
      else toast.err("Fout bij verwijderen");
    });
  };

  const selectedSoort=soorten.find(s=>s.id===Number(selectedSoortId));

  return <div style={{padding:"0 16px 100px"}}>
    <div className="sh"><span className="st">🐾 Rassen beheer</span></div>
    <p style={{fontSize:12,color:"var(--txtl)",marginBottom:12}}>Rassen zijn gedeeld over alle parken. Kies een soort om de bijbehorende rassen te beheren.</p>

    <div className="fg" style={{marginBottom:16}}>
      <label className="fl">Soort</label>
      <select className="fi" value={selectedSoortId} onChange={e=>setSelectedSoortId(e.target.value)}>
        <option value="">— Kies een soort —</option>
        {soorten.map(s=><option key={s.id} value={s.id}>{s.icon} {s.naam}</option>)}
      </select>
    </div>

    {selectedSoortId&&<>
      <div className="sh"><span className="st">{selectedSoort?.icon} {selectedSoort?.naam} — {rassenVoorSoort.length} rassen</span></div>

      {rassenVoorSoort.map(r=><div key={r.id} className="card" style={{padding:"10px 12px",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:14,fontWeight:500}}>{r.ras_naam}</span>
        <button onClick={()=>deleteRas(r.id)} style={{width:28,height:28,borderRadius:7,border:"1px solid var(--brd)",background:"#fff",cursor:"pointer",color:"var(--red)",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
      </div>)}

      {rassenVoorSoort.length===0&&<div className="card"><EmptyState icon="🏷️" title="Nog geen rassen" sub={`Voeg het eerste ras toe voor ${selectedSoort?.naam}.`}/></div>}

      <div style={{display:"flex",gap:8,marginTop:12}}>
        <input className="fi" style={{flex:1}} value={nieuwRas} onChange={e=>setNieuwRas(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&addRas()}
          placeholder={`Bijv. Brahma, Sussex, …`}/>
        <button className="fbtn" style={{flexShrink:0}} onClick={addRas} disabled={loading||!nieuwRas.trim()}>
          {loading?"…":"+ Toevoegen"}
        </button>
      </div>
    </>}
  </div>;
}

function AdminPanel({onLogout, onEnterPark, onWijzigWachtwoord, parken, setParken, actieveIds, setActieveIds, loggedInUser, modal, setModal, editParkId, setEditParkId}){
  const [tab,setTab]=useState("parken");
  const [selectedParkId,setSelectedParkId]=useState(null);
  const [parkGebruikers,setParkGebruikers]=useState({});
  const [allUsers,setAllUsers]=useState([]);
  const [adminMsgText,setAdminMsgText]=useState("");
  const [selectedUserId,setSelectedUserId]=useState(null);
  const [parkSearch,setParkSearch]=useState("");
  const [geocodingId,setGeocodingId]=useState(null);

  const activeParks=parken.filter(p=>actieveIds.includes(p.id));
  const filteredParks=parkSearch.trim()
    ? parken.filter(p=>{const q=parkSearch.toLowerCase();return p.naam.toLowerCase().includes(q)||(p.roepnaam||"").toLowerCase().includes(q)||(p.plaats||"").toLowerCase().includes(q);})
    : parken;

  const geocodePark = async (park) => {
    if(!park.adres && !park.plaats){ toast.warn("Geen adres ingevuld voor dit park"); return; }
    setGeocodingId(park.id);
    try {
      let lat = null, lng = null;

      // 1. Probeer PDOK eerst (officiële BAG, 100% NL dekking)
      const pdokQuery = [park.adres, park.plaats].filter(Boolean).join(" ");
      try {
        const pr = await fetch(
          "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q="+encodeURIComponent(pdokQuery)+"&fq=type:adres&rows=1",
          { headers: {"Accept":"application/json"} }
        );
        const pd = await pr.json();
        const doc = pd?.response?.docs?.[0];
        if(doc?.centroide_ll){
          const m = doc.centroide_ll.match(/POINT\(([^ ]+) ([^ )]+)\)/);
          if(m){ lng = parseFloat(m[1]); lat = parseFloat(m[2]); }
        }
      } catch {}

      // 2. Fallback: Nominatim
      if(!lat){
        const adresZonderPostcode = (park.adres||"").replace(/\b\d{4}\s*[A-Z]{2}\b/g,"").trim();
        const queries = [
          [park.adres, park.plaats, "Nederland"].filter(Boolean).join(", "),
          [adresZonderPostcode, park.plaats, "Nederland"].filter(Boolean).join(", "),
          park.plaats ? park.plaats+", Nederland" : null,
        ].filter(Boolean).filter((q,i,arr)=>arr.indexOf(q)===i);

        for(const query of queries){
          const res = await fetch(
            "https://nominatim.openstreetmap.org/search?q="+encodeURIComponent(query)+"&format=json&limit=1&countrycodes=nl",
            { headers: {"Accept-Language":"nl","User-Agent":"Dierenparkje/1.0"} }
          );
          const data = await res.json();
          if(data.length){ lat = parseFloat(data[0].lat); lng = parseFloat(data[0].lon); break; }
          await new Promise(r => setTimeout(r, 300));
        }
      }

      if(!lat){ toast.warn("Geen resultaat gevonden — controleer het adres"); return; }
      await db.updatePark(park.id, {lat, lng});
      setParken(prev => prev.map(p => p.id===park.id ? {...p, lat, lng} : p));
      toast.ok("Coordinaten bijgewerkt: "+lat.toFixed(5)+", "+lng.toFixed(5));
    } catch(err){
      toast.err("Geocoderen mislukt: "+err.message);
    } finally {
      setGeocodingId(null);
    }
  };

  // Load all users from all parks
  useEffect(()=>{
    (async()=>{
      const allUsersData=[];
      for(const park of parken){
        const parkUsers=await db.getGebruikers(park.id);
        allUsersData.push(...parkUsers.map(u=>({...u,park_id:park.id,park_naam:park.naam,roepnaam:u.roepnaam||u.naam})));
      }
      setAllUsers(allUsersData);
    })();
  },[parken]);

  // Load gebruikers for a park
  const loadGebruikers=async(parkId)=>{
    const data=await db.getGebruikers(parkId);
    setParkGebruikers(prev=>({...prev,[parkId]:data.map(u=>({...u,name:u.naam}))}));
  };

  // NieuwParkForm moved to module level


  // ── Park bewerken ──
  function BewerkParkForm({park}){
    const provincies=["Drenthe","Flevoland","Friesland","Gelderland","Groningen","Limburg","Noord-Brabant","Noord-Holland","Overijssel","Utrecht","Zeeland","Zuid-Holland"];
    const [f,setF]=useState({
      naam:park.naam||"",roepnaam:park.roepnaam||"",plaats:park.plaats||"",
      provincie:park.provincie||"",adres:park.adres||"",
      lat:String(park.lat||52.0),lng:String(park.lng||5.1),
      beschrijving:park.beschrijving||"",openingstijden:park.openingstijden||"",
      telefoon:park.telefoon||"",website:park.website||"",instagram:park.instagram||"",actief:park.actief!==false,
    });
    const [saving,setSaving]=useState(false);
    const [geocoding,setGeocoding]=useState(false);
    const [adresZoek,setAdresZoek]=useState("");
    const [adresSuggesties,setAdresSuggesties]=useState([]);
    const [zoekLoading,setZoekLoading]=useState(false);
    const [provLoading,setProvLoading]=useState(false);
    const [provOpties,setProvOpties]=useState([]);
    const u=(k,v)=>setF(p=>({...p,[k]:v}));

    const onPlaatsBlur=async()=>{
      if(!f.plaats.trim()||f.provincie) return;
      setProvLoading(true);
      const provs=await lookupProvincie(f.plaats);
      setProvLoading(false);
      if(provs.length===1){ setF(p=>({...p,provincie:provs[0]})); toast.ok("Provincie automatisch ingevuld: "+provs[0]); }
      else if(provs.length>1){ setProvOpties(provs); }
      else toast.warn("Provincie niet automatisch gevonden — kies handmatig");
    };

    const zoekAdres=async(q)=>{
      setAdresZoek(q);
      if(q.length < 4){ setAdresSuggesties([]); return; }
      setZoekLoading(true);
      try {
        // Run PDOK (BAG addresses) and Nominatim (POIs/names) in parallel
        const [pdokRes, nominatimRes] = await Promise.allSettled([
          fetch("https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?q="+encodeURIComponent(q)+"&fq=type:adres&rows=4", {headers:{"Accept":"application/json"}}),
          fetch("https://nominatim.openstreetmap.org/search?q="+encodeURIComponent(q+", Nederland")+"&format=json&limit=3&countrycodes=nl&addressdetails=1", {headers:{"Accept-Language":"nl","User-Agent":"Dierenparkje/1.0"}}),
        ]);
        const pdokHits = pdokRes.status==="fulfilled" ? (await pdokRes.value.json())?.response?.docs||[] : [];
        const nominatimHits = nominatimRes.status==="fulfilled" ? await nominatimRes.value.json() : [];

        // PDOK hits get type "pdok", Nominatim get type "nominatim"
        const combined = [
          ...pdokHits.map(h=>({...h, _source:"pdok", _label: h.weergavenaam})),
          ...nominatimHits.filter(h=>!pdokHits.some(p=>p.weergavenaam?.toLowerCase().includes(h.display_name?.split(",")[0]?.toLowerCase())))
                          .map(h=>({...h, _source:"nominatim", _label: h.display_name})),
        ];
        setAdresSuggesties(combined);
      } catch { setAdresSuggesties([]); }
      setZoekLoading(false);
    };

    const kiesAdres=async(item)=>{
      try {
        if(item._source==="pdok"){
          // PDOK lookup for precise coordinates
          const res = await fetch(
            "https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup?id="+encodeURIComponent(item.id),
            { headers: {"Accept":"application/json"} }
          );
          const data = await res.json();
          const doc = data?.response?.docs?.[0];
          if(!doc){ toast.err("Kon adresdetails niet ophalen"); return; }
          const match = (doc.centroide_ll||"").match(/POINT\(([^ ]+) ([^ )]+)\)/);
          const lng = match ? parseFloat(match[1]) : null;
          const lat = match ? parseFloat(match[2]) : null;
          const straat = [doc.straatnaam, doc.huisnummer, doc.huisletter, doc.huisnummertoevoeging].filter(Boolean).join(" ");
          setF(p=>({...p,
            adres: [straat, doc.postcode].filter(Boolean).join(", ") || doc.weergavenaam,
            plaats: doc.woonplaatsnaam || p.plaats,
            lat: lat ? String(lat.toFixed(6)) : p.lat,
            lng: lng ? String(lng.toFixed(6)) : p.lng,
          }));
        } else {
          // Nominatim
          const addr = item.address || {};
          const straat = [addr.road||addr.pedestrian||addr.footway||addr.path, addr.house_number].filter(Boolean).join(" ");
          const postcode = addr.postcode||"";
          const stad = addr.city||addr.town||addr.village||addr.municipality||"";
          setF(p=>({...p,
            adres: [straat, postcode].filter(Boolean).join(", ") || item.display_name.split(",")[0].trim(),
            plaats: stad || p.plaats,
            lat: String(parseFloat(item.lat).toFixed(6)),
            lng: String(parseFloat(item.lon).toFixed(6)),
          }));
        }
        toast.ok("📍 Adres en coördinaten ingevuld!");
      } catch(e) { toast.err("Fout: "+e.message); }
      setAdresZoek("");
      setAdresSuggesties([]);
    };

    const save=async()=>{
      if(!f.naam.trim()||!f.roepnaam.trim()||!f.plaats.trim()){toast.warn("Naam, roepnaam en plaats zijn verplicht");return;}
      setSaving(true);
      const updated=await db.updatePark(park.id,{
        naam:f.naam,roepnaam:f.roepnaam,plaats:f.plaats,provincie:f.provincie,
        adres:f.adres,lat:parseFloat(f.lat)||52.0,lng:parseFloat(f.lng)||5.1,
        beschrijving:f.beschrijving,openingstijden:f.openingstijden,
        telefoon:f.telefoon,website:f.website||null,instagram:f.instagram.replace(/^@/,"")||null,actief:f.actief,
      });
      if(updated){
        setParken(prev=>prev.map(p=>p.id===park.id?{...p,...updated}:p));
        if(!f.actief) setActieveIds(prev=>prev.filter(id=>id!==park.id));
        else if(!actieveIds.includes(park.id)) setActieveIds(prev=>[...prev,park.id]);
        toast.ok(`${f.naam} bijgewerkt ✅`);
      } else { toast.err("Opslaan mislukt"); }
      setSaving(false);
      setModal(null); setEditParkId(null);
    };
    return <Modal title={`✏️ ${park.naam} bewerken`} onClose={()=>{setModal(null);setEditParkId(null);}}>
      <div className="fg"><label className="fl">Naam *</label><input className="fi" value={f.naam} onChange={e=>u("naam",e.target.value)}/></div>
      <div className="fg"><label className="fl">Roepnaam * <span style={{fontWeight:400,color:"var(--txtl)"}}>(in de app)</span></label><input className="fi" value={f.roepnaam} onChange={e=>u("roepnaam",e.target.value)}/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">Plaats *</label><input className="fi" value={f.plaats} onChange={e=>u("plaats",e.target.value)} onBlur={onPlaatsBlur}/></div>
        <div className="fg">
          <label className="fl">Provincie {provLoading&&<span style={{fontWeight:400,color:"var(--txtl)"}}>⏳</span>}</label>
          {provOpties.length>1
            ? <select className="fi" value={f.provincie} onChange={e=>{u("provincie",e.target.value);setProvOpties([]);}}>
                <option value="">— kies provincie —</option>
                {provOpties.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            : <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input className="fi" value={f.provincie} readOnly={!!f.provincie} onChange={e=>u("provincie",e.target.value)}
                  style={{background:f.provincie?"#f0f7eb":"#fff",flex:1}} placeholder="Wordt automatisch ingevuld"/>
                {f.provincie&&<button onClick={()=>u("provincie","")} style={{padding:"6px 10px",borderRadius:8,border:"1px solid var(--brd)",background:"#fff",cursor:"pointer",fontSize:12,whiteSpace:"nowrap"}}>✏️ Wijzig</button>}
              </div>
          }
        </div>
      </div>

      {/* Adres zoeken */}
      <div className="fg">
        <label className="fl">🔍 Adres zoeken <span style={{fontWeight:400,color:"var(--txtl)"}}>(vult adres + coördinaten automatisch in)</span></label>
        <div style={{position:"relative"}}>
          <input className="fi" value={adresZoek} onChange={e=>zoekAdres(e.target.value)}
            placeholder="Typ straat + plaatsnaam, bijv: Oude Bovendijk 220 Rotterdam"/>
          {zoekLoading&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--txtl)"}}>⏳</div>}
          {adresSuggesties.length>0&&<div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--brd)",borderRadius:10,boxShadow:"0 4px 16px rgba(0,0,0,.12)",zIndex:999,maxHeight:200,overflowY:"auto"}}>
            {adresSuggesties.map((s,i)=><div key={i} onClick={()=>kiesAdres(s)}
              style={{padding:"10px 14px",cursor:"pointer",fontSize:12,borderBottom:i<adresSuggesties.length-1?"1px solid var(--brd)":"none",lineHeight:1.4}}
              onMouseEnter={e=>e.currentTarget.style.background="#f0f7eb"}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              {s._source==="pdok"?"📍":"🔍"} {s._label}
            </div>)}
          </div>}
        </div>
      </div>

      <div className="fg"><label className="fl">Adres</label><input className="fi" value={f.adres} onChange={e=>u("adres",e.target.value)}/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">Lat</label><input className="fi" type="number" step="0.0001" value={f.lat} onChange={e=>u("lat",e.target.value)}/></div>
        <div className="fg"><label className="fl">Lng</label><input className="fi" type="number" step="0.0001" value={f.lng} onChange={e=>u("lng",e.target.value)}/></div>
      </div>
      <div className="fg"><label className="fl">Openingstijden</label><input className="fi" value={f.openingstijden} onChange={e=>u("openingstijden",e.target.value)} placeholder="Di–zo 10:00–17:00"/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div className="fg"><label className="fl">Telefoon</label><input className="fi" value={f.telefoon} onChange={e=>u("telefoon",e.target.value)}/></div>
        <div className="fg"><label className="fl">🌐 Website</label><input className="fi" value={f.website} onChange={e=>u("website",e.target.value)} placeholder="https://..."/></div>
      </div>
      <div className="fg"><label className="fl">📸 Instagram</label><input className="fi" value={f.instagram} onChange={e=>u("instagram",e.target.value)} placeholder="@gebruikersnaam"/></div>
      <div className="fg"><label className="fl">Beschrijving</label><textarea className="fi" rows={3} value={f.beschrijving} onChange={e=>u("beschrijving",e.target.value)}/></div>
      <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--gp)",borderRadius:10,marginBottom:8,cursor:"pointer"}}>
        <input type="checkbox" checked={f.actief} onChange={e=>u("actief",e.target.checked)} style={{width:18,height:18}}/>
        <div><div style={{fontWeight:600,fontSize:14}}>Park actief</div><div style={{fontSize:11,color:"var(--txtl)"}}>Inactieve parken zijn niet zichtbaar voor bezoekers</div></div>
      </label>
      <div style={{fontSize:11,color:"var(--txtl)",padding:"6px 10px",background:"#f5f5f5",borderRadius:8,marginBottom:10}}>🔒 Park ID: <code>{park.id}</code></div>
      <PawBtn onClick={save} loading={saving}>💾 Wijzigingen opslaan</PawBtn>
    </Modal>;
  }

  // ── Gebruikers van een park ──
  function ParkGebruikers({parkId}){
    const park=parken.find(p=>p.id===parkId);
    const users=parkGebruikers[parkId]||[];
    const [showAdd,setShowAdd]=useState(false);
    const [newUser,setNewUser]=useState({name:"",email:"",role:"vrijwilliger"});
    const u=(k,v)=>setNewUser(p=>({...p,[k]:v}));
    const addUser=async()=>{
      if(!newUser.name.trim()) return;
      console.log("🔍 addUser DEBUG - parkId value:", parkId);
      console.log("🔍 addUser DEBUG - newUser:", newUser);
      const nw=await db.addGebruiker({naam:newUser.name,email:newUser.email,role:newUser.role,actief:true,park_id:parkId,is_bezoeker:false});
      console.log("🔍 addUser DEBUG - returned record:", nw);
      if(nw) {
        setParkGebruikers(prev=>({...prev,[parkId]:[...users,{...nw,name:nw.naam}]}));
        // Note: User is already created in database by db.addGebruiker with correct park_id
        // Invite sending would need to be done separately (TODO: implement email invite for park staff)
      }
      setNewUser({name:"",email:"",role:"vrijwilliger"});setShowAdd(false);
    };
    const toggleActive=async(uid)=>{
      const usr=users.find(u=>u.id===uid);
      if(!usr) return;
      await db.updateGebruiker(uid,{actief:!usr.actief});
      setParkGebruikers(prev=>({...prev,[parkId]:users.map(u=>u.id===uid?{...u,actief:!u.actief}:u)}));
    };
    const delUser=async(uid)=>{
      const usr=users.find(u=>u.id===uid);
      const token=await db.getToken();
      if(token){
        await fetch(`${SUPABASE_URL}/functions/v1/invite-user`,{
          method:"DELETE",
          headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
          body:JSON.stringify({gebruiker_id:uid, auth_id:usr?.auth_id||null})
        });
      } else {
        await db.deleteGebruiker(uid);
      }
      setParkGebruikers(prev=>({...prev,[parkId]:users.filter(u=>u.id!==uid)}));
    };
    const setRole=async(uid,role)=>{
      await db.updateGebruiker(uid,{type:role});
      setParkGebruikers(prev=>({...prev,[parkId]:users.map(u=>u.id===uid?{...u,role}:u)}));
    };
    return <Modal title={`Gebruikers: ${park?.naam}`} onClose={()=>{setModal(null);setSelectedParkId(null);}}>
      {users.map(usr=><div key={usr.id} className="uc" style={{opacity:usr.actief?1:.5}}>
        <div className="ua" style={{background:rCol(usr.role)}}>{(usr.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
        <div className="uinf">
          <div className="unm">{usr.name}{!usr.actief&&" (inactief)"}</div>
          <div className="uem">{usr.email}</div>
          <select value={usr.role} onChange={e=>setRole(usr.id,e.target.value)} style={{fontSize:16,padding:"2px 6px",borderRadius:7,border:"1px solid var(--brd)",fontFamily:"inherit",background:rCol(usr.role)+"12",color:rCol(usr.role),fontWeight:600,marginTop:3}}>
            {Object.entries(PERMISSIONS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="uab">
          <button className="ubtn" onClick={()=>toggleActive(usr.id)}>{usr.actief?IC.chk:IC.X}</button>
          <button className="ubtn" onClick={()=>toast.confirm(`${usr.name||usr.naam||"Gebruiker"} verwijderen?`,()=>delUser(usr.id))}>{IC.trash}</button>
        </div>
      </div>)}
      {users.length===0&&<EmptyState icon="🔍" title="Geen gebruikers gevonden" sub="Pas de zoekopdracht aan."/>}
      {!showAdd
        ?<button className="fbtn" style={{background:"var(--cd)",color:"var(--g)",marginTop:8}} onClick={()=>setShowAdd(true)}>{IC.plus} Gebruiker toevoegen</button>
        :<div style={{marginTop:12,padding:12,border:"1px solid var(--brd)",borderRadius:14}}>
          <div className="fg"><label className="fl">Naam</label><input className="fi" value={newUser.name} onChange={e=>u("name",e.target.value)}/></div>
          <div className="fg"><label className="fl">E-mail</label><input className="fi" value={newUser.email} onChange={e=>u("email",e.target.value)}/></div>
          <div className="fg"><label className="fl">Rol</label><select className="fi" value={newUser.role} onChange={e=>u("role",e.target.value)}>{Object.entries(PERMISSIONS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select></div>
          <div style={{display:"flex",gap:8}}><button className="fbtn" onClick={addUser}>Toevoegen</button><button className="fbtn" style={{background:"var(--cd)",color:"var(--txtl)"}} onClick={()=>setShowAdd(false)}>Annuleren</button></div>
        </div>}
    </Modal>;
  }

  return <div style={{minHeight:"100vh",maxWidth:480,margin:"0 auto",background:"var(--cream)"}}>
    {/* Header */}
    <div style={{background:"linear-gradient(135deg,#1a1a2e,#16213e)",color:"#fff",padding:"14px 20px",position:"sticky",top:0,zIndex:100}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:8,background:"rgba(255,255,255,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>⚙️</div>
          <div>
            <div style={{fontFamily:"'Fraunces',serif",fontSize:16,fontWeight:700}}>{loggedInUser?.naam||"App Admin"}</div>
            <div style={{fontSize:11,opacity:.6}}>Dierenparkje.nl Platform</div>
          </div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <button className="hbtn" style={{fontSize:12,padding:"5px 10px"}} onClick={onWijzigWachtwoord} title="Wachtwoord wijzigen">🔑</button>
          <button className="hbtn" onClick={onLogout}>{IC.logout} Uitloggen</button>
        </div>
      </div>
    </div>

    {/* Stats */}
    <div style={{padding:"14px 16px 0"}}>
      <div className="ds">
        <div className="sc"><div className="sn">{activeParks.length}</div><div className="sl">Actieve parken</div></div>
        <div className="sc"><div className="sn">{parken.length}</div><div className="sl">In register</div></div>
      </div>
    </div>

    {/* Tabs */}
    <div style={{padding:"0 16px"}}>
      <div className="fr">
        {["parken","berichten"].map(t=><button key={t} className={`fc ${tab===t?"a":""}`} onClick={()=>setTab(t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
      </div>
    </div>

    {/* Parken tab */}
    {tab==="parken"&&<div style={{padding:"0 16px 100px"}}>
      <div className="sh"><span className="st">Alle parken ({parken.length}) · {activeParks.length} actief</span></div>
      <div style={{position:"relative",marginBottom:12}}>
        <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16,pointerEvents:"none"}}>🔍</span>
        <input className="fi" style={{paddingLeft:36,fontSize:14}} placeholder="Zoek op parknaam..." value={parkSearch} onChange={e=>setParkSearch(e.target.value)}/>
        {parkSearch&&<button onClick={()=>setParkSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--txtl)"}}>×</button>}
      </div>
      {filteredParks.length===0&&<div className="card" style={{textAlign:"center",padding:24,color:"var(--txtl)"}}>
        <div style={{fontSize:32,marginBottom:8}}>🔍</div>
        <p style={{fontSize:14}}>Geen parken gevonden voor "<strong>{parkSearch}</strong>"</p>
      </div>}
      {filteredParks.map(park=>{
        const isActive=actieveIds.includes(park.id);
        return <div key={park.id} className="card" style={{marginBottom:10,opacity:isActive?1:0.7,borderLeft:isActive?"none":"3px solid #ccc"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
            <div style={{width:44,height:44,borderRadius:12,background:isActive?"var(--gp)":"#eee",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>🐾</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <div style={{fontWeight:700,fontSize:15,color:isActive?"var(--g)":"var(--txtl)"}}>{park.naam}</div>
                {!isActive&&<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:20,background:"#e0e0e0",color:"#888"}}>Inactief</span>}
              </div>
              <div style={{fontSize:12,color:"var(--txtl)"}}>{park.plaats} · {park.provincie}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>onEnterPark(park)} style={{flex:2,padding:"9px",borderRadius:10,border:"none",background:isActive?"var(--g)":"#888",color:"#fff",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>{IC.login} Inloggen als Admin</button>
            <button onClick={()=>{setSelectedParkId(park.id);loadGebruikers(park.id);setModal("gebruikers");}} style={{flex:1,padding:"9px",borderRadius:10,border:"1px solid var(--brd)",background:"#fff",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>{IC.team} Gebruikers</button>
            <button onClick={()=>{setEditParkId(park.id);setModal("bewerkPark");}} style={{padding:"9px 12px",borderRadius:10,border:"1px solid var(--brd)",background:"#fff",fontFamily:"inherit",fontSize:15,cursor:"pointer"}} title="Park bewerken">✏️</button>
            <button onClick={()=>geocodePark(park)} disabled={geocodingId===park.id} style={{padding:"9px 12px",borderRadius:10,border:"1px solid var(--brd)",background:geocodingId===park.id?"#f5f5f5":"#fff",fontFamily:"inherit",fontSize:15,cursor:geocodingId===park.id?"not-allowed":"pointer",opacity:geocodingId===park.id?0.5:1}} title={"Coordinaten herberekenen op basis van: "+(park.adres||"geen adres")}>
              {geocodingId===park.id ? "⏳" : "📍"}
            </button>
          </div>
        </div>;
      })}
      <button className="fab" onClick={()=>setModal("nieuwPark")}>{IC.plus}</button>
    </div>}

    {/* Berichten tab */}
    {tab==="berichten"&&<div style={{padding:"0 16px 100px"}}>
      <div className="sh"><span className="st">📧 Berichten sturen naar gebruikers</span></div>
      <div className="card" style={{marginBottom:12,borderLeft:"3px solid var(--bl)"}}>
        <div style={{fontSize:13,color:"var(--txtl)",marginBottom:12}}>Stuur systeemberichten, waarschuwingen en beleid updates naar alle teamleden.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--txtl)"}}>📝 Kies gebruiker</label>
            <select value={selectedUserId||""} onChange={e=>setSelectedUserId(Number(e.target.value))} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--brd)",fontFamily:"inherit",fontSize:16,marginTop:4}}>
              <option value="">— Iedereen (broadcast) —</option>
              {allUsers.map(u=><option key={u.id} value={u.id}>{u.roepnaam}-{u.park_naam}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:"var(--txtl)"}}>🏷️ Type bericht</label>
            <select style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--brd)",fontFamily:"inherit",fontSize:16,marginTop:4}}>
              <option value="info">ℹ️ Info</option>
              <option value="warning">⚠️ Waarschuwing</option>
              <option value="policy">📋 Beleid</option>
              <option value="urgent">🔴 Dringend</option>
            </select>
          </div>
        </div>
        <label style={{fontSize:12,fontWeight:600,color:"var(--txtl)"}}>✉️ Bericht</label>
        <textarea value={adminMsgText} onChange={e=>setAdminMsgText(e.target.value)} placeholder="Typ je bericht hier..." style={{width:"100%",padding:"10px",borderRadius:8,border:"1px solid var(--brd)",fontFamily:"inherit",fontSize:16,resize:"vertical",minHeight:80,marginTop:4,boxSizing:"border-box"}}/>
        <button onClick={()=>{console.log("Send to:",selectedUserId||"all","-",adminMsgText);setAdminMsgText("");}} style={{marginTop:10,padding:"10px 16px",borderRadius:8,border:"none",background:"var(--g)",color:"#fff",fontFamily:"inherit",fontSize:16,fontWeight:600,cursor:"pointer"}}>📤 Versturen</button>
      </div>
      
      <div className="sh"><span className="st">Alle gebruikers ({allUsers.length})</span></div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {allUsers.map(u=><div key={u.id} className="card" style={{marginBottom:0,padding:"10px",background:u.actief?"#fff":"#f5f5f5",opacity:u.actief?1:0.7}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontWeight:600,fontSize:13}}>{u.roepnaam}-{u.park_naam}</div>
              <div style={{fontSize:11,color:"var(--txtl)"}}>{u.name} ({u.role})</div>
            </div>
            <div style={{fontSize:11,color:"var(--txtl)"}}>{u.actief?"Actief":"Inactief"}</div>
          </div>
        </div>)}
      </div>
    </div>}

    {tab==="rassen"&&<RassenBeheer/>}

    {/* Modals */}
    {modal==="nieuwPark"&&<NieuwParkForm setParken={setParken} setActieveIds={setActieveIds} setModal={setModal}/>}
    {modal==="bewerkPark"&&editParkId&&<BewerkParkForm park={parken.find(p=>p.id===editParkId)}/>}
    {modal==="gebruikers"&&selectedParkId&&<ParkGebruikers parkId={selectedParkId}/>}
  </div>;
}

/* ═══════════════════════════════════════════════════════════
   WACHTWOORD WIJZIGEN MODAL
   ═══════════════════════════════════════════════════════════ */
function WijzigWachtwoordModal({onClose, isFirstLogin=false, onComplete}){
  const [pw,setPw]=useState("");
  const [bevestig,setBevestig]=useState("");
  const [err,setErr]=useState("");
  const [ok,setOk]=useState(false);
  const [loading,setLoading]=useState(false);

  const save=async()=>{
    if(pw.length<6){setErr("Minimaal 6 tekens.");return;}
    if(pw!==bevestig){setErr("Wachtwoorden komen niet overeen.");return;}
    setLoading(true);setErr("");
    const {error}=await db.updatePassword(pw);
    if(error){setErr("Opslaan mislukt: "+error.message);setLoading(false);return;}
    // Eerste login afgerond — vlag resetten
    if(isFirstLogin){
      const {data:{session}} = await sb.auth.getSession();
      if(session?.user?.id){
        await sb.from("gebruikers").update({eerste_login:false}).eq("auth_id",session.user.id);
      }
    }
    setOk(true);setLoading(false);
  };

  const handleVerder=()=>{
    onClose();
    onComplete?.();
  };

  return <Modal title={isFirstLogin?"Welkom! Stel je wachtwoord in":"Wachtwoord wijzigen"} onClose={isFirstLogin?null:onClose}>
    {isFirstLogin&&<div style={{padding:"8px 12px",background:"var(--gp)",borderRadius:8,marginBottom:12,fontSize:13,color:"var(--g)"}}>
      👋 Welkom! Stel eerst een persoonlijk wachtwoord in voordat je verder gaat.
    </div>}
    {ok?<div style={{textAlign:"center",padding:"16px 0"}}>
      <div style={{fontSize:36,marginBottom:8}}>✅</div>
      <p style={{fontSize:14,color:"var(--g)"}}>Wachtwoord succesvol ingesteld!</p>
      <button className="fbtn" style={{marginTop:12}} onClick={handleVerder}>Verder naar de app →</button>
    </div>:<form onSubmit={e=>{e.preventDefault();save();}} autoComplete="off">
      <div className="fg"><label className="fl">Nieuw wachtwoord</label>
        <input className="fi" type="password" autoComplete="new-password" value={pw} onChange={e=>{setPw(e.target.value);setErr("");}} placeholder="Minimaal 6 tekens"/>
      </div>
      <div className="fg"><label className="fl">Bevestig wachtwoord</label>
        <input className="fi" type="password" autoComplete="new-password" value={bevestig} onChange={e=>{setBevestig(e.target.value);setErr("");}} placeholder="Herhaal wachtwoord"/>
      </div>
      {err&&<p style={{fontSize:12,color:"var(--red)",marginBottom:8}}>{err}</p>}
      <PawBtn type="submit" loading={loading}>Wachtwoord opslaan</PawBtn>
    </form>}
  </Modal>;
}


function PublicVirtueelParkPage({parkId, park, onBack, soorten=[]}){
  const TYPE_ICON={"voliere":"🐦","stal":"🐄","aquarium":"🐠","terrarium":"🦎","wei":"🌿","ren":"🐔","hok":"🪵","afdak":"⛺","bak":"🪣","overig":"🏠"};
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [verblijven,setVerblijven]=useState([]);
  const [bewoners,setBewoners]=useState({});
  const [expanded,setExpanded]=useState(null);
  const [playingSound,setPlayingSound]=useState(null);
  const [selectedBewoner,setSelectedBewoner]=useState(null); // {type:'dier'|'groep', data:bewoner}
  const [carouselIdx,setCarouselIdx]=useState(0);
  const audioRef=useRef(null);

  useEffect(()=>{
    let cancelled=false;
    const load=async()=>{
      setLoading(true); setError("");
      try{
        const vList=await db.getVerblijvenByPark(parkId,true);
        if(cancelled) return;
        setVerblijven(vList);
        const bMap={};
        await Promise.all(vList.map(async v=>{
          try{ bMap[v.id]=await db.getBewoners(v.id); }catch{ bMap[v.id]=[]; }
        }));
        if(!cancelled) setBewoners(bMap);
      }catch(e){
        if(!cancelled) setError("Er ging iets mis bij het laden.");
      }finally{
        if(!cancelled) setLoading(false);
      }
    };
    load();
    return ()=>{ cancelled=true; if(audioRef.current){ audioRef.current.pause(); audioRef.current=null; } };
  },[parkId]);

  const playSound=(url,id,e)=>{
    if(e) e.stopPropagation();
    if(!url) return;
    if(audioRef.current) audioRef.current.pause();
    const audio=new Audio(url);
    audioRef.current=audio;
    setPlayingSound(id);
    audio.onended=()=>setPlayingSound(c=>c===id?null:c);
    audio.onerror=()=>setPlayingSound(null);
    audio.play().catch(()=>setPlayingSound(null));
  };

  // Verblijf icoontje: handmatig > afgeleid van bewoners > type-icoon
  const getVerblijfIcon=(v)=>{
    if(v.icoon) return v.icoon;
    const bList=bewoners[v.id]||[];
    // Tel soort_id voorkomens bij individuele dieren
    const tally={};
    bList.forEach(b=>{ if(b.dier_id&&b.dieren?.soort_id){ tally[b.dieren.soort_id]=(tally[b.dieren.soort_id]||0)+1; } });
    const topSoortId=Object.entries(tally).sort((a,b)=>b[1]-a[1])[0]?.[0];
    if(topSoortId){ const soort=soorten.find(s=>String(s.id)===String(topSoortId)); if(soort?.icon) return soort.icon; }
    return TYPE_ICON[v.type]||"🏠";
  };

  // Leeftijd berekenen
  const berekenLeeftijd=(geboortedatum)=>{
    if(!geboortedatum) return null;
    const birth=new Date(geboortedatum); const today=new Date();
    const jaren=today.getFullYear()-birth.getFullYear();
    const maanden=today.getMonth()-birth.getMonth()+(today.getDate()<birth.getDate()?-1:0);
    const totaalMaanden=jaren*12+maanden;
    if(totaalMaanden<3) return `${Math.floor(totaalMaanden*4.3)} weken`;
    if(totaalMaanden<24) return `${totaalMaanden} maanden`;
    return `${jaren} jaar`;
  };

  const [dierInfo,setDierInfo]=useState([]);
  const [loadingInfo,setLoadingInfo]=useState(false);

  const openDier=(b,e)=>{
    e.stopPropagation(); setCarouselIdx(0); setSelectedBewoner({type:"dier",data:b});
    if(b.dieren?.id||b.dieren?.soort_id){
      setLoadingInfo(true);
      db.getDierInfo({dierId:b.dieren.id, soortId:b.dieren.soort_id, rasId:b.dieren.ras_id||null, parkId:parkId})
        .then(d=>setDierInfo(d||[])).catch(()=>setDierInfo([])).finally(()=>setLoadingInfo(false));
    }
  };
  const openGroep=(b,e)=>{ e.stopPropagation(); setCarouselIdx(0); setSelectedBewoner({type:"groep",data:b}); setDierInfo([]); };
  const sluitDrawer=()=>{ setSelectedBewoner(null); setDierInfo([]); };

  const parkNaam=park?.naam||"Dierenpark";

  const DierDrawer=()=>{
    const [openCats,setOpenCats]=useState({});
    if(!selectedBewoner) return null;
    const {type,data:b}=selectedBewoner;
    const isDier=type==="dier";
    const d=isDier?b.dieren:null;
    const fotos=isDier?(d?.fotos||[]):(b.groep_foto_url?[b.groep_foto_url]:[]);
    const soort=isDier?soorten.find(s=>s.id===d?.soort_id):null;
    const leeftijd=isDier?berekenLeeftijd(d?.geboortedatum):null;
    const ras=isDier?d?.rassen?.ras_naam:null;
    const subs=!isDier?(b.groep_subsoorten||[]):[];
    const naam=isDier?(d?.naam||"Onbekend"):(b.groep_naam||"Groep");

    const rows=[];
    if(isDier){
      if(soort) rows.push({label:"Soort",val:`${soort.icon} ${soort.naam}${ras?` \u00b7 ${ras}`:""}`});
      if(d?.geslacht) rows.push({label:"Geslacht",val:d.geslacht==="man"?"\u2642 Man":"\u2640 Vrouw"});
      if(leeftijd) rows.push({label:"Leeftijd",val:leeftijd});
      if(d?.geboortedatum) rows.push({label:"Geboren",val:new Date(d.geboortedatum).toLocaleDateString("nl-NL")});
      if(d?.herkomst) rows.push({label:"Herkomst",val:d.herkomst});
    } else {
      if(b.groep_aantal) rows.push({label:"Aantal",val:`${b.groep_aantal} dieren`});
      if(subs.length>0) rows.push({label:"Subgroepen",val:null,subs});
    }

    const infoPerCat={};
    if(isDier) Object.keys(BEZOEKER_VRAGEN).forEach(cat=>{
      const items=BEZOEKER_VRAGEN[cat].map(({code,label})=>{
        const cands=dierInfo.filter(r=>r.categorie===cat&&r.vraag===code);
        const dierRec=cands.find(r=>r.dier_id===d?.id);
        const rasRec=cands.find(r=>r.ras_id&&!r.dier_id);
        const soortParkRec=cands.find(r=>r.soort_id&&!r.dier_id&&r.park_id);
        const soortPlatformRec=cands.find(r=>r.soort_id&&!r.dier_id&&!r.park_id);
        const rec=dierRec||rasRec||soortParkRec||soortPlatformRec;
        if(!rec?.tekst) return null;
        const niveau=dierRec?"dier":rasRec?"ras":"soort";
        return {label,tekst:rec.tekst,niveau};
      }).filter(Boolean);
      if(items.length>0) infoPerCat[cat]=items;
    });
    const catIcons={algemeen:"\uD83C\uDF3F",voeding:"\uD83E\uDD55",verzorging:"\uD83E\uDDF4"};

    return <div style={{position:"fixed",inset:0,zIndex:1000,background:"#f8f5ee",overflowY:"auto",animation:"fadeIn .2s"}}>
      <div style={{position:"relative",width:"100%",aspectRatio:"4/3",background:"linear-gradient(135deg,#f0f7eb,#e8f4e0)",maxHeight:"45vh",overflow:"hidden"}}>
        {fotos.length>0
          ?<img src={fotos[carouselIdx]} alt={naam} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          :<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:96}}>{soort?.icon||"\uD83D\uDC3E"}</div>
        }
        {fotos.length>1&&<>
          <button onClick={e=>{e.stopPropagation();setCarouselIdx(i=>(i-1+fotos.length)%fotos.length);}} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",width:36,height:36,borderRadius:18,border:"none",background:"rgba(0,0,0,.4)",color:"#fff",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
          <button onClick={e=>{e.stopPropagation();setCarouselIdx(i=>(i+1)%fotos.length);}} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",width:36,height:36,borderRadius:18,border:"none",background:"rgba(0,0,0,.4)",color:"#fff",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
          <div style={{position:"absolute",bottom:10,left:0,right:0,display:"flex",justifyContent:"center",gap:5}}>
            {fotos.map((_,i)=><div key={i} onClick={e=>{e.stopPropagation();setCarouselIdx(i);}} style={{width:7,height:7,borderRadius:4,background:i===carouselIdx?"#fff":"rgba(255,255,255,.5)",cursor:"pointer"}}/>)}
          </div>
        </>}
        <button onClick={sluitDrawer} style={{position:"absolute",top:14,left:14,width:36,height:36,borderRadius:18,border:"none",background:"rgba(0,0,0,.35)",color:"#fff",fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
      </div>
      <div style={{maxWidth:600,margin:"0 auto",padding:"20px 16px 48px"}}>
        <div style={{marginBottom:16}}>
          <h1 style={{fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:800,color:"#1a3d0f",margin:"0 0 4px"}}>{naam}</h1>
          {isDier&&soort&&<div style={{fontSize:15,color:"#5a8a3f",fontWeight:500}}>{soort.icon} {soort.naam}{ras&&<span style={{color:"#aaa",fontWeight:400}}> \u00b7 {ras}</span>}</div>}
        </div>
        {!isDier&&b.groep_geluid_url&&<button onClick={e=>playSound(b.groep_geluid_url,b.id,e)} style={{display:"flex",alignItems:"center",gap:8,background:playingSound===b.id?"#2d5a1b":"#f0f7eb",color:playingSound===b.id?"#fff":"#2d5a1b",border:"none",borderRadius:20,padding:"10px 18px",fontSize:14,fontWeight:600,cursor:"pointer",marginBottom:16}}>
          {playingSound===b.id?"\u23F8 Stoppen":"\u25B6 Hoor het geluid"}
        </button>}
        {rows.length>0&&<div style={{background:"#fff",borderRadius:14,overflow:"hidden",marginBottom:16,boxShadow:"0 1px 6px rgba(0,0,0,.06)"}}>
          {rows.map((r,i)=><div key={i} style={{display:"flex",gap:12,padding:"11px 16px",borderBottom:i<rows.length-1?"1px solid #f0ede6":"none"}}>
            <div style={{minWidth:84,fontSize:12,color:"#999",fontWeight:500,paddingTop:2}}>{r.label}</div>
            {r.subs?<div style={{display:"flex",flexWrap:"wrap",gap:4}}>{r.subs.map((s,j)=><span key={j} style={{background:"#f0f7eb",color:"#2d5a1b",borderRadius:20,padding:"2px 10px",fontSize:12}}>{s.naam}{s.aantal?` (${s.aantal})`:""}</span>)}</div>
            :<div style={{fontSize:14,color:"#1a3d0f",fontWeight:500}}>{r.val}</div>}
          </div>)}
        </div>}
        {isDier&&d?.notitie&&<div style={{background:"#f0f7eb",borderRadius:14,padding:"14px 16px",fontSize:14,color:"#2d5a1b",lineHeight:1.65,marginBottom:16}}>
          <div style={{fontWeight:700,marginBottom:6,fontSize:11,textTransform:"uppercase",letterSpacing:.6,color:"#5a8a3f"}}>\uD83D\uDCDD Over {naam}</div>
          {d.notitie}
        </div>}
        {loadingInfo&&<div style={{textAlign:"center",padding:24,color:"#aaa",fontSize:13}}>⏳ Laden...</div>}
        {!loadingInfo&&isDier&&(()=>{
          // Splits alle items in soort-niveau en dier-niveau
          const soortPerCat={}, dierPerCat={};
          Object.entries(infoPerCat).forEach(([cat,items])=>{
            const si=items.filter(x=>x.niveau!=="dier");
            const di=items.filter(x=>x.niveau==="dier");
            if(si.length>0) soortPerCat[cat]=si;
            if(di.length>0) dierPerCat[cat]=di;
          });
          const heeftSoort=Object.keys(soortPerCat).length>0;
          const heeftDier=Object.keys(dierPerCat).length>0;
          return <>
            {/* ── Soort Informatie ── */}
            {heeftSoort&&<div style={{marginBottom:heeftDier?20:0}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <h2 style={{fontFamily:"'Fraunces',serif",fontSize:18,fontWeight:800,color:"#1a3d0f",margin:0}}>📖 Soort Informatie</h2>
                <button onClick={()=>setOpenCats(p=>({...p,soortblok:!p.soortblok}))} style={{fontSize:12,color:"#5a8a3f",background:"#f0f7eb",border:"none",borderRadius:20,padding:"4px 12px",cursor:"pointer",fontWeight:600}}>
                  {openCats.soortblok?"Verbergen":"Tonen"}
                </button>
              </div>
              {openCats.soortblok&&<div style={{display:"grid",gap:10}}>
                {Object.entries(soortPerCat).map(([cat,items])=>{
                  const catKey=`cat_${cat}`;
                  const isOpen=openCats[catKey];
                  return <div key={cat} style={{background:"#f0f7eb",borderRadius:14,overflow:"hidden"}}>
                    <button onClick={()=>setOpenCats(p=>({...p,[catKey]:!isOpen}))}
                      style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",background:"none",border:"none",cursor:"pointer"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:16}}>{catIcons[cat]}</span>
                        <span style={{fontWeight:700,fontSize:14,color:"#1a3d0f"}}>{CAT_LABELS[cat].split(" ")[1]}</span>
                      </div>
                      <span style={{fontSize:12,color:"#5a8a3f"}}>{isOpen?"▲":"▼"}</span>
                    </button>
                    {isOpen&&<div style={{display:"grid",gap:8,padding:"0 12px 12px"}}>
                      {items.map(({label,tekst},i)=><div key={i} style={{background:"#fff",borderRadius:10,padding:"10px 14px"}}>
                        <div style={{fontSize:11,fontWeight:700,color:"#5a8a3f",textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>{label}</div>
                        <div style={{fontSize:14,color:"#2a2a2a",lineHeight:1.65}}>{tekst}</div>
                      </div>)}
                    </div>}
                  </div>;
                })}
              </div>}
            </div>}
            {/* ── Over Mij ── */}
            {heeftDier&&<div>
              <h2 style={{fontFamily:"'Fraunces',serif",fontSize:18,fontWeight:800,color:"#1a3d0f",margin:"0 0 10px"}}>🐾 Over Mij</h2>
              <div style={{display:"grid",gap:10}}>
                {Object.entries(dierPerCat).map(([cat,items])=><div key={cat}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                    <span style={{fontSize:14}}>{catIcons[cat]}</span>
                    <span style={{fontWeight:600,fontSize:13,color:"#5a8a3f"}}>{CAT_LABELS[cat].split(" ")[1]}</span>
                  </div>
                  <div style={{display:"grid",gap:8}}>
                    {items.map(({label,tekst},i)=><div key={i} style={{background:"#fff",borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 6px rgba(0,0,0,.06)",borderLeft:"3px solid #a8d890"}}>
                      <div style={{fontSize:11,fontWeight:700,color:"#5a8a3f",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>{label}</div>
                      <div style={{fontSize:14,color:"#2a2a2a",lineHeight:1.65}}>{tekst}</div>
                    </div>)}
                  </div>
                </div>)}
              </div>
            </div>}
          </>;
        })()}
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </div>;
  };
  return <div style={{minHeight:"100vh",background:"linear-gradient(180deg,#f8f5ee 0%,#fff 100%)",padding:"0 0 60px"}}>
    {/* Header */}
    <div style={{background:"linear-gradient(135deg,#2d5a1b 0%,#1a3d0f 100%)",padding:"20px 16px 24px",position:"relative"}}>
      <button onClick={onBack} style={{position:"absolute",top:16,left:16,background:"rgba(255,255,255,0.15)",border:"none",borderRadius:8,padding:"6px 10px",color:"#fff",fontSize:13,cursor:"pointer"}}>← Terug</button>
      <div style={{textAlign:"center",paddingTop:8}}>
        <div style={{fontSize:36,marginBottom:4}}>🏡</div>
        <h1 style={{fontFamily:"'Fraunces',serif",color:"#fff",fontSize:24,margin:"0 0 4px",fontWeight:800}}>{parkNaam}</h1>
        <p style={{color:"rgba(255,255,255,0.8)",fontSize:13,margin:0}}>Virtueel Parkje · Ontdek onze verblijven</p>
      </div>
    </div>

    <div style={{maxWidth:600,margin:"0 auto",padding:"16px 16px 0"}}>
      {loading&&<div style={{textAlign:"center",padding:48,color:"var(--txtl)"}}>
        <div style={{fontSize:40,marginBottom:12,animation:"bounce 1.2s infinite"}}>🐾</div>
        <div>Laden...</div>
      </div>}

      {error&&<div style={{textAlign:"center",padding:32,color:"var(--red)"}}>{error}</div>}

      {!loading&&!error&&verblijven.length===0&&<div style={{textAlign:"center",padding:48,color:"var(--txtl)"}}>
        <div style={{fontSize:40,marginBottom:12}}>🏡</div>
        <div style={{fontWeight:600,marginBottom:8}}>Nog geen verblijven</div>
        <div style={{fontSize:13}}>Dit park heeft nog geen virtuele verblijven ingesteld.</div>
      </div>}

      {/* Verblijven */}
      {!loading&&verblijven.map(v=>{
        const bList=bewoners[v.id]||[];
        const isOpen=expanded===v.id;
        const icon=getVerblijfIcon(v);
        return <div key={v.id} style={{background:"#fff",borderRadius:16,boxShadow:"0 2px 12px rgba(0,0,0,.07)",marginBottom:12,overflow:"hidden",border:isOpen?"2px solid #2d5a1b":"2px solid transparent",transition:"border .2s"}}>
          {/* Verblijf header */}
          <div onClick={()=>setExpanded(isOpen?null:v.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:"pointer",userSelect:"none"}}>
            <div style={{fontSize:32,width:44,textAlign:"center",flexShrink:0,lineHeight:1}}>{icon}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:16,color:"#1a3d0f",fontFamily:"'Fraunces',serif"}}>{v.naam}</div>
              {v.omschrijving&&<div style={{fontSize:12,color:"#888",marginTop:2}}>{v.omschrijving}</div>}
              <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{bList.length} bewoner{bList.length!==1?"s":""}</div>
            </div>
            <div style={{fontSize:20,color:"#2d5a1b",transition:"transform .2s",transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▾</div>
          </div>

          {/* Bewoners */}
          {isOpen&&<div style={{borderTop:"1px solid #f0ede6",padding:"12px 16px",background:"#faf9f6"}}>
            {bList.length===0&&<div style={{color:"#aaa",fontSize:13,textAlign:"center",padding:16}}>Geen bewoners in dit verblijf.</div>}
            <div style={{display:"grid",gap:10}}>
              {bList.map(b=>{
                if(b.dier_id&&b.dieren){
                  const d=b.dieren;
                  const foto=d.fotos?.[0];
                  const soort=soorten.find(s=>s.id===d.soort_id);
                  return <div key={b.id} onClick={e=>openDier(b,e)} style={{display:"flex",gap:12,alignItems:"center",background:"#fff",borderRadius:12,padding:10,boxShadow:"0 1px 4px rgba(0,0,0,.05)",cursor:"pointer",transition:"box-shadow .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow="0 3px 12px rgba(45,90,27,.15)"}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.05)"}>
                    <div style={{width:56,height:56,borderRadius:12,overflow:"hidden",flexShrink:0,background:"#f0f7eb",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {foto?<img src={foto} alt={d.naam} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:28}}>{soort?.icon||"🐾"}</span>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:14,color:"#1a3d0f"}}>{d.naam}</div>
                      <div style={{fontSize:12,color:"#5a8a3f"}}>{soort?`${soort.icon} ${soort.naam}`:d.soort}</div>
                      {d.rassen?.ras_naam&&<div style={{fontSize:11,color:"#aaa"}}>{d.rassen.ras_naam}</div>}
                    </div>
                    <div style={{fontSize:16,color:"#c0d8b0"}}>›</div>
                  </div>;
                } else {
                  const subs=b.groep_subsoorten||[];
                  return <div key={b.id} onClick={e=>openGroep(b,e)} style={{background:"#fff",borderRadius:12,padding:10,boxShadow:"0 1px 4px rgba(0,0,0,.05)",cursor:"pointer",transition:"box-shadow .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow="0 3px 12px rgba(45,90,27,.15)"}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.05)"}>
                    <div style={{display:"flex",gap:12,alignItems:"center"}}>
                      <div style={{width:56,height:56,borderRadius:12,overflow:"hidden",flexShrink:0,background:"#f0f7eb",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {b.groep_foto_url?<img src={b.groep_foto_url} alt={b.groep_naam} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:28}}>🐾</span>}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:14,color:"#1a3d0f"}}>{b.groep_naam}</div>
                        {b.groep_aantal&&<div style={{fontSize:12,color:"#5a8a3f"}}>{b.groep_aantal} dieren</div>}
                        {subs.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:3,marginTop:3}}>
                          {subs.slice(0,3).map((s,i)=><span key={i} style={{background:"#f0f7eb",color:"#2d5a1b",borderRadius:20,padding:"1px 7px",fontSize:10}}>{s.naam}{s.aantal?` (${s.aantal})`:""}</span>)}
                          {subs.length>3&&<span style={{fontSize:10,color:"#aaa",padding:"1px 4px"}}>+{subs.length-3}</span>}
                        </div>}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        {b.groep_geluid_url&&<button onClick={e=>playSound(b.groep_geluid_url,b.id,e)} style={{width:32,height:32,borderRadius:16,border:"none",background:playingSound===b.id?"#2d5a1b":"#f0f7eb",color:playingSound===b.id?"#fff":"#2d5a1b",fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {playingSound===b.id?"⏸":"▶"}
                        </button>}
                        <div style={{fontSize:16,color:"#c0d8b0"}}>›</div>
                      </div>
                    </div>
                  </div>;
                }
              })}
            </div>
          </div>}
        </div>;
      })}
    </div>

    <DierDrawer/>
    <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}`}</style>
  </div>;
}

function InviteActivatiePagina({token}){
  const supabaseUrl = decodeURIComponent(token);
  const handleActiveer = () => { window.location.href = supabaseUrl; };
  return <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#f5f5f0",padding:20}}>
    <div style={{background:"#fff",borderRadius:16,padding:32,maxWidth:400,width:"100%",textAlign:"center",boxShadow:"0 4px 24px rgba(0,0,0,.08)"}}>
      <div style={{fontSize:48,marginBottom:12}}>🐾</div>
      <h1 style={{fontFamily:"'Fraunces',serif",color:"#2d5016",fontSize:24,margin:"0 0 8px"}}>Dierenparkje.nl</h1>
      <p style={{color:"var(--txtl)",fontSize:14,marginBottom:24,lineHeight:1.6}}>
        Je bent uitgenodigd om mee te werken in de app. Klik op de knop hieronder om je account te activeren en een wachtwoord in te stellen.
      </p>
      <button onClick={handleActiveer} style={{background:"#2d5016",color:"#fff",border:"none",borderRadius:10,padding:"14px 32px",fontSize:16,fontWeight:600,cursor:"pointer",width:"100%"}}>
        Account activeren →
      </button>
      <p style={{fontSize:11,color:"#ccc",marginTop:16}}>Deze link is 24 uur geldig na ontvangst van de email.</p>
    </div>
  </div>;
}

export default function App(){
  const [soorten, setSoorten] = useState([]);
  
  useEffect(() => {
    const fetchSoorten = async () => {
      try {
        const {data} = await sb.from("soorten").select("*").order("naam");
        setSoorten(data || []);
      } catch(err) {
        console.error("Error fetching soorten:", err);
      }
    };
    fetchSoorten();
  }, []);
  const [photoModal, setPhotoModal] = useState(null); // {photos, dierName, initialIndex, showNav}

  // Ensure Leaflet is loaded for map functionality
  useEffect(()=>{
    if(!window.L){
      const link=document.createElement('link');
      link.rel='stylesheet';
      link.href='https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);
      const script=document.createElement('script');
      script.src='https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      document.head.appendChild(script);
    }
  },[]);

  const [route,setRoute]=useState("landing");
  const [selectedPark,setSelectedPark]=useState(null);
  const [loggedInUser,setLoggedInUser]=useState(null);
  const [isAppAdmin,setIsAppAdmin]=useState(false);
  const [dataReady,setDataReady]=useState(false);
  const [authReady,setAuthReady]=useState(false);
  const [authLoading,setAuthLoading]=useState(false);
  const [loadTimeout,setLoadTimeout]=useState(false);
  const [showWijzigPw,setShowWijzigPw]=useState(false);
  const [adminModal,setAdminModal]=useState(null);
  const [adminEditParkId,setAdminEditParkId]=useState(null);
  const [isFirstLogin,setIsFirstLogin]=useState(false);
  const [bezoeken, setBezoeken] = useState([]); // Visitor park visits
  const [parkAnimals,setParkAnimals]=useState({}); // Cache animals per park
  const [qrParams] = useState(() => {
    const pathMatch = window.location.pathname.match(/^\/park\/([^/]+)\/?$/);
    if(pathMatch){
      return { parkId: decodeURIComponent(pathMatch[1]), isVirtueelPark: true };
    }
    const params = new URLSearchParams(window.location.search);
    const parkId = params.get("park") || "";
    return { parkId, isVirtueelPark: !!parkId };
  });

  const [inviteToken] = useState(() => {
    if(window.location.pathname === "/invite"){
      const params = new URLSearchParams(window.location.search);
      return params.get("t") || "";
    }
    return "";
  });

  // Afgeleide loading state: we laden zolang data OF auth nog niet klaar is
  const loading = !dataReady || !authReady;

  // Timeout — auto-retry na 5 sec, toon knop na 15 sec
  useEffect(() => {
  if (!loading) return;

  // We remove the autoRetryTimer entirely.
  // We only keep the timer that shows the "Retry" UI component.
  const t = setTimeout(() => {
    setLoadTimeout(true);
  }, 10000); // Increased to 10 seconds to be safer

  return () => {
		clearTimeout(t);
	  };
	}, [loading]);


  // Loaded from Supabase
  const [parken,setParken]=useState([]);
  const [actieveIds,setActieveIds]=useState([]);
  // Load parken on mount
  useEffect(()=>{
    async function loadParken(){
      try {
        const dbParken = await db.getAllParken();
        const dbIds = dbParken.map(p=>p.id);
        const merged = [...dbParken, ...PARKEN_STATISCH.filter(p=>!dbIds.includes(p.id))];
        setParken(merged);
        setActieveIds(dbParken.filter(p=>p.actief).map(p=>p.id));
      } catch(e) {
        console.error("Fout bij laden parken:", e);
      }
      setDataReady(true);
    }
    loadParken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    if(!dataReady) return;
    if(qrParams.parkId && qrParams.isVirtueelPark){
      const park = parken.find((p)=>p.id===qrParams.parkId);
      if(park) setSelectedPark(park);
      setRoute("virtueelpark");
    }
  }, [dataReady, parken, qrParams]);

  // Handle Supabase Auth state
  useEffect(()=>{
    // Check URL voor fouten (verlopen links etc.)
    const urlParams = new URLSearchParams(window.location.search);
    const urlError = urlParams.get("error_code");
    if(urlError === "otp_expired"){
      toast.warn("⏱️ Deze link is verlopen. Vraag een nieuwe wachtwoord reset aan via de inlogpagina.");
      window.history.replaceState(null,"","/");
      setAuthReady(true);
      return;
    }

    // Lees hash METEEN — voordat Supabase hem verwerkt
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.replace("#",""));
    const tokenType = hashParams.get("type"); // "invite", "recovery", etc.
    const accessToken = hashParams.get("access_token");
    const hashError = hashParams.get("error");
    const hashErrorCode = hashParams.get("error_code");

    console.log("URL hash type:", tokenType, "token aanwezig:", !!accessToken, "error:", hashError);

    // Verlopen of ongeldige link
    if(hashError){
      window.history.replaceState(null,"","/");
      if(hashErrorCode==="otp_expired"){
        toast.err("Deze uitnodigingslink is verlopen. Vraag de beheerder om een nieuwe uitnodiging te sturen.");
      } else {
        toast.err("Ongeldige link: " + (hashParams.get("error_description")||hashError).replace(/\+/g," "));
      }
      setAuthReady(true);
      return;
    }

    // Als er een token in de hash zit, laat Supabase het verwerken via detectSessionInUrl
    // en luister naar het SIGNED_IN event
    const sessionTimeout = setTimeout(()=>{
      console.warn("Session check timed out");
      setAuthReady(true);
    }, 12000);

    // Luister EERST naar auth changes voordat we sessie checken
    let authHandled = false;
    const {data: {subscription}} = sb.auth.onAuthStateChange(async (event, session)=>{
      console.log("Auth event:", event, "session:", !!session, "tokenType:", tokenType, "authHandled:", authHandled);
      clearTimeout(sessionTimeout);

      if(event==="SIGNED_IN" || event==="TOKEN_REFRESHED"){
        // Skip als INITIAL_SESSION al succesvol was afgehandeld
        if(authHandled){
          console.log("Auth al afgehandeld, skip", event);
          return;
        }
        if(session){
          const isInvite = tokenType==="invite";
          const isRecovery = tokenType==="recovery";
          await handleAuthUser(session.user, isInvite||isRecovery);
          authHandled = true;
          if(isInvite){
            setIsFirstLogin(true); setShowWijzigPw(true);
            window.history.replaceState(null,"","/");
          } else if(isRecovery){
            setIsFirstLogin(false); setShowWijzigPw(true);
            window.history.replaceState(null,"","/");
          }
        }
      } else if(event==="PASSWORD_RECOVERY"){
        if(session){
          await handleAuthUser(session.user, true);
          authHandled = true;
          setIsFirstLogin(false); setShowWijzigPw(true);
          window.history.replaceState(null,"","/");
        }
      } else if(event==="SIGNED_OUT"){
        authHandled = false;
        setLoggedInUser(null);setSelectedPark(null);setIsAppAdmin(false);
        setRoute("landing");setAuthReady(true);
      } else if(event==="INITIAL_SESSION"){
        if(session){
          const isInvite = tokenType==="invite";
          const isRecovery = tokenType==="recovery";
          await handleAuthUser(session.user, isInvite||isRecovery);
          authHandled = true;
          if(isInvite){
            setIsFirstLogin(true); setShowWijzigPw(true);
            window.history.replaceState(null,"","/");
          } else if(isRecovery){
            setIsFirstLogin(false); setShowWijzigPw(true);
            window.history.replaceState(null,"","/");
          }
        } else {
          setAuthReady(true);
        }
      }
    });

    return ()=>subscription?.unsubscribe?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Refresh bezoeken when navigating to mijnparkjes
  useEffect(() => {
    if (route === "mijnparkjes" && loggedInUser) {
      (async () => {
        try {
          const visits = await db.getBezoeken(loggedInUser.id);
          setBezoeken(visits);
          console.log("✅ Bezoeken vernieuwd:", visits.length);
        } catch(err) {
          console.error("Error refreshing bezoeken:", err);
        }
      })();
    }
  }, [route, loggedInUser]);

  const authProcessingRef = useRef(false);

  const handleAuthUser=async(authUser, skipRouting=false)=>{
    // Voorkom dubbele aanroepen die elkaar blokkeren
    if(authProcessingRef.current){
      console.log("handleAuthUser al bezig, overgeslagen voor:", authUser.email);
      return;
    }
    authProcessingRef.current = true;
    setAuthLoading(true);
    try {
      console.log("Auth user:", authUser.id, authUser.email);
      console.log("getUserByAuthId start...");
      const gebruiker = await db.getUserByAuthId(authUser.id);
      console.log("Gevonden gebruiker:", gebruiker);

      if(!gebruiker){
        // Fallback: probeer op email te zoeken
        const {data:byEmail} = await sb.from("gebruikers").select("*").eq("email", authUser.email).maybeSingle();
        console.log("Fallback op email:", byEmail);

        if(byEmail){
          // Koppel auth_id alsnog
          await sb.from("gebruikers").update({auth_id: authUser.id}).eq("id", byEmail.id);
          console.log("auth_id gekoppeld aan bestaande gebruiker");
          authProcessingRef.current = false;
          return handleAuthUser(authUser);
        }

        setAuthLoading(false);setAuthReady(true);
        toast.err(`Account niet gevonden (${authUser.email}). Controleer of je email correct is.`);
        await db.signOut();
        authProcessingRef.current = false;
        return;
      }

      const user={...gebruiker, name: gebruiker.naam, role: gebruiker.role||gebruiker.type, isAppAdmin: (gebruiker.role||gebruiker.type)==="app_admin"};

      // Laad modules voor deelbeheer gebruikers
      if(user.role==="deelbeheer"){
        const modRecords=await db.getGebruikerModules(gebruiker.id);
        const modules={};
        modRecords.forEach(r=>{ modules[r.module]=r.niveau; });
        user.modules=modules;
      }

      setLoggedInUser(user);
      setIsAppAdmin((gebruiker.role||gebruiker.type)==="app_admin");

      // Eerste login na invite → toon wachtwoord modal voor routing
      if(gebruiker.eerste_login){
        setIsFirstLogin(true);
        setShowWijzigPw(true);
        setAuthReady(true);
        authProcessingRef.current=false;
        return;
      }

      if(skipRouting){ setAuthReady(true); authProcessingRef.current=false; return; }

      const hasVirtueelPark = !!(qrParams?.parkId && qrParams?.isVirtueelPark);
      if(hasVirtueelPark){
        const qrPark = parken.find(p=>p.id===qrParams.parkId)
          || (await sb.from("parken").select("*").eq("id", qrParams.parkId).single()).data;
        if(qrPark) setSelectedPark(qrPark);
        setRoute("virtueelpark");
      } else if((gebruiker.role||gebruiker.type)==="app_admin"){
        setSelectedPark(null);
        setRoute("admin");
      } else if(gebruiker.is_bezoeker){
        // Bezoeker - load their visits and go to mijnparkjes
        try {
          const visits = await db.getBezoeken(gebruiker.id);
          setBezoeken(visits);
        } catch(err) {
          console.log("Geen bezoeken geladen (nog geen bezoeken)");
        }
        setRoute("mijnparkjes");
      } else if(gebruiker.park_id){
        const park=parken.find(p=>p.id===gebruiker.park_id)
          || (await sb.from("parken").select("*").eq("id",gebruiker.park_id).single()).data;
        setSelectedPark(park);
        setRoute("app");
      } else {
        toast.err("Je account is niet gekoppeld aan een park. Neem contact op met de beheerder.");
        await db.signOut();
      }
    } catch(e) {
      console.error("Auth error:", e);
      await db.signOut();
    }
    setAuthLoading(false);setAuthReady(true);
    authProcessingRef.current = false;
  };

  const handleLogout=async()=>{
    try {
      // Sign out from Supabase
      await sb.auth.signOut();
      console.log("✅ Signed out from Supabase");
    } catch(err) {
      console.error("Logout error:", err);
    }
    
    // Clear all local state
    setLoggedInUser(null);
    setSelectedPark(null);
    setRoute("landing");
    setBezoeken([]);
    setParkAnimals({});
    
    // Clear localStorage to force clean state
    try {
      const beforeCount = localStorage.length;
      localStorage.clear();
      sessionStorage.clear();
      console.log("🧹 Cleared localStorage (was", beforeCount, "items)");
    } catch(e) {
      console.error("Error clearing storage:", e);
    }
    
    // Force reload after small delay
    setTimeout(()=>window.location.href = "/", 100);
  };

  const handleAdminEnterPark=(park)=>{
    const adminUser={...loggedInUser,isAppAdmin:true};
    setSelectedPark(park);setLoggedInUser(adminUser);setRoute("app");
  };

  const handleExitPark=()=>{
    if(isAppAdmin){setRoute("admin");setSelectedPark(null);}
    else handleLogout();
  };

  const navigate=(target,park)=>{
    if(target!=="virtueelpark" && (window.location.search || window.location.pathname.startsWith("/park/"))){
      window.history.replaceState(null, "", "/");
    }
    if(target==="welkomPark"&&park){
      setSelectedPark(park);
    } else if(target==="parkVisit"&&park){
      setSelectedPark(park);
      // Altijd frisse dieren laden (niet uit cache)
      (async()=>{
        const animals=await db.getDieren(park.id);
        setParkAnimals(prev=>({...prev,[park.id]:animals}));
      })();
    }
    setRoute(target);
  };

  const handleBackFromVirtueelPark=()=>{
    if(loggedInUser?.is_bezoeker){
      navigate("mijnparkjes");
      return;
    }
    if(loggedInUser?.park_id || loggedInUser?.isAppAdmin){
      if(loggedInUser?.isAppAdmin && !selectedPark){
        navigate("admin");
      } else {
        navigate("app");
      }
      return;
    }
    navigate("landing");
  };

  if(!dataReady||!authReady) return <>
    <style>{CSS}</style>
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--cream)",flexDirection:"column",gap:16}}>
      <div style={{fontSize:48,animation:"bounce 1.2s infinite"}}
        >🐾</div>
      <div style={{fontFamily:"'Fraunces',serif",fontSize:18,color:"var(--g)"}}>
        Laden...
      </div>
      {loadTimeout&&<div style={{textAlign:"center",marginTop:8}}>
        <p style={{fontSize:13,color:"var(--txtl)",marginBottom:12}}>Dit duurt langer dan normaal...</p>
        <button className="l-btn l-btn-primary" style={{padding:"10px 24px"}} onClick={()=>window.location.reload()}>
          🔄 Opnieuw proberen
        </button>
      </div>}
      <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}`}</style>
    </div>
  </>;

  const inner = <>
    <style>{CSS}</style>
    {route==="landing"&&<LandingPage onNavigate={navigate} loggedInUser={loggedInUser}/>}
    {route==="bezoekerRegister"&&<BezoekerAuthPage onNavigate={navigate} onSuccess={()=>setRoute("landing")} initialMode="register"/>}
    {route==="bezoekerLogin"&&<BezoekerAuthPage onNavigate={navigate} onSuccess={()=>setRoute("mijnparkjes")} initialMode="login"/>}
    {route==="aanvraag"&&<AanvraagPage onNavigate={navigate} db={sb}/>}
    {route==="info"&&<InfoPage onNavigate={navigate}/>}
    {route==="privacy"&&<PrivacyPolicy onClose={()=>navigate("landing")}/>}
    {inviteToken&&<InviteActivatiePagina token={inviteToken}/>}
    {route==="virtueelpark"&&qrParams.parkId&&<PublicVirtueelParkPage parkId={qrParams.parkId} park={selectedPark||parken.find(p=>p.id===qrParams.parkId)} onBack={handleBackFromVirtueelPark} soorten={soorten}/>}
    {route==="zoek"&&<ZoekPage onNavigate={navigate} onSelectPark={(park)=>{setSelectedPark(park);setRoute("welkomPark");}} parken={parken} actieveIds={actieveIds} isBezoeker={loggedInUser?.is_bezoeker}/>}
    {route==="mijnparkjes"&&loggedInUser&&<MijnDierenparkjesPage onNavigate={navigate} bezoeken={bezoeken} parken={parken} user={loggedInUser} setSelectedPark={setSelectedPark}/>}
    {route==="welkomPark"&&selectedPark&&<WelkomParkPage onNavigate={navigate} park={selectedPark} user={loggedInUser} onStartSpotting={async()=>{const animals=await db.getDieren(selectedPark.id);setParkAnimals(prev=>({...prev,[selectedPark.id]:animals.filter(a=>a.actief!==false&&!a.datum_overlijden)}));}}/>}
    {route==="parkVisit"&&selectedPark&&<ParkVisitorPage key={selectedPark.id} onNavigate={navigate} park={selectedPark} parken={parken} animals={parkAnimals[selectedPark.id]||[]} loggedInUser={loggedInUser} soorten={soorten} setPhotoModal={setPhotoModal} photoModal={photoModal}/>}
    {route==="login"&&<LoginPage onBack={()=>setRoute("landing")}/>}
    {route==="admin"&&loggedInUser&&<AdminPanel
      onLogout={handleLogout}
      onEnterPark={handleAdminEnterPark}
      onWijzigWachtwoord={()=>setShowWijzigPw(true)}
      parken={parken}
      setParken={setParken}
      actieveIds={actieveIds}
      setActieveIds={setActieveIds}
      loggedInUser={loggedInUser}
      modal={adminModal}
      setModal={setAdminModal}
      editParkId={adminEditParkId}
      setEditParkId={setAdminEditParkId}
    />}
    {route==="app"&&selectedPark&&loggedInUser&&<MgmtApp
      park={selectedPark}
      initialUser={loggedInUser}
      onExitPark={handleExitPark}
      onWijzigWachtwoord={()=>setShowWijzigPw(true)}
      soorten={soorten}
      isAppAdmin={isAppAdmin}
      setPhotoModal={setPhotoModal}
      photoModal={photoModal}
      db={db}
      onParkUpdate={(updates)=>{
        setSelectedPark(prev=>({...prev,...updates}));
        setParken(prev=>prev.map(p=>p.id===selectedPark.id?{...p,...updates}:p));
      }}
    />}
    {showWijzigPw&&<WijzigWachtwoordModal
      isFirstLogin={isFirstLogin}
      onClose={()=>{setShowWijzigPw(false);setIsFirstLogin(false);}}
      onComplete={isFirstLogin&&loggedInUser ? ()=>{
        const g=loggedInUser;
        if((g.role||g.type)==="app_admin"){ setRoute("admin"); }
        else if(g.park_id){
          const park=parken.find(p=>p.id===g.park_id);
          if(park){ setSelectedPark(park); setRoute("app"); }
        }
      } : undefined}
    />}
  </>;
  return <ToastProvider>{inner}</ToastProvider>;
}
